import MemberCviDetails from "./components/MemberCviDetails";
import KpiFormulaTemplates from "./components/KpiFormulaTemplates";
import { AdminPage, AdminPageHeader, AdminPanel, AdminStat, AdminBadge, AdminTabs, AdminSegmented, AdminField, AdminSwitch, AdminEmpty, AdminCallout, AdminProgress, AdminWeight, AdminDialog, initialsOf } from "./components/admin/AdminUi";
import { describeKpiParameters, isStandardKpiTemplate, kpiMethodCopy, kpiTemplateName } from "./components/admin/kpiTemplateCopy";
import { positionReadiness, unitRoutineReadiness } from "./components/admin/readiness";
import { scheduleTemplateValidation, approvalDeadlineFromPeriodEnd } from "./components/admin/scheduleRules";
import { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from "react";
import UiIcon from "./components/UiIcon";
import AccountMenu from "./components/AccountMenu";
import { organizePerformanceNavigation, NavigationItem, isNavigationActive } from "./components/PerformanceNavigation";
import UxProvider, { useUx } from "./components/UxProvider";
import FlexibleOrgPrototype from "./components/FlexibleOrgPrototype";
import IndividualGoalsPage from "./components/IndividualGoalsPage";
import HistoryArchivePage from "./components/HistoryArchivePage";
import CycleManagement from "./core/cycle/CycleManagement";
import { replacePhrases } from "./i18n/phrase-replace";
import { formatPointsChange, normalizeFrenchTypography } from "./i18n/number-format";
import CANADA_COPY_OVERRIDES from "./i18n/canada-overrides";
import { localizePeriodLabel } from "./i18n/period-labels";
import useLiveCvi from "./experience/useLiveCvi";
import { averageScore, cviValue, formatScore, consistentTeamKpiScore, useMemberCvi, useScopedCvi } from "./experience/serverCvi";
import { buildPerformanceHistory, liveEntryFromServer, normalizeSnapshotScores, historicalCviAverage, consecutiveScoreRuns } from "./experience/performance-history";
import { CheckInsPage, RecognitionPage, LearningPage, TeamChallengesPage, PrivacyPage } from "./experience/ExperiencePages";
import { TeamReviewCycleCard } from "./experience/DashboardPanels";
import ExecutiveDashboard from "./experience/ExecutiveDashboard";
import AdminExperience from "./experience/AdminExperience";
import { experienceCopy } from "./experience/copy";
import { NextStepsList, ReviewCycleTracker, GoalsPanel, ContributionsPanel, CheckInPanel, RecognitionPanel, LearningPanel, ChallengesSection, PrivacyFooterLink } from "./experience/DashboardPanels";
import "./styles/cvi-design-system.css";
import "./styles/cvi-experience.css";
import "./styles/cvi-performance.css";
import "./styles/cvi-admin.css";
import "./styles/cvi-workspace.css";
import "./styles/cvi-executive.css";
import "./styles/cvi-canada-polish.css";
import { ReviewInbox } from "./experience/ReviewInbox";
import { WorkCenter } from "./experience/WorkCenter";
import { EvaluationDutiesView, useEvaluationDuties } from "./experience/EvaluationDuties";
import EvaluationAccountabilitySection from "./experience/EvaluationAccountability";
import { evaluationResponsibilityCopy } from "./components/routine/evaluationResponsibilityCopy";
import { PeerFeedbackWorkspace } from "./experience/PeerFeedback";
import { FeedbackItem, STATUS_TONE } from "./experience/WorkspaceUi";
import ContributionForm from "./components/contributions/ContributionForm";
import ContributionDetail from "./components/contributions/ContributionDetail";
import { contributionCopy } from "./components/contributions/contributionCopy";
import { buildWorkflow } from "./components/contributions/contributionWorkflow";
import CoreResponsibilitiesPage from "./components/routine/CoreResponsibilitiesPage";
import ManagerRoutineReview, { RoutineMemberList } from "./components/routine/ManagerRoutineReview";
import EvaluationResponsibilityPanel from "./components/routine/EvaluationResponsibilityPanel";
import EvaluationWorkspace from "./components/evaluation/EvaluationWorkspace";
import TeamDashboardView from "./components/team/TeamDashboardView";
import { indicatorRows as teamIndicatorRowsFor, memberRows } from "./components/team/teamModel";
import { formatStamp, statusLabel, workspaceCopy } from "./experience/workspaceCopy";

// Same-origin /api is proxied to Canada Backend by IIS. Explicit URL can override it.
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL ?? "").replace(/\/+$/, "");

/* ════════════════════════════════════════════════════════════════════════
   CVI — Contribution Value Index
   لاگین واحد + مسیردهی بر اساس نقش + پنل ادمین برای تکمیل داده‌های پایه
   همه‌ی داده‌ها (ساختار سازمانی، افراد، شاخص‌ها، ...) از یک Context مشترک
   خونده می‌شن تا تغییرات ادمین بلافاصله در پنل کارشناس/مدیر منعکس بشه.
   ════════════════════════════════════════════════════════════════════════ */

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
const C = {
    navy: "#000064",
    cyan: "#00C8E6",
    cyanLt: "#F0FDFE",
    cyanBg: "rgba(0,200,230,0.1)",
    white: "#FFFFFF",
    bg: "#F0F2F5",
    card: "#FFFFFF",
    border: "#E8E8EE",
    borderMd: "#E4E4EC",
    text: "#111111",
    muted: "#888888",
    hint: "#AAAAAA",
    purple: "#534AB7",
    purpleLt: "#EEEDFE",
    teal: "#0F6E56",
    tealLt: "#E1F5EE",
    amber: "#854F0B",
    amberLt: "#FAEEDA",
    red: "#E24B4A",
    redLt: "#FCEBEB",
};


function uxNotify(message, tone = null) {
    if (!message) return;
    // Callers should pass a tone; without one, confirmation wording (EN/FR, or legacy server text) means success.
    const SUCCESS = /\b(saved|created|updated|submitted|approved|activated|reopened|finali[sz]ed|locked|archived|restored|deleted|removed|added|sent|successfully)\b|enregistr|créé|mis à jour|soumis|approuv|activé|rouvert|finalisé|archivé|supprimé|ajouté|envoyé|موفق|ذخیره شد|اعمال.*شد|ثبت شد|بازگشایی شد|نهایی.*شد/i;
    const inferred = tone || (SUCCESS.test(String(message)) && !/\b(could not|cannot|failed|unable)\b|impossible|échec|n’a pas pu/i.test(String(message)) ? "success" : "error");
    window.dispatchEvent(new CustomEvent("cvi-notify", { detail: { message: String(message), tone: inferred } }));
}

function uxConfirm(message, options = {}) {
    return new Promise(resolve => window.dispatchEvent(new CustomEvent("cvi-confirm-request", {
        detail: { message: String(message || ""), resolve, ...options }
    })));
}

function uxPrompt(message, options = {}) {
    return new Promise(resolve => window.dispatchEvent(new CustomEvent("cvi-prompt-request", {
        detail: { message: String(message || ""), resolve, ...options }
    })));
}

// ── INTERNATIONALIZATION FOUNDATION ───────────────────────────────────────────
const SUPPORTED_LANGUAGES = {
    en: { label: "English (Canada)", dir: "ltr", locale: "en-CA" },
    fr: { label: "Français (Canada)", dir: "ltr", locale: "fr-CA" },
};

const UI_DICTIONARY = {
    en: {
        "common.back": "Back",
        "common.loading": "Loading...",
        "common.retry": "Try again",
        "common.noActivePeriod": "No active period",
        "common.completed": "Completed",
        "common.pending": "Pending",
        "common.responses": "responses",
        "common.response": "response",
        "common.questionWeight": "Question weight",
        "login.title": "Welcome back",
        "login.subtitle": "Sign in to your CVI workspace",
        "login.username": "Work email or username",
        "login.password": "Password",
        "login.submit": "Sign in",
        "login.submitting": "Signing in...",
        "login.required": "Enter username and password",
        "login.invalid": "Incorrect username or password",
        "login.failed": "Sign in failed. Please try again",
        "login.invalidResponse": "Unexpected response. Please try again.",
        "login.connectionError": "Could not connect to CVI. Check your connection and try again.",
        "peer.title": "Peer Feedback",
        "peer.subtitle": "Feedback requests and feedback received",
        "peer.myReviews": "Feedback to give",
        "peer.myResults": "My feedback result",
        "peer.feedbackAbout": "Feedback about {name}",
        "peer.allRequired": "All questions are required",
        "peer.submitFailed": "Could not submit peer feedback",
        "peer.loading": "Loading peer feedback...",
        "peer.submit": "Submit final feedback",
        "peer.submitting": "Submitting...",
        "peer.noAssignments": "No feedback request has been assigned to you.",
        "peer.waiting": "Awaiting your feedback",
        "peer.start": "Give feedback",
        "peer.sent": "Submitted",
        "peer.notEnabled": "Peer feedback is not enabled for you.",
        "peer.threshold": "Results are shown after at least {min} responses are received.",
        "peer.progress": "So far, {done} of {total} responses have been received.",
        "peer.finalScore": "Final peer feedback score",
        "peer.validResponses": "{count} valid responses",
        "peer.anonymous": "Results are confidential and feedback provider names are not displayed.",
        "rating.5": "Excellent",
        "rating.4": "Good",
        "rating.3": "Average",
        "rating.2": "Weak",
        "rating.1": "Very weak"
    },
    fr: {
        "common.back": "Retour", "common.loading": "Chargement...", "common.retry": "Réessayer",
        "common.noActivePeriod": "Aucune période active", "common.completed": "Terminé", "common.pending": "En attente",
        "common.responses": "réponses", "common.response": "réponse", "common.questionWeight": "Poids de la question",
        "login.title": "Bon retour", "login.subtitle": "Connectez-vous à votre espace CVI", "login.username": "Courriel professionnel ou nom d’utilisateur",
        "login.password": "Mot de passe", "login.submit": "Se connecter", "login.submitting": "Connexion...",
        "login.required": "Saisissez le nom d’utilisateur et le mot de passe", "login.invalid": "Nom d’utilisateur ou mot de passe incorrect",
        "login.failed": "Échec de la connexion. Réessayez", "login.invalidResponse": "Réponse inattendue. Veuillez réessayer.",
        "login.connectionError": "Impossible de joindre CVI. Vérifiez votre connexion et réessayez.",
        "peer.title": "Rétroaction des collègues", "peer.subtitle": "Demandes de rétroaction et rétroactions reçues",
        "peer.myReviews": "Rétroactions à donner", "peer.myResults": "Mon résultat", "peer.feedbackAbout": "Rétroaction sur {name}",
        "peer.allRequired": "Toutes les questions sont obligatoires", "peer.submitFailed": "Impossible d’envoyer la rétroaction",
        "peer.loading": "Chargement des rétroactions...", "peer.submit": "Envoyer la rétroaction finale", "peer.submitting": "Envoi...",
        "peer.noAssignments": "Aucune demande de rétroaction ne vous a été attribuée.", "peer.waiting": "En attente de votre rétroaction",
        "peer.start": "Donner la rétroaction", "peer.sent": "Envoyé", "peer.notEnabled": "La rétroaction des collègues n’est pas activée pour vous.",
        "peer.threshold": "Le résultat s’affiche après réception d’au moins {min} réponses.",
        "peer.progress": "À ce jour, {done} réponses reçues sur {total}.",
        "peer.finalScore": "Résultat final de la rétroaction", "peer.validResponses": "{count} réponses valides",
        "peer.anonymous": "Les résultats sont confidentiels et les noms des répondants ne sont pas affichés.",
        "rating.5": "Excellent", "rating.4": "Bon", "rating.3": "Moyen", "rating.2": "Faible", "rating.1": "Très faible"
    },
    ar: {
        "common.back": "رجوع", "common.loading": "جارٍ التحميل...", "common.retry": "حاول مجددًا",
        "common.noActivePeriod": "لا توجد فترة نشطة", "common.completed": "مكتمل", "common.pending": "قيد الانتظار",
        "common.responses": "ردود", "common.response": "رد", "common.questionWeight": "وزن السؤال",
        "login.title": "تسجيل الدخول", "login.subtitle": "سجّل الدخول باستخدام حسابك", "login.username": "اسم المستخدم",
        "login.password": "كلمة المرور", "login.submit": "دخول", "login.submitting": "جارٍ الدخول...",
        "login.required": "أدخل اسم المستخدم وكلمة المرور", "login.invalid": "اسم المستخدم أو كلمة المرور غير صحيحة",
        "login.failed": "فشل تسجيل الدخول. حاول مرة أخرى", "login.invalidResponse": "استجابة الخادم غير صالحة",
        "login.connectionError": "تعذر الاتصال بالخادم",
        "peer.title": "ملاحظات الزملاء", "peer.subtitle": "التقييمات الموكلة ونتائج الملاحظات المستلمة",
        "peer.myReviews": "تقييماتي", "peer.myResults": "نتيجة ملاحظاتي", "peer.feedbackAbout": "ملاحظات حول {name}",
        "peer.allRequired": "الإجابة على جميع الأسئلة إلزامية", "peer.submitFailed": "تعذر إرسال الملاحظات",
        "peer.loading": "جارٍ تحميل ملاحظات الزملاء...", "peer.submit": "إرسال الملاحظات النهائية", "peer.submitting": "جارٍ الإرسال...",
        "peer.noAssignments": "لم يتم إسناد أي تقييم نشط إليك.", "peer.waiting": "بانتظار التقييم",
        "peer.start": "بدء التقييم", "peer.sent": "تم الإرسال", "peer.notEnabled": "ملاحظات الزملاء غير مفعلة لك.",
        "peer.threshold": "تظهر النتيجة بعد استلام {min} ردود على الأقل.",
        "peer.progress": "حتى الآن اكتمل {done} من أصل {total} تقييمات.",
        "peer.finalScore": "النتيجة النهائية لملاحظات الزملاء", "peer.validResponses": "{count} ردود صالحة",
        "peer.anonymous": "النتائج سرية ولا يتم عرض أسماء المقيمين.",
        "rating.5": "ممتاز", "rating.4": "جيد", "rating.3": "Moderate", "rating.2": "ضعيف", "rating.1": "ضعيف جدًا"
    },
    es: {
        "common.back": "Volver", "common.loading": "Cargando...", "common.retry": "Intentar de nuevo",
        "common.noActivePeriod": "No hay un período activo", "common.completed": "Completado", "common.pending": "Pendiente",
        "common.responses": "respuestas", "common.response": "respuesta", "common.questionWeight": "Peso de la pregunta",
        "login.title": "Iniciar sesión", "login.subtitle": "Inicia sesión con tu cuenta", "login.username": "Usuario",
        "login.password": "Contraseña", "login.submit": "Entrar", "login.submitting": "Iniciando sesión...",
        "login.required": "Introduce usuario y contraseña", "login.invalid": "Usuario o contraseña incorrectos",
        "login.failed": "No se pudo iniciar sesión. Inténtalo de nuevo", "login.invalidResponse": "Respuesta del servidor no válida",
        "login.connectionError": "No se pudo conectar con el servidor",
        "peer.title": "Comentarios de colegas", "peer.subtitle": "Evaluaciones asignadas y comentarios recibidos",
        "peer.myReviews": "Mis evaluaciones", "peer.myResults": "Mi resultado", "peer.feedbackAbout": "Comentarios sobre {name}",
        "peer.allRequired": "Todas las preguntas son obligatorias", "peer.submitFailed": "No se pudieron enviar los comentarios",
        "peer.loading": "Cargando comentarios de colegas...", "peer.submit": "Enviar comentarios finales", "peer.submitting": "Enviando...",
        "peer.noAssignments": "No tienes evaluaciones activas asignadas.", "peer.waiting": "Pendiente de evaluación",
        "peer.start": "Iniciar evaluación", "peer.sent": "Enviado", "peer.notEnabled": "Los comentarios de colegas no están habilitados para ti.",
        "peer.threshold": "El resultado se muestra después de recibir al menos {min} respuestas.",
        "peer.progress": "Hasta ahora se han completado {done} de {total} evaluaciones.",
        "peer.finalScore": "Puntuación final de comentarios", "peer.validResponses": "{count} respuestas válidas",
        "peer.anonymous": "Los resultados son confidenciales y no se muestran los nombres de los evaluadores.",
        "rating.5": "Excelente", "rating.4": "Bueno", "rating.3": "Promedio", "rating.2": "Débil", "rating.1": "Muy débil"
    },
    fa: {
        "common.back": "بازگشت", "common.loading": "در حال بارگذاری...", "common.retry": "تلاش مجدد",
        "common.noActivePeriod": "دوره‌ای فعال نیست", "common.completed": "تکمیل‌شده", "common.pending": "در انتظار",
        "common.responses": "پاسخ", "common.response": "پاسخ", "common.questionWeight": "وزن سؤال",
        "login.title": "ورود به سامانه", "login.subtitle": "با اطلاعات کاربری خود وارد شوید", "login.username": "نام کاربری",
        "login.password": "رمز عبور", "login.submit": "ورود", "login.submitting": "در حال ورود...",
        "login.required": "نام کاربری و رمز عبور را وارد کنید", "login.invalid": "نام کاربری یا رمز عبور اشتباه است",
        "login.failed": "ورود به سامانه انجام نشد. دوباره تلاش کنید", "login.invalidResponse": "پاسخ دریافتی از سرور معتبر نیست",
        "login.connectionError": "ارتباط با سرور برقرار نشد",
        "peer.title": "بازخورد همکاران", "peer.subtitle": "ارزیابی‌های محول‌شده و نتیجه بازخوردهای دریافت‌شده",
        "peer.myReviews": "ارزیابی‌های من", "peer.myResults": "نتیجه بازخورد من", "peer.feedbackAbout": "بازخورد درباره {name}",
        "peer.allRequired": "پاسخ همه سؤال‌ها الزامی است", "peer.submitFailed": "ارسال بازخورد انجام نشد",
        "peer.loading": "در حال دریافت بازخورد همکاران...", "peer.submit": "ارسال نهایی بازخورد", "peer.submitting": "در حال ارسال...",
        "peer.noAssignments": "ارزیابی فعالی به شما محول نشده است.", "peer.waiting": "در انتظار ارزیابی",
        "peer.start": "شروع ارزیابی", "peer.sent": "ارسال‌شده", "peer.notEnabled": "بازخورد همکاران برای شما فعال نشده است.",
        "peer.threshold": "نتیجه پس از دریافت حداقل {min} پاسخ نمایش داده می‌شود.",
        "peer.progress": "تا این لحظه {done} از {total} ارزیابی تکمیل شده است.",
        "peer.finalScore": "امتیاز نهایی بازخورد همکاران", "peer.validResponses": "{count} پاسخ معتبر",
        "peer.anonymous": "نتایج محرمانه‌اند و نام ارزیاب‌ها نمایش داده نمی‌شود.",
        "rating.5": "عالی", "rating.4": "خوب", "rating.3": "Moderate", "rating.2": "ضعیف", "rating.1": "بسیار ضعیف"
    }
};

const SETTINGS_DICTIONARY = {
    en: {
        "language.label": "Language",
        "login.tagline": "Make contribution visible. Connect everyday work to team, business unit and organizational outcomes.",
        "language.display": "Language & display",
        "language.description": "Choose the language used across CVI. The interface direction changes automatically.",
        "language.saved": "Language preference saved",
        "language.localOnly": "Language changed on this device. Server preference is not available yet.",
        "language.saveFailed": "The language changed, but your preference could not be saved.",
        "settings.account": "Account settings",
        "settings.profile": "Profile",
        "settings.currentLanguage": "Interface language"
    },
    fr: {
        "language.label": "Langue",
        "login.tagline": "Rendez la contribution visible. Reliez le travail quotidien aux résultats de l’équipe et de l’organisation.",
        "language.display": "Langue et affichage",
        "language.description": "Choisissez la langue utilisée dans CVI. Le sens de l’interface s’adapte automatiquement.",
        "language.saved": "Préférence de langue enregistrée",
        "language.localOnly": "La langue a été modifiée sur cet appareil. La préférence serveur n’est pas encore disponible.",
        "language.saveFailed": "La langue a été modifiée, mais votre préférence n’a pas pu être enregistrée.",
        "settings.account": "Paramètres du compte",
        "settings.profile": "Profil",
        "settings.currentLanguage": "Langue de l’interface"
    },
    ar: {
        "language.label": "اللغة",
        "login.tagline": "الأداء الفردي المتوافق مع المؤشرات الاستراتيجية",
        "language.display": "اللغة والعرض",
        "language.description": "اختر اللغة المستخدمة في CVI. يتغير اتجاه الواجهة تلقائيًا.",
        "language.saved": "تم حفظ تفضيل اللغة",
        "language.localOnly": "تم تغيير اللغة على هذا الجهاز. تفضيل الخادم غير متاح بعد.",
        "language.saveFailed": "تم تغيير اللغة، ولكن تعذر حفظ التفضيل على الخادم.",
        "settings.account": "إعدادات الحساب",
        "settings.profile": "الملف الشخصي",
        "settings.currentLanguage": "لغة الواجهة"
    },
    es: {
        "language.label": "Idioma",
        "login.tagline": "Desempeño individual alineado con los indicadores estratégicos",
        "language.display": "Idioma y visualización",
        "language.description": "Elige el idioma utilizado en CVI. La dirección de la interfaz cambia automáticamente.",
        "language.saved": "Preferencia de idioma guardada",
        "language.localOnly": "El idioma cambió en este dispositivo. La preferencia del servidor aún no está disponible.",
        "language.saveFailed": "El idioma cambió, pero no se pudo guardar la preferencia en el servidor.",
        "settings.account": "Configuración de la cuenta",
        "settings.profile": "Perfil",
        "settings.currentLanguage": "Idioma de la interfaz"
    },
    fa: {
        "language.label": "زبان",
        "login.tagline": "ارزش مشارکت؛ حاصل عملکرد فردی و موفقیت جمعی",
        "language.display": "زبان و نمایش",
        "language.description": "زبان مورد استفاده در کل CVI را انتخاب کنید. جهت رابط کاربری به‌صورت خودکار تغییر می‌کند.",
        "language.saved": "زبان انتخابی ذخیره شد",
        "language.localOnly": "زبان روی این دستگاه تغییر کرد. ذخیره ترجیح در سرور هنوز در دسترس نیست.",
        "language.saveFailed": "زبان تغییر کرد، اما ذخیره ترجیح در سرور انجام نشد.",
        "settings.account": "تنظیمات حساب",
        "settings.profile": "پروفایل",
        "settings.currentLanguage": "زبان رابط کاربری"
    }
};


const WORKFLOW_DICTIONARY = {
    en: {
        "workflow.title": "Work center", "workflow.tasks": "Pending tasks", "workflow.history": "History",
        "workflow.emptyTasks": "You have no pending tasks.", "workflow.emptyHistory": "No history is available for this period.",
        "workflow.loading": "Loading work center...", "workflow.error": "Could not load work center.", "workflow.overdue": "Overdue",
        "workflow.refresh": "Refresh", "workflow.close": "Close", "workflow.taskCount": "{count} pending",
        "workflow.activity": "Activity", "workflow.routine": "Routine", "workflow.peer": "Peer feedback",
        "workflow.indicator": "Indicator", "workflow.period": "Period", "workflow.for": "For {name}",
        "file.download": "Download", "file.delete": "Delete", "file.deleting": "Deleting...", "file.failed": "File operation failed"
    },
    fr: {
        "workflow.title": "Centre de travail", "workflow.tasks": "Tâches en attente", "workflow.history": "Historique",
        "workflow.emptyTasks": "Vous n’avez aucune tâche en attente.", "workflow.emptyHistory": "Aucun historique pour cette période.",
        "workflow.loading": "Chargement du centre de travail...", "workflow.error": "Impossible de charger le centre de travail.", "workflow.overdue": "En retard",
        "workflow.refresh": "Actualiser", "workflow.close": "Fermer", "workflow.taskCount": "{count} en attente",
        "workflow.activity": "Activité", "workflow.routine": "Routine", "workflow.peer": "Rétroaction des collègues",
        "workflow.indicator": "Indicateur stratégique", "workflow.period": "Période", "workflow.for": "Pour {name}",
        "file.download": "Télécharger", "file.delete": "Supprimer", "file.deleting": "Suppression...", "file.failed": "Échec de l’opération sur le fichier"
    },
    ar: {
        "workflow.title": "مركز العمل", "workflow.tasks": "المهام المعلقة", "workflow.history": "السجل",
        "workflow.emptyTasks": "لا توجد مهام معلقة.", "workflow.emptyHistory": "لا يوجد سجل لهذه الفترة.",
        "workflow.loading": "جارٍ تحميل مركز العمل...", "workflow.error": "تعذر تحميل مركز العمل.", "workflow.overdue": "متأخر",
        "workflow.refresh": "تحديث", "workflow.close": "إغلاق", "workflow.taskCount": "{count} معلقة",
        "workflow.activity": "نشاط", "workflow.routine": "تقييم دوري", "workflow.peer": "ملاحظات الزملاء",
        "workflow.indicator": "مؤشر استراتيجي", "workflow.period": "الفترة", "workflow.for": "لـ {name}",
        "file.download": "تنزيل", "file.delete": "حذف", "file.deleting": "جارٍ الحذف...", "file.failed": "فشلت عملية الملف"
    },
    es: {
        "workflow.title": "Centro de trabajo", "workflow.tasks": "Tareas pendientes", "workflow.history": "Historial",
        "workflow.emptyTasks": "No tienes tareas pendientes.", "workflow.emptyHistory": "No hay historial para este período.",
        "workflow.loading": "Cargando centro de trabajo...", "workflow.error": "No se pudo cargar el centro de trabajo.", "workflow.overdue": "Vencido",
        "workflow.refresh": "Actualizar", "workflow.close": "Cerrar", "workflow.taskCount": "{count} pendientes",
        "workflow.activity": "Actividad", "workflow.routine": "Rutina", "workflow.peer": "Comentarios de colegas",
        "workflow.indicator": "Indicador estratégico", "workflow.period": "Período", "workflow.for": "Para {name}",
        "file.download": "Descargar", "file.delete": "Eliminar", "file.deleting": "Eliminando...", "file.failed": "Falló la operación del archivo"
    },
    fa: {
        "workflow.title": "مرکز کارها", "workflow.tasks": "کارهای در انتظار", "workflow.history": "تاریخچه یکپارچه",
        "workflow.emptyTasks": "کار در انتظاری ندارید.", "workflow.emptyHistory": "برای این دوره رویدادی ثبت نشده است.",
        "workflow.loading": "در حال دریافت مرکز کارها...", "workflow.error": "دریافت مرکز کارها انجام نشد.", "workflow.overdue": "سررسید گذشته",
        "workflow.refresh": "به‌روزرسانی", "workflow.close": "بستن", "workflow.taskCount": "{count} کار در انتظار",
        "workflow.activity": "فعالیت", "workflow.routine": "ارزیابی وظایف مستمر", "workflow.peer": "بازخورد همکاران",
        "workflow.indicator": "شاخص راهبردی", "workflow.period": "دوره", "workflow.for": "برای {name}",
        "file.download": "دانلود", "file.delete": "حذف", "file.deleting": "در حال حذف...", "file.failed": "عملیات فایل انجام نشد"
    }
};

// Self-reporting labels (employee declaration; zero direct CVI weight)
Object.assign(UI_DICTIONARY.fa, {
    "self.title": "خوداظهاری",
    "self.subtitle": "ثبت ارزیابی شما از عملکرد خود در دوره جاری؛ این بخش مستقیماً در امتیاز CVI وزن ندارد.",
    "self.month1": "ماه اول",
    "self.month2": "ماه دوم",
    "self.month3": "ماه سوم",
    "self.submitted": "ارسال‌شده",
    "self.evaluated": "ارزیابی‌شده",
    "self.draft": "در حال تکمیل",
    "self.deadline": "مهلت ثبت",
    "self.overdue": "با تأخیر",
    "self.pending": "در انتظار",
    "self.noQuestions": "برای این ماه سؤالی تعریف نشده است.",
    "self.history": "تاریخچه خوداظهاری"
});
Object.assign(UI_DICTIONARY.en, {
    "self.title": "Self-Assessment",
    "self.subtitle": "Record your own assessment of your performance for the current period; this section has no direct weight in CVI.",
    "self.month1": "Month 1", "self.month2": "Month 2", "self.month3": "Month 3",
    "self.submitted": "Submitted", "self.evaluated": "Evaluated", "self.draft": "In progress",
    "self.deadline": "Submission deadline", "self.overdue": "Overdue", "self.pending": "Pending",
    "self.noQuestions": "No questions are defined for this month.", "self.history": "Self-assessment history",
    "self.due": "Due {date}", "self.rated": "{done} of {total} rated", "self.saving": "Saving…", "self.saved": "Saved",
    "self.autosave": "Ratings and comments are saved automatically. Submit the month once every question is rated; submitted answers are locked.",
    "self.submit": "Submit {month}", "self.submitting": "Submitting…", "self.lockedNote": "This month has been submitted and is locked.",
    "self.weight": "Weight {value}%", "self.comment": "Comment or supporting evidence (optional)", "self.months": "Months",
    "self.loading": "Loading self-assessment…", "self.noPeriod": "There is no active period. Self-assessment opens when a period is activated."
});
Object.assign(UI_DICTIONARY.fr, {
    "self.title": "Autoévaluation",
    "self.subtitle": "Consignez votre propre évaluation de votre rendement pour la période en cours; cette section n’a pas de poids direct dans le CVI.",
    "self.month1": "Mois 1", "self.month2": "Mois 2", "self.month3": "Mois 3",
    "self.submitted": "Soumis", "self.evaluated": "Évalué", "self.draft": "En cours",
    "self.deadline": "Date limite", "self.overdue": "En retard", "self.pending": "En attente",
    "self.noQuestions": "Aucune question n’est définie pour ce mois.", "self.history": "Historique des autoévaluations",
    "self.due": "Échéance : {date}", "self.rated": "{done} sur {total} évaluées", "self.saving": "Enregistrement…", "self.saved": "Enregistré",
    "self.autosave": "Les évaluations et commentaires sont enregistrés automatiquement. Soumettez le mois lorsque toutes les questions sont évaluées; les réponses soumises sont verrouillées.",
    "self.submit": "Soumettre le {month}", "self.submitting": "Soumission…", "self.lockedNote": "Ce mois a été soumis et est verrouillé.",
    "self.weight": "Pondération {value}\u00a0%", "self.comment": "Commentaire ou preuve à l’appui (facultatif)", "self.months": "Mois",
    "self.loading": "Chargement de l’autoévaluation…", "self.noPeriod": "Aucune période n’est active. L’autoévaluation s’ouvre lorsqu’une période est activée."
});
Object.assign(UI_DICTIONARY.es, {
    "self.title": "Autodeclaración",
    "self.subtitle": "Registra tu propia valoración del desempeño del período actual; esta sección no tiene peso directo en el CVI.",
    "self.month1": "Mes 1", "self.month2": "Mes 2", "self.month3": "Mes 3",
    "self.submitted": "Enviado", "self.evaluated": "Evaluado", "self.draft": "En curso",
    "self.deadline": "Fecha límite", "self.overdue": "Con retraso", "self.pending": "Pendiente",
    "self.noQuestions": "No hay preguntas definidas para este mes.", "self.history": "Historial de autodeclaración"
});
Object.assign(UI_DICTIONARY.ar, {
    "self.title": "الإقرار الذاتي",
    "self.subtitle": "سجّل تقييمك لأدائك في الفترة الحالية؛ لا يحمل هذا القسم وزناً مباشراً في CVI.",
    "self.month1": "الشهر الأول", "self.month2": "الشهر الثاني", "self.month3": "الشهر الثالث",
    "self.submitted": "تم الإرسال", "self.evaluated": "تم التقييم", "self.draft": "قيد الاستكمال",
    "self.deadline": "موعد التسجيل", "self.overdue": "متأخر", "self.pending": "قيد الانتظار",
    "self.noQuestions": "لا توجد أسئلة محددة لهذا الشهر.", "self.history": "سجل الإقرار الذاتي"
});

const LEGACY_UI_TRANSLATIONS = {"پیش‌نویس":{"en":"Draft","fr":"Brouillon","es":"Borrador","ar":"مسودة"},"در انتظار بررسی":{"en":"Pending review","fr":"En attente d’examen","es":"Pendiente de revisión","ar":"بانتظار المراجعة"},"نیازمند اصلاح":{"en":"Revision required","fr":"Révision requise","es":"Revisión requerida","ar":"يتطلب تعديلاً"},"ارسال مجدد":{"en":"Resubmitted","fr":"Soumis de nouveau","es":"Reenviado","ar":"أعيد الإرسال"},"تأییدشده":{"en":"Approved","fr":"Approuvé","es":"Aprobado","ar":"مُعتمد"},"ردشده":{"en":"Rejected","fr":"Rejeté","es":"Rechazado","ar":"مرفوض"},"کارشناس":{"en":"Specialist","fr":"Spécialiste","es":"Especialista","ar":"أخصائي"},"مدیر":{"en":"Manager","fr":"Gestionnaire","es":"Gerente","ar":"مدير"},"سالانه":{"en":"Annual","fr":"Annuel","es":"Anual","ar":"سنوي"},"فصلی":{"en":"Quarterly","fr":"Trimestriel","es":"Trimestral","ar":"ربع سنوي"},"بخش‌های صفحه":{"en":"Page sections","fr":"Sections de la page","es":"Secciones de la página","ar":"أقسام الصفحة"},"تأیید شده":{"en":"Approved","fr":"Approuvé","es":"Aprobado","ar":"تم الاعتماد"},"رد شده":{"en":"Rejected","fr":"Rejeté","es":"Rechazado","ar":"تم الرفض"},"وظایف و فعالیت‌ها":{"en":"Responsibilities & Contributions","fr":"Responsabilités et contributions","es":"Logros y actividades","ar":"الإنجازات والأنشطة"},"فعالیت‌های مستمر":{"en":"Core Responsibilities","fr":"Responsabilités principales","es":"Desempeño de tareas rutinarias","ar":"أداء المهام الروتينية"},"بازخورد همکاران":{"en":"Peer Feedback","fr":"Rétroaction des collègues","es":"Comentarios de colegas","ar":"ملاحظات الزملاء"},"مشارکت جمعی":{"en":"Organizational Performance","fr":"Performance organisationnelle","es":"Contribución colectiva","ar":"المساهمة الجماعية"},"سازمانی":{"en":"Organization","fr":"Organisation","es":"Organización","ar":"المؤسسة"},"معاونت":{"en":"Division","fr":"Division","es":"División","ar":"الإدارة"},"تیمی":{"en":"Team","fr":"Équipe","es":"Equipo","ar":"الفريق"},"لوگو":{"en":"Logo","fr":"Logo","es":"Logotipo","ar":"الشعار"},"خروج":{"en":"Sign out","fr":"Se déconnecter","es":"Cerrar sesión","ar":"تسجيل الخروج"},"رمز فعلی را وارد کنید":{"en":"Enter your current password","fr":"Saisissez votre mot de passe actuel","es":"Introduce tu contraseña actual","ar":"أدخل كلمة المرور الحالية"},"رمز فعلی اشتباه است":{"en":"The current password is incorrect","fr":"Le mot de passe actuel est incorrect","es":"La contraseña actual es incorrecta","ar":"كلمة المرور الحالية غير صحيحة"},"رمز جدید باید حداقل ۳ کاراکتر باشد":{"en":"The new password must be at least 6 characters.","fr":"Le nouveau mot de passe doit contenir au moins 6 caractères.","es":"La nueva contraseña debe tener al menos 3 caracteres","ar":"يجب أن تتكون كلمة المرور الجديدة من 3 أحرف على الأقل"},"تکرار رمز جدید مطابقت ندارد":{"en":"The new password confirmation does not match","fr":"La confirmation du nouveau mot de passe ne correspond pas","es":"La confirmación de la nueva contraseña no coincide","ar":"تأكيد كلمة المرور الجديدة غير متطابق"},"فقط فایل تصویری قابل بارگذاری است":{"en":"Only image files can be uploaded","fr":"Seuls les fichiers image peuvent être téléversés","es":"Solo se pueden cargar archivos de imagen","ar":"يمكن رفع ملفات الصور فقط"},"حجم عکس باید کمتر از ۳ مگابایت باشد":{"en":"The image must be smaller than 3 MB","fr":"L’image doit faire moins de 3 Mo","es":"La imagen debe pesar menos de 3 MB","ar":"يجب أن يكون حجم الصورة أقل من 3 ميغابايت"},"بارگذاری عکس انجام نشد؛ دوباره تلاش کنید":{"en":"Image upload failed; please try again","fr":"Le téléversement de l’image a échoué; réessayez","es":"No se pudo cargar la imagen; inténtalo de nuevo","ar":"فشل رفع الصورة؛ حاول مرة أخرى"},"تغییر عکس":{"en":"Change photo","fr":"Changer la photo","es":"Cambiar foto","ar":"تغيير الصورة"},"بارگذاری عکس":{"en":"Upload photo","fr":"Téléverser une photo","es":"Cargar foto","ar":"رفع صورة"},"بخش‌های بازخورد همکاران":{"en":"Peer feedback sections","fr":"Sections de rétroaction des collègues","es":"Secciones de comentarios de colegas","ar":"أقسام ملاحظات الزملاء"},"ثبت فعالیت انجام نشد":{"en":"Activity could not be saved","fr":"L’activité n’a pas pu être enregistrée","es":"No se pudo guardar la actividad","ar":"تعذر حفظ النشاط"},"داشبورد من":{"en":"My dashboard","fr":"Mon tableau de bord","es":"Mi panel","ar":"لوحة التحكم الخاصة بي"},"فعالیت‌ها":{"en":"Activities","fr":"Activités","es":"Actividades","ar":"الأنشطة"},"شاخص‌های من":{"en":"My Indicators","fr":"Mes indicateurs","es":"Mis indicadores","ar":"مؤشراتي"},"تاریخچه":{"en":"History","fr":"Historique","es":"Historial","ar":"السجل"},"پروفایل":{"en":"Profile","fr":"Profil","es":"Perfil","ar":"الملف الشخصي"},"دوره‌ای فعال نیست":{"en":"No active period","fr":"Aucune période active","es":"No hay un período activo","ar":"لا توجد فترة نشطة"},"ثبت‌نشده":{"en":"Not submitted","fr":"Non soumis","es":"No enviado","ar":"غير مُرسل"},"ارسال‌شده":{"en":"Submitted","fr":"Soumis","es":"Enviado","ar":"تم الإرسال"},"قفل‌شده":{"en":"Locked","fr":"Verrouillé","es":"Bloqueado","ar":"مقفل"},"بسته‌شده بدون داده":{"en":"Closed without data","fr":"Fermé sans données","es":"Cerrado sin datos","ar":"مغلق بدون بيانات"},"نیازمند اقدام":{"en":"Action required","fr":"Action requise","es":"Acción requerida","ar":"يتطلب إجراءً"},"دوره نامشخص":{"en":"Unknown period","fr":"Période inconnue","es":"Período desconocido","ar":"فترة غير معروفة"},"هنوز تعیین نشده":{"en":"Not set yet","fr":"Pas encore défini","es":"Aún no definido","ar":"لم يُحدد بعد"},"علت انحراف":{"en":"Reason for variance","fr":"Raison de l’écart","es":"Motivo de la desviación","ar":"سبب الانحراف"},"اقدام اصلاحی":{"en":"Corrective action","fr":"Action corrective","es":"Acción correctiva","ar":"إجراء تصحيحي"},"بارگذاری مستند":{"en":"Upload document","fr":"Téléverser un document","es":"Cargar documento","ar":"رفع مستند"},"وضعیت شاخص‌های من":{"en":"My indicator status","fr":"État de mes indicateurs","es":"Estado de mis indicadores","ar":"حالة مؤشراتي"},"ثبت‌کننده":{"en":"Data submitter","fr":"Responsable de saisie","es":"Responsable de registro","ar":"مسجل البيانات"},"مالک":{"en":"Owner","fr":"Responsable","es":"Propietario","ar":"المالك"},"تأییدکننده":{"en":"Approver","fr":"Approbateur","es":"Aprobador","ar":"المعتمد"},"امروز آخرین مهلت است":{"en":"Today is the deadline","fr":"Échéance aujourd’hui","es":"Hoy es la fecha límite","ar":"اليوم هو الموعد النهائي"},"یادداشت بررسی (برای اصلاح الزامی)":{"en":"Review note (required for revision)","fr":"Note d’examen (obligatoire pour une révision)","es":"Nota de revisión (obligatoria para solicitar cambios)","ar":"ملاحظة المراجعة (مطلوبة للتعديل)"},"شاخص‌های راهبردی":{"en":"Performance Indicators","fr":"Indicateurs de performance","es":"Indicadores estratégicos","ar":"المؤشرات الاستراتيجية"},"نوع موارد صندوق بررسی":{"en":"Review inbox item type","fr":"Type d’élément de la boîte d’examen","es":"Tipo de elemento de la bandeja de revisión","ar":"نوع عنصر صندوق المراجعة"},"ثبت تصمیم انجام نشد":{"en":"Decision could not be saved","fr":"La décision n’a pas pu être enregistrée","es":"No se pudo guardar la decisión","ar":"تعذر حفظ القرار"},"بررسی فعالیت":{"en":"Activity review","fr":"Examen de l’activité","es":"Revisión de actividad","ar":"مراجعة النشاط"},"بازخورد یا توضیح برای کارشناس...":{"en":"Feedback or note for the specialist...","fr":"Rétroaction ou note pour le spécialiste...","es":"Comentario o nota para el especialista...","ar":"ملاحظة أو تعليق للأخصائي..."},"تیم":{"en":"Team","fr":"Équipe","es":"Equipo","ar":"الفريق"},"درآمد (نمایشی)":{"en":"Revenue (sample)","fr":"Revenu (exemple)","es":"Ingresos (ejemplo)","ar":"الإيرادات (مثال)"},"تومان":{"en":"CAD","fr":"CAD","es":"Toman","ar":"تومان"},"میانگین CVI":{"en":"Average CVI","fr":"CVI moyen","es":"CVI promedio","ar":"متوسط CVI"},"از ۱۰۰":{"en":"out of 100","fr":"sur 100","es":"de 100","ar":"من 100"},"تحقق اهداف":{"en":"Goal attainment","fr":"Atteinte des objectifs","es":"Cumplimiento de objetivos","ar":"تحقيق الأهداف"},"میانگین شاخص‌ها":{"en":"Indicator average","fr":"Moyenne des indicateurs","es":"Promedio de indicadores","ar":"متوسط المؤشرات"},"نفر در تیم":{"en":"team members","fr":"membres de l’équipe","es":"miembros del equipo","ar":"أعضاء الفريق"},"فعالیت تأیید شده":{"en":"approved activities","fr":"activités approuvées","es":"actividades aprobadas","ar":"أنشطة معتمدة"},"همه بررسی شده":{"en":"All reviewed","fr":"Tout a été examiné","es":"Todo revisado","ar":"تمت مراجعة الكل"},"ماه اول":{"en":"Month 1","fr":"Mois 1","es":"Mes 1","ar":"الشهر الأول"},"ماه دوم":{"en":"Month 2","fr":"Mois 2","es":"Mes 2","ar":"الشهر الثاني"},"ماه سوم":{"en":"Month 3","fr":"Mois 3","es":"Mes 3","ar":"الشهر الثالث"},"اعضای تیم":{"en":"Team members","fr":"Membres de l’équipe","es":"Miembros del equipo","ar":"أعضاء الفريق"},"هنوز عضوی به این مدیر متصل نشده":{"en":"No members are assigned to this manager yet","fr":"Aucun membre n’est encore rattaché à ce gestionnaire","es":"Aún no hay miembros asignados a este gerente","ar":"لم يتم تعيين أعضاء لهذا المدير بعد"},"فردی":{"en":"Individual","fr":"Individuel","es":"Individual","ar":"فردي"},"ثبت نشده":{"en":"Not submitted","fr":"Non soumis","es":"No enviado","ar":"غير مُرسل"},"عضوی با این فیلتر پیدا نشد":{"en":"No member matches this filter","fr":"Aucun membre ne correspond à ce filtre","es":"Ningún miembro coincide con este filtro","ar":"لا يوجد عضو مطابق لهذا المرشح"},"عملکرد بالا":{"en":"High performance","fr":"Haute performance","es":"Alto desempeño","ar":"أداء مرتفع"},"عملکرد قابل قبول":{"en":"Acceptable performance","fr":"Performance acceptable","es":"Desempeño aceptable","ar":"أداء مقبول"},"نیازمند توجه":{"en":"Needs attention","fr":"À surveiller","es":"Requiere atención","ar":"يحتاج إلى اهتمام"},"هنوز سؤال وظایف مستمری توسط ادمین تعریف نشده است.":{"en":"No routine questions have been defined by the admin yet.","fr":"Aucune question de routine n’a encore été définie par l’administrateur.","es":"El administrador aún no ha definido preguntas rutinarias.","ar":"لم يحدد المسؤول أي أسئلة روتينية بعد."},"ذخیره ارزیابی وظایف مستمر انجام نشد":{"en":"Routine evaluation could not be saved","fr":"L’évaluation de routine n’a pas pu être enregistrée","es":"No se pudo guardar la evaluación rutinaria","ar":"تعذر حفظ التقييم الروتيني"},"عضوی یافت نشد":{"en":"No member found","fr":"Aucun membre trouvé","es":"No se encontró ningún miembro","ar":"لم يتم العثور على عضو"},"ارزیابی وظایف مستمر":{"en":"Core Responsibilities review","fr":"Évaluation des responsabilités principales","es":"Evaluación rutinaria","ar":"التقييم الروتيني"},"هنوز سؤال وظایف مستمری برای تیم شما توسط ادمین تعریف نشده است.":{"en":"No routine questions have been defined for your team yet.","fr":"Aucune question de routine n’a encore été définie pour votre équipe.","es":"Aún no se han definido preguntas rutinarias para tu equipo.","ar":"لم يتم تحديد أسئلة روتينية لفريقك بعد."},"داشبورد تیم":{"en":"Team dashboard","fr":"Tableau de bord de l’équipe","es":"Panel del equipo","ar":"لوحة تحكم الفريق"},"صندوق بررسی":{"en":"Review inbox","fr":"Boîte d’examen","es":"Bandeja de revisión","ar":"صندوق المراجعة"},"ارزیابی وظایف مستمر مدیرها":{"en":"Managers’ routine evaluation","fr":"Évaluation de routine des gestionnaires","es":"Evaluación rutinaria de gerentes","ar":"التقييم الروتيني للمديرين"},"هنوز مدیری در این معاونت تعریف نشده":{"en":"No manager has been defined in this business unit yet","fr":"Aucun gestionnaire n’a encore été défini dans cette unité d’affaires","es":"Aún no se ha definido ningún gerente en esta división","ar":"لم يتم تحديد مدير في هذه الإدارة بعد"},"هنوز سؤال وظایف مستمری برای این معاونت توسط ادمین تعریف نشده است.":{"en":"No core-responsibility questions have been defined for this business unit yet.","fr":"Aucune question sur les responsabilités principales n’a encore été définie pour cette unité d’affaires.","es":"Aún no se han definido preguntas rutinarias para esta división.","ar":"لم يتم تحديد أسئلة روتينية لهذه الإدارة بعد."},"اعضای معاونت":{"en":"Business Unit members","fr":"Membres de l’unité d’affaires","es":"Miembros de la división","ar":"أعضاء الإدارة"},"هنوز عضوی در این معاونت تعریف نشده":{"en":"No members have been defined in this business unit yet","fr":"Aucun membre n’a encore été défini dans cette unité d’affaires","es":"Aún no se han definido miembros en esta división","ar":"لم يتم تحديد أعضاء في هذه الإدارة بعد"},"داشبورد معاونت":{"en":"Business Unit dashboard","fr":"Tableau de bord de l’unité d’affaires","es":"Panel de la división","ar":"لوحة تحكم الإدارة"},"نهایی":{"en":"Final","fr":"Final","es":"Final","ar":"نهائي"},"موقت":{"en":"Temporary","fr":"Temporaire","es":"Temporal","ar":"مؤقت"},"ارزیابی وظایف مستمر معاون‌ها":{"en":"Business Unit Leads’ routine evaluation","fr":"Évaluation de routine des responsables de direction","es":"Evaluación rutinaria de responsables de división","ar":"التقييم الروتيني لرؤساء الإدارات"},"هنوز معاونی تعریف نشده":{"en":"No business unit lead has been defined yet","fr":"Aucun responsable d’unité d’affaires n’a encore été défini","es":"Aún no se ha definido ningún responsable de división","ar":"لم يتم تحديد رئيس إدارة بعد"},"هنوز سؤال وظایف مستمری برای سطح سازمان توسط ادمین تعریف نشده است.":{"en":"No organization-level routine questions have been defined yet.","fr":"Aucune question de routine au niveau de l’organisation n’a encore été définie.","es":"Aún no se han definido preguntas rutinarias a nivel de organización.","ar":"لم يتم تحديد أسئلة روتينية على مستوى المؤسسة بعد."},"اعضای شرکت":{"en":"Company members","fr":"Membres de l’entreprise","es":"Miembros de la empresa","ar":"أعضاء الشركة"},"هنوز عضوی در شرکت تعریف نشده":{"en":"No company members have been defined yet","fr":"Aucun membre de l’entreprise n’a encore été défini","es":"Aún no se han definido miembros de la empresa","ar":"لم يتم تحديد أعضاء في الشركة بعد"},"داشبورد کل شرکت":{"en":"Company dashboard","fr":"Tableau de bord de l’entreprise","es":"Panel de la empresa","ar":"لوحة تحكم الشركة"},"Select":{"en":"Select","fr":"Sélectionner","es":"Seleccionar","ar":"اختر"},"ذخیره":{"en":"Save","fr":"Enregistrer","es":"Guardar","ar":"حفظ"},"انصراف":{"en":"Cancel","fr":"Annuler","es":"Cancelar","ar":"إلغاء"},"ویرایش":{"en":"Edit","fr":"Modifier","es":"Editar","ar":"تعديل"},"حذف":{"en":"Delete","fr":"Supprimer","es":"Eliminar","ar":"حذف"},"ساختار سازمانی":{"en":"Organizational Structure","fr":"Structure organisationnelle","es":"Estructura organizativa","ar":"الهيكل التنظيمي"},"افراد":{"en":"People","fr":"Personnes","es":"Personas","ar":"الأفراد"},"نقش‌ها و وزن‌دهی":{"en":"Roles and weighting","fr":"Rôles et pondération","es":"Roles y ponderación","ar":"الأدوار والأوزان"},"هیچ نقشی تعریف نشده":{"en":"No roles have been defined","fr":"Aucun rôle n’a été défini","es":"No se han definido roles","ar":"لم يتم تحديد أدوار"},"جمع وزن‌های راهبردی یا فردی یک یا چند نقش ۱۰۰٪ نیست":{"en":"Strategic or individual weights for one or more roles do not total 100%","fr":"Les pondérations stratégiques ou individuelles d’un ou plusieurs rôles ne totalisent pas 100 %","es":"Las ponderaciones estratégicas o individuales de uno o más roles no suman 100 %","ar":"مجموع الأوزان الاستراتيجية أو الفردية لدور واحد أو أكثر لا يساوي 100٪"},"سؤالات وظایف مستمر تیم‌ها":{"en":"Team routine questions","fr":"Questions de routine des équipes","es":"Preguntas rutinarias de equipos","ar":"أسئلة الفرق الروتينية"},"ابتدا تیمی تعریف کنید":{"en":"Define a team first","fr":"Définissez d’abord une équipe","es":"Primero define un equipo","ar":"حدد فريقاً أولاً"},"سؤالات وظایف مستمر معاونت‌ها":{"en":"Division routine questions","fr":"Questions de routine des directions","es":"Preguntas rutinarias de divisiones","ar":"أسئلة الإدارات الروتينية"},"ابتدا معاونتی تعریف کنید":{"en":"Define a division first","fr":"Définissez d’abord une direction","es":"Primero define una división","ar":"حدد إدارة أولاً"},"سؤالات وظایف مستمر سازمان":{"en":"Organization routine questions","fr":"Questions de routine de l’organisation","es":"Preguntas rutinarias de la organización","ar":"أسئلة المؤسسة الروتينية"},"هنوز سؤالی تعریف نشده":{"en":"No questions have been defined yet","fr":"Aucune question n’a encore été définie","es":"Aún no se han definido preguntas","ar":"لم يتم تحديد أسئلة بعد"},"شاخص‌های سازمانی":{"en":"Organization indicators","fr":"Indicateurs de l’organisation","es":"Indicadores de la organización","ar":"مؤشرات المؤسسة"},"هدف‌گذاری دوره جاری":{"en":"Current-cycle target setting","fr":"Définition des cibles de la période en cours","es":"Definición de objetivos del período actual","ar":"تحديد أهداف الفترة الحالية"},"ثبت عملکرد دوره جاری":{"en":"Current-cycle performance entry","fr":"Saisie de la performance de la période en cours","es":"Registro de desempeño del período actual","ar":"إدخال أداء الفترة الحالية"},"تعیین نشده":{"en":"Not set","fr":"Non défini","es":"No definido","ar":"غير محدد"},"،":{"en":",","fr":",","es":",","ar":"،"},"نام معاونت":{"en":"Business unit name","fr":"Nom de l’unité d’affaires","es":"Nombre de la división","ar":"اسم الإدارة"},"مثال: فروش و بازاریابی":{"en":"Example: Sales and Marketing","fr":"Exemple : Ventes et marketing","es":"Ejemplo: Ventas y marketing","ar":"مثال: المبيعات والتسويق"},"معاون مربوطه":{"en":"Business Unit Lead","fr":"Responsable d’unité d’affaires","es":"Responsable de la división","ar":"رئيس الإدارة"},"نام تیم":{"en":"Team name","fr":"Nom de l’équipe","es":"Nombre del equipo","ar":"اسم الفريق"},"مثال: تیم فروش":{"en":"Example: Sales team","fr":"Exemple : Équipe des ventes","es":"Ejemplo: Equipo de ventas","ar":"مثال: فريق المبيعات"},"انتخاب معاونت":{"en":"Select business unit","fr":"Sélectionner une unité d’affaires","es":"Seleccionar división","ar":"اختر الإدارة"},"مدیر تیم":{"en":"Team manager","fr":"Gestionnaire de l’équipe","es":"Gerente del equipo","ar":"مدير الفريق"},"معاونت‌ها":{"en":"Business Units","fr":"Unités d’affaires","es":"Divisiones","ar":"الإدارات"},"تیم‌ها":{"en":"Teams","fr":"Équipes","es":"Equipos","ar":"الفرق"},"بخش‌های ساختار سازمانی":{"en":"Organization structure sections","fr":"Sections de la structure organisationnelle","es":"Secciones de la estructura organizativa","ar":"أقسام الهيكل التنظيمي"},"نام معاونت الزامی است":{"en":"Business unit name is required","fr":"Le nom de l’unité d’affaires est obligatoire","es":"El nombre de la división es obligatorio","ar":"اسم الإدارة مطلوب"},"نام تیم الزامی است":{"en":"Team name is required","fr":"Le nom de l’équipe est obligatoire","es":"El nombre del equipo es obligatorio","ar":"اسم الفريق مطلوب"},"انتخاب معاونت الزامی است":{"en":"Selecting a business unit is required","fr":"La sélection d’une unité d’affaires est obligatoire","es":"Seleccionar una división es obligatorio","ar":"اختيار الإدارة مطلوب"},"معاون":{"en":"Business Unit Lead","fr":"Responsable d’unité d’affaires","es":"Responsable de división","ar":"رئيس إدارة"},"مدیرعامل":{"en":"CEO","fr":"PDG","es":"CEO","ar":"الرئيس التنفيذي"},"ادمین":{"en":"Admin","fr":"Administrateur","es":"Administrador","ar":"مسؤول النظام"},"نام و نام‌خانوادگی":{"en":"Full name","fr":"Nom complet","es":"Nombre completo","ar":"الاسم الكامل"},"مثال: علی رضایی":{"en":"Example: Sophie Tremblay","fr":"Exemple : Sophie Tremblay","es":"Ejemplo: Ali Rezaei","ar":"مثال: علي رضائي"},"کد پرسنلی":{"en":"Employee code","fr":"Code employé","es":"Código de empleado","ar":"رمز الموظف"},"کد ملی":{"en":"National ID","fr":"Identifiant national","es":"Identificación nacional","ar":"الرقم الوطني"},"ایمیل":{"en":"Email","fr":"Courriel","es":"Correo electrónico","ar":"البريد الإلكتروني"},"نام کاربری":{"en":"Username","fr":"Nom d’utilisateur","es":"Usuario","ar":"اسم المستخدم"},"رمز عبور":{"en":"Password","fr":"Mot de passe","es":"Contraseña","ar":"كلمة المرور"},"سطح دسترسی":{"en":"Access level","fr":"Niveau d’accès","es":"Nivel de acceso","ar":"مستوى الوصول"},"انتخاب سطح دسترسی":{"en":"Select access level","fr":"Sélectionner le niveau d’accès","es":"Seleccionar nivel de acceso","ar":"اختر مستوى الوصول"},"نقش":{"en":"Role","fr":"Rôle","es":"Rol","ar":"الدور"},"انتخاب نقش":{"en":"Select role","fr":"Sélectionner le rôle","es":"Seleccionar rol","ar":"اختر الدور"},"انتخاب تیم":{"en":"Select team","fr":"Sélectionner une équipe","es":"Seleccionar equipo","ar":"اختر الفريق"},"مدیر مستقیم":{"en":"Direct manager","fr":"Gestionnaire immédiat","es":"Gerente directo","ar":"المدير المباشر"},"بدون مدیر مستقیم":{"en":"No direct manager","fr":"Aucun gestionnaire immédiat","es":"Sin gerente directo","ar":"بدون مدير مباشر"},"نام الزامی است":{"en":"Name is required","fr":"Le nom est obligatoire","es":"El nombre es obligatorio","ar":"الاسم مطلوب"},"کد پرسنلی الزامی است":{"en":"Employee code is required","fr":"Le code employé est obligatoire","es":"El código de empleado es obligatorio","ar":"رمز الموظف مطلوب"},"کد ملی الزامی است":{"en":"National ID is required","fr":"L’identifiant national est obligatoire","es":"La identificación nacional es obligatoria","ar":"الرقم الوطني مطلوب"},"کد ملی باید ۱۰ رقم باشد":{"en":"National ID must be 10 digits","fr":"L’identifiant national doit comporter 10 chiffres","es":"La identificación nacional debe tener 10 dígitos","ar":"يجب أن يتكون الرقم الوطني من 10 أرقام"},"ایمیل الزامی است":{"en":"Email is required","fr":"Le courriel est obligatoire","es":"El correo electrónico es obligatorio","ar":"البريد الإلكتروني مطلوب"},"ایمیل معتبر نیست":{"en":"Email is not valid","fr":"Le courriel n’est pas valide","es":"El correo electrónico no es válido","ar":"البريد الإلكتروني غير صالح"},"نام کاربری الزامی است":{"en":"Username is required","fr":"Le nom d’utilisateur est obligatoire","es":"El usuario es obligatorio","ar":"اسم المستخدم مطلوب"},"رمز عبور الزامی است":{"en":"Password is required","fr":"Le mot de passe est obligatoire","es":"La contraseña es obligatoria","ar":"كلمة المرور مطلوبة"},"سطح دسترسی الزامی است":{"en":"Access level is required","fr":"Le niveau d’accès est obligatoire","es":"El nivel de acceso es obligatorio","ar":"مستوى الوصول مطلوب"},"برای معاون، انتخاب معاونت الزامی است":{"en":"A division must be selected for a business unit lead","fr":"Une direction doit être sélectionnée pour un responsable d’unité d’affaires","es":"Debe seleccionarse una división para un responsable de división","ar":"يجب اختيار إدارة لرئيس الإدارة"},"برای مدیر/کارشناس، انتخاب تیم الزامی است":{"en":"A team must be selected for a manager/specialist","fr":"Une équipe doit être sélectionnée pour un gestionnaire/spécialiste","es":"Debe seleccionarse un equipo para un gerente/especialista","ar":"يجب اختيار فريق للمدير/الأخصائي"},"نام گروه فعالیت":{"en":"Activity group name","fr":"Nom du groupe d’activités","es":"Nombre del grupo de actividades","ar":"اسم مجموعة الأنشطة"},"سقف امتیاز":{"en":"Maximum score","fr":"Nombre maximal de points","es":"Puntuación máxima","ar":"الحد الأقصى للدرجة"},"آیکون":{"en":"Icon (emoji)","fr":"Icône (emoji)","es":"Icono (emoji)","ar":"الأيقونة (رمز تعبيري)"},"زیرگروه‌ها (با کاما جدا کنید)":{"en":"Subgroups (comma-separated)","fr":"Sous-groupes (séparés par des virgules)","es":"Subgrupos (separados por comas)","ar":"المجموعات الفرعية (مفصولة بفواصل)"},"دوره تخصصی, دوره عمومی":{"en":"Specialized course, general course","fr":"Formation spécialisée, formation générale","es":"Curso especializado, curso general","ar":"دورة تخصصية، دورة عامة"},"عنوان سطح تأثیر":{"en":"Impact level title","fr":"Nom du niveau d’impact","es":"Título del nivel de impacto","ar":"عنوان مستوى التأثير"},"امتیاز":{"en":"Score","fr":"Résultat","es":"Puntuación","ar":"الدرجة"},"عنوان سطح":{"en":"Level title","fr":"Nom du niveau","es":"Título del nivel","ar":"عنوان المستوى"},"لوگو و برند":{"en":"Logo and branding","fr":"Logo et image de marque","es":"Logotipo y marca","ar":"الشعار والهوية"},"دسته‌های فعالیت":{"en":"Activity categories","fr":"Catégories d’activités","es":"Categorías de actividades","ar":"فئات الأنشطة"},"سطوح تأثیر":{"en":"Impact levels","fr":"Niveaux d’impact","es":"Niveles de impacto","ar":"مستويات التأثير"},"روتین":{"en":"Core Responsibilities","fr":"Responsabilités principales","es":"Rutina","ar":"روتيني"},"بخش‌های تنظیمات پایه":{"en":"Base settings sections","fr":"Sections des paramètres de base","es":"Secciones de configuración básica","ar":"أقسام الإعدادات الأساسية"},"تغییر لوگو":{"en":"Change logo","fr":"Changer le logo","es":"Cambiar logotipo","ar":"تغيير الشعار"},"آپلود لوگو":{"en":"Upload logo","fr":"Téléverser le logo","es":"Cargar logotipo","ar":"رفع الشعار"},"نام نقش جدید را وارد کنید:":{"en":"Enter the new role name:","fr":"Saisissez le nom du nouveau rôle :","es":"Introduce el nombre del nuevo rol:","ar":"أدخل اسم الدور الجديد:"},"سطح تیم":{"en":"Team level","fr":"Niveau équipe","es":"Nivel de equipo","ar":"مستوى الفريق"},"سطح معاونت":{"en":"Business Unit level","fr":"Niveau unité d’affaires","es":"Nivel de división","ar":"مستوى الإدارة"},"سطح سازمان":{"en":"Organization level","fr":"Niveau organisation","es":"Nivel de organización","ar":"مستوى المؤسسة"},"وظایف مستمر":{"en":"Core Responsibilities","fr":"Responsabilités principales","es":"Tareas rutinarias","ar":"المهام الروتينية"},"نام نقش و جمع ۱۰۰٪ هر دو بخش را بررسی کنید":{"en":"Check the role name and ensure both sections total 100%","fr":"Vérifiez le nom du rôle et assurez-vous que les deux sections totalisent 100 %","es":"Comprueba el nombre del rol y que ambas secciones sumen 100 %","ar":"تحقق من اسم الدور وأن مجموع كل قسم يساوي 100٪"},"نام گروه الزامی است":{"en":"Group name is required","fr":"Le nom du groupe est obligatoire","es":"El nombre del grupo es obligatorio","ar":"اسم المجموعة مطلوب"},"عنوان الزامی است":{"en":"Title is required","fr":"Le titre est obligatoire","es":"El título es obligatorio","ar":"العنوان مطلوب"},"سازمان":{"en":"Organization","fr":"Organisation","es":"Organización","ar":"المؤسسة"},"نام شاخص":{"en":"Indicator name","fr":"Nom de l’indicateur","es":"Nombre del indicador","ar":"اسم المؤشر"},"مثال: درآمد تیم فروش":{"en":"Example: Sales team revenue","fr":"Exemple : Revenu de l’équipe des ventes","es":"Ejemplo: Ingresos del equipo de ventas","ar":"مثال: إيرادات فريق المبيعات"},"سطح":{"en":"Level","fr":"Niveau","es":"Nivel","ar":"المستوى"},"انتخاب سطح":{"en":"Select level","fr":"Sélectionner le niveau","es":"Seleccionar nivel","ar":"اختر المستوى"},"تیم مربوطه":{"en":"Related team","fr":"Équipe concernée","es":"Equipo correspondiente","ar":"الفريق المعني"},"معاونت مربوطه":{"en":"Related business unit","fr":"Unité d’affaires concernée","es":"División correspondiente","ar":"الإدارة المعنية"},"واحد اندازه‌گیری":{"en":"Unit of measure","fr":"Unité de mesure","es":"Unidad de medida","ar":"وحدة القياس"},"تومان، نفر، درصد":{"en":"CAD, people, percent","fr":"CAD, personnes, pourcentage","es":"Toman, personas, porcentaje","ar":"تومان، أشخاص، نسبة مئوية"},"وزن در سطح خودش":{"en":"Weight within its level","fr":"Pondération dans son niveau","es":"Peso dentro de su nivel","ar":"الوزن ضمن مستواه"},"مالک شاخص":{"en":"Indicator owner","fr":"Responsable de l’indicateur","es":"Responsable del indicador","ar":"مالك المؤشر"},"انتخاب مالک":{"en":"Select owner","fr":"Sélectionner le responsable","es":"Seleccionar responsable","ar":"اختر المالك"},"ثبت‌کننده مقدار واقعی":{"en":"Actual-value submitter","fr":"Responsable de saisie de la valeur réelle","es":"Responsable de registrar el valor real","ar":"مسجل القيمة الفعلية"},"انتخاب ثبت‌کننده":{"en":"Select submitter","fr":"Sélectionner le responsable de saisie","es":"Seleccionar responsable de registro","ar":"اختر المسجل"},"انتخاب تأییدکننده":{"en":"Select approver","fr":"Sélectionner l’approbateur","es":"Seleccionar aprobador","ar":"اختر المعتمد"},"الگوی زمان‌بندی":{"en":"Schedule template","fr":"Modèle de planification","es":"Plantilla de programación","ar":"قالب الجدول الزمني"},"انتخاب الگو":{"en":"Select template","fr":"Sélectionner un modèle","es":"Seleccionar plantilla","ar":"اختر القالب"},"تجمیع نوبت‌ها":{"en":"Occurrence aggregation","fr":"Agrégation des occurrences","es":"Agregación de ocurrencias","ar":"تجميع الفترات"},"جمع":{"en":"Sum","fr":"Somme","es":"Suma","ar":"المجموع"},"میانگین":{"en":"Average","fr":"Moyenne","es":"Promedio","ar":"المتوسط"},"آخرین مقدار":{"en":"Latest value","fr":"Dernière valeur","es":"Último valor","ar":"آخر قيمة"},"روش تجمیع":{"en":"Aggregation method","fr":"Méthode d’agrégation","es":"Método de agregación","ar":"طريقة التجميع"},"مستند الزامی":{"en":"Document required","fr":"Document obligatoire","es":"Documento obligatorio","ar":"المستند مطلوب"},"بله":{"en":"Yes","fr":"Oui","es":"Sí","ar":"نعم"},"خیر":{"en":"No","fr":"Non","es":"No","ar":"لا"},"نام شاخص الزامی است":{"en":"Indicator name is required","fr":"Le nom de l’indicateur est obligatoire","es":"El nombre del indicador es obligatorio","ar":"اسم المؤشر مطلوب"},"انتخاب سطح الزامی است":{"en":"Selecting a level is required","fr":"La sélection d’un niveau est obligatoire","es":"Seleccionar un nivel es obligatorio","ar":"اختيار المستوى مطلوب"},"برای سطح تیم، انتخاب تیم الزامی است":{"en":"A team must be selected for team level","fr":"Une équipe doit être sélectionnée pour le niveau équipe","es":"Debe seleccionarse un equipo para el nivel de equipo","ar":"يجب اختيار فريق لمستوى الفريق"},"برای سطح معاونت، انتخاب معاونت الزامی است":{"en":"A division must be selected for business unit level","fr":"Une direction doit être sélectionnée pour le niveau unité d’affaires","es":"Debe seleccionarse una división para el nivel de división","ar":"يجب اختيار إدارة لمستوى الإدارة"},"مالک، ثبت‌کننده و تأییدکننده باید مشخص شوند":{"en":"Owner, submitter, and approver must be specified","fr":"Le responsable, le responsable de saisie et l’approbateur doivent être définis","es":"Deben especificarse el responsable, el registrador y el aprobador","ar":"يجب تحديد المالك والمسجل والمعتمد"},"ثبت‌کننده نمی‌تواند تأییدکننده همان شاخص باشد":{"en":"The submitter cannot approve the same indicator","fr":"Le responsable de saisie ne peut pas approuver le même indicateur","es":"El registrador no puede aprobar el mismo indicador","ar":"لا يمكن للمسجل اعتماد المؤشر نفسه"},"انتخاب الگوی زمان‌بندی الزامی است":{"en":"Selecting a schedule template is required","fr":"La sélection d’un modèle de calendrier est obligatoire","es":"Seleccionar una plantilla de programación es obligatorio","ar":"اختيار قالب الجدول الزمني مطلوب"},"روش تجمیع نوبت‌ها الزامی است":{"en":"An occurrence aggregation method is required","fr":"Une méthode d’agrégation des occurrences est obligatoire","es":"Se requiere un método de agregación de ocurrencias","ar":"طريقة تجميع الفترات مطلوبة"},"نام الگو":{"en":"Template name","fr":"Nom du modèle","es":"Nombre de la plantilla","ar":"اسم القالب"},"گزارش فصلی استاندارد":{"en":"Standard quarterly report","fr":"Rapport trimestriel standard","es":"Informe trimestral estándar","ar":"تقرير ربع سنوي قياسي"},"تناوب":{"en":"Frequency","fr":"Fréquence","es":"Frecuencia","ar":"التكرار"},"ماهانه":{"en":"Monthly","fr":"Mensuel","es":"Mensual","ar":"شهري"},"شروع ثبت؛ روز بعد از پایان":{"en":"Submission start; days after period end","fr":"Début de saisie; jours après la fin de la période","es":"Inicio del registro; días después del fin del período","ar":"بدء التسجيل؛ أيام بعد نهاية الفترة"},"مهلت ثبت؛ روز بعد از پایان":{"en":"Submission deadline; days after period end","fr":"Date limite de saisie; jours après la fin de la période","es":"Fecha límite de registro; días después del fin del período","ar":"موعد التسجيل؛ أيام بعد نهاية الفترة"},"مهلت تأیید؛ روز پس از ثبت":{"en":"Approval deadline; days after submission","fr":"Date limite d’approbation; jours après la saisie","es":"Fecha límite de aprobación; días después del registro","ar":"موعد الاعتماد؛ أيام بعد التسجيل"},"قفل؛ روز بعد از پایان":{"en":"Lock; days after period end","fr":"Verrouillage; jours après la fin de la période","es":"Bloqueo; días después del fin del período","ar":"القفل؛ أيام بعد نهاية الفترة"},"یادآوری؛ چند روز قبل":{"en":"Reminder; days before","fr":"Rappel; jours avant","es":"Recordatorio; días antes","ar":"التذكير؛ أيام قبل"},"این الگو به یک یا چند شاخص متصل است و قابل حذف نیست.":{"en":"This template is linked to one or more indicators and cannot be deleted.","fr":"Ce modèle est lié à un ou plusieurs indicateurs et ne peut pas être supprimé.","es":"Esta plantilla está vinculada a uno o más indicadores y no puede eliminarse.","ar":"هذا القالب مرتبط بمؤشر واحد أو أكثر ولا يمكن حذفه."},"نام و تناوب الگو الزامی است":{"en":"Template name and frequency are required","fr":"Le nom et la fréquence du modèle sont obligatoires","es":"El nombre y la frecuencia de la plantilla son obligatorios","ar":"اسم القالب وتكراره مطلوبان"},"تاریخ قفل باید بعد از پایان مهلت تأیید باشد":{"en":"The lock date must be after the approval deadline","fr":"La date de verrouillage doit être postérieure à la date limite d’approbation","es":"La fecha de bloqueo debe ser posterior a la fecha límite de aprobación","ar":"يجب أن يكون تاريخ القفل بعد موعد الاعتماد"},"شروع ثبت":{"en":"Submission start","fr":"Début de saisie","es":"Inicio del registro","ar":"بدء التسجيل"},"مهلت ثبت":{"en":"Submission deadline","fr":"Échéance de soumission","es":"Fecha límite de registro","ar":"موعد التسجيل"},"مهلت تأیید":{"en":"Approval deadline","fr":"Échéance d’approbation","es":"Fecha límite de aprobación","ar":"موعد الاعتماد"},"تاریخ قفل":{"en":"Lock date","fr":"Date de verrouillage","es":"Fecha de bloqueo","ar":"تاريخ القفل"},"دلیل استثنا (الزامی)":{"en":"Exception reason (required)","fr":"Motif de l’exception (obligatoire)","es":"Motivo de la excepción (obligatorio)","ar":"سبب الاستثناء (مطلوب)"},"متن سؤال":{"en":"Question text","fr":"Texte de la question","es":"Texto de la pregunta","ar":"نص السؤال"},"مثال: رعایت مهلت‌های تعیین‌شده":{"en":"Example: Meeting established deadlines","fr":"Exemple : Respect des échéances établies","es":"Ejemplo: Cumplimiento de los plazos establecidos","ar":"مثال: الالتزام بالمواعيد المحددة"},"وزن":{"en":"Weight","fr":"Pondération","es":"Peso","ar":"الوزن"},"٪":{"en":"%","fr":"%","es":"%","ar":"٪"},"متن سؤال الزامی است":{"en":"Question text is required","fr":"Le texte de la question est obligatoire","es":"El texto de la pregunta es obligatorio","ar":"نص السؤال مطلوب"},"مثال: همسویی با استراتژی سازمان":{"en":"Example: Alignment with organizational strategy","fr":"Exemple : Alignement sur la stratégie de l’organisation","es":"Ejemplo: Alineación con la estrategia de la organización","ar":"مثال: التوافق مع استراتيجية المؤسسة"},"سطح تیم (مدیر ← کارشناس)":{"en":"Team level (Manager ← Specialist)","fr":"Niveau équipe (Gestionnaire ← Spécialiste)","es":"Nivel de equipo (Gerente ← Especialista)","ar":"مستوى الفريق (المدير ← الأخصائي)"},"سطح معاونت (معاون ← مدیر)":{"en":"Business Unit level (Business Unit Lead ← Manager)","fr":"Niveau unité d’affaires (Responsable ← Gestionnaire)","es":"Nivel de división (Responsable ← Gerente)","ar":"مستوى الإدارة (رئيس الإدارة ← المدير)"},"سطح سازمان (مدیرعامل ← معاون)":{"en":"Organization level (CEO ← Business Unit Lead)","fr":"Niveau organisation (PDG ← Responsable d’unité d’affaires)","es":"Nivel de organización (CEO ← Responsable de división)","ar":"مستوى المؤسسة (الرئيس التنفيذي ← رئيس الإدارة)"},"سطوح پرسش‌های وظایف مستمر":{"en":"Routine question levels","fr":"Niveaux des questions de routine","es":"Niveles de preguntas rutinarias","ar":"مستويات الأسئلة الروتينية"},"آماده‌سازی":{"en":"Setup","fr":"Préparation","es":"Preparación","ar":"إعداد"},"فعال":{"en":"Active","fr":"Actif","es":"Activo","ar":"نشط"},"در حال نهایی‌سازی":{"en":"Finalizing","fr":"Finalisation","es":"Finalizando","ar":"جارٍ الإنهاء"},"نهایی و قفل‌شده":{"en":"Final and locked","fr":"Final et verrouillé","es":"Final y bloqueado","ar":"نهائي ومقفل"},"دوره بسته نشد؛ ابتدا همه شاخص‌ها باید تأیید شوند.":{"en":"The period could not be closed; all indicators must be approved first.","fr":"La période n’a pas pu être clôturée; tous les indicateurs doivent d’abord être approuvés.","es":"No se pudo cerrar el período; primero deben aprobarse todos los indicadores.","ar":"تعذر إغلاق الفترة؛ يجب اعتماد جميع المؤشرات أولاً."},"دلیل بازگشایی دوره را وارد کنید:":{"en":"Enter the reason for reopening the period:","fr":"Saisissez le motif de réouverture de la période :","es":"Introduce el motivo para reabrir el período:","ar":"أدخل سبب إعادة فتح الفترة:"},"فصل چهارم ۱۴۰۴":{"en":"Q4 2026","fr":"T4 2026","es":"Cuarto trimestre 1404","ar":"الربع الرابع 1404"},"این دوره نهایی و قفل‌شده است":{"en":"This period is final and locked","fr":"Cette période est finale et verrouillée","es":"Este período es final y está bloqueado","ar":"هذه الفترة نهائية ومقفلة"},"مقدار واقعی فقط توسط ثبت‌کننده تعیین‌شده وارد می‌شود":{"en":"The actual value can only be entered by the designated submitter","fr":"La valeur réelle ne peut être saisie que par le responsable désigné","es":"El valor real solo puede ser registrado por el responsable designado","ar":"لا يمكن إدخال القيمة الفعلية إلا بواسطة المسجل المحدد"},"هدف":{"en":"Target","fr":"Cible","es":"Objetivo","ar":"الهدف"},"واقعی":{"en":"Actual","fr":"Réel","es":"Real","ar":"الفعلي"},"قفل دوره":{"en":"Lock period","fr":"Verrouiller la période","es":"Bloquear período","ar":"قفل الفترة"},"بازگشایی دوره":{"en":"Reopen period","fr":"Rouvrir la période","es":"Reabrir período","ar":"إعادة فتح الفترة"},"مثال: همکاری مؤثر با دیگران":{"en":"Example: Effective collaboration with others","fr":"Exemple : Collaboration efficace avec les autres","es":"Ejemplo: Colaboración eficaz con otros","ar":"مثال: التعاون الفعال مع الآخرين"},"بانک سؤال":{"en":"Question bank","fr":"Banque de questions","es":"Banco de preguntas","ar":"بنك الأسئلة"},"تخصیص ارزیاب‌ها":{"en":"Reviewer assignments","fr":"Affectation des évaluateurs","es":"Asignación de evaluadores","ar":"تعيين المقيّمين"},"تنظیمات بازخورد همکاران":{"en":"Peer feedback settings","fr":"Paramètres de rétroaction des collègues","es":"Configuración de comentarios de colegas","ar":"إعدادات ملاحظات الزملاء"},"— باید ۱۰۰٪ باشد":{"en":"— must equal 100%","fr":"— doit totaliser 100 %","es":"— debe ser igual a 100 %","ar":"— يجب أن يساوي 100٪"},"ارزیابی پیشنهاد نشد":{"en":"No review suggested","fr":"Aucune évaluation suggérée","es":"No se sugirió evaluación","ar":"لم يُقترح تقييم"},"معتبر":{"en":"Valid","fr":"Valide","es":"Válido","ar":"صالح"},"نیازمند تکمیل دستی":{"en":"Manual completion required","fr":"Complément manuel requis","es":"Se requiere completar manualmente","ar":"يتطلب إكمالاً يدوياً"},"بررسی وضعیت":{"en":"Check status","fr":"Vérifier l’état","es":"Revisar estado","ar":"فحص الحالة"},"تنظیمات پایه":{"en":"Evaluation Settings","fr":"Paramètres d’évaluation","es":"Configuración básica","ar":"الإعدادات الأساسية"},"سؤالات وظایف مستمر":{"en":"Routine questions","fr":"Questions de routine","es":"Preguntas rutinarias","ar":"الأسئلة الروتينية"},"شاخص‌ها":{"en":"Indicators","fr":"Indicateurs","es":"Indicadores","ar":"المؤشرات"},"زمان‌بندی شاخص‌ها":{"en":"Indicator scheduling","fr":"Planification des indicateurs","es":"Programación de indicadores","ar":"جدولة المؤشرات"},"برنامه‌ریزی دوره":{"en":"Period planning","fr":"Planification de la période","es":"Planificación del período","ar":"تخطيط الفترة"},"پایش ثبت عملکرد":{"en":"Performance entry monitoring","fr":"Suivi de la saisie de performance","es":"Monitoreo del registro de desempeño","ar":"متابعة إدخال الأداء"},"برای دوره‌ی انتخاب‌شده، مقدار هدف هر شاخص را وارد کنید":{"en":"Enter the target value for each indicator for the selected period","fr":"Saisissez la valeur cible de chaque indicateur pour la période sélectionnée","es":"Introduce el valor objetivo de cada indicador para el período seleccionado","ar":"أدخل القيمة المستهدفة لكل مؤشر للفترة المحددة"},"ثبت عملکرد":{"en":"Performance entry","fr":"Saisie de performance","es":"Registro de desempeño","ar":"إدخال الأداء"},"وضعیت ثبت و تأیید مقادیر واقعی را پایش کنید؛ ورود اطلاعات توسط مسئول هر شاخص انجام می‌شود":{"en":"Monitor the submission and approval status of actual values; data entry is performed by each indicator’s designated submitter","fr":"Suivez l’état de saisie et d’approbation des valeurs réelles; la saisie est effectuée par le responsable désigné de chaque indicateur","es":"Supervisa el estado de registro y aprobación de los valores reales; los datos los registra el responsable designado de cada indicador","ar":"تابع حالة إدخال واعتماد القيم الفعلية؛ يقوم بإدخال البيانات المسجل المحدد لكل مؤشر"},"CVI دوره جاری":{"en":"Current-cycle CVI","fr":"CVI de la période en cours","es":"CVI del período actual","ar":"CVI للفترة الحالية"},"شاخص ارزیابی مشارکت":{"en":"Contribution Value Index","fr":"Indice de valeur de contribution","es":"Índice de Valor de Contribución","ar":"مؤشر قيمة المساهمة"},"خروج از سامانه":{"en":"Sign out","fr":"Se déconnecter","es":"Cerrar sesión","ar":"تسجيل الخروج"},"مشارکت فردی":{"en":"Individual Contribution","fr":"Contribution individuelle","es":"Contribución individual","ar":"المساهمة الفردية"},"ورود به سامانه":{"en":"Sign in","fr":"Connexion","es":"Iniciar sesión","ar":"تسجيل الدخول"},"با اطلاعات کاربری خود وارد شوید":{"en":"Sign in with your account","fr":"Connectez-vous avec votre compte","es":"Inicia sesión con tu cuenta","ar":"سجّل الدخول باستخدام حسابك"},"ورود سریع (نمونه)":{"en":"Canadian demo accounts","fr":"Comptes de démonstration canadiens","es":"Acceso rápido (demo)","ar":"دخول سريع (تجريبي)"},"یک نقش نمونه را انتخاب کنید...":{"en":"Choose a Canadian demo account...","fr":"Choisissez un compte de démonstration canadien...","es":"Selecciona un rol de demostración...","ar":"اختر دوراً تجريبياً..."},"تاریخچه فرایند فعالیت":{"en":"Activity process history","fr":"Historique du processus d’activité","es":"Historial del proceso de actividad","ar":"سجل عملية النشاط"},"فعالیت ارسال شد":{"en":"Activity submitted","fr":"Activité soumise","es":"Actividad enviada","ar":"تم إرسال النشاط"},"فعالیت شما برای بررسی به مدیر ارسال شد.":{"en":"Your activity was sent to your manager for review.","fr":"Votre activité a été envoyée à votre gestionnaire pour examen.","es":"Tu actividad se envió a tu gerente para revisión.","ar":"تم إرسال نشاطك إلى مديرك للمراجعة."},"پس از تأیید، امتیاز به داشبورد شما اضافه می‌شود.":{"en":"Once approved, the score will be added to your dashboard.","fr":"Après approbation, le résultat sera ajouté à votre tableau de bord.","es":"Una vez aprobada, la puntuación se añadirá a tu panel.","ar":"بعد الاعتماد، ستُضاف الدرجة إلى لوحة التحكم الخاصة بك."},"بازگشت به داشبورد":{"en":"Back to dashboard","fr":"Retour au tableau de bord","es":"Volver al panel","ar":"العودة إلى لوحة التحكم"},"پاک‌کردن پیش‌نویس":{"en":"Clear draft","fr":"Effacer le brouillon","es":"Borrar borrador","ar":"مسح المسودة"},"گروه فعالیت *":{"en":"Activity group *","fr":"Groupe d’activités *","es":"Grupo de actividades *","ar":"مجموعة النشاط *"},"انتخاب گروه فعالیت":{"en":"Select activity group","fr":"Sélectionner le groupe d’activités","es":"Seleccionar grupo de actividades","ar":"اختر مجموعة النشاط"},"زیرگروه *":{"en":"Subgroup *","fr":"Sous-groupe *","es":"Subgrupo *","ar":"المجموعة الفرعية *"},"سطح تأثیر فعالیت توسط مدیر شما مشخص می‌شود. در توضیحات، تأثیر کار را شرح دهید.":{"en":"Your manager determines the activity’s impact level. Describe the impact of your work in the details.","fr":"Votre gestionnaire détermine le niveau d’impact de l’activité. Décrivez l’impact de votre travail dans les détails.","es":"Tu gerente determina el nivel de impacto de la actividad. Describe el impacto de tu trabajo en los detalles.","ar":"يحدد مديرك مستوى تأثير النشاط. صف تأثير عملك في التفاصيل."},"عنوان فعالیت *":{"en":"Activity title *","fr":"Titre de l’activité *","es":"Título de la actividad *","ar":"عنوان النشاط *"},"توضیحات *":{"en":"Description *","fr":"Description *","es":"Descripción *","ar":"الوصف *"},"تأثیر این فعالیت روی کار خود را توضیح دهید — مدیر بر اساس همین توضیحات سطح تأثیر را تعیین می‌کند.":{"en":"Explain how this activity affected your work — your manager will use this description to determine the impact level.","fr":"Expliquez l’impact de cette activité sur votre travail — votre gestionnaire utilisera cette description pour déterminer le niveau d’impact.","es":"Explica cómo afectó esta actividad a tu trabajo; tu gerente usará esta descripción para determinar el nivel de impacto.","ar":"اشرح تأثير هذا النشاط على عملك — سيستخدم مديرك هذا الوصف لتحديد مستوى التأثير."},"مستندات پشتیبان":{"en":"Supporting documents","fr":"Documents justificatifs","es":"Documentos de respaldo","ar":"المستندات الداعمة"},"این بخش اختیاری است، اما می‌تواند به ارزیابی بهتر فعالیت کمک کند.":{"en":"This section is optional, but it can support a better activity evaluation.","fr":"Cette section est facultative, mais elle peut faciliter une meilleure évaluation de l’activité.","es":"Esta sección es opcional, pero puede ayudar a evaluar mejor la actividad.","ar":"هذا القسم اختياري، لكنه قد يساعد في تقييم النشاط بشكل أفضل."},"برای آپلود کلیک کنید":{"en":"Click to upload","fr":"Cliquer pour téléverser","es":"Haz clic para cargar","ar":"انقر للرفع"},"گواهی، گزارش، تصویر — حداکثر ۵ مگابایت":{"en":"Certificate, report, image — max 5 MB","fr":"Certificat, rapport, image — 5 Mo max.","es":"Certificado, informe, imagen — máx. 5 MB","ar":"شهادة، تقرير، صورة — بحد أقصى 5 ميغابايت"},"خلاصه فعالیت":{"en":"Activity summary","fr":"Résumé de l’activité","es":"Resumen de la actividad","ar":"ملخص النشاط"},"ارسال برای مدیر ✓":{"en":"Send to manager ✓","fr":"Envoyer au gestionnaire ✓","es":"Enviar al gerente ✓","ar":"إرسال إلى المدير ✓"},"اصلاح فعالیت":{"en":"Revise activity","fr":"Réviser l’activité","es":"Revisar actividad","ar":"تعديل النشاط"},"توضیحات اصلاح‌شده *":{"en":"Revised description *","fr":"Description révisée *","es":"Descripción revisada *","ar":"الوصف المعدل *"},"مستندات":{"en":"Documents","fr":"Documents","es":"Documentos","ar":"المستندات"},"انتخاب مستند جدید":{"en":"📎 Select new document","fr":"📎 Sélectionner un nouveau document","es":"📎 Seleccionar nuevo documento","ar":"📎 اختر مستنداً جديداً"},"ثبت اصلاحات و ارسال مجدد":{"en":"Save revisions and resubmit","fr":"Enregistrer les révisions et soumettre de nouveau","es":"Guardar cambios y reenviar","ar":"حفظ التعديلات وإعادة الإرسال"},"ویرایش و ارسال مجدد":{"en":"Edit and resubmit","fr":"Modifier et soumettre de nouveau","es":"Editar y reenviar","ar":"تعديل وإعادة الإرسال"},"مقایسه با دوره قبل":{"en":"Compare with previous period","fr":"Comparer avec la période précédente","es":"Comparar con el período anterior","ar":"مقارنة بالفترة السابقة"},"فعالیت‌های غیرمستمر به تفکیک حوزه":{"en":"Non-routine activities by category","fr":"Activités non routinières par catégorie","es":"Actividades no rutinarias por categoría","ar":"الأنشطة غير الروتينية حسب الفئة"},"معیارهای ارزیابی وظایف مستمر":{"en":"Routine evaluation criteria","fr":"Critères d’évaluation de routine","es":"Criterios de evaluación rutinaria","ar":"معايير التقييم الروتيني"},"جزئیات ارزیابی وظایف مستمر برای این دوره ثبت نشده است":{"en":"Routine evaluation details have not been recorded for this period","fr":"Les détails de l’évaluation de routine n’ont pas été enregistrés pour cette période","es":"No se han registrado detalles de evaluación rutinaria para este período","ar":"لم يتم تسجيل تفاصيل التقييم الروتيني لهذه الفترة"},"جمع‌بندی این دوره":{"en":"Cycle highlights","fr":"Faits saillants du cycle","es":"Resumen del período","ar":"ملخص الفترة"},"نقطه قوت":{"en":"Strength","fr":"Point fort","es":"Fortaleza","ar":"نقطة قوة"},"فعالیت‌های ثبت‌شده دوره":{"en":"Activities recorded in this period","fr":"Activités enregistrées pour cette période","es":"Actividades registradas en este período","ar":"الأنشطة المسجلة في هذه الفترة"},"جزئیات فعالیت‌های این دوره در دسترس نیست":{"en":"Activity details for this period are not available","fr":"Les détails des activités de cette période ne sont pas disponibles","es":"Los detalles de las actividades de este período no están disponibles","ar":"تفاصيل أنشطة هذه الفترة غير متاحة"},"روند CVI — دوره‌های اخیر":{"en":"CVI trend — recent periods","fr":"Tendance du CVI — périodes récentes","es":"Tendencia del CVI — períodos recientes","ar":"اتجاه CVI — الفترات الأخيرة"},"جزئیات هر دوره":{"en":"Period details","fr":"Détails de la période","es":"Detalles del período","ar":"تفاصيل الفترة"},"جاری":{"en":"Current","fr":"Actuel","es":"Actual","ar":"الحالية"},"امتیاز فعالیت‌های غیرمستمر از مجموع فعالیت‌های تأییدشده‌ی خودثبتی، به تفکیک گروه، تشکیل می‌شود. روی هر گروه بزنید تا فعالیت‌های آن را ببینید.":{"en":"The non-routine score is calculated from your approved self-reported activities by category. Select a category to view its activities.","fr":"Le score non routinier est calculé à partir de vos activités autodéclarées approuvées, par catégorie. Sélectionnez une catégorie pour voir ses activités.","es":"La puntuación no rutinaria se calcula a partir de tus actividades autodeclaradas aprobadas por categoría. Selecciona una categoría para ver sus actividades.","ar":"تُحسب درجة الأنشطة غير الروتينية من أنشطتك المعتمدة والمسجلة ذاتياً حسب الفئة. اختر فئة لعرض أنشطتها."},"فعالیت تأییدشده‌ای در این گروه ثبت نشده":{"en":"No approved activities have been recorded in this group","fr":"Aucune activité approuvée n’a été enregistrée dans ce groupe","es":"No se han registrado actividades aprobadas en este grupo","ar":"لم يتم تسجيل أنشطة معتمدة في هذه المجموعة"},"هنوز سؤال وظایف مستمری برای شما تعریف نشده است.":{"en":"No routine questions have been defined for you yet.","fr":"Aucune question de routine n’a encore été définie pour vous.","es":"Aún no se han definido preguntas rutinarias para ti.","ar":"لم يتم تحديد أسئلة روتينية لك بعد."},"شاخصی برای این سطح تعریف نشده است.":{"en":"No indicators have been defined for this level.","fr":"Aucun indicateur n’a été défini pour ce niveau.","es":"No se han definido indicadores para este nivel.","ar":"لم يتم تحديد مؤشرات لهذا المستوى."},"مشاهده نمودار روند دوره‌ها":{"en":"📈 View period trend chart ←","fr":"📈 Voir le graphique de tendance des périodes ←","es":"📈 Ver gráfico de tendencia de períodos ←","ar":"📈 عرض مخطط اتجاه الفترات ←"},"فعالیت‌های اخیر":{"en":"Recent activities","fr":"Activités récentes","es":"Actividades recientes","ar":"الأنشطة الأخيرة"},"مشاهده همه":{"en":"View all","fr":"Voir tout","es":"Ver todo","ar":"عرض الكل"},"هنوز فعالیتی ثبت نکرده‌اید":{"en":"You have not recorded any activities yet","fr":"Vous n’avez encore enregistré aucune activité","es":"Aún no has registrado actividades","ar":"لم تسجل أي أنشطة بعد"},"وضعیت":{"en":"Status","fr":"Statut","es":"Estado","ar":"الحالة"},"ثبت فعالیت جدید":{"en":"+ Add new activity","fr":"+ Ajouter une activité","es":"+ Registrar nueva actividad","ar":"+ إضافة نشاط جديد"},"فعالیت‌های من":{"en":"My activities","fr":"Mes activités","es":"Mis actividades","ar":"أنشطتي"},"جدید":{"en":"+ New","fr":"+ Nouveau","es":"+ Nuevo","ar":"+ جديد"},"تاریخچه دوره‌ها":{"en":"Performance cycle history","fr":"Historique des cycles de performance","es":"Historial de períodos","ar":"سجل الفترات"},"نمودار رشد CVI":{"en":"CVI growth chart","fr":"Graphique d’évolution du CVI","es":"Gráfico de evolución del CVI","ar":"مخطط نمو CVI"},"حذف عکس":{"en":"Remove photo","fr":"Supprimer la photo","es":"Eliminar foto","ar":"إزالة الصورة"},"تغییر رمز عبور":{"en":"Change password","fr":"Changer le mot de passe","es":"Cambiar contraseña","ar":"تغيير كلمة المرور"},"رمز فعلی":{"en":"Current password","fr":"Mot de passe actuel","es":"Contraseña actual","ar":"كلمة المرور الحالية"},"رمز جدید":{"en":"New password","fr":"Nouveau mot de passe","es":"Nueva contraseña","ar":"كلمة المرور الجديدة"},"تکرار رمز جدید":{"en":"Confirm new password","fr":"Confirmer le nouveau mot de passe","es":"Confirmar nueva contraseña","ar":"تأكيد كلمة المرور الجديدة"},"رمز عبور با موفقیت تغییر کرد":{"en":"✅ Password changed successfully","fr":"✅ Mot de passe modifié avec succès","es":"✅ Contraseña cambiada correctamente","ar":"✅ تم تغيير كلمة المرور بنجاح"},"ذخیره‌ی رمز جدید":{"en":"Save new password","fr":"Enregistrer le nouveau mot de passe","es":"Guardar nueva contraseña","ar":"حفظ كلمة المرور الجديدة"},"ثبت فعالیت غیرفعال است":{"en":"Activity submission is disabled","fr":"La saisie d’activité est désactivée","es":"El registro de actividades está deshabilitado","ar":"إدخال الأنشطة معطل"},"دوره فعالی وجود ندارد؛ فعالیت جدید فقط در دوره فعال ثبت می‌شود.":{"en":"There is no active period; new activities can only be recorded during an active period.","fr":"Aucune période n’est active; les nouvelles activités ne peuvent être enregistrées que pendant une période active.","es":"No hay un período activo; las nuevas actividades solo pueden registrarse durante un período activo.","ar":"لا توجد فترة نشطة؛ لا يمكن تسجيل أنشطة جديدة إلا خلال فترة نشطة."},"میانگین CVI تیم":{"en":"Team average CVI","fr":"CVI moyen de l’équipe","es":"CVI promedio del equipo","ar":"متوسط CVI للفريق"},"همه بررسی شده‌اند":{"en":"✅ All reviewed","fr":"✅ Tout a été examiné","es":"✅ Todo revisado","ar":"✅ تمت مراجعة الكل"},"تحقق اهداف تیم":{"en":"Team goal attainment","fr":"Atteinte des objectifs de l’équipe","es":"Cumplimiento de objetivos del equipo","ar":"تحقيق أهداف الفريق"},"شاخصی برای این تیم تعریف نشده":{"en":"No indicators have been defined for this team","fr":"Aucun indicateur n’a été défini pour cette équipe","es":"No se han definido indicadores para este equipo","ar":"لم يتم تحديد مؤشرات لهذا الفريق"},"Timeline و Audit log":{"en":"Timeline and audit log","fr":"Chronologie et journal d’audit","es":"Cronología y registro de auditoría","ar":"الخط الزمني وسجل التدقيق"},"هنوز رویدادی ثبت نشده است.":{"en":"No events have been recorded yet.","fr":"Aucun événement n’a encore été enregistré.","es":"Aún no se han registrado eventos.","ar":"لم يتم تسجيل أحداث بعد."},"بازگشت":{"en":"→ Back","fr":"→ Retour","es":"→ Volver","ar":"→ رجوع"},"هدف دوره:":{"en":"Period target:","fr":"Cible de la période :","es":"Objetivo del período:","ar":"هدف الفترة:"},"قفل خودکار":{"en":"Automatic lock","fr":"Verrouillage automatique","es":"Bloqueo automático","ar":"قفل تلقائي"},"پنجره ثبت هنوز باز نشده است.":{"en":"The submission window is not open yet.","fr":"La saisie n’est pas encore ouverte.","es":"La ventana de registro aún no está abierta.","ar":"نافذة التسجيل لم تُفتح بعد."},"این نوبت به‌صورت خودکار قفل شده است.":{"en":"This occurrence was locked automatically.","fr":"Cette occurrence a été verrouillée automatiquement.","es":"Esta ocurrencia se bloqueó automáticamente.","ar":"تم قفل هذه الفترة تلقائياً."},"مقدار واقعی *":{"en":"Actual value *","fr":"Valeur réelle *","es":"Valor real *","ar":"القيمة الفعلية *"},"توضیح یا منبع داده":{"en":"Description or data source","fr":"Description ou source de données","es":"Descripción o fuente de datos","ar":"الوصف أو مصدر البيانات"},"تحقق کمتر از ۸۰٪ است؛ علت انحراف و اقدام اصلاحی الزامی است.":{"en":"Achievement is below 80%; a reason for variance and corrective action are required.","fr":"L’atteinte est inférieure à 80 % ; la raison de l’écart et une action corrective sont obligatoires.","es":"El cumplimiento es inferior al 80 %; se requieren el motivo de la desviación y una acción correctiva.","ar":"نسبة التحقيق أقل من 80٪؛ يجب إدخال سبب الانحراف والإجراء التصحيحي."},"مستند برای ارسال الزامی است.":{"en":"A supporting document is required for submission.","fr":"Un document justificatif est requis pour la soumission.","es":"Se requiere un documento de respaldo para enviar.","ar":"مستند داعم مطلوب للإرسال."},"ذخیره پیش‌نویس":{"en":"Save draft","fr":"Enregistrer le brouillon","es":"Guardar borrador","ar":"حفظ المسودة"},"ارسال برای تأیید":{"en":"Submit for approval","fr":"Soumettre pour approbation","es":"Enviar para aprobación","ar":"إرسال للاعتماد"},"شاخص‌های مسئولیت من":{"en":"My assigned indicators","fr":"Mes indicateurs attribués","es":"Mis indicadores asignados","ar":"المؤشرات المسندة إليّ"},"مواردی که در آن مالک، ثبت‌کننده یا تأییدکننده هستید.":{"en":"Indicators where you are the owner, submitter, or approver.","fr":"Indicateurs pour lesquels vous êtes responsable, responsable de saisie ou approbateur.","es":"Indicadores en los que eres responsable, registrador o aprobador.","ar":"المؤشرات التي تكون فيها مالكاً أو مسجلاً أو معتمداً."},"دوره فعالی وجود ندارد.":{"en":"There is no active period.","fr":"Aucune période n’est active.","es":"No hay un período activo.","ar":"لا توجد فترة نشطة."},"در این بخش شاخصی برای شما وجود ندارد.":{"en":"There are no indicators for you in this section.","fr":"Aucun indicateur ne vous est attribué dans cette section.","es":"No hay indicadores para ti en esta sección.","ar":"لا توجد مؤشرات لك في هذا القسم."},"تأیید":{"en":"Approve","fr":"Approuver","es":"Aprobar","ar":"اعتماد"},"درخواست اصلاح":{"en":"Request revision","fr":"Demander une révision","es":"Solicitar revisión","ar":"طلب تعديل"},"هیچ شاخصی در انتظار تأیید شما نیست.":{"en":"No indicators are awaiting your approval.","fr":"Aucun indicateur n’attend votre approbation.","es":"No hay indicadores pendientes de tu aprobación.","ar":"لا توجد مؤشرات بانتظار اعتمادك."},"در انتظار تأیید":{"en":"Pending approval","fr":"En attente d’approbation","es":"Pendiente de aprobación","ar":"بانتظار الاعتماد"},"مواردی که برای تصمیم‌گیری شما ارسال شده‌اند.":{"en":"Items submitted for your decision.","fr":"Éléments soumis à votre décision.","es":"Elementos enviados para tu decisión.","ar":"العناصر المرسلة لاتخاذ قرارك."},"تصمیم مدیر":{"en":"Manager decision","fr":"Décision du gestionnaire","es":"Decisión del gerente","ar":"قرار المدير"},"سطح تأثیر *":{"en":"Impact level *","fr":"Niveau d’impact *","es":"Nivel de impacto *","ar":"مستوى التأثير *"},"یادداشت (برای درخواست اصلاح الزامی)":{"en":"Note (required when requesting revision)","fr":"Note (obligatoire pour demander une révision)","es":"Nota (obligatoria para solicitar revisión)","ar":"ملاحظة (مطلوبة عند طلب التعديل)"},"✓ تأیید":{"en":"✓ Approve","fr":"✓ Approuver","es":"✓ Aprobar","ar":"✓ اعتماد"},"اصلاح":{"en":"Revise","fr":"Réviser","es":"Revisar","ar":"تعديل"},"رد":{"en":"Reject","fr":"Rejeter","es":"Rechazar","ar":"رفض"},"همه بررسی شدند":{"en":"All reviewed","fr":"Tout a été examiné","es":"Todo revisado","ar":"تمت مراجعة الكل"},"هیچ فعالیتی در انتظار بررسی نیست":{"en":"No activities are awaiting review","fr":"Aucune activité n’attend d’être examinée","es":"No hay actividades pendientes de revisión","ar":"لا توجد أنشطة بانتظار المراجعة"},"تحقق شاخص‌های تیم":{"en":"Team indicator attainment","fr":"Atteinte des indicateurs de l’équipe","es":"Cumplimiento de indicadores del equipo","ar":"تحقيق مؤشرات الفريق"},"روند درآمد تیم (نمایشی)":{"en":"Team revenue trend (sample)","fr":"Tendance du revenu de l’équipe (exemple)","es":"Tendencia de ingresos del equipo (ejemplo)","ar":"اتجاه إيرادات الفريق (مثال)"},"سؤال وظایف مستمری برای این فرد تعریف نشده است.":{"en":"No routine questions have been defined for this person.","fr":"Aucune question de routine n’a été définie pour cette personne.","es":"No se han definido preguntas rutinarias para esta persona.","ar":"لم يتم تحديد أسئلة روتينية لهذا الشخص."},"ترکیب مشارکت فردی":{"en":"Individual performance composition","fr":"Répartition de la contribution individuelle","es":"Composición de la puntuación individual","ar":"مكونات الدرجة الفردية"},"جزئیات سؤالات وظایف مستمر":{"en":"Routine question details","fr":"Détails des questions de routine","es":"Detalles de preguntas rutinarias","ar":"تفاصيل الأسئلة الروتينية"},"امتیاز گروه‌ها":{"en":"Category scores","fr":"Résultats par catégorie","es":"Puntuaciones por categoría","ar":"درجات الفئات"},"فعالیت‌های ثبت‌شده":{"en":"Recorded activities","fr":"Activités enregistrées","es":"Actividades registradas","ar":"الأنشطة المسجلة"},"هنوز فعالیتی ثبت نشده":{"en":"No activities have been recorded yet","fr":"Aucune activité n’a encore été enregistrée","es":"Aún no se han registrado actividades","ar":"لم يتم تسجيل أنشطة بعد"},"همه معاونت‌ها":{"en":"All business units","fr":"Toutes les unités d’affaires","es":"Todas las divisiones","ar":"كل الإدارات"},"همه تیم‌ها":{"en":"All teams","fr":"Toutes les équipes","es":"Todos los equipos","ar":"كل الفرق"},"میانگین CVI معاونت":{"en":"Business Unit average CVI","fr":"CVI moyen de l’unité d’affaires","es":"CVI promedio de la división","ar":"متوسط CVI للإدارة"},"شاخص‌های معاونت":{"en":"Business Unit performance indicators","fr":"Indicateurs de performance de l’unité d’affaires","es":"Indicadores de la división","ar":"مؤشرات الإدارة"},"شاخصی در سطح معاونت تعریف نشده است":{"en":"No business-unit-level performance indicators have been defined","fr":"Aucun indicateur de performance au niveau de l’unité d’affaires n’a été défini","es":"No se han definido indicadores a nivel de división","ar":"لم يتم تحديد مؤشرات على مستوى الإدارة"},"مقایسه‌ی تیم‌ها":{"en":"Team comparison","fr":"Comparaison des équipes","es":"Comparación de equipos","ar":"مقارنة الفرق"},"تیمی برای این معاونت تعریف نشده است":{"en":"No teams have been defined for this business unit","fr":"Aucune équipe n’a été définie pour cette unité d’affaires","es":"No se han definido equipos para esta división","ar":"لم يتم تحديد فرق لهذه الإدارة"},"میانگین CVI سازمان":{"en":"Organization average CVI","fr":"CVI moyen de l’organisation","es":"CVI promedio de la organización","ar":"متوسط CVI للمؤسسة"},"حداکثر CVI سازمان":{"en":"Highest organization CVI","fr":"CVI maximal de l’organisation","es":"CVI máximo de la organización","ar":"أعلى CVI في المؤسسة"},"تکمیل ارزیابی‌های وظایف مستمر":{"en":"Routine evaluation completion","fr":"Achèvement des évaluations de routine","es":"Finalización de evaluaciones rutinarias","ar":"اكتمال التقييمات الروتينية"},"منتظر تصمیم مدیرعامل":{"en":"Awaiting CEO decision","fr":"En attente de la décision du PDG","es":"A la espera de la decisión del CEO","ar":"بانتظار قرار الرئيس التنفيذي"},"شاخص سازمانی بحرانی":{"en":"Critical organization indicator","fr":"Indicateur critique de l’organisation","es":"Indicador crítico de la organización","ar":"مؤشر مؤسسي حرج"},"توزیع عملکرد کارکنان":{"en":"Employee performance distribution","fr":"Répartition de la performance des employés","es":"Distribución del desempeño de empleados","ar":"توزيع أداء الموظفين"},"شاخصی در سطح سازمان تعریف نشده است":{"en":"No organization-level indicators have been defined","fr":"Aucun indicateur au niveau organisation n’a été défini","es":"No se han definido indicadores a nivel de organización","ar":"لم يتم تحديد مؤشرات على مستوى المؤسسة"},"مقایسه‌ی معاونت‌ها":{"en":"Business Unit comparison","fr":"Comparaison des unités d’affaires","es":"Comparación de divisiones","ar":"مقارنة الإدارات"},"معاونتی تعریف نشده است":{"en":"No business units have been defined","fr":"Aucune unité d’affaires n’a été définie","es":"No se han definido divisiones","ar":"لم يتم تحديد إدارات"},"تیمی تعریف نشده است":{"en":"No teams have been defined","fr":"Aucune équipe n’a été définie","es":"No se han definido equipos","ar":"لم يتم تحديد فرق"},"در حال دریافت ارزیابی وظایف مستمر از سرور...":{"en":"Loading routine evaluations from the server...","fr":"Chargement des évaluations de routine depuis le serveur...","es":"Cargando evaluaciones rutinarias desde el servidor...","ar":"جارٍ تحميل التقييمات الروتينية من الخادم..."},"دوره فعالی وجود ندارد؛ ارزیابی‌های دوره‌های بسته فقط قابل مشاهده‌اند.":{"en":"There is no active period; evaluations from closed periods are view-only.","fr":"Aucune période n’est active; les évaluations des périodes clôturées sont en lecture seule.","es":"No hay un período activo; las evaluaciones de períodos cerrados son de solo lectura.","ar":"لا توجد فترة نشطة؛ تقييمات الفترات المغلقة للعرض فقط."},"امتیاز وظایف مستمر فصل جاری":{"en":"Current-quarter routine score","fr":"Score de routine du trimestre en cours","es":"Puntuación rutinaria del trimestre actual","ar":"درجة الروتين للربع الحالي"},"سؤالی برای این ماه تعریف نشده است.":{"en":"No questions have been defined for this month.","fr":"Aucune question n’a été définie pour ce mois.","es":"No se han definido preguntas para este mes.","ar":"لم يتم تحديد أسئلة لهذا الشهر."},"هنوز پاسخ داده نشده":{"en":"Not answered yet","fr":"Pas encore répondu","es":"Aún sin respuesta","ar":"لم تتم الإجابة بعد"},"(باید توسط ادمین به ۱۰۰٪ برسد)":{"en":"(Admin must set this to 100%)","fr":"(L’administrateur doit régler ce total à 100 %)","es":"(El administrador debe llevar este total al 100 %)","ar":"(يجب على المسؤول ضبط المجموع إلى 100٪)"},"مشاهده داشبورد معاونت ←":{"en":"View division dashboard ←","fr":"Voir le tableau de bord de la direction ←","es":"Ver panel de la división ←","ar":"عرض لوحة تحكم الإدارة ←"},"تاریخچه عملکرد شرکت":{"en":"Company performance history","fr":"Historique de la performance de l’entreprise","es":"Historial de desempeño de la empresa","ar":"سجل أداء الشركة"},"روند میانگین CVI شرکت":{"en":"Company average CVI trend","fr":"Tendance du CVI moyen de l’entreprise","es":"Tendencia del CVI promedio de la empresa","ar":"اتجاه متوسط CVI للشركة"},"عملیات":{"en":"Actions","fr":"Actions","es":"Acciones","ar":"الإجراءات"},"هنوز موردی ثبت نشده":{"en":"No items have been recorded yet","fr":"Aucun élément n’a encore été enregistré","es":"Aún no se han registrado elementos","ar":"لم يتم تسجيل عناصر بعد"},"افزودن مورد جدید":{"en":"Add new item","fr":"Ajouter un élément","es":"Agregar nuevo elemento","ar":"إضافة عنصر جديد"},"افزودن":{"en":"+ Add","fr":"+ Ajouter","es":"+ Agregar","ar":"+ إضافة"},"بررسی وضعیت راه‌اندازی":{"en":"Review setup status","fr":"Vérifier l’état de configuration","es":"Revisar estado de configuración","ar":"مراجعة حالة الإعداد"},"برو":{"en":"Go ←","fr":"Aller ←","es":"Ir ←","ar":"انتقل ←"},"اول معاونت‌ها را بسازید، بعد تیم‌های هر معاونت را اضافه کنید. مسئولِ هر معاونت یا تیم نیازی به انتخاب دستی ندارد — وقتی از تب «افراد» به کسی نقش «معاون» یا «مدیر» بدهید و معاونت/تیمش را مشخص کنید، همین‌جا به‌صورت خودکار نمایش داده می‌شود.":{"en":"Create business units first, then add the teams in each business unit. Business Unit Leads and team managers do not need to be selected manually — when you assign someone the Business Unit Lead or Manager role in the People tab and set their business unit/team, they will appear here automatically.","fr":"Créez d’abord les unités d’affaires, puis ajoutez les équipes de chaque unité d’affaires. Les responsables de direction et les gestionnaires d’équipe n’ont pas besoin d’être sélectionnés manuellement — lorsque vous attribuez à une personne le rôle de responsable d’unité d’affaires ou de gestionnaire dans l’onglet Personnes et définissez son unité d’affaires/équipe, elle apparaîtra ici automatiquement.","es":"Crea primero las divisiones y luego añade los equipos de cada división. No es necesario seleccionar manualmente a los responsables de división o gerentes de equipo: cuando asignes a alguien el rol de Responsable de división o Gerente en la pestaña Personas y definas su división/equipo, aparecerá aquí automáticamente.","ar":"أنشئ الإدارات أولاً، ثم أضف فرق كل إدارة. لا يلزم اختيار رؤساء الإدارات أو مديري الفرق يدوياً — عند تعيين دور رئيس إدارة أو مدير لشخص من تبويب الأفراد وتحديد إدارته/فريقه، سيظهر هنا تلقائياً."},"برای «معاون» فقط معاونت انتخاب می‌شود. برای «مدیر» و «کارشناس»، ابتدا معاونت را انتخاب کنید تا لیست تیم‌ها بر اساس همان معاونت فیلتر شود، سپس تیم را مشخص کنید.":{"en":"For a Business Unit Lead, only the business unit is selected. For a Manager or Specialist, first select the business unit to filter the team list, then select the team.","fr":"Pour un responsable d’unité d’affaires, seule l’unité d’affaires est sélectionnée. Pour un gestionnaire ou un spécialiste, sélectionnez d’abord l’unité d’affaires afin de filtrer la liste des équipes, puis sélectionnez l’équipe.","es":"Para un Responsable de división, solo se selecciona la división. Para un Gerente o Especialista, selecciona primero la división para filtrar la lista de equipos y luego selecciona el equipo.","ar":"بالنسبة لرئيس الإدارة، تُحدد الإدارة فقط. أما المدير أو الأخصائي، فاختر الإدارة أولاً لتصفية قائمة الفرق ثم اختر الفريق."},"لوگوی سازمان":{"en":"Organization logo","fr":"Logo de l’organisation","es":"Logotipo de la organización","ar":"شعار المؤسسة"},"این لوگو در نوار بالای همه‌ی پنل‌ها نمایش داده می‌شود.":{"en":"This logo appears in the top bar of all dashboards. ⚠️ Note: because this version has no server-side storage for the logo, it remains only for the current session and is cleared when the page reloads.","fr":"Ce logo apparaît dans la barre supérieure de tous les tableaux de bord. ⚠️ Remarque : comme cette version ne stocke pas le logo côté serveur, il reste uniquement pendant la session en cours et disparaît au rechargement de la page.","es":"Este logotipo aparece en la barra superior de todos los paneles. ⚠️ Nota: como esta versión no guarda el logotipo en el servidor, solo permanece durante la sesión actual y se elimina al recargar la página.","ar":"يظهر هذا الشعار في الشريط العلوي لجميع لوحات التحكم. ⚠️ ملاحظة: لأن هذه النسخة لا تحفظ الشعار على الخادم، فإنه يبقى خلال الجلسة الحالية فقط ويُمسح عند إعادة تحميل الصفحة."},"حذف لوگو":{"en":"Remove logo","fr":"Retirer le logo","es":"Eliminar logotipo","ar":"إزالة الشعار"},"برای هر نقش، وزن مشارکت جمعی و مؤلفه‌های مشارکت فردی را جداگانه تعیین کنید. جمع هر بخش باید دقیقاً ۱۰۰٪ باشد.":{"en":"Set organizational-performance and individual-performance weights separately for each role. Each section must total exactly 100%.","fr":"Définissez séparément les pondérations des indicateurs de performance et des composantes de contribution individuelle pour chaque rôle. Chaque section doit totaliser exactement 100 %.","es":"Define por separado las ponderaciones de indicadores estratégicos y componentes de desempeño individual para cada rol. Cada sección debe sumar exactamente 100 %.","ar":"حدد أوزان المؤشرات الاستراتيجية ومكونات الأداء الفردي بشكل منفصل لكل دور. يجب أن يساوي مجموع كل قسم 100٪ بالضبط."},"نقش جدید":{"en":"+ New role","fr":"+ Nouveau rôle","es":"+ Nuevo rol","ar":"+ دور جديد"},"حذف نقش":{"en":"Delete role","fr":"Supprimer le rôle","es":"Eliminar rol","ar":"حذف الدور"},"نام نقش":{"en":"Role name","fr":"Nom du rôle","es":"Nombre del rol","ar":"اسم الدور"},"وزن مشارکت جمعی":{"en":"Organizational performance weights","fr":"Pondération des indicateurs de performance","es":"Ponderaciones de indicadores estratégicos","ar":"أوزان المؤشرات الاستراتيجية"},"ترکیب سطوح تیم، معاونت و سازمان":{"en":"Combination of team, business unit, and organization levels","fr":"Combinaison des niveaux équipe, unité d’affaires et organisation","es":"Combinación de niveles de equipo, división y organización","ar":"مزيج مستويات الفريق والإدارة والمؤسسة"},"وزن مشارکت فردی":{"en":"Individual performance weights","fr":"Pondération de la contribution individuelle","es":"Ponderaciones de contribución individual","ar":"أوزان المساهمة الفردية"},"ترکیب سه مؤلفه مشارکت فردی":{"en":"Combination of the three individual evaluation components","fr":"Combinaison des trois composantes de l’évaluation individuelle","es":"Combinación de los tres componentes de evaluación individual","ar":"مزيج مكونات التقييم الفردي الثلاثة"},"ذخیره تنظیمات نقش":{"en":"Save role settings","fr":"Enregistrer les paramètres du rôle","es":"Guardar configuración del rol","ar":"حفظ إعدادات الدور"},"ذخیره پس از تکمیل نام نقش و رسیدن جمع هر دو بخش به ۱۰۰٪ فعال می‌شود.":{"en":"Saving is enabled after the role name is completed and both sections total 100%.","fr":"L’enregistrement est activé lorsque le nom du rôle est renseigné et que les deux sections totalisent 100 %.","es":"El guardado se habilita cuando se completa el nombre del rol y ambas secciones suman 100 %.","ar":"يتم تفعيل الحفظ بعد إدخال اسم الدور ووصول مجموع كل قسم إلى 100٪."},"سطوح کیفیِ ارزیابی وظایف مستمر":{"en":"Routine evaluation rating levels","fr":"Niveaux qualitatifs de l’évaluation de routine","es":"Niveles cualitativos de evaluación rutinaria","ar":"مستويات التقييم الروتيني"},"مدیر برای پاسخ به هر سؤال وظایف مستمر یکی از این سطوح را انتخاب می‌کند (حداکثر تا ۵ سطح توصیه می‌شود).":{"en":"The manager selects one of these levels for each routine question (up to 5 levels are recommended).","fr":"Le gestionnaire sélectionne l’un de ces niveaux pour chaque question de routine (un maximum de 5 niveaux est recommandé).","es":"El gerente selecciona uno de estos niveles para cada pregunta rutinaria (se recomiendan hasta 5 niveles).","ar":"يختار المدير أحد هذه المستويات لكل سؤال روتيني (يوصى بحد أقصى 5 مستويات)."},"شاخص‌های سازمانی — تعریف":{"en":"Organization indicators — definition","fr":"Indicateurs de l’organisation — définition","es":"Indicadores de la organización — definición","ar":"مؤشرات المؤسسة — التعريف"},"ادمین ساختار، مسئولیت‌ها و زمان‌بندی شاخص را تعریف می‌کند. مقدار واقعی فقط توسط ثبت‌کننده تعیین‌شده وارد و پس از تأیید رسمی وارد محاسبات می‌شود.":{"en":"The admin defines each indicator’s structure, responsibilities, and schedule. Actual values are entered only by the designated submitter and included in calculations after formal approval.","fr":"L’administrateur définit la structure, les responsabilités et le calendrier de chaque indicateur. Les valeurs réelles sont saisies uniquement par le responsable désigné et intégrées aux calculs après approbation formelle.","es":"El administrador define la estructura, responsabilidades y programación de cada indicador. Los valores reales solo los registra el responsable designado y se incluyen en los cálculos tras la aprobación formal.","ar":"يحدد المسؤول هيكل كل مؤشر ومسؤولياته وجدوله الزمني. تُدخل القيم الفعلية فقط بواسطة المسجل المحدد وتدخل في الحسابات بعد الاعتماد الرسمي."},"تقویم و الگوهای زمان‌بندی شاخص‌ها":{"en":"Indicator calendar and schedule templates","fr":"Calendrier des indicateurs et modèles de planification","es":"Calendario de indicadores y plantillas de programación","ar":"تقويم المؤشرات وقوالب الجدولة"},"تاریخ‌های هر نوبت از پایان بازه به‌صورت خودکار محاسبه می‌شوند. تغییر یک الگو روی دوره‌های آینده اثر دارد؛ تاریخ‌های ذخیره‌شده در رکوردهای قبلی ثابت می‌مانند.":{"en":"Dates for each occurrence are calculated automatically from the end of its interval. Changes to a template affect future periods; dates already stored in previous records remain unchanged.","fr":"Les dates de chaque occurrence sont calculées automatiquement à partir de la fin de son intervalle. Les modifications d’un modèle affectent les périodes futures; les dates déjà enregistrées restent inchangées.","es":"Las fechas de cada ocurrencia se calculan automáticamente desde el final de su intervalo. Los cambios en una plantilla afectan a períodos futuros; las fechas ya guardadas permanecen sin cambios.","ar":"تُحسب تواريخ كل فترة تلقائياً من نهاية نطاقها. تؤثر تغييرات القالب على الفترات المستقبلية؛ أما التواريخ المحفوظة سابقاً فتبقى ثابتة."},"Indicator Exception":{"en":"Indicator exception for a period","fr":"Exception d’indicateur pour une période","es":"Excepción de indicador para un período","ar":"استثناء مؤشر لفترة"},"Select indicator":{"en":"Select indicator","fr":"Sélectionner un indicateur","es":"Seleccionar indicador","ar":"اختر المؤشر"},"Select period":{"en":"Select period","fr":"Sélectionner une période","es":"Seleccionar período","ar":"اختر الفترة"},"Save exception":{"en":"Save exception","fr":"Enregistrer l’exception","es":"Guardar excepción","ar":"حفظ الاستثناء"},"این سؤالات مربوط به عملکرد وظایف مستمر و تکراری افراد است که توسط سرپرست مستقیم — نه خودِ فرد — در پایان هر ماه پاسخ داده می‌شود. در هر سطح، ۹ سؤال (۳ سؤال × ۳ ماه) با جمع وزن دقیقاً ۱۰۰٪ تعریف می‌شود.":{"en":"These questions assess recurring routine performance and are answered by the direct supervisor — not the employee — at the end of each month. At each level, 9 questions (3 questions × 3 months) are defined with weights totaling exactly 100%.","fr":"Ces questions évaluent la performance routinière et récurrente et sont répondues par le supérieur direct — et non par l’employé — à la fin de chaque mois. À chaque niveau, 9 questions (3 questions × 3 mois) sont définies avec une pondération totale de 100 %.","es":"Estas preguntas evalúan el desempeño rutinario y recurrente y las responde el supervisor directo — no el empleado — al final de cada mes. En cada nivel se definen 9 preguntas (3 preguntas × 3 meses) con una ponderación total exacta de 100 %.","ar":"تقيّم هذه الأسئلة الأداء الروتيني والمتكرر ويجيب عنها المشرف المباشر — وليس الموظف نفسه — في نهاية كل شهر. في كل مستوى تُحدد 9 أسئلة (3 أسئلة × 3 أشهر) بمجموع أوزان يساوي 100٪ بالضبط."},"دوره":{"en":"Period","fr":"Période","es":"Período","ar":"الفترة"},"فعال‌کردن این دوره به‌عنوان دوره جاری":{"en":"Set this as the current period","fr":"Définir comme période en cours","es":"Establecer como período actual","ar":"تعيين هذه الفترة كفترة حالية"},"بستن، محاسبه نهایی و قفل دوره":{"en":"Close, finalize calculations, and lock period","fr":"Clôturer, finaliser les calculs et verrouiller la période","es":"Cerrar, finalizar cálculos y bloquear el período","ar":"إغلاق الفترة وإنهاء الحسابات وقفلها"},"بازگشایی کنترل‌شده برای اصلاح":{"en":"Controlled reopening for corrections","fr":"Réouverture contrôlée pour corrections","es":"Reapertura controlada para correcciones","ar":"إعادة فتح مضبوطة للتصحيح"},"اطلاعات این دوره فقط‌خواندنی است. بازگشایی فقط با ثبت دلیل انجام می‌شود و Snapshot قبلی تا بستن مجدد محفوظ می‌ماند.":{"en":"This period is read-only. Reopening requires a recorded reason, and the previous snapshot is retained until the period is closed again.","fr":"Cette période est en lecture seule. La réouverture nécessite un motif enregistré et le snapshot précédent est conservé jusqu’à la prochaine clôture.","es":"Este período es de solo lectura. La reapertura requiere un motivo registrado y el snapshot anterior se conserva hasta volver a cerrar el período.","ar":"هذه الفترة للقراءة فقط. تتطلب إعادة الفتح تسجيل سبب، وتبقى اللقطة السابقة محفوظة حتى إغلاق الفترة مرة أخرى."},"برچسب دوره جدید":{"en":"New period label","fr":"Libellé de la nouvelle période","es":"Etiqueta del nuevo período","ar":"تسمية الفترة الجديدة"},"شروع":{"en":"Start","fr":"Début","es":"Inicio","ar":"البداية"},"پایان":{"en":"End","fr":"Fin","es":"Fin","ar":"النهاية"},"دوره جدید":{"en":"+ New period","fr":"+ Nouvelle période","es":"+ Nuevo período","ar":"+ فترة جديدة"},"هنوز شاخصی تعریف نشده — اول از تب «شاخص‌ها» چند شاخص بسازید.":{"en":"No indicators have been defined yet — create a few from the Indicators tab first.","fr":"Aucun indicateur n’a encore été défini — créez-en d’abord dans l’onglet Indicateurs.","es":"Aún no se han definido indicadores — crea algunos primero desde la pestaña Indicadores.","ar":"لم يتم تحديد مؤشرات بعد — أنشئ بعض المؤشرات أولاً من تبويب المؤشرات."},"هیچ دوره‌ای ثبت نشده — از فرم بالا یک دوره بسازید.":{"en":"No periods have been created — use the form above to create one.","fr":"Aucune période n’a été créée — utilisez le formulaire ci-dessus pour en créer une.","es":"No se han creado períodos — usa el formulario de arriba para crear uno.","ar":"لم يتم إنشاء فترات — استخدم النموذج أعلاه لإنشاء فترة."},"Audit log دوره":{"en":"Period audit log","fr":"Journal d’audit de la période","es":"Registro de auditoría del período","ar":"سجل تدقيق الفترة"},"سؤال‌های مشترک، ارزیاب‌های هر فرد و سطح محرمانگی را مدیریت کنید.":{"en":"Manage shared questions, each person’s reviewers, and confidentiality level.","fr":"Gérez les questions communes, les évaluateurs de chaque personne et le niveau de confidentialité.","es":"Gestiona las preguntas comunes, los evaluadores de cada persona y el nivel de confidencialidad.","ar":"أدر الأسئلة المشتركة ومقيّمي كل شخص ومستوى السرية."},"تخصیص خودکار گروهی":{"en":"Bulk automatic assignment","fr":"Affectation automatique en lot","es":"Asignación automática masiva","ar":"تعيين تلقائي جماعي"},"سیستم ابتدا همکاران هم‌سطح همان تیم، سپس همان معاونت و در نهایت سایر همکاران هم‌سطح را انتخاب می‌کند. مدیر مستقیم همیشه حذف می‌شود و بار ارزیابی بین افراد متعادل خواهد شد.":{"en":"The system first selects same-level colleagues from the same team, then from the same business unit, and finally other same-level colleagues. Direct managers are always excluded, and review workload is balanced across people.","fr":"Le système sélectionne d’abord les collègues de même niveau dans la même équipe, puis dans la même unité d’affaires, et enfin les autres collègues de même niveau. Le gestionnaire immédiat est toujours exclu et la charge d’évaluation est répartie équitablement.","es":"El sistema selecciona primero colegas del mismo nivel en el mismo equipo, luego en la misma división y finalmente otros colegas del mismo nivel. El gerente directo siempre se excluye y la carga de evaluación se equilibra entre las personas.","ar":"يختار النظام أولاً الزملاء من المستوى نفسه في الفريق نفسه، ثم في الإدارة نفسها، وأخيراً زملاء آخرين من المستوى نفسه. يُستبعد المدير المباشر دائماً وتُوازن أعباء التقييم بين الأشخاص."},"همه نقش‌ها":{"en":"All roles","fr":"Tous les rôles","es":"Todos los roles","ar":"كل الأدوار"},"تعداد ارزیاب":{"en":"Number of reviewers","fr":"Nombre d’évaluateurs","es":"Número de evaluadores","ar":"عدد المقيّمين"},"پیشنهاد سیستمی ارزیاب‌ها":{"en":"System-recommended reviewers","fr":"Évaluateurs recommandés par le système","es":"Evaluadores recomendados por el sistema","ar":"المقيّمون المقترحون من النظام"},"پیش‌نمایش تخصیص‌ها":{"en":"Assignment preview","fr":"Aperçu des affectations","es":"Vista previa de asignaciones","ar":"معاينة التعيينات"},"تأیید و اعمال همه":{"en":"Confirm and apply all","fr":"Confirmer et tout appliquer","es":"Confirmar y aplicar todo","ar":"تأكيد وتطبيق الكل"},"فرد مورد ارزیابی":{"en":"Person being reviewed","fr":"Personne évaluée","es":"Persona evaluada","ar":"الشخص محل التقييم"},"فعال‌سازی بازخورد در مشارکت فردی":{"en":"Include peer feedback in individual score","fr":"Inclure la rétroaction des collègues dans la contribution individuelle","es":"Incluir comentarios de colegas en la puntuación individual","ar":"تضمين ملاحظات الزملاء في الدرجة الفردية"},"نمایش نتیجه":{"en":"Result visibility","fr":"Visibilité du résultat","es":"Visibilidad del resultado","ar":"عرض النتيجة"},"محرمانه — فقط میانگین":{"en":"Confidential — average only","fr":"Confidentiel — moyenne seulement","es":"Confidencial — solo promedio","ar":"سري — المتوسط فقط"},"گروهی — بدون نام":{"en":"Grouped — no names","fr":"Groupé — sans noms","es":"Agrupado — sin nombres","ar":"مجمّع — بدون أسماء"},"شفاف — همراه نام":{"en":"Transparent — with names","fr":"Transparent — avec noms","es":"Transparente — con nombres","ar":"شفاف — مع الأسماء"},"حداقل پاسخ برای نمایش":{"en":"Minimum responses to show results","fr":"Nombre minimal de réponses pour afficher le résultat","es":"Respuestas mínimas para mostrar resultados","ar":"الحد الأدنى من الردود لعرض النتيجة"},"هم‌تیمی":{"en":"Teammate","fr":"Coéquipier","es":"Compañero de equipo","ar":"زميل في الفريق"},"تعداد ارزیاب‌ها از حداقل پاسخ لازم کمتر است.":{"en":"The number of reviewers is below the minimum responses required.","fr":"Le nombre d’évaluateurs est inférieur au minimum de réponses requis.","es":"El número de evaluadores es inferior al mínimo de respuestas requerido.","ar":"عدد المقيّمين أقل من الحد الأدنى المطلوب من الردود."},"در حال دریافت اطلاعات سازمان...":{"en":"Loading organization data...","fr":"Chargement des données de l’organisation...","es":"Cargando datos de la organización...","ar":"جارٍ تحميل بيانات المؤسسة..."},"تلاش مجدد":{"en":"Try again","fr":"Réessayer","es":"Intentar de nuevo","ar":"حاول مجدداً"},"روز تا پایان":{"en":"days until end of","fr":"jours avant la fin de","es":"días hasta el final de","ar":"أيام حتى نهاية"},"به پایان رسیده":{"en":"has ended","fr":"est terminée","es":"ha finalizado","ar":"انتهت"},"روز تأخیر":{"en":"days overdue","fr":"jours de retard","es":"días de retraso","ar":"أيام تأخير"},"روز تا مهلت ثبت":{"en":"days until submission deadline","fr":"jours avant la date limite de saisie","es":"días hasta la fecha límite de registro","ar":"أيام حتى موعد التسجيل"},"در انتظار":{"en":"pending","fr":"en attente","es":"pendiente","ar":"قيد الانتظار"},"تکمیل‌شده":{"en":"completed","fr":"terminé","es":"completado","ar":"مكتمل"},"روز":{"en":"day","fr":"jour","es":"día","ar":"يوم"},"نفر ثبت شده":{"en":"people registered","fr":"personnes enregistrées","es":"personas registradas","ar":"أشخاص مسجلون"},"نقش، هر دو گروه وزن معتبر است":{"en":"roles, both weight groups are valid","fr":"rôles, les deux groupes de pondération sont valides","es":"roles, ambos grupos de ponderación son válidos","ar":"أدوار، مجموعتا الأوزان صالحتان"},"شاخص تعریف شده":{"en":"indicators defined","fr":"indicateurs définis","es":"indicadores definidos","ar":"مؤشرات محددة"},"شاخص هدف‌گذاری شده":{"en":"indicators have targets","fr":"indicateurs ont une cible","es":"indicadores tienen objetivo","ar":"مؤشرات لها أهداف"},"شاخص تأیید شده":{"en":"indicators approved","fr":"indicateurs approuvés","es":"indicadores aprobados","ar":"مؤشرات معتمدة"}};
Object.assign(LEGACY_UI_TRANSLATIONS, {"فعالیت‌های مستمر":{"en":"Core Responsibilities","fr":"Responsabilités principales","ar":"الأنشطة المستمرة","es":"Actividades recurrentes"},"ثبت فعالیت جدید":{"en":"Register new activity","fr":"Enregistrer une nouvelle activité","ar":"تسجيل نشاط جديد","es":"Registrar nueva actividad"},"فهرست فعالیت‌های غیرمستمر ثبت‌شده":{"en":"Recorded achievements and non-routine activities","fr":"Réalisations et activités non récurrentes enregistrées","ar":"الإنجازات والأنشطة غير المستمرة المسجلة","es":"Logros y actividades no recurrentes registrados"}});
Object.assign(LEGACY_UI_TRANSLATIONS, {"پروفایل و تنظیمات":{"en":"Profile and settings","fr":"Profil et paramètres","ar":"الملف الشخصي والإعدادات","es":"Perfil y configuración"},"اطلاعات شخصی، حساب کاربری و ترجیحات شما":{"en":"Your personal details, account and preferences","fr":"Vos informations personnelles, compte et préférences","ar":"معلوماتك الشخصية وحسابك وتفضيلاتك","es":"Tus datos personales, cuenta y preferencias"},"اطلاعات فردی":{"en":"Personal details","fr":"Informations personnelles","ar":"المعلومات الشخصية","es":"Datos personales"},"اطلاعات سازمانی":{"en":"Organization","fr":"Organisation","ar":"المؤسسة","es":"Organización"},"ترجیحات":{"en":"Preferences","fr":"Préférences","ar":"التفضيلات","es":"Preferencias"}});
Object.assign(LEGACY_UI_TRANSLATIONS, {
  "مشارکت فردی": { en: "Individual Contribution", fr: "Contribution individuelle" },
  "عملکرد فردی": { en: "Individual Contribution", fr: "Contribution individuelle" },
  "مشارکت جمعی": { en: "Collective Contribution", fr: "Contribution collective" },
  "موفقیت جمعی": { en: "Collective Success", fr: "Réussite collective" }
});
Object.assign(LEGACY_UI_TRANSLATIONS, {
  "نمای کلی": { en: "Overview", fr: "Vue d’ensemble" }, "مدیریت": { en: "Management", fr: "Gestion" }, "سازمان": { en: "Organization", fr: "Organisation" },
  "افراد و دسترسی‌ها": { en: "People & Access", fr: "Personnes et accès" }, "مدل CVI": { en: "CVI Model", fr: "Modèle CVI" }, "عملکرد فردی": { en: "Individual Contribution", fr: "Contribution individuelle" },
  "مشارکت جمعی": { en: "Collective Contribution", fr: "Contribution collective" }, "تنظیمات پیشرفته": { en: "Advanced Settings", fr: "Paramètres avancés" },
  "برنامه‌ریزی دوره": { en: "Period Planning", fr: "Planification de la période" }, "پایش ثبت عملکرد": { en: "Performance Monitoring", fr: "Suivi de la performance" },
  "دوره و اجرا": { en: "Period & Operations", fr: "Période et opérations" }, "راهنما": { en: "Help", fr: "Aide" }, "حساب کاربری": { en: "Account", fr: "Compte" },
  "خانه": { en: "Home", fr: "Accueil" }, "افراد": { en: "People", fr: "Personnes" }, "دوره": { en: "Period", fr: "Période" }, "عملکرد": { en: "Performance", fr: "Performance" }, "بیشتر": { en: "More", fr: "Plus" },
  "مدیریت CVI": { en: "CVI Administration", fr: "Administration du CVI" }, "وضعیت کلی سیستم، آمادگی دوره و موارد نیازمند اقدام": { en: "System status, period readiness, and items requiring attention", fr: "État du système, préparation de la période et éléments nécessitant une attention" },
  "آمادگی سیستم": { en: "System Readiness", fr: "Préparation du système" }, "اعضای فعال": { en: "Active People", fr: "Personnes actives" }, "وضعیت دوره جاری": { en: "Current Period", fr: "Période en cours" }, "نیازمند اقدام": { en: "Action Required", fr: "Action requise" },
  "نیازمند توجه شما": { en: "Needs Your Attention", fr: "Éléments à traiter" }, "همه چیز آماده است": { en: "Everything is ready", fr: "Tout est prêt" }, "وضعیت پیکربندی سیستم": { en: "System Configuration Status", fr: "État de la configuration du système" },
  "ساختار سازمانی": { en: "Organizational Structure", fr: "Structure organisationnelle" }, "سمت‌های شغلی": { en: "Job Positions", fr: "Postes" }, "وظایف مستمر واحدها": { en: "Core Responsibilities by Unit", fr: "Responsabilités principales par unité" },
  "شاخص‌های مشارکت جمعی": { en: "Collective Contribution Indicators", fr: "Indicateurs de contribution collective" }, "هدف‌گذاری دوره": { en: "Period Targets", fr: "Cibles de la période" }, "ثبت عملکرد دوره": { en: "Period Performance", fr: "Performance de la période" },
  "کارشناس": { en: "Employee", fr: "Employé" }, "مدیر": { en: "Manager", fr: "Gestionnaire" }, "معاون": { en: "Business Unit Lead", fr: "Responsable d’unité d’affaires" }, "مدیرعامل": { en: "Executive", fr: "Haute direction" }, "ادمین": { en: "Administrator", fr: "Administrateur" },
  "نام و نام‌خانوادگی": { en: "Full name", fr: "Nom complet" }, "کد پرسنلی": { en: "Employee ID", fr: "Matricule" }, "کد ملی": { en: "Internal ID", fr: "Identifiant interne" }, "ایمیل": { en: "Email", fr: "Courriel" }, "نام کاربری": { en: "Username", fr: "Nom d’utilisateur" }, "رمز عبور": { en: "Password", fr: "Mot de passe" },
  "سطح دسترسی": { en: "Access Level", fr: "Niveau d’accès" }, "واحد سازمانی": { en: "Organization Unit", fr: "Unité organisationnelle" }, "مدیر مستقیم": { en: "Direct Manager", fr: "Gestionnaire immédiat" },
  "اجزای ارزیابی عملکرد فردی را از یک بخش مدیریت کنید.": { en: "Manage all Individual Contribution components in one place.", fr: "Gérez toutes les composantes de la contribution individuelle au même endroit." },
  "وظایف مستمر": { en: "Core Responsibilities", fr: "Responsabilités principales" }, "بازخورد همکاران": { en: "Peer Feedback", fr: "Rétroaction des collègues" },
  "شاخص‌های کلان سازمان و شاخص‌های واحدهای سازمانی را تعریف و زمان‌بندی کنید.": { en: "Define and schedule organization-wide and unit-level contribution indicators.", fr: "Définissez et planifiez les indicateurs de contribution à l’échelle de l’organisation et des unités." },
  "شاخص‌ها": { en: "Indicators", fr: "Indicateurs" }, "زمان‌بندی": { en: "Scheduling", fr: "Planification" }, "کل سازمان": { en: "Organization-wide", fr: "À l’échelle de l’organisation" },
  "سطح ارزیابی": { en: "Evaluation Scope", fr: "Portée de l’évaluation" }, "الگوی ارزیابی فعال": { en: "Active Evaluation Template", fr: "Modèle d’évaluation actif" }, "تغییر الگو": { en: "Change Template", fr: "Changer le modèle" },
  "عنوان شاخص": { en: "Indicator", fr: "Indicateur" }, "سطح اهمیت": { en: "Importance Level", fr: "Niveau d’importance" }, "وزن محاسباتی": { en: "Normalized Weight", fr: "Poids normalisé" }, "وزن محاسباتی فعلی": { en: "Current normalized weight", fr: "Poids normalisé actuel" },
  "واحد اندازه‌گیری": { en: "Unit of Measure", fr: "Unité de mesure" }, "روش تجمیع": { en: "Aggregation Method", fr: "Méthode d’agrégation" }, "مالک شاخص": { en: "Indicator Owner", fr: "Responsable de l’indicateur" }, "ثبت‌کننده": { en: "Data Submitter", fr: "Responsable de saisie" }, "تأییدکننده": { en: "Approver", fr: "Approbateur" },
  "بانک سؤالات": { en: "Question Bank", fr: "Banque de questions" }, "مقیاس اهمیت": { en: "Importance Scale", fr: "Échelle d’importance" }, "الگوهای ارزیابی": { en: "Evaluation Templates", fr: "Modèles d’évaluation" }, "دسته‌های فعالیت": { en: "Additional Contribution Categories", fr: "Catégories de contributions additionnelles" }, "سطوح تأثیر": { en: "Impact Levels", fr: "Niveaux d’impact" }
});

Object.assign(LEGACY_UI_TRANSLATIONS, {
  "توضیح یا شواهد (اختیاری)": { en: "Optional comment or supporting evidence", fr: "Commentaire ou pièce justificative (facultatif)" },
  "توضیح یا شواهد": { en: "Comment or supporting evidence", fr: "Commentaire ou pièce justificative" },
  "اختیاری": { en: "optional", fr: "facultatif" },
  "مستمر": { en: "Core Responsibilities", fr: "Responsabilités principales" },
  "غیرمستمر": { en: "Additional Contributions", fr: "Contributions additionnelles" },
  "تلاش مجدد": { en: "Try again", fr: "Réessayer" },
  "سایر بخش‌های پنل": { en: "Additional workspace sections", fr: "Autres sections de l’espace de travail" },
  "در مراحل بعدی توسعه اضافه می‌شود": { en: "will be added in a future release", fr: "seront ajoutées dans une prochaine version" },
  "توضیحات": { en: "Comments", fr: "Commentaires" },
  "شواهد": { en: "Supporting evidence", fr: "Pièces justificatives" },
  "بستن": { en: "Close", fr: "Fermer" }
});

// Canadian EN/FR copy for Persian labels that can still arrive as data (older records, server-side
// defaults). Restored after it was dropped in the structural port; existing entries keep priority.
for (const [faText, copy] of Object.entries(CANADA_COPY_OVERRIDES)) {
  if (!LEGACY_UI_TRANSLATIONS[faText]) LEGACY_UI_TRANSLATIONS[faText] = copy;
}

// Labels the server still sends in Persian (template defaults, CVI component and level names,
// default importance scale). Explicit so they never depend on partial phrase matches.
Object.assign(LEGACY_UI_TRANSLATIONS, {
  "الگوی جدید": { en: "New template", fr: "Nouveau modèle" },
  "شاخص‌محور": { en: "Indicator-based", fr: "Axé sur les indicateurs" },
  "ارزیابی کیفی": { en: "Qualitative evaluation", fr: "Évaluation qualitative" },
  "ترکیبی": { en: "Blended", fr: "Mixte" },
  "اهداف فردی": { en: "Individual goals", fr: "Objectifs individuels" },
  "مشارکت تیمی": { en: "Team contribution", fr: "Contribution de l’équipe" },
  "مشارکت واحد بالادستی": { en: "Parent-unit contribution", fr: "Contribution des unités parentes" },
  "مشارکت سازمانی": { en: "Organization contribution", fr: "Contribution de l’organisation" },
});

// Reuse the canonical i18n dictionary as a reverse lookup for legacy Persian literals.
// This prevents already-translated product copy from falling through to the legacy guard.
for (const [key, faText] of Object.entries(UI_DICTIONARY.fa || {})) {
  if (typeof faText !== "string" || !faText) continue;
  const existing = LEGACY_UI_TRANSLATIONS[faText] || {};
  LEGACY_UI_TRANSLATIONS[faText] = {
    ...existing,
    en: UI_DICTIONARY.en?.[key] ?? existing.en,
    fr: UI_DICTIONARY.fr?.[key] ?? existing.fr,
  };
}

const LEGACY_UI_TRANSLATION_KEYS = Object.keys(LEGACY_UI_TRANSLATIONS)
  .filter(key => key.length >= 3)
  .sort((a, b) => b.length - a.length);

function translateLegacyUiText(value, language) {
  if (value == null || language === "fa") return value;
  const source = String(value);
  const exact = LEGACY_UI_TRANSLATIONS[source]?.[language];
  if (exact != null) return exact;
  let translated = source;
  for (const key of LEGACY_UI_TRANSLATION_KEYS) {
    if (!translated.includes(key)) continue;
    const replacement = LEGACY_UI_TRANSLATIONS[key]?.[language];
    if (replacement != null) translated = translated.split(key).join(replacement);
  }
  const digitMap = { "۰":"0", "۱":"1", "۲":"2", "۳":"3", "۴":"4", "۵":"5", "۶":"6", "۷":"7", "۸":"8", "۹":"9" };
  translated = translated.replace(/[۰-۹]/g, digit => digitMap[digit] || digit);
  // Zero-width joiners only make sense between Persian letters; drop any left beside English/French text.
  translated = translated.replace(/[\u200C\u200D]/g, "");
  if (/[\u0600-\u06FF]/.test(translated)) {
    if (typeof console !== "undefined" && console.warn) console.warn("Unmapped legacy Persian UI copy:", source);
    // Never surface an internal localization failure message to end users.
    // Keep any already-translated Latin/numeric context; otherwise use a neutral em dash.
    const cleaned = translated
      .replace(/[\u0600-\u06FF\u200C\u200D\u200E\u200F]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(/\s+([,.:;%])/g, "$1")
      .trim();
    return cleaned || "—";
  }
  return translated;
}

const CANADA_EN_FR_COPY = {
  "Current period · out of 100": "Période en cours · sur 100",
  "No team indicators are configured for this period.": "Aucun indicateur d’équipe n’est configuré pour cette période.",
  "Period readiness": "Préparation de la période",
  "Period progress": "Avancement de la période",
  "Use this step to confirm the period setup, indicator targets, and activation readiness.": "Utilisez cette étape pour confirmer la configuration de la période, les cibles des indicateurs et la préparation à l’activation.",
  "Period indicators": "Indicateurs de la période",
  "Period status": "Statut de la période",
  "All indicator targets are ready for this period.": "Toutes les cibles des indicateurs sont prêtes pour cette période.",
  "CVI combines Individual Contribution and Collective Contribution using a geometric mean. Only eligible data available for the current period is included.": "Le CVI combine la contribution individuelle et la contribution collective au moyen d’une moyenne géométrique. Seules les données admissibles disponibles pour la période en cours sont incluses.",
  "Overview":"Vue d’ensemble",
  "Management":"Gestion",
  "Organization":"Organisation",
  "People & Access":"Personnes et accès",
  "Job Positions":"Postes",
  "Individual Contribution":"Contribution individuelle",
  "Collective Contribution":"Contribution collective",
  "Advanced Settings":"Paramètres avancés",
  "Period Planning":"Planification de la période",
  "Performance Monitoring":"Suivi de la performance",
  "Inbox":"Boîte de réception",
  "Employee Reviews":"Évaluations des employés",
  "Period & Operations":"Période et opérations",
  "Help":"Aide",
  "Profile":"Profil",
  "Account":"Compte",
  "Home":"Accueil",
  "People":"Personnes",
  "Period":"Période",
  "Performance":"Performance",
  "More":"Plus",
  "CVI Administration":"Administration du CVI",
  "Setup Readiness":"Préparation",
  "Active Period":"Période active",
  "Needs Attention":"À traiter",
  "Needs Your Attention":"Éléments à traiter",
  "Setup Progress":"Progression de la configuration",
  "Everything is ready":"Tout est prêt",
  "No configuration issues require attention.":"Aucun problème de configuration ne nécessite votre attention.",
  "Review":"Examiner",
  "Open":"Ouvrir",
  "Organizational Structure":"Structure organisationnelle",
  "Core Responsibilities by Unit":"Responsabilités principales par unité",
  "Collective Contribution Indicators":"Indicateurs de contribution collective",
  "Period Targets":"Cibles de la période",
  "Period Performance":"Performance de la période",
  "No active period":"Aucune période active",
  "Employee":"Employé",
  "Manager":"Gestionnaire",
  "Director":"Directeur",
  "Executive":"Haute direction",
  "Administrator":"Administrateur",
  "Full name":"Nom complet",
  "Employee ID":"Matricule",
  "Internal ID":"Identifiant interne",
  "Email":"Courriel",
  "Username":"Nom d’utilisateur",
  "Password":"Mot de passe",
  "Access level":"Niveau d’accès",
  "Job position":"Poste",
  "Organizational unit":"Unité organisationnelle",
  "Direct manager":"Gestionnaire immédiat",
  "Select access level":"Sélectionner le niveau d’accès",
  "Select job position":"Sélectionner le poste",
  "Select organizational unit":"Sélectionner une unité organisationnelle",
  "Select direct manager":"Sélectionner le gestionnaire immédiat",
  "Search by name, email, username, or employee ID…":"Rechercher par nom, courriel, nom d’utilisateur ou matricule…",
  "All access levels":"Tous les niveaux d’accès",
  "All organizational units":"Toutes les unités organisationnelles",
  "Clear":"Effacer",
  "Close":"Fermer",
  "Person added successfully.":"Personne ajoutée avec succès.",
  "Person details saved.":"Informations enregistrées.",
  "Person deactivated.":"Personne désactivée.",
  "Core Configuration":"Configuration de base",
  "Period Review":"Révision de la période",
  "Period Data":"Données de période",
  "Evaluation Templates":"Modèles d’évaluation",
  "Scales & Ratings":"Échelles et niveaux",
  "Additional Contribution Categories":"Catégories de contributions additionnelles",
  "Importance Scale":"Échelle d’importance",
  "Impact Rating Scale":"Échelle d’impact",
  "Core Responsibility Rating Scale":"Échelle des responsabilités principales",
  "Core Responsibilities":"Responsabilités principales",
  "Peer Feedback":"Rétroaction des collègues",
  "Indicators":"Indicateurs",
  "Scheduling":"Planification",
  "Organization-wide":"À l’échelle de l’organisation",
  "Evaluation Scope":"Portée de l’évaluation",
  "Active Evaluation Template":"Modèle d’évaluation actif",
  "Change Template":"Changer le modèle",
  "Indicator":"Indicateur",
  "Importance Level":"Niveau d’importance",
  "Normalized Weight":"Poids normalisé",
  "Unit of Measure":"Unité de mesure",
  "Aggregation Method":"Méthode d’agrégation",
  "Indicator Owner":"Responsable de l’indicateur",
  "Data Submitter":"Responsable de saisie",
  "Approver":"Approbateur",
  "Edit":"Modifier",
  "Delete":"Supprimer",
  "Save":"Enregistrer",
  "Cancel":"Annuler",
  "Save changes":"Enregistrer les modifications",
  "Add indicator":"Ajouter un indicateur",
  "Select person":"Sélectionner une personne",
  "Template name":"Nom du modèle",
  "Frequency":"Fréquence",
  "Monthly":"Mensuel",
  "Quarterly":"Trimestriel",
  "Annual":"Annuel",
  "Question":"Question",
  "Question Bank":"Banque de questions",
  "Month 1":"Mois 1",
  "Month 2":"Mois 2",
  "Month 3":"Mois 3",
  "Selected Period":"Période sélectionnée",
  "Year":"Année",
  "Quarter / Period":"Trimestre / période",
  "New period":"Nouvelle période",
  "Create period":"Créer la période",
  "Activate period":"Activer la période",
  "Targets Set":"Cibles définies",
  "Period Status":"Statut de la période",
  "Submitted":"Soumis",
  "Pending Approval":"En attente d’approbation",
  "Not Submitted":"Non soumis",
  "Target":"Cible",
  "Actual":"Réel",
  "Status":"Statut",
  "Not set":"Non défini",
  "Finalize Period":"Finaliser la période",
  "Finalize & Lock":"Finaliser et verrouiller",
  "Period Finalized":"Période finalisée",
  "Reopen for Correction":"Rouvrir pour correction",
  "Period History":"Historique de la période",
  "Feedback provider assignments":"Affectation des répondants",
  "Peer Feedback Settings":"Paramètres de rétroaction des collègues",
  "Automatic feedback provider assignment":"Affectation automatique des répondants",
  "Generate Suggestions":"Générer des suggestions",
  "Assignment Preview":"Aperçu des affectations",
  "Apply Assignments":"Appliquer les affectations",
  "Person receiving feedback":"Personne qui reçoit la rétroaction",
  "Feedback visibility":"Visibilité de la rétroaction",
  "Minimum responses required":"Réponses minimales requises",
  "Selected feedback providers":"Répondants sélectionnés",
  "Same unit":"Même unité",
  "Save Feedback Settings":"Enregistrer les paramètres de rétroaction",
  "Organization Profile":"Profil de l’organisation",
  "Organization profile and branding":"Profil et image de marque de l’organisation",
  "Organization Name":"Nom de l’organisation",
  "Default language":"Langue par défaut",
  "Time zone":"Fuseau horaire",
  "Date format":"Format de date",
  "Accent colour":"Couleur d’accentuation",
  "Change Logo":"Changer le logo",
  "Upload Logo":"Téléverser un logo",
  "Remove logo":"Retirer le logo",
  "Save Organization Settings":"Enregistrer les paramètres de l’organisation",
  "Loading...":"Chargement…",
  "Organizational units":"Unités organisationnelles",
  "Active people":"Personnes actives",
  "Top-level organizational units":"Unités organisationnelles de premier niveau",
  "Organization Chart":"Organigramme",
  "Add unit":"Ajouter une unité",
  "Search units or teams...":"Rechercher une unité ou une équipe…",
  "Active indicators":"Indicateurs actifs",
  "Child units":"Sous-unités",
  "Quick actions":"Actions rapides",
  "Add child unit":"Ajouter une sous-unité",
  "Edit unit":"Modifier l’unité",
  "View people":"Voir les personnes",
  "View indicators":"Voir les indicateurs",
  "Collective Contribution Template":"Modèle de contribution collective",
  "Individual Contribution Template":"Modèle de contribution individuelle",
  "Default":"Par défaut",
  "Contribution Assessment":"Évaluation de la contribution",
  "Contribution Assessment Templates":"Modèles d’évaluation de la contribution",
  "Collective Contribution Templates":"Modèles de contribution collective",
  "Individual Contribution Templates":"Modèles de contribution individuelle",
  "Qualitative review":"Évaluation qualitative",
  "Team":"Équipe",
  "Parent units":"Unités parentes",
  "Responsibilities":"Responsabilités",
  "Goals":"Objectifs",
  "Peer feedback":"Rétroaction des collègues",
  "Core responsibilities":"Responsabilités principales",
  "Additional contributions":"Contributions additionnelles",
  "Default template":"Modèle par défaut",
  "Set as default":"Définir par défaut",
  "Delete template":"Supprimer le modèle",
  "Save templates":"Enregistrer les modèles",
  "Saving...":"Enregistrement…",
  "New Template":"Nouveau modèle",
  "All templates are valid ✓":"Tous les modèles sont valides ✓",
  "One or more templates need attention.":"Un ou plusieurs modèles nécessitent une attention.",
  "— must equal 100%":"— doit totaliser 100\u00a0%",
  "person":"personne",
  "people":"personnes",
  "A clear view of setup readiness, the active period, and what needs your attention.":"Une vue claire de la préparation, de la période active et des éléments à traiter.",
  "Connect people to positions, organizational units, and direct managers.":"Associez les personnes aux postes, aux unités organisationnelles et aux gestionnaires immédiats.",
  "Search and filter people quickly. Open a profile to review access, reporting relationships, and position details.":"Recherchez et filtrez rapidement les personnes. Ouvrez un profil pour vérifier les accès, les liens hiérarchiques et les détails du poste.",
  "Configure organization-wide CVI rules that usually change infrequently.":"Configurez les règles CVI à l’échelle de l’organisation qui changent rarement.",
  "Used across CVI wherever an administrator selects qualitative importance instead of entering a raw weight.":"Utilisée dans CVI lorsqu’un administrateur choisit un niveau d’importance qualitatif plutôt qu’un poids brut.",
  "Defines the rating levels used to describe the impact of additional contributions.":"Définit les niveaux servant à décrire l’impact des contributions additionnelles.",
  "Define the rating choices used when employees are assessed on core responsibilities. Three to five levels are usually enough.":"Définissez les choix de notation utilisés pour l’évaluation des responsabilités principales. Trois à cinq niveaux suffisent généralement.",
  "Keep this list concise and reusable. These categories organize additional contributions without changing the core CVI model.":"Gardez cette liste concise et réutilisable. Ces catégories structurent les contributions additionnelles sans modifier le modèle CVI de base.",
  "Define how day-to-day responsibilities and peer feedback contribute to Individual Contribution.":"Définissez comment les responsabilités quotidiennes et la rétroaction des collègues contribuent à la contribution individuelle.",
  "Define the few shared indicators that connect team and organization results to CVI.":"Définissez quelques indicateurs communs qui relient les résultats des équipes et de l’organisation au CVI.",
  "Choose whether these indicators apply to the whole organization or to a specific unit.":"Choisissez si ces indicateurs s’appliquent à toute l’organisation ou à une unité précise.",
  "These indicators apply across the organization.":"Ces indicateurs s’appliquent à l’ensemble de l’organisation.",
  "No evaluation template has been defined yet.":"Aucun modèle d’évaluation n’a encore été défini.",
  "No indicators have been added for this scope yet.":"Aucun indicateur n’a encore été ajouté pour cette portée.",
  "weights are normalized automatically":"les poids sont normalisés automatiquement",
  "Require supporting evidence for performance submissions":"Exiger une pièce justificative pour les valeurs d’indicateurs soumises",
  "Complete the indicator name, unit, importance, owners, and schedule.":"Complétez le nom de l’indicateur, l’unité, l’importance, les responsables et la planification.",
  "The data submitter and approver must be different people.":"Le responsable de saisie et l’approbateur doivent être deux personnes différentes.",
  "Create a review period, set indicator targets, and activate it when the setup is ready.":"Créez une période d’évaluation, définissez les cibles des indicateurs et activez-la lorsque la configuration est prête.",
  "Track submissions and approvals, resolve outstanding items, and finalize the period when complete.":"Suivez les soumissions et les approbations, traitez les éléments en attente et finalisez la période lorsqu’elle est complète.",
  "No periods have been created yet.":"Aucune période n’a encore été créée.",
  "No indicators are configured yet. Add the required indicators under Collective Contribution first.":"Aucun indicateur n’est configuré. Ajoutez d’abord les indicateurs requis dans Contribution collective.",
  "Actual values can only be entered by the assigned data submitter.":"Les valeurs réelles ne peuvent être saisies que par le responsable de saisie désigné.",
  "Once submissions and approvals are complete, finalize the period to lock the results.":"Lorsque les soumissions et les approbations sont terminées, finalisez la période pour verrouiller les résultats.",
  "Results are read-only. Reopen the period only when a correction is required.":"Les résultats sont en lecture seule. Rouvrez la période uniquement lorsqu’une correction est nécessaire.",
  "Manage shared questions, feedback provider assignments and feedback privacy.":"Gérez les questions communes, l’affectation des répondants et la confidentialité de la rétroaction.",
  "CVI prioritizes peers in the same unit, then nearby units in the organization structure, while balancing review workload. Direct managers are excluded from peer assignments.":"CVI privilégie les collègues de la même unité, puis les unités proches dans la structure organisationnelle, tout en équilibrant la charge d’évaluation. Les gestionnaires immédiats sont exclus.",
  "Include peer feedback in Individual Contribution":"Inclure la rétroaction des collègues dans la contribution individuelle",
  "The number of selected feedback providers is below the minimum number of responses.":"Le nombre de répondants sélectionnés est inférieur au nombre minimal de réponses.",
  "Manage organization details, organizational structure, and positions in one place.":"Gérez les renseignements sur l’organisation, sa structure organisationnelle et ses postes au même endroit.",
  "Keep the company name, logo, language, and regional display settings consistent across CVI.":"Gardez le nom de l’entreprise, le logo, la langue et les paramètres régionaux cohérents dans CVI.",
  "Logo file must be smaller than 750 KB.":"Le fichier du logo doit faire moins de 750 Ko.",
  "Could not add the person.":"Impossible d’ajouter la personne.",
  "Could not update the person.":"Impossible de mettre à jour la personne.",
  "Could not remove the person.":"Impossible de retirer la personne.",
  "Full name is required.":"Le nom complet est requis.",
  "Employee ID is required.":"Le matricule est requis.",
  "Email is required.":"Le courriel est requis.",
  "Enter a valid email address.":"Entrez une adresse courriel valide.",
  "Username is required.":"Le nom d’utilisateur est requis.",
  "Password must be at least 6 characters.":"Le mot de passe doit contenir au moins 6 caractères.",
  "Access level is required.":"Le niveau d’accès est requis.",
  "Select a job position.":"Sélectionnez un poste.",
  "Select an organization unit.":"Sélectionnez une unité organisationnelle.",
  "Select a direct manager.":"Sélectionnez un gestionnaire immédiat.",
  "Could not save the importance scale.":"Impossible d’enregistrer l’échelle d’importance.",
  "Could not delete the level.":"Impossible de supprimer le niveau.",
  "A name is required.":"Un nom est requis.",
  "Score must be greater than zero.":"La valeur doit être supérieure à zéro.",
  "Could not load evaluation templates.":"Impossible de charger les modèles d’évaluation.",
  "Evaluation template updated.":"Modèle d’évaluation mis à jour.",
  "Could not change the evaluation template.":"Impossible de changer le modèle d’évaluation.",
  "Indicator updated.":"Indicateur mis à jour.",
  "Indicator added.":"Indicateur ajouté.",
  "Could not save the indicator.":"Impossible d’enregistrer l’indicateur.",
  "Could not delete the indicator.":"Impossible de supprimer l’indicateur.",
  "Could not save the indicator target.":"Impossible d’enregistrer la cible de l’indicateur.",
  "Could not activate the period.":"Impossible d’activer la période.",
  "Could not create the period.":"Impossible de créer la période.",
  "Period finalized and locked successfully.":"Période finalisée et verrouillée avec succès.",
  "Could not finalize the period.":"Impossible de finaliser la période.",
  "Enter a reason for reopening the period:":"Entrez la raison de la réouverture de la période :",
  "Could not apply feedback provider assignments.":"Impossible d’appliquer les affectations des répondants.",
  "Feedback settings saved.":"Paramètres de rétroaction enregistrés.",
  "Could not save feedback settings.":"Impossible d’enregistrer les paramètres de rétroaction.",
  "Organization settings saved.":"Paramètres de l’organisation enregistrés.",
  "Could not save organization settings.":"Impossible d’enregistrer les paramètres de l’organisation.",
  "Active":"Actif",
  "Active-period settings and assignments saved.":"Les paramètres et affectations de la période active ont été enregistrés.",
  "Activity":"Activité",
  "Contribution category":"Catégorie de contribution",
  "Contribution category name is required.":"Le nom de la catégorie de contribution est requis.",
  "Advanced Settings sections":"Sections des paramètres avancés",
  "Approval deadline (days after submission)":"Délai d’approbation (jours après la soumission)",
  "Authentication required.":"Authentification requise.",
  "Average":"Moyenne",
  "Based on the current configuration and active period":"Selon la configuration actuelle et la période active",
  "CVI Model":"Modèle CVI",
  "CVI Organization":"Organisation CVI",
  "Collaboration / Team":"Collaboration / Équipe",
  "Collective Contribution sections":"Sections de la contribution collective",
  "Complete the new indicator details.":"Renseignez les détails du nouvel indicateur.",
  "Core Responsibility Question Levels":"Niveaux d’évaluation des responsabilités principales",
  "Could not add the contribution category.":"Impossible d’ajouter la catégorie de contribution.",
  "Could not add the core responsibility question.":"Impossible d’ajouter la question sur les responsabilités principales.",
  "Could not add the feedback question.":"Impossible d’ajouter la question de rétroaction.",
  "Could not add the impact level.":"Impossible d’ajouter le niveau d’impact.",
  "Could not add the question.":"Impossible d’ajouter la question.",
  "Could not add the responsibility level.":"Impossible d’ajouter le niveau de responsabilité.",
  "Could not add the selected question.":"Impossible d’ajouter la question sélectionnée.",
  "Could not delete the contribution category.":"Impossible de supprimer la catégorie de contribution.",
  "Could not delete the core responsibility question.":"Impossible de supprimer la question sur les responsabilités principales.",
  "Could not delete the feedback question.":"Impossible de supprimer la question de rétroaction.",
  "Could not delete the impact level.":"Impossible de supprimer le niveau d’impact.",
  "Could not delete the responsibility level.":"Impossible de supprimer le niveau de responsabilité.",
  "Could not load the importance scale.":"Impossible de charger l’échelle d’importance.",
  "Could not update the contribution category.":"Impossible de mettre à jour la catégorie de contribution.",
  "Could not update the core responsibility question.":"Impossible de mettre à jour la question sur les responsabilités principales.",
  "Could not update the feedback question.":"Impossible de mettre à jour la question de rétroaction.",
  "Could not update the impact level.":"Impossible de mettre à jour le niveau d’impact.",
  "Could not update the responsibility level.":"Impossible de mettre à jour le niveau de responsabilité.",
  "Draft":"Brouillon",
  "Edit Indicator":"Modifier l’indicateur",
  "Ends":"Fin",
  "Enter a valid internal reference.":"Saisissez une référence interne valide.",
  "Enter the question and select an importance level.":"Saisissez la question et sélectionnez un niveau d’importance.",
  "Exception reason (required)":"Motif de l’exception (obligatoire)",
  "Finalize & Lock Period":"Finaliser et verrouiller la période",
  "Finalized and locked":"Finalisée et verrouillée",
  "Finalizing":"Finalisation",
  "Goal":"Objectif",
  "Icon":"Icône",
  "Impact level name":"Nom du niveau d’impact",
  "Importance scale load failed":"Échec du chargement de l’échelle d’importance",
  "Importance scale load failed:":"Échec du chargement de l’échelle d’importance :",
  "Improvement / Performance":"Amélioration / Performance",
  "Indicator target save failed:":"Échec de l’enregistrement de la cible de l’indicateur :",
  "Individual Contribution sections":"Sections de la contribution individuelle",
  "Internal reference is required.":"La référence interne est requise.",
  "Latest value":"Dernière valeur",
  "Level name":"Nom du niveau",
  "Lock date (days after period end)":"Date de verrouillage (jours après la fin de la période)",
  "Lock date must be after the approval deadline.":"La date de verrouillage doit être postérieure au délai d’approbation.",
  "Maximum score":"Nombre maximal de points",
  "Minimum 6 characters":"Minimum de 6 caractères",
  "Needs manual assignment":"Affectation manuelle requise",
  "One or more job positions need configuration":"Un ou plusieurs postes doivent être configurés",
  "Organization Logo":"Logo de l’organisation",
  "Organizational Units":"Unités organisationnelles",
  "Professional certification, internal training":"Certification professionnelle, formation interne",
  "Question is required.":"La question est requise.",
  "Reminder (days before deadline)":"Rappel (jours avant l’échéance)",
  "Reopen Period":"Rouvrir la période",
  "Score":"Résultat",
  "Select importance level":"Sélectionner un niveau d’importance",
  "Session expired.":"Session expirée.",
  "Administration and settings":"Administration et paramètres",
  "Settings saved. Activate a period before generating feedback provider assignments.":"Paramètres enregistrés. Activez une période avant de générer les affectations des répondants.",
  "Standard Quarterly Review":"Révision trimestrielle standard",
  "Subcategories (comma-separated)":"Sous-catégories (séparées par des virgules)",
  "Submission deadline (days after period end)":"Échéance de soumission (jours après la fin de la période)",
  "Submission opens (days after period end)":"Ouverture des soumissions (jours après la fin de la période)",
  "Sum":"Somme",
  "Template name and frequency are required.":"Le nom du modèle et la fréquence sont requis.",
  "The operation could not be completed.":"L’opération n’a pas pu être effectuée.",
  "The operation could not be completed..":"L’opération n’a pas pu être effectuée.",
  "This period is finalized and locked.":"Cette période est finalisée et verrouillée.",
  "This template is assigned to one or more indicators and cannot be deleted.":"Ce modèle est attribué à un ou plusieurs indicateurs et ne peut pas être supprimé.",
  "Update the indicator details and save your changes.":"Mettez à jour les détails de l’indicateur, puis enregistrez vos modifications.",
  "Valid":"Valide",
  "Balanced":"Équilibré",
  "Collaboration-Focused":"Axé sur la collaboration",
  "Collapse child units":"Réduire les sous-unités",
  "Could not load organization data. Please try again.":"Impossible de charger les données de l’organisation. Veuillez réessayer.",
  "Could not load the requested data. Please try again.":"Impossible de charger les données demandées. Veuillez réessayer.",
  "Could not load unit indicators.":"Impossible de charger les indicateurs de l’unité.",
  "Could not save templates.":"Impossible d’enregistrer les modèles.",
  "Dashboard Preview":"Aperçu du tableau de bord",
  "Expand child units":"Développer les sous-unités",
  "Goal-Focused":"Axé sur les objectifs",
  "Hybrid":"Hybride",
  "Indicator-Based":"Axé sur les indicateurs",
  "Local Impact":"Impact local",
  "More options":"Plus d’options",
  "Not assigned":"Non attribué",
  "Qualitative":"Qualitatif",
  "Standard":"Standard",
  "Strategic":"Stratégique",
  "Task-Focused":"Axé sur les tâches",
  "Unit":"Unité",
  "Unit-Focused":"Axé sur l’unité",
  "Internal reference":"Référence interne",
  "No items yet":"Aucun élément pour le moment",
  "Add the first item to get started.":"Ajoutez le premier élément pour commencer.",
  "View":"Voir",
  "Deactivate item":"Désactiver l’élément",
  "This item will be removed from the active list. Historical records will be retained.":"Cet élément sera retiré de la liste active. Les données historiques seront conservées.",
  "Deactivate":"Désactiver",
  "Add item":"Ajouter un élément",
  "Update the information and save your changes.":"Mettez à jour les renseignements, puis enregistrez vos modifications.",
  "Complete the details for the new item.":"Renseignez les détails du nouvel élément.",
  "Could not add the item.":"Impossible d’ajouter l’élément.",
  "Could not save the changes.":"Impossible d’enregistrer les modifications.",
  "Very low":"Très faible",
};
// My Performance and dashboard copy that predates the Canada EN/FR layer.
Object.assign(CANADA_EN_FR_COPY, {
  "My Performance": "Mes résultats",
  "View components": "Voir les composantes",
  "out of 100": "sur 100",
  "Current CVI": "CVI actuel",
  "Current period": "Période en cours",
  "View another period": "Voir une autre période",
  "View summary": "Voir le sommaire",
  "Responsibilities progress": "Progression des responsabilités",
  "Goal progress": "Progression des objectifs",
  "Feedback coverage": "Couverture de la rétroaction",
  "No responsibility checks configured": "Aucune vérification des responsabilités configurée",
  "responsibility checks complete": "vérifications des responsabilités terminées",
  "responses received": "réponses reçues",
  "Peer feedback not configured": "Rétroaction des collègues non configurée",
  "No active goals": "Aucun objectif actif",
  "active goals": "objectifs actifs",
  "active goal": "objectif actif",
  "PERFORMANCE OVER TIME": "RÉSULTATS DANS LE TEMPS",
  "Your CVI trajectory": "Votre trajectoire CVI",
  "See how your CVI, Individual Contribution, and Collective Contribution change from one period to the next.": "Suivez l’évolution de votre CVI, de votre contribution individuelle et de votre contribution collective d’une période à l’autre.",
  "CVI trend": "Tendance du CVI",
  "CVI, Individual Contribution, and Collective Contribution across recent periods": "CVI, contribution individuelle et contribution collective au cours des dernières périodes",
  "CVI across the last four periods": "CVI des quatre dernières périodes",
  "Best period": "Meilleure période",
  "View period history →": "Voir l’historique des périodes →",
  "Trend data will appear after the first completed period.": "La tendance s’affichera après la première période terminée.",
  "Privacy & data rights": "Confidentialité et droits",
  "Employee Experience": "Expérience employé",
  "Period Management": "Gestion des périodes",
  "Learn what your CVI reflects": "Comprendre ce que reflète votre CVI",
  "SCORE COMPONENTS": "COMPOSANTES DU RÉSULTAT",
  "SCORE DETAILS": "DÉTAIL DU RÉSULTAT",
  "Open full details →": "Voir tout le détail →",
  "Individual goals": "Objectifs individuels",
  "Parent units": "Unités parentes",
  "Responsibilities & contributions": "Responsabilités et contributions",
  "Close score details": "Fermer le détail",
  "← Back to workflow": "← Retour",
  "Individual Goals": "Objectifs individuels",
  "My Indicators": "Mes indicateurs",
  "Team Members": "Membres de l’équipe",
  "UNDERSTANDING YOUR CVI": "COMPRENDRE VOTRE CVI",
  "What your CVI represents": "Ce que représente votre CVI",
  "Close CVI explanation": "Fermer l’explication du CVI",
  "CVI gives a balanced view of your contribution by combining your Individual Contribution with your Collective Contribution.": "Le CVI offre une vision équilibrée de votre contribution en combinant votre contribution individuelle et votre contribution collective.",
  "Your direct results through responsibilities, goals, and contributions.": "Vos résultats directs grâce à vos responsabilités, objectifs et contributions.",
  "How your role supports the success of your team and organization.": "La façon dont votre rôle soutient la réussite de votre équipe et de l’organisation.",
  "WHY IT MATTERS": "POURQUOI C’EST IMPORTANT",
  "Strong personal results matter, but so does contributing to shared outcomes. CVI combines both dimensions using a geometric mean, so a very low result in one area is not fully hidden by a high result in the other.": "De bons résultats personnels comptent, tout comme la contribution aux résultats communs. Le CVI combine les deux dimensions par une moyenne géométrique : un résultat très faible dans un domaine n’est donc pas entièrement masqué par un résultat élevé dans l’autre.",
});
// Admin Panel redesign — new and previously untranslated admin copy.
Object.assign(CANADA_EN_FR_COPY, {
  "Submission, approval and lock days are required.": "Les jours de soumission, d’approbation et de verrouillage sont requis.",
  "Day offsets must be whole numbers.": "Les décalages en jours doivent être des nombres entiers.",
  "Reminder days cannot be negative.": "Les jours de rappel ne peuvent pas être négatifs.",
  "The approval deadline must be on or after the submission deadline.": "L’échéance d’approbation doit être égale ou postérieure à l’échéance de soumission.",
  "Approval deadline (days after period end)": "Échéance d’approbation (jours après la fin de la période)",
  "Not available yet: the server does not support editing or deleting schedule templates.": "Pas encore offert : le serveur ne permet pas de modifier ni de supprimer les modèles de planification.",
  "New templates are saved to the server. Editing and deleting existing templates is not available yet because the server does not support it; create a new template instead.": "Les nouveaux modèles sont enregistrés sur le serveur. La modification et la suppression des modèles existants ne sont pas encore offertes, car le serveur ne les prend pas en charge; créez plutôt un nouveau modèle.",
  "Override the schedule dates of one indicator for one period.": "Remplacer les dates de planification d’un indicateur pour une période.",
  "Not available yet.": "Pas encore offert.",
  "Schedule exceptions cannot be saved because the server has no endpoint for them. Exceptions entered here previously were kept only in the browser and were lost on reload; they never changed the deadlines stored on the server.": "Les exceptions de planification ne peuvent pas être enregistrées, car le serveur n’offre aucun point d’accès pour celles-ci. Les exceptions saisies ici auparavant n’étaient conservées que dans le navigateur et étaient perdues au rechargement; elles n’ont jamais modifié les échéances enregistrées sur le serveur.",
  "No job positions yet": "Aucun poste pour le moment",
  "Define the Core Responsibility Rating Scale first.": "Définissez d’abord l’échelle des responsabilités principales.",
  "Feedback providers answer each question on a fixed 1–5 scale; the average answer is converted to a score out of 100. This scale is separate from the Core Responsibility Rating Scale.": "Les répondants répondent à chaque question sur une échelle fixe de 1 à 5; la réponse moyenne est convertie en résultat sur 100. Cette échelle est distincte de l’échelle des responsabilités principales.",
  "Add organizational unit": "Ajouter une unité organisationnelle",
  "Edit organizational unit": "Modifier l’unité organisationnelle",
  "Create a unit in the organization structure. Unit type is used for display and reporting only.": "Créez une unité dans la structure organisationnelle. Le type d’unité sert uniquement à l’affichage et aux rapports.",
  "Update the unit's name, type, parent unit or manager.": "Modifiez le nom, le type, l’unité parente ou le gestionnaire de l’unité.",
  "e.g., Division, Department, Team": "p. ex. Division, Service, Équipe",
  "Top level (no parent)": "Premier niveau (aucune unité parente)",
  "Managers see the CVI of people in the units they manage.": "Les gestionnaires voient le CVI des personnes des unités qu’ils gèrent.",
  "Unit name is required.": "Le nom de l’unité est requis.",
  "Organizational unit added.": "Unité organisationnelle ajoutée.",
  "Organizational unit updated.": "Unité organisationnelle mise à jour.",
  "Could not save the organizational unit.": "Impossible d’enregistrer l’unité organisationnelle.",
  "Add job position": "Ajouter un poste",
  "Rename job position": "Renommer le poste",
  "Archive job position": "Archiver le poste",
  "New positions start from the selected templates. Weights are managed under Evaluation Templates.": "Les nouveaux postes reprennent les modèles sélectionnés. Les pondérations sont gérées dans Modèles d’évaluation.",
  "Only the name changes; weights and templates stay as they are.": "Seul le nom change; les pondérations et les modèles restent inchangés.",
  "Position name is required.": "Le nom du poste est requis.",
  "Job position added.": "Poste ajouté.",
  "Job position renamed.": "Poste renommé.",
  "Job position archived.": "Poste archivé.",
  "Could not save the job position.": "Impossible d’enregistrer le poste.",
  "Could not archive the job position.": "Impossible d’archiver le poste.",
  "will be removed from the active list. Historical records are kept.": "sera retiré de la liste active. Les données historiques sont conservées.",
  "This position is still assigned to active people and cannot be archived. Reassign them first.": "Ce poste est encore attribué à des personnes actives et ne peut pas être archivé. Réattribuez-les d’abord.",
  "No active person holds this position.": "Aucune personne active n’occupe ce poste.",
  "The organization itself is edited under Organization Profile.": "L’organisation elle-même se modifie dans Profil de l’organisation.",
  "+ Add job position": "+ Ajouter un poste",
  "+ Add child unit": "+ Ajouter une sous-unité",
  "+ Add unit": "+ Ajouter une unité",
  "e.g., Customer retention rate": "p. ex. Taux de fidélisation de la clientèle",
  "%, count, days, score...": "%, nombre, jours, note…",
  "e.g., Alex Morgan": "p. ex. Alex Morgan",
  "e.g., HR-1048": "p. ex. RH-1048",
  "e.g., Meets agreed delivery deadlines": "p. ex. Respecte les échéances convenues",
  "e.g., Collaborates effectively across the team": "p. ex. Collabore efficacement au sein de l’équipe",
  "e.g., Aligns work with strategic priorities": "p. ex. Aligne son travail sur les priorités stratégiques",
  "e.g., 2026-10-05": "p. ex. 2026-10-05",
  "name@company.com": "nom@entreprise.com",
  "Add a question or copy one from the Question Bank.": "Ajoutez une question ou copiez-en une depuis la banque de questions.",
  "Add a small number of shared indicators that reflect the unit’s collective results.": "Ajoutez un petit nombre d’indicateurs communs qui reflètent les résultats collectifs de l’unité.",
  "Add category": "Ajouter une catégorie",
  "Add level": "Ajouter un niveau",
  "Add person": "Ajouter une personne",
  "Add qualitative question": "Ajouter une question qualitative",
  "Add question": "Ajouter une question",
  "Add schedule template": "Ajouter un modèle de planification",
  "Add the first person to get started.": "Ajoutez la première personne pour commencer.",
  "Add to unit": "Ajouter à l’unité",
  "Adjust the search or clear the filters.": "Modifiez la recherche ou effacez les filtres.",
  "All organization units": "Toutes les unités organisationnelles",
  "All roles": "Tous les rôles",
  "Between 1 and 10.": "Entre 1 et 10.",
  "Calculation method": "Méthode de calcul",
  "Canada — Atlantic": "Canada — Atlantique",
  "Canada — Eastern": "Canada — Est",
  "Canada — Mountain": "Canada — Rocheuses",
  "Canada — Pacific": "Canada — Pacifique",
  "Choose a unit above to review its indicators and qualitative questions.": "Choisissez une unité ci-dessus pour consulter ses indicateurs et ses questions qualitatives.",
  "Confidential — anonymous": "Confidentielle — anonyme",
  "Identified — feedback provider names visible": "Identifiée — noms des répondants visibles",
  "Period activated.": "Période activée.",
  "Period created.": "Période créée.",
  "Period finalized and locked.": "Période finalisée et verrouillée.",
  "Period reopened.": "Période rouverte.",
  "The period could not be created.": "La période n’a pas pu être créée.",
  "The period could not be activated.": "La période n’a pas pu être activée.",
  "The period could not be finalized.": "La période n’a pas pu être finalisée.",
  "The period could not be reopened.": "La période n’a pas pu être rouverte.",
  "Each occurrence is calculated from the period end date. Template changes affect future periods only; dates already stored in previous records remain unchanged.": "Chaque occurrence est calculée à partir de la date de fin de la période. Les modifications d’un modèle ne touchent que les périodes futures; les dates déjà enregistrées restent inchangées.",
  "Enter a reason for reopening this period:": "Indiquez la raison de la réouverture de cette période :",
  "Enter the question and select an importance level. The normalized weight is calculated automatically.": "Saisissez la question et sélectionnez un niveau d’importance. Le poids normalisé est calculé automatiquement.",
  "Evaluation questions are defined for each organization unit. The Question Bank stores reusable prompts that can be copied into unit-level evaluations.": "Les questions d’évaluation sont définies pour chaque unité organisationnelle. La banque de questions contient des énoncés réutilisables qui peuvent être copiés dans les évaluations des unités.",
  "Finalizing this period locks its results. People will no longer be able to change their entries. Continue?": "La finalisation de cette période verrouille ses résultats. Les personnes ne pourront plus modifier leurs saisies. Continuer?",
  "How several values recorded in one period are combined.": "Comment plusieurs valeurs saisies au cours d’une période sont combinées.",
  "Indicator Exception": "Exception d’indicateur",
  "Indicator Scheduling": "Planification des indicateurs",
  "Indicators Requiring Action": "Indicateurs nécessitant une action",
  "Individual Feedback Settings": "Paramètres de rétroaction individuels",
  "KPI calculation method": "Méthode de calcul KPI",
  "Leave blank to keep the current password.": "Laissez vide pour conserver le mot de passe actuel.",
  "Letters, numbers, dots, dashes or underscores.": "Lettres, chiffres, points, tirets ou traits de soulignement.",
  "Manage KPI calculation templates": "Gérer les modèles de calcul KPI",
  "No people match these filters": "Aucune personne ne correspond à ces filtres",
  "No people yet": "Aucune personne pour le moment",
  "No qualitative questions have been defined yet.": "Aucune question qualitative n’a encore été définie.",
  "No questions for this month": "Aucune question pour ce mois",
  "No questions have been added to the Question Bank yet.": "Aucune question n’a encore été ajoutée à la banque de questions.",
  "No feedback provider suggested": "Aucun répondant suggéré",
  "Number of feedback providers": "Nombre de répondants",
  "Organization & access": "Organisation et accès",
  "Organization profile": "Profil de l’organisation",
  "Override the schedule dates of one indicator for one period. A reason is required and is kept with the exception.": "Remplacez les dates de planification d’un indicateur pour une période. Un motif est requis et conservé avec l’exception.",
  "PNG, JPG, WebP or SVG · max 750 KB": "PNG, JPG, WebP ou SVG · 750 Ko max.",
  "Peer feedback questions": "Questions de rétroaction des collègues",
  "Profile and organization access": "Profil et accès à l’organisation",
  "Qualitative question added.": "Question qualitative ajoutée.",
  "Question Bank entries are reusable templates. They do not affect scores until they are copied into a unit; normalized weights here are shown within each month.": "Les questions de la banque sont des modèles réutilisables. Elles n’influencent aucun résultat tant qu’elles ne sont pas copiées dans une unité; les poids normalisés sont affichés ici par mois.",
  "Questions are defined per unit. Normalized weights are relative to all questions of the selected unit across the three months.": "Les questions sont définies par unité. Les poids normalisés sont calculés par rapport à toutes les questions de l’unité sélectionnée sur les trois mois.",
  "Ready to run a complete CVI period": "Prêt à mener une période CVI complète",
  "Relative weight; must be greater than zero.": "Poids relatif; doit être supérieur à zéro.",
  "Resolve these items to run a complete CVI period.": "Réglez ces éléments pour mener une période CVI complète.",
  "Review and adjust peer feedback for one person at a time.": "Consultez et ajustez la rétroaction des collègues, une personne à la fois.",
  "Save exception": "Enregistrer l’exception",
  "Saved exceptions": "Exceptions enregistrées",
  "Schedule & evidence": "Planification et pièces justificatives",
  "Schedule template": "Modèle de planification",
  "Search people…": "Rechercher une personne…",
  "Select a template": "Sélectionner un modèle",
  "Select an organizational unit": "Sélectionnez une unité organisationnelle",
  "Select from Question Bank": "Choisir dans la banque de questions",
  "Select indicator": "Sélectionner un indicateur",
  "Select organization unit": "Sélectionner une unité organisationnelle",
  "Select period": "Sélectionner une période",
  "Suggested feedback providers": "Répondants suggérés",
  "Unsaved changes": "Modifications non enregistrées",
  "Used for highlights across CVI. The navy brand colour is unchanged.": "Utilisée pour les mises en évidence dans CVI. Le bleu marine de la marque reste inchangé.",
  "Edit person": "Modifier la personne",
  "Edit level": "Modifier le niveau",
  "Edit question": "Modifier la question",
  "Edit category": "Modifier la catégorie",
  "Edit schedule template": "Modifier le modèle de planification",
  "Weights are normalized automatically from each indicator’s importance level.": "Les poids sont normalisés automatiquement à partir du niveau d’importance de chaque indicateur.",
  "Submission opens": "Ouverture des soumissions",
  "Submission deadline": "Échéance de soumission",
  "Approval deadline": "Échéance d’approbation",
  "Lock date": "Date de verrouillage",
  "Activate a period to begin tracking performance.": "Activez une période pour commencer le suivi de la performance.",
  "Select from the Question Bank": "Choisir dans la banque de questions",
  "Selected questions are copied to the chosen unit and can be edited independently.": "Les questions sélectionnées sont copiées dans l’unité choisie et peuvent être modifiées séparément.",
  "Schedule template is required.": "Le modèle de planification est requis.",
});
// French for UI copy that used to be written as legacy Persian literals (now English source text).
// Sentences and template fragments: matched as whole phrases inside a text node.
Object.assign(CANADA_EN_FR_COPY, {
  "(Admin must set this to 100%)": "(L’administrateur doit régler ce total à 100\u00a0%)",
  ". These scores are shown for comparison only and carry no weight in the CVI.": ". Ces résultats sont affichés à titre comparatif seulement et n’ont aucun poids dans le CVI.",
  "A supporting document is required for submission.": "Un document justificatif est requis pour la soumission.",
  "Achievement is below 80%. Briefly explain the variance and the planned corrective action.": "L’atteinte est inférieure à 80\\u00a0%. Expliquez brièvement l’écart et la mesure corrective prévue.",
  "Contributions recorded in this period": "Contributions enregistrées pour cette période",
  "Activity could not be saved": "L’activité n’a pas pu être enregistrée",
  "Activity details for this period are not available": "Les détails des activités de cette période ne sont pas disponibles",
  "Activity submission is disabled": "La saisie d’activité est désactivée",
  "Add an individual goal": "Ajouter un objectif individuel",
  "Additional contributions included in the calculation": "Contributions additionnelles incluses dans le calcul",
  "Additional contributions — category details": "Contributions additionnelles — détail par catégorie",
  "After the final submission, your self-assessment answers for this month are locked. Continue?": "Après la soumission finale, vos réponses d’autoévaluation de ce mois seront verrouillées. Continuer?",
  "Approved:": "Approuvées :",
  "At the end of each month, answer that month’s questions for each person. Total weight of": "À la fin de chaque mois, répondez aux questions du mois pour chaque personne. Pondération totale de",
  "at the end of each month.": "à la fin de chaque mois.",
  "Back to team members": "Retour aux membres de l’équipe",
  "Based on the period’s current data": "Selon les données actuelles de la période",
  "Business Unit Lead ·": "Responsable d’unité d’affaires ·",
  "Business Unit Leads’ Core Responsibilities ratings": "Évaluation des responsabilités principales des responsables d’unité d’affaires",
  "Certificate, report, image — max 5 MB": "Certificat, rapport, image — 5 Mo max.",
  "Challenge / support need ·": "Défi / besoin de soutien ·",
  "Challenge / support need:": "Défi / besoin de soutien :",
  "Challenge and support need ·": "Défi et besoin de soutien ·",
  "Challenge or support need (optional)": "Défi ou besoin de soutien (facultatif)",
  "Challenges and support needs": "Défis et besoins de soutien",
  "Collective Contribution / strategic indicators": "Contribution collective / indicateurs stratégiques",
  "Collective Contribution indicators could not be loaded. Please try again.": "Les indicateurs de contribution collective n’ont pas pu être chargés. Veuillez réessayer.",
  "Compare with previous period": "Comparer avec la période précédente",
  "Read-only performance archive for each period, most recent first.": "Archive des résultats de chaque période, en lecture seule, de la plus récente à la plus ancienne.",
  "Contributions could not be loaded. Please try again.": "Les contributions n’ont pas pu être chargées. Veuillez réessayer.",
  "Core responsibilities data could not be loaded. Please try again.": "Les données des responsabilités principales n’ont pas pu être chargées. Veuillez réessayer.",
  "Core responsibilities — question details": "Responsabilités principales — détail des questions",
  "CVI trend — recent periods": "Tendance du CVI — périodes récentes",
  "Data could not be loaded. Please try again.": "Les données n’ont pas pu être chargées. Veuillez réessayer.",
  "days overdue": "jours de retard",
  "days until submission deadline": "jours avant la date limite de saisie",
  "Define the indicators specific to “": "Définissez les indicateurs propres à « ",
  "Define the qualitative questions specific to “": "Définissez les questions qualitatives propres à « ",
  "Description / success measure": "Description / mesure de réussite",
  "Description or data source": "Description ou source de données",
  "Document upload is not yet connected to server storage. To test a submission, use an indicator that does not require a supporting document.": "Le téléversement de documents n’est pas encore relié au stockage du serveur. Pour tester une soumission, utilisez un indicateur qui n’exige pas de pièce justificative.",
  "Draft saved automatically:": "Brouillon enregistré automatiquement :",
  "Enter a contribution title": "Saisissez le titre de la contribution",
  "Enter your current password": "Saisissez votre mot de passe actuel",
  "Evaluated:": "Évaluées :",
  "Evidence / link / context": "Preuve / lien / contexte",
  "Example: Completed Bizagi BPMS training": "Exemple : formation Bizagi BPMS terminée",
  "Example: Reduce customer response time": "Exemple : réduire le délai de réponse aux clients",
  "Explain how this activity affected your work — your manager will use this description to determine the impact level.": "Expliquez l’impact de cette activité sur votre travail — votre gestionnaire utilisera cette description pour déterminer le niveau d’impact.",
  "External or cross-unit dependency": "Dépendance externe ou entre unités",
  "File type not allowed. Choose a PDF, Word, JPG or PNG file.": "Type de fichier non autorisé. Choisissez un fichier PDF, Word, JPG ou PNG.",
  "Goal title, due date and importance level are required.": "Le titre, la date d’échéance et le niveau d’importance de l’objectif sont obligatoires.",
  "Historical data is not available.": "Les données historiques ne sont pas disponibles.",
  "History could not be loaded": "L’historique n’a pas pu être chargé",
  "If something is blocking this contribution, record it here so your manager can follow up. This section does not directly affect the contribution score.": "Si un obstacle freine cette contribution, consignez-le ici pour que votre gestionnaire puisse assurer le suivi. Cette section n’influence pas directement les points de la contribution.",
  "Image upload failed; please try again": "Le téléversement de l’image a échoué; réessayez",
  "Indicators where you are the owner, submitter, or approver.": "Indicateurs pour lesquels vous êtes responsable, responsable de saisie ou approbateur.",
  "Indicators:": "Indicateurs :",
  "Linked strategic indicator (optional)": "Indicateur stratégique lié (facultatif)",
  "Locked automatically by the indicator schedule": "Verrouillé automatiquement selon le calendrier de l’indicateur",
  "Manager review opens after the employee submits their self-assessment": "L’évaluation du gestionnaire s’ouvre après la soumission de l’autoévaluation de la personne évaluée",
  "Managers’ core responsibilities review": "Évaluation des responsabilités principales des gestionnaires",
  "Members:": "Membres :",
  "Month 10": "Mois 10",
  "Month 11": "Mois 11",
  "Month 12": "Mois 12",
  "Month 4": "Mois 4",
  "Month 5": "Mois 5",
  "Month 6": "Mois 6",
  "Month 7": "Mois 7",
  "Month 8": "Mois 8",
  "Month 9": "Mois 9",
  "No contributions have been recorded yet": "Aucune contribution n’a encore été enregistrée",
  "No approved activities have been recorded in this group": "Aucune activité approuvée n’a été enregistrée dans ce groupe",
  "No business unit lead has been defined yet": "Aucun responsable d’unité d’affaires n’a encore été défini",
  "No company members have been defined yet": "Aucun membre de l’entreprise n’a encore été défini",
  "No core responsibilities questions have been defined for this unit yet.": "Aucune question sur les responsabilités principales n’a encore été définie pour cette unité.",
  "No period matches the selected filters.": "Aucune période ne correspond aux filtres sélectionnés.",
  "No employee matches the selected filters.": "Aucune personne ne correspond aux filtres sélectionnés.",
  "No events have been recorded yet.": "Aucun événement n’a encore été enregistré.",
  "No goals have been recorded for this person yet. You can add the first goal.": "Aucun objectif n’a encore été enregistré pour cette personne. Vous pouvez ajouter le premier objectif.",
  "No goals have been recorded here yet.": "Aucun objectif n’a encore été enregistré ici.",
  "No historical snapshot is available for this person.": "Aucun instantané historique n’est disponible pour cette personne.",
  "No indicators have been defined for this level.": "Aucun indicateur n’a été défini pour ce niveau.",
  "No indicators have been defined for this unit": "Aucun indicateur n’a été défini pour cette unité",
  "No information has been recorded for this section.": "Aucune information n’a été enregistrée pour cette section.",
  "No managers have been assigned to this unit yet": "Aucun gestionnaire n’a encore été affecté à cette unité",
  "No member matches this filter": "Aucun membre ne correspond à ce filtre",
  "No members are assigned to this manager yet": "Aucun membre n’est encore rattaché à ce gestionnaire",
  "No members have been assigned to this unit yet": "Aucun membre n’a encore été affecté à cette unité",
  "No one reports directly to you yet.": "Personne ne relève encore directement de vous.",
  "No organization-level core responsibilities questions have been defined yet.": "Aucune question sur les responsabilités principales à l’échelle de l’organisation n’a encore été définie.",
  "No organization-wide indicators have been defined": "Aucun indicateur à l’échelle de l’organisation n’a été défini",
  "No questions have been defined for this month.": "Aucune question n’a été définie pour ce mois.",
  "This person has no goals from previous periods that can be reused.": "Cette personne n’a aucun objectif réutilisable des périodes précédentes.",
  "No Core Responsibilities criteria have been defined by the administrator yet.": "Aucun critère des responsabilités principales n’a encore été défini par l’administration.",
  "No Core Responsibilities criteria have been defined for this person.": "Aucun critère des responsabilités principales n’a été défini pour cette personne.",
  "No Core Responsibilities criteria have been defined for you yet.": "Aucun critère des responsabilités principales n’a encore été défini pour vous.",
  "No sub-units have been defined here": "Aucune sous-unité n’a été définie ici",
  "No top-level units have been defined": "Aucune unité de premier niveau n’a été définie",
  "No units have been defined": "Aucune unité n’a été définie",
  "No parent unit applies to this person.": "Aucune unité parente ne s’applique à cette personne.",
  "Additional Contributions by category": "Contributions additionnelles par catégorie",
  "Not yet answered by": "Pas encore rempli par",
  "Nothing to follow up": "Aucun suivi requis",
  "Once approved, the score will be added to your dashboard.": "Après approbation, le résultat sera ajouté à votre tableau de bord.",
  "Only image files can be uploaded": "Seuls les fichiers image peuvent être téléversés",
  "Optional comment or supporting evidence": "Commentaire ou pièce justificative (facultatif)",
  "Organization units:": "Unités organisationnelles :",
  "out of 100 · Weight": "sur 100 · Pondération",
  "Peer feedback data could not be loaded. Please try again.": "Les données de rétroaction des collègues n’ont pas pu être chargées. Veuillez réessayer.",
  "Peer feedback was not enabled for this person.": "La rétroaction des collègues n’était pas activée pour cette personne.",
  "People:": "Personnes :",
  "Please answer every question for this month first.": "Veuillez d’abord répondre à toutes les questions de ce mois.",
  "Previous goals could not be loaded": "Les objectifs antérieurs n’ont pas pu être chargés",
  "Propose an individual goal": "Proposer un objectif individuel",
  "Read-only archive: data from a locked period is never changed or recalculated with a new formula.": "Archive en lecture seule : les données d’une période verrouillée ne sont jamais modifiées ni recalculées avec une nouvelle formule.",
  "Recorded achievements and additional contributions": "Réalisations et contributions additionnelles enregistrées",
  "Results directly attributable to the person": "Résultats directement attribuables à la personne",
  "Reuse a previous goal": "Réutiliser un objectif antérieur",
  "The Core Responsibilities rating could not be saved": "L’évaluation des responsabilités principales n’a pas pu être enregistrée",
  "No Core Responsibilities ratings have been recorded for this period": "Aucune évaluation des responsabilités principales n’a été enregistrée pour cette période",
  "Save revisions and resubmit": "Enregistrer les révisions et soumettre de nouveau",
  "See the performance of this area’s sub-units and indicators in one place.": "Consultez en un seul endroit la performance des sous-unités et des indicateurs de ce secteur.",
  "Select a category first": "Sélectionnez d’abord une catégorie",
  "Select a contribution category": "Sélectionnez une catégorie de contribution",
  "Select a person to see all of their past periods in one place.": "Sélectionnez une personne pour voir toutes ses périodes antérieures au même endroit.",
  "Select a unit to define its indicators and evaluation questions.": "Sélectionnez une unité pour définir ses indicateurs et ses questions d’évaluation.",
  "Select an importance level": "Sélectionnez un niveau d’importance",
  "Self-assessments submitted for months": "Autoévaluations soumises pour les mois",
  "Send to manager ✓": "Envoyer au gestionnaire ✓",
  "Sub-units:": "Sous-unités :",
  "Submission window closed without data": "Période de saisie fermée sans donnée",
  "submission(s) are close to or past their deadline.": "saisie(s) arrivent à échéance ou sont en retard.",
  "Submit self-assessment for": "Soumettre l’autoévaluation pour le",
  "Team members’ goals (": "Objectifs des membres de l’équipe (",
  "The additional contributions score is built from your approved contributions, grouped by category. Select a category to see its contributions.": "Le résultat des contributions additionnelles repose sur vos contributions approuvées, regroupées par catégorie. Sélectionnez une catégorie pour voir ses contributions.",
  "The contribution could not be resubmitted": "La contribution n’a pas pu être soumise de nouveau",
  "The current password is incorrect": "Le mot de passe actuel est incorrect",
  "The description must be at least 10 characters": "La description doit contenir au moins 10 caractères",
  "The employee has not yet submitted a self-assessment for this item.": "La personne n’a pas encore soumis d’autoévaluation pour cet élément.",
  "The file could not be read. Please try again.": "Le fichier n’a pas pu être lu. Veuillez réessayer.",
  "The file must be smaller than 5 MB": "Le fichier doit faire moins de 5 Mo",
  "The goal becomes active after your manager approves it.": "L’objectif devient actif après l’approbation de votre gestionnaire.",
  "The goal becomes active when it is saved.": "L’objectif devient actif dès son enregistrement.",
  "The image must be smaller than 3 MB": "L’image doit faire moins de 3 Mo",
  "The importance scale could not be loaded": "L’échelle d’importance n’a pas pu être chargée",
  "The indicator value could not be saved": "La valeur de l’indicateur n’a pas pu être enregistrée",
  "The new password confirmation does not match": "La confirmation du nouveau mot de passe ne correspond pas",
  "The person’s share of related units’ results": "Part de la personne dans les résultats des unités liées",
  "The self-assessment could not be submitted": "L’autoévaluation n’a pas pu être soumise",
  "The submission window is not open yet.": "La saisie n’est pas encore ouverte.",
  "No periods have been finalized yet. Results appear here once a period is locked.": "Aucune période n’a encore été finalisée. Les résultats s’afficheront ici une fois la période verrouillée.",
  "There are no indicators for you in this section.": "Aucun indicateur ne vous est attribué dans cette section.",
  "There is no active period.": "Aucune période n’est active.",
  "There is no active period; evaluations from closed periods are view-only.": "Aucune période n’est active; les évaluations des périodes clôturées sont en lecture seule.",
  "There is no active period; new activities can only be recorded during an active period.": "Aucune période n’est active; les nouvelles activités ne peuvent être enregistrées que pendant une période active.",
  "These questions cover your recurring core responsibilities and are answered by your": "Ces questions portent sur vos responsabilités principales récurrentes; elles sont remplies par votre",
  "They are used as templates only; the previous period’s record does not change.": "Ils servent uniquement de modèles; le dossier de la période précédente ne change pas.",
  "This occurrence was locked automatically.": "Cette occurrence a été verrouillée automatiquement.",
  "This person has not submitted any monthly self-assessment yet. The manager review of each item opens once that month’s self-assessment is submitted.": "Cette personne n’a encore soumis aucune autoévaluation mensuelle. L’évaluation de chaque élément par le gestionnaire s’ouvre dès la soumission de l’autoévaluation du mois.",
  "This section combines two components: core responsibilities and additional contributions.": "Cette section combine deux composantes : les responsabilités principales et les contributions additionnelles.",
  "This section is optional, but it can support a better activity evaluation.": "Cette section est facultative, mais elle peut faciliter une meilleure évaluation de l’activité.",
  "This section is read-only. Total weight of": "Cette section est en lecture seule. Pondération totale de",
  "Today is the deadline": "Échéance aujourd’hui",
  "Top-level units:": "Unités de premier niveau :",
  "Use in current period →": "Utiliser dans la période en cours →",
  "View the additional contributions recorded this period": "Voir les contributions additionnelles enregistrées pendant cette période",
  "View the evaluation details for your core responsibilities": "Voir le détail de l’évaluation de vos responsabilités principales",
  "View unit dashboard →": "Voir le tableau de bord de l’unité →",
  "Waiting for the employee’s self-assessment for this item": "En attente de l’autoévaluation de la personne évaluée pour cet élément",
  "What did you contribute, and what impact did it have on your work or team?": "Quelle a été votre contribution et quel impact a-t-elle eu sur votre travail ou votre équipe?",
  "You have not recorded any contributions yet": "Vous n’avez encore enregistré aucune contribution",
  "Your contribution was sent to your manager for review.": "Votre contribution a été envoyée à votre gestionnaire pour examen.",
  "Your draft is saved automatically": "Votre brouillon est enregistré automatiquement",
  "Your manager determines the activity’s impact level. Describe the impact of your work in the details.": "Votre gestionnaire détermine le niveau d’impact de l’activité. Décrivez l’impact de votre travail dans les détails.",
  "Your personal details, account and preferences": "Vos informations personnelles, compte et préférences",
  "Your self-assessment could not be loaded": "Votre autoévaluation n’a pas pu être chargée",
  "Your self-assessment could not be saved": "Votre autoévaluation n’a pas pu être enregistrée",
  "· Due": "· Échéance",
  "· Month": "· Mois",
  "” workspace will be added in a future release": " » seront ajoutées dans une prochaine version",
  "📎 Select new document": "📎 Sélectionner un nouveau document",
  "🚧 More sections of the “": "🚧 D’autres sections de l’espace « ",
});
const CANADA_EN_FR_KEYS = Object.keys(CANADA_EN_FR_COPY).sort((a,b)=>b.length-a.length);
// Short, generic words are only translated when they are the whole text of a node,
// so a user's own text ("Change request", "Current state") is never rewritten.
const CANADA_EN_FR_EXACT = {
  "Dashboard": "Tableau de bord", "Current": "Actuel", "Change": "Variation", "My Team": "Mon équipe",
  "Weight": "Pondération", "Team": "Équipe", "Organization": "Organisation", "Specialist": "Spécialiste", "Manager": "Gestionnaire",
};
// Admin Panel: short labels translated only when they are the whole text of a node.
Object.assign(CANADA_EN_FR_EXACT, {
  "Used by": "Utilisé par",
  "Unit name": "Nom de l’unité",
  "Unit type": "Type d’unité",
  "Parent unit": "Unité parente",
  "Unit manager": "Gestionnaire de l’unité",
  "Position name": "Nom du poste",
  "Organization default": "Valeur par défaut de l’organisation",
  "Add position": "Ajouter le poste",
  "Archive position": "Archiver le poste",
  "Rename position": "Renommer le poste",
  "Accountability": "Responsabilités",
  "Actions": "Actions",
  "Add": "Ajouter",
  "Administration": "Administration",
  "Period": "Période",
  "Definition": "Définition",
  "Directory": "Répertoire",
  "Discard": "Annuler les modifications",
  "High": "Élevé",
  "Low": "Faible",
  "Moderate": "Modéré",
  "Very high": "Très élevé",
  "Identity": "Identité",
  "Importance": "Importance",
  "Inactive": "Inactif",
  "Ready": "Prêt",
  "Reason": "Motif",
  "Role": "Rôle",
  "Select": "Sélectionner",
  "Sign-in": "Connexion",
  "Needs setup": "À configurer",
  "Organization unit": "Unité organisationnelle",
  "Evidence required": "Pièce justificative requise",
});
// Short labels from the same legacy screens: translated only when they are the whole text of a node,
// so names and other user-entered text are never rewritten.
Object.assign(CANADA_EN_FR_EXACT, {
  "% indicators": "% indicateurs",
  "% qualitative": "% qualitatif",
  "% weight": "% de pondération",
  "% · Result": "% · Résultat",
  "(optional)": "(facultatif)",
  "60–79": "60 à 79",
  "Access Level": "Niveau d’accès",
  "Action Required": "Action requise",
  "Activity group *": "Groupe d’activités *",
  "Activity submitted": "Activité soumise",
  "Activity summary": "Résumé de l’activité",
  "Activity title *": "Titre de l’activité *",
  "Actual value *": "Valeur réelle *",
  "Actual:": "Réel :",
  "Add a goal": "Ajouter un objectif",
  "Add contribution": "Ajouter une contribution",
  "Additional Contributions": "Contributions additionnelles",
  "All business units": "Toutes les unités d’affaires",
  "All quarters": "Tous les trimestres",
  "All teams": "Toutes les équipes",
  "All years": "Toutes les années",
  "Approve goal": "Approuver l’objectif",
  "Approved": "Approuvé",
  "approved contributions": "contributions approuvées",
  "Automatic lock": "Verrouillage automatique",
  "Average CVI:": "CVI moyen :",
  "Awaiting senior leadership decision": "En attente de la décision de la direction",
  "Back": "Retour",
  "Back to periods": "Retour aux périodes",
  "Back to dashboard": "Retour au tableau de bord",
  "Below 65% achievement": "Atteinte inférieure à 65\u00a0%",
  "Business Unit dashboard": "Tableau de bord de l’unité d’affaires",
  "Business Unit indicators": "Indicateurs de l’unité d’affaires",
  "Business Unit Lead": "Responsable d’unité d’affaires",
  "By period": "Par période",
  "By person": "Par personne",
  "Calculation details": "Détails du calcul",
  "Cancelled": "Annulé",
  "Category": "Catégorie",
  "Category scores": "Résultats par catégorie",
  "Challenge description": "Description du défi",
  "Change history": "Historique des modifications",
  "Change password": "Changer le mot de passe",
  "Change photo": "Changer la photo",
  "Checking self-assessment status...": "Vérification de l’état de l’autoévaluation...",
  "Clear draft": "Effacer le brouillon",
  "Clear filters": "Effacer les filtres",
  "Click to upload": "Cliquer pour téléverser",
  "Closed without data": "Fermé sans données",
  "Collective score": "Résultat collectif",
  "Company members": "Membres de l’entreprise",
  "Completed": "Terminé",
  "Confirm new password": "Confirmer le nouveau mot de passe",
  "Contribution details": "Détails de la contribution",
  "Contribution Value Index": "Indice de valeur de contribution",
  "contributions need changes": "contributions à modifier",
  "Core responsibilities performance": "Résultat des responsabilités principales",
  "Core Responsibilities review": "Évaluation des responsabilités principales",
  "Corrective action": "Action corrective",
  "Counted contributions": "Contributions comptabilisées",
  "Criterion": "Critère",
  "Critical organization indicator": "Indicateur critique de l’organisation",
  "Current password": "Mot de passe actuel",
  "Current-period CVI": "CVI de la période en cours",
  "Current-quarter Core Responsibilities score": "Résultat des responsabilités principales du trimestre en cours",
  "Period archive": "Archive de la période",
  "Period chart": "Graphique des périodes",
  "Period goals": "Objectifs de la période",
  "Period highlights": "Faits saillants de la période",
  "Direct Manager": "Gestionnaire immédiat",
  "Division": "Division",
  "Due date *": "Date d’échéance *",
  "Due:": "Échéance :",
  "Edit and resubmit": "Modifier et soumettre de nouveau",
  "CVI distribution": "Répartition des résultats CVI",
  "Employee self-assessment:": "Autoévaluation de la personne :",
  "Evaluated": "Évalué",
  "Evaluation criterion": "Critère d’évaluation",
  "Evaluation question": "Question d’évaluation",
  "Evaluation template:": "Modèle d’évaluation :",
  "Evidence:": "Pièce justificative :",
  "Final achievement:": "Atteinte finale :",
  "Final review": "Évaluation finale",
  "Final snapshot": "Instantané final",
  "First recorded period": "Première période enregistrée",
  "Formula version:": "Version de la formule :",
  "Goal importance *": "Importance de l’objectif *",
  "Goal title *": "Titre de l’objectif *",
  "goals": "objectifs",
  "Hide details ▲": "Masquer les détails ▲",
  "80 and above": "80 et plus",
  "Highest current score": "Résultat actuel le plus élevé",
  "Highest organization CVI": "CVI maximal de l’organisation",
  "History": "Historique",
  "Impact level": "Niveau d’impact",
  "Importance:": "Importance :",
  "In progress": "En cours",
  "indicators": "indicateurs",
  "Individual": "Individuel",
  "Individual Contribution breakdown": "Répartition de la contribution individuelle",
  "Individual Contribution:": "Contribution individuelle :",
  "Individual score": "Résultat individuel",
  "Live calculation": "Calcul en direct",
  "Loading": "Chargement",
  "Loading employee history...": "Chargement de l’historique du personnel...",
  "Loading goals...": "Chargement des objectifs...",
  "Loading history...": "Chargement de l’historique...",
  "Loading indicators...": "Chargement des indicateurs...",
  "Loading previous goals...": "Chargement des objectifs antérieurs...",
  "Loading self-assessment...": "Chargement de l’autoévaluation...",
  "Locked": "Verrouillé",
  "Locked period": "Période verrouillée",
  "Sign out": "Se déconnecter",
  "Management decision": "Décision de la direction",
  "Manager comment:": "Commentaire du gestionnaire :",
  "Manager review:": "Évaluation du gestionnaire :",
  "Manager score:": "Résultat attribué par le gestionnaire :",
  "Manager’s final score:": "Résultat final attribué par le gestionnaire :",
  "member": "membre",
  "My contributions": "Mes contributions",
  "My assigned indicators": "Mes indicateurs attribués",
  "My dashboard": "Mon tableau de bord",
  "My goals (": "Mes objectifs (",
  "My indicator status": "État de mes indicateurs",
  "Needs attention": "À surveiller",
  "New indicator": "Nouvel indicateur",
  "New password": "Nouveau mot de passe",
  "New question": "Nouvelle question",
  "No challenge": "Aucun défi",
  "No change": "Aucun changement",
  "No data": "Aucune donnée",
  "No direct link": "Aucun lien direct",
  "No member found": "Aucun membre trouvé",
  "No open items": "Aucun élément ouvert",
  "No role": "Aucun rôle",
  "No supporting file": "Aucune pièce justificative",
  "None": "Aucun",
  "Not answered yet": "Pas encore répondu",
  "Not assessed": "Non évalué",
  "Not set yet": "Pas encore défini",
  "Not submitted": "Non soumis",
  "On track": "En bonne voie",
  "Organization average CVI": "CVI moyen de l’organisation",
  "Organization dashboard": "Tableau de bord de l’organisation",
  "Organization indicators": "Indicateurs de l’organisation",
  "Organization Unit": "Unité organisationnelle",
  "organization units": "unités organisationnelles",
  "Organization-wide indicators": "Indicateurs de l’ensemble de l’organisation",
  "Other": "Autre",
  "out of": "sur",
  "Overall progress": "Avancement global",
  "Owner": "Responsable",
  "Page sections": "Sections de la page",
  "Password changed successfully": "Mot de passe modifié avec succès",
  "Pending": "En attente",
  "Pending review": "En attente d’examen",
  "Percent, hours, count...": "Pourcentage, heures, nombre...",
  "Performance trend": "Tendance des résultats",
  "Period details": "Détails de la période",
  "Period target:": "Cible de la période :",
  "Person": "Personne",
  "Personal details": "Informations personnelles",
  "Position": "Poste",
  "Preferences": "Préférences",
  "Previous period": "Période précédente",
  "Previous goals": "Objectifs antérieurs",
  "Previous target:": "Cible précédente :",
  "Previous value:": "Valeur précédente :",
  "Previous weight": "Pondération précédente",
  "Process issue": "Problème de processus",
  "Profile and settings": "Profil et paramètres",
  "Progress": "Avancement",
  "Progress %": "Avancement %",
  "Propose a goal": "Proposer un objectif",
  "Proposed": "Proposé",
  "Qualitative evaluation": "Évaluation qualitative",
  "Quarter": "Trimestre",
  "quarterly questions answered": "questions trimestrielles répondues",
  "questions answered": "questions répondues",
  "questions this quarter:": "questions du trimestre :",
  "questions:": "questions :",
  "Reason for variance": "Raison de l’écart",
  "Recent contributions": "Contributions récentes",
  "Recorded": "Enregistré",
  "Recorded contributions": "Contributions enregistrées",
  "Reject": "Rejeter",
  "Rejected": "Rejeté",
  "Remove photo": "Supprimer la photo",
  "Resolved": "Résolu",
  "Resource constraint": "Contrainte de ressources",
  "Responsibilities & Contributions": "Responsabilités et contributions",
  "Resubmitted": "Soumis de nouveau",
  "Result source": "Source du résultat",
  "Approvals": "Approbations",
  "Revise activity": "Réviser l’activité",
  "Revised description *": "Description révisée *",
  "Revision requested:": "Révision demandée :",
  "Revision required": "Révision requise",
  "Core Responsibilities rating completion": "Achèvement de l’évaluation des responsabilités principales",
  "Core Responsibilities criteria": "Critères des responsabilités principales",
  "Core Responsibilities criteria details": "Détail des critères des responsabilités principales",
  "Save & activate": "Enregistrer et activer",
  "Save draft": "Enregistrer le brouillon",
  "Save new password": "Enregistrer le nouveau mot de passe",
  "Save progress": "Enregistrer l’avancement",
  "Select a person": "Sélectionner une personne",
  "Select a subcategory": "Sélectionner une sous-catégorie",
  "Select activity group": "Sélectionner le groupe d’activités",
  "Self-Assessment": "Autoévaluation",
  "Status:": "Statut :",
  "Strength": "Point fort",
  "Sub-unit": "Sous-unité",
  "Sub-unit comparison": "Comparaison des sous-unités",
  "Sub-units": "Sous-unités",
  "sub-units": "sous-unités",
  "Subcategory": "Sous-catégorie",
  "Subgroup *": "Sous-groupe *",
  "Submission start": "Début de saisie",
  "Submit for approval": "Soumettre pour approbation",
  "Submit proposal": "Soumettre la proposition",
  "Submitting...": "Soumission…",
  "Suggested action": "Action suggérée",
  "Suggested action:": "Action suggérée :",
  "Summary": "Résumé",
  "Support needed": "Soutien requis",
  "Support needed:": "Soutien requis :",
  "Supporting documents": "Documents justificatifs",
  "System": "Système",
  "System or tool": "Système ou outil",
  "Target:": "Cible :",
  "Team indicators": "Indicateurs de l’équipe",
  "Title": "Titre",
  "Top-level unit comparison": "Comparaison des unités de premier niveau",
  "top-level units ·": "unités de premier niveau ·",
  "Total indicator weight:": "Pondération totale des indicateurs :",
  "Total question weight:": "Pondération totale des questions :",
  "Total weight": "Pondération totale",
  "Try again": "Réessayer",
  "Type of challenge": "Type de défi",
  "Unit average CVI": "CVI moyen de l’unité",
  "Unit dashboard": "Tableau de bord de l’unité",
  "Unit indicator achievement": "Atteinte des indicateurs de l’unité",
  "Unit indicators": "Indicateurs de l’unité",
  "Unit members": "Membres de l’unité",
  "Unknown period": "Période inconnue",
  "Unresolved": "Non résolu",
  "Update progress": "Mettre à jour l’avancement",
  "Upload document": "Téléverser un document",
  "Upload photo": "Téléverser une photo",
  "Valid responses:": "Réponses valides :",
  "View all": "Voir tout",
  "View details": "Voir les détails",
  "View details →": "Voir les détails →",
  "View history": "Voir l’historique",
  "Working...": "Traitement...",
  "Your role:": "Votre rôle :",
  "· Achievement": "· Atteinte",
  "· Actual:": "· Réel :",
  "· Score:": "· Résultat :",
  "· Status:": "· Statut :",
  "· Target:": "· Cible :",
  "· Weight": "· Pondération",
  "— default": "— par défaut",
  "”.": " ».",
  "→ New value:": "→ Nouvelle valeur :",
});
// Admin & Expert UX audit (2026-10): copy introduced or reworded during the audit.
Object.assign(CANADA_EN_FR_COPY, {
  "Draft · not submitted": "Brouillon · non soumis",
  "Managers’ core responsibilities review": "Évaluation des responsabilités principales des gestionnaires",
  "Business Unit Leads’ Core Responsibilities ratings": "Évaluation des responsabilités principales des responsables d’unité d’affaires",
  "Core Responsibilities review": "Évaluation des responsabilités principales",
  "Organization members": "Membres de l’organisation",
  "Team members": "Membres de l’équipe",
  "Members you are authorized to see, with their current CVI. Select a person to open their detailed results.": "Membres dont vous pouvez consulter les résultats, avec leur CVI actuel. Sélectionnez une personne pour ouvrir ses résultats détaillés.",
  "Average CVI": "CVI moyen",
  "Organization overview": "Vue d’ensemble de l’organisation",
  "No members have been added to the organization yet.": "Aucun membre n’a encore été ajouté à l’organisation.",
  "After finalization": "À la finalisation",
  "% indicators ·": "% indicateurs ·",
  "CVI evaluation templates": "Modèles d’évaluation CVI",
  "Use ready-made templates for most roles. Edit weights only when your organization needs a different model.": "Utilisez des modèles prêts à l’emploi pour la plupart des rôles. Modifiez les pondérations seulement si votre organisation a besoin d’un modèle différent.",
  "Choose whether a unit is assessed through measurable indicators, qualitative review, or a balanced combination of both.": "Choisissez si une unité est évaluée au moyen d’indicateurs mesurables, d’une évaluation qualitative ou d’une combinaison équilibrée des deux.",
  "+ New template": "+ Nouveau modèle",
  "Assign the default Collective Contribution and Individual Contribution templates for each job position.": "Attribuez à chaque poste les modèles par défaut de contribution collective et de contribution individuelle.",
  "Organization units": "Unités organisationnelles",
  "Top-level units": "Unités de premier niveau",
  "Select a unit to view its manager, people, and related details.": "Sélectionnez une unité pour voir son gestionnaire, ses membres et les détails connexes.",
  "Use the +/− controls to expand or collapse branches.": "Utilisez les commandes +/− pour développer ou réduire les branches.",
  "Weights and calculation details are intentionally hidden here. Manage them under": "Les pondérations et les détails de calcul ne sont pas affichés ici. Gérez-les dans",
  "Unit type is used for display and reporting only; CVI logic follows the organization hierarchy rather than fixed titles.": "Le type d’unité sert uniquement à l’affichage et aux rapports; la logique du CVI suit la hiérarchie de l’organisation plutôt que des titres fixes.",
  "Use at least 6 characters. You stay signed in on this device after the change.": "Utilisez au moins 6 caractères. Votre session reste ouverte sur cet appareil après la modification.",
  "Rate your core responsibilities each month and record the additional contributions you make beyond your role.": "Évaluez vos responsabilités principales chaque mois et consignez les contributions additionnelles que vous apportez au-delà de votre rôle.",
  "Goals for": "Objectifs de",
  "Agree on measurable outcomes with your manager and keep progress up to date.": "Convenez de résultats mesurables avec votre gestionnaire et tenez l’avancement à jour.",
  "Collective Contribution indicators for which you are the owner, data submitter or approver.": "Indicateurs de contribution collective dont vous êtes responsable, responsable de la saisie ou approbateur.",
  "Nothing needs your action right now.": "Aucune action n’est requise de votre part pour le moment.",
  "Indicator values you need to enter or correct for the active period appear here when their submission window opens.": "Les valeurs d’indicateurs à saisir ou à corriger pour la période active s’affichent ici à l’ouverture de leur fenêtre de soumission.",
  "You are not the owner, data submitter or approver of any indicator. Your administrator assigns these roles in Collective Contribution.": "Aucun indicateur ne vous est attribué comme responsable, responsable de la saisie ou approbateur. L’administration attribue ces rôles dans Contribution collective.",
  "No values are awaiting approval.": "Aucune valeur n’est en attente d’approbation.",
  "Values you submit stay here until the approver reviews them.": "Les valeurs que vous soumettez restent ici jusqu’à ce que l’approbateur les examine.",
  "No approved values yet.": "Aucune valeur approuvée pour le moment.",
  "Approved and locked indicator values from past submissions appear here.": "Les valeurs d’indicateurs approuvées et verrouillées des soumissions antérieures s’affichent ici.",
  "Indicator submissions open once an administrator activates a period.": "Les soumissions d’indicateurs s’ouvrent lorsqu’un administrateur active une période.",
  "Achievements, projects and initiatives beyond your core responsibilities. Your manager reviews each submission.": "Réalisations, projets et initiatives qui dépassent vos responsabilités principales. Votre gestionnaire examine chaque soumission.",
  "You have not recorded any additional contributions yet.": "Vous n’avez encore enregistré aucune contribution additionnelle.",
  "Log a project, improvement, learning activity or support you provided. Your manager reviews it and it counts toward Individual Contribution.": "Consignez un projet, une amélioration, une activité d’apprentissage ou un soutien apporté. Votre gestionnaire l’examine et il compte dans la contribution individuelle.",
  "Log a contribution": "Ajouter une contribution",
  "You have no goals for this period yet.": "Vous n’avez encore aucun objectif pour cette période.",
  "Propose a goal to agree on a measurable outcome with your manager. It becomes active once approved.": "Proposez un objectif pour convenir d’un résultat mesurable avec votre gestionnaire. Il devient actif une fois approuvé.",
  "The goal becomes active as soon as it is saved.": "L’objectif devient actif dès qu’il est enregistré.",
  "Save and activate": "Enregistrer et activer",
  "Goal title": "Titre de l’objectif",
  "Description and success measure": "Description et mesure de réussite",
  "Percent, hours, count…": "Pourcentage, heures, nombre…",
  "Reject goal": "Refuser l’objectif",
  "Progress (%)": "Avancement (%)",
  "Loading goals…": "Chargement des objectifs…",
  "Team members’ goals": "Objectifs des membres de l’équipe",
  "Progress must be between 0% and 100%.": "L’avancement doit être compris entre 0 et 100\u00a0%.",
  "Update password": "Mettre à jour le mot de passe",
  "Your password has been updated.": "Votre mot de passe a été mis à jour.",
  "Enter your current password.": "Saisissez votre mot de passe actuel.",
  "The new password must be at least 6 characters.": "Le nouveau mot de passe doit contenir au moins 6 caractères.",
  "The new password and its confirmation do not match.": "Le nouveau mot de passe et sa confirmation ne correspondent pas.",
  "The current password is incorrect.": "Le mot de passe actuel est incorrect.",
  "The new password must be different from the current password.": "Le nouveau mot de passe doit être différent du mot de passe actuel.",
  "Too many attempts. Please try again later.": "Trop de tentatives. Veuillez réessayer plus tard.",
  "Your password could not be changed. Please try again.": "Votre mot de passe n’a pas pu être modifié. Veuillez réessayer.",
  "Your preference:": "Votre préférence :",
  "Organization default:": "Valeur par défaut de l’organisation :",
  "Untitled contribution": "Contribution sans titre",
  "Collective Contribution indicators": "Indicateurs de contribution collective",
  "← Back to periods": "← Retour aux périodes",
  "← Back to My Indicators": "← Retour à Mes indicateurs",
  "Value submitted for approval.": "Valeur soumise pour approbation.",
  "Draft saved.": "Brouillon enregistré.",
  "Attach a supporting document before submitting this value.": "Joignez une pièce justificative avant de soumettre cette valeur.",
  "Loading indicators…": "Chargement des indicateurs…",
  "Unexpected response. Please try again.": "Réponse inattendue. Veuillez réessayer.",
  "This file is no longer available.": "Ce fichier n’est plus disponible.",
  "The file could not be downloaded. Please try again.": "Le fichier n’a pas pu être téléchargé. Veuillez réessayer.",
  "Something went wrong on our side. Please try again.": "Un problème est survenu de notre côté. Veuillez réessayer.",
  "The request could not be completed. Please try again.": "La demande n’a pas pu être traitée. Veuillez réessayer.",
  "Manager evaluation": "Évaluation du gestionnaire",
  "Existing schedule templates can't be edited or deleted. Create a new template instead.": "Les modèles de planification existants ne peuvent pas être modifiés ni supprimés. Créez plutôt un nouveau modèle.",
  "Dates are counted in days from the end of each period (monthly, quarterly or annual). The reminder is sent the given number of days before the submission deadline.": "Les dates sont calculées en jours à partir de la fin de chaque période (mensuelle, trimestrielle ou annuelle). Le rappel est envoyé le nombre de jours indiqué avant l’échéance de soumission.",
  "Existing schedule templates can't be edited or deleted. To change a schedule, create a new template and assign it to the indicators that should use it. Dates already stored for previous periods do not change.": "Les modèles de planification existants ne peuvent pas être modifiés ni supprimés. Pour changer une planification, créez un nouveau modèle et attribuez-le aux indicateurs concernés. Les dates déjà enregistrées pour les périodes précédentes ne changent pas.",
  "Indicator-specific date exceptions are not supported in this version. Every indicator follows the dates of its schedule template.": "Les exceptions de dates propres à un indicateur ne sont pas prises en charge dans cette version. Chaque indicateur suit les dates de son modèle de planification.",
  "Indicator Exceptions": "Exceptions par indicateur",
  "Not yet evaluated by your manager": "Pas encore évalué par votre gestionnaire",
});
Object.assign(CANADA_EN_FR_EXACT, {
  "Good": "Bon",
  "Meets expectations": "Répond aux attentes",
  "Needs improvement": "À améliorer",
  "Unsatisfactory": "Insatisfaisant",
  "Action required": "Action requise",
  "My goals": "Mes objectifs",
  "Submitting…": "Soumission…",
  "Saving…": "Enregistrement…",
  "History view": "Vue de l’historique",
  "Due date": "Date d’échéance",
  "Department": "Service",
  "Limited": "Limité",
  "Very High": "Très élevé",
  "Periods": "Périodes",
  "Opens": "Ouverture", "Submission due": "Échéance de soumission", "Approval due": "Échéance d’approbation", "Locks": "Verrouillage", "Reminder": "Rappel",
});
// Phase 1 terminology (localization audit 2026-10): French for reworded legacy copy.
Object.assign(CANADA_EN_FR_COPY, {
  "Choose how much a role is influenced by its own unit, its parent units and organization-wide results.": "Choisissez dans quelle mesure un poste est influencé par sa propre unité, ses unités parentes et les résultats de l’ensemble de l’organisation.",
  "Parent-unit weight follows the organization path and is redistributed automatically when a level has no valid contribution data.": "La pondération des unités parentes suit le parcours dans l’organisation et est redistribuée automatiquement lorsqu’un niveau n’a aucune donnée de contribution valide.",
  "Approved contributions from the selected period": "Contributions approuvées de la période sélectionnée",
});
// The phrase index is built before the blocks above are merged; rebuild it so every entry is used.
CANADA_EN_FR_KEYS.splice(0, CANADA_EN_FR_KEYS.length, ...Object.keys(CANADA_EN_FR_COPY).sort((a, b) => b.length - a.length));
function translateCanadaEnglishPhrases(translated) {
  const exact = CANADA_EN_FR_COPY[translated];
  if (exact) return exact;
  const trimmed = translated.trim();
  if (CANADA_EN_FR_EXACT[trimmed]) return translated.replace(trimmed, CANADA_EN_FR_EXACT[trimmed]);
  return replacePhrases(translated, CANADA_EN_FR_COPY, CANADA_EN_FR_KEYS);
}
export function translateCanadaEnglishUiText(value, language) {
  if (language !== "fr" || value == null) return value;
  // Québec typography (non-breaking space before % and ":", none before ; ? !) on the translated text.
  return normalizeFrenchTypography(translateCanadaEnglishPhrases(String(value)));
}

const LEGACY_UI_ORIGINAL_TEXT = new WeakMap();
const LEGACY_UI_ORIGINAL_ATTRIBUTES = new WeakMap();
// What the translator itself last wrote. A mutation that only reflects our own write
// must not be treated as new source text, otherwise non-idempotent translations loop forever.
const LEGACY_UI_WRITTEN_TEXT = new WeakMap();
const LEGACY_UI_WRITTEN_ATTRIBUTES = new WeakMap();

function LegacyUiTranslator() {
  const { language } = useI18n();
  useEffect(() => {
    if (typeof document === "undefined" || !document.body) return undefined;
    const hasPersian = value => /[\u0600-\u06FF]/.test(String(value || ""));
    const translatableAttributes = ["placeholder", "title", "aria-label"];
    // Components that render their own EN/FR copy opt out with data-cvi-i18n="managed".
    const isManaged = element => Boolean(element?.closest?.('[data-cvi-i18n="managed"]'));
    const translateTextNode = node => {
      if (!node || node.nodeType !== Node.TEXT_NODE) return;
      if (isManaged(node.parentElement)) return;
      const current = node.data;
      if (LEGACY_UI_WRITTEN_TEXT.get(node) === current && LEGACY_UI_ORIGINAL_TEXT.has(node)) {
        const expected = LEGACY_UI_ORIGINAL_TEXT.get(node);
        const next = hasPersian(expected) ? translateLegacyUiText(expected, language) : (language === "fr" ? translateCanadaEnglishUiText(expected, language) : expected);
        if (next !== current) { LEGACY_UI_WRITTEN_TEXT.set(node, next); node.data = next; }
        return;
      }
      if (hasPersian(current)) LEGACY_UI_ORIGINAL_TEXT.set(node, current);
      else if (language === "fr" && translateCanadaEnglishUiText(current, "fr") !== current) LEGACY_UI_ORIGINAL_TEXT.set(node, current);
      const original = LEGACY_UI_ORIGINAL_TEXT.get(node);
      if (original == null) return;
      const next = hasPersian(original) ? translateLegacyUiText(original, language) : (language === "fr" ? translateCanadaEnglishUiText(original, language) : original);
      if (node.data !== next) { LEGACY_UI_WRITTEN_TEXT.set(node, next); node.data = next; }
    };
    const translateElementAttributes = element => {
      if (!element || element.nodeType !== Node.ELEMENT_NODE) return;
      if (isManaged(element)) return;
      let originals = LEGACY_UI_ORIGINAL_ATTRIBUTES.get(element);
      if (!originals) {
        originals = {};
        LEGACY_UI_ORIGINAL_ATTRIBUTES.set(element, originals);
      }
      let written = LEGACY_UI_WRITTEN_ATTRIBUTES.get(element);
      if (!written) {
        written = {};
        LEGACY_UI_WRITTEN_ATTRIBUTES.set(element, written);
      }
      for (const attribute of translatableAttributes) {
        const current = element.getAttribute(attribute);
        if (current == null) continue;
        if (written[attribute] === current && originals[attribute] != null) continue;
        if (hasPersian(current)) originals[attribute] = current;
        else if (language === "fr" && translateCanadaEnglishUiText(current, "fr") !== current) originals[attribute] = current;
        const original = originals[attribute];
        if (original == null) continue;
        const next = hasPersian(original) ? translateLegacyUiText(original, language) : (language === "fr" ? translateCanadaEnglishUiText(original, language) : original);
        if (current !== next) { written[attribute] = next; element.setAttribute(attribute, next); }
      }
    };
    const translateTree = root => {
      if (!root) return;
      if (root.nodeType === Node.TEXT_NODE) {
        translateTextNode(root);
        return;
      }
      if (root.nodeType === Node.ELEMENT_NODE) translateElementAttributes(root);
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        if (node.nodeType === Node.TEXT_NODE) translateTextNode(node);
        else if (node.nodeType === Node.ELEMENT_NODE) translateElementAttributes(node);
        node = walker.nextNode();
      }
    };
    translateTree(document.body);
    const observer = new MutationObserver(records => {
      for (const record of records) {
        if (record.type === "characterData") {
          translateTextNode(record.target);
          continue;
        }
        if (record.type === "attributes") {
          translateElementAttributes(record.target);
          continue;
        }
        for (const node of record.addedNodes) translateTree(node);
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: translatableAttributes,
    });
    return () => observer.disconnect();
  }, [language]);
  return null;
}

const I18nContext = createContext(null);

function I18nProvider({ children }) {
    const [language, setLanguage] = useState(() => {
        try {
            const saved = localStorage.getItem("cvi_language");
            return SUPPORTED_LANGUAGES[saved] ? saved : "en";
        } catch {
            return "en";
        }
    });

    const direction = SUPPORTED_LANGUAGES[language]?.dir || "ltr";

    useEffect(() => {
        try { localStorage.setItem("cvi_language", language); } catch { }
        if (typeof document !== "undefined") {
            document.documentElement.lang = language;
            document.documentElement.dir = direction;
        }
    }, [language, direction]);

    const t = (key, params = {}) => {
        const template = WORKFLOW_DICTIONARY[language]?.[key] ?? SETTINGS_DICTIONARY[language]?.[key] ?? UI_DICTIONARY[language]?.[key] ?? WORKFLOW_DICTIONARY.en?.[key] ?? SETTINGS_DICTIONARY.en?.[key] ?? UI_DICTIONARY.en?.[key] ?? key;
        return Object.entries(params).reduce(
            (value, [name, replacement]) => value.replaceAll(`{${name}}`, String(replacement)),
            template
        );
    };

    return <I18nContext.Provider value={{ language, setLanguage, direction, t, languages:SUPPORTED_LANGUAGES }}>
    <LegacyUiTranslator />
    <GlobalResponsiveStyles />
    {children}
  </I18nContext.Provider>;
}

function useI18n() {
    const context = useContext(I18nContext);
    if (!context) throw new Error("useI18n must be used inside I18nProvider.");
    return context;
}

function LanguageSelector({ compact = false, onChange, value }) {
    const { language, setLanguage, languages } = useI18n();
    const selectedValue = value || language;
    const handleChange = event => {
        const next = event.target.value;
        if (onChange) onChange(next);
        else setLanguage(next);
    };
    return <select value={selectedValue} onChange={handleChange} aria-label="Language"
        style={{
            border: "1px solid rgba(255,255,255,0.22)", background: compact ? "rgba(255,255,255,0.08)" : "#fff",
            color: compact ? "#fff" : C.navy, borderRadius: 7, padding: compact ? "5px 7px" : "8px 10px",
            fontSize: compact ? 10 : 12, fontFamily: "inherit", cursor: "pointer", direction: "ltr"
        }}>
        {Object.entries(languages).map(([code, meta]) => <option key={code} value={code} style={{ color: C.text }}>{meta.label}</option>)}
    </select>;
}


// ── SEED DATA (حالت اولیه — همه‌ی این‌ها بعداً از پنل ادمین قابل ویرایشن) ──────
const INIT_USERS = [
    { id: "u1", name: "Daniel Wilson", role: "ceo", posId: "ceo", teamId: null, deptId: null, managerId: null, username: "daniel.w", password: "123", employeeCode: "1001", nationalId: "", email: "daniel.w@donexsolution.com" },
    { id: "u2", name: "Sophie Tremblay", role: "admin", posId: "admin_operations_support", teamId: null, deptId: null, managerId: "u1", username: "sophie.t", password: "123", employeeCode: "1002", nationalId: "", email: "sophie.t@donexsolution.com" },
    { id: "u3", name: "Liam Chen", role: "deputy", posId: "director_product_technology", teamId: null, deptId: "d1", managerId: "u1", username: "liam.c", password: "123", employeeCode: "1003", nationalId: "", email: "liam.c@donexsolution.com" },
    { id: "u4", name: "Emma Roy", role: "manager", posId: "product_technology_manager", teamId: "t1", deptId: "d1", managerId: "u3", username: "emma.r", password: "123", employeeCode: "1004", nationalId: "", email: "emma.r@donexsolution.com" },
    { id: "u5", name: "Thomas Campbell", role: "deputy", posId: "director_revenue_growth", teamId: null, deptId: "d2", managerId: "u1", username: "thomas.c", password: "123", employeeCode: "1005", nationalId: "", email: "thomas.c@donexsolution.com" },
    { id: "u6", name: "Sarah Johnson", role: "manager", posId: "sales_customer_success_manager", teamId: "t2", deptId: "d2", managerId: "u5", username: "sarah.j", password: "123", employeeCode: "1006", nationalId: "", email: "sarah.j@donexsolution.com" },
    { id: "u7", name: "Charlotte Dubois", role: "manager", posId: "marketing_manager", teamId: "t3", deptId: "d2", managerId: "u5", username: "charlotte.d", password: "123", employeeCode: "1007", nationalId: "", email: "charlotte.d@donexsolution.com" },
    { id: "u8", name: "Marie Bouchard", role: "deputy", posId: "director_corporate_services", teamId: null, deptId: "d3", managerId: "u1", username: "marie.b", password: "123", employeeCode: "1008", nationalId: "", email: "marie.b@donexsolution.com" },
    { id: "u9", name: "William Scott", role: "manager", posId: "operations_administration_manager", teamId: "t4", deptId: "d3", managerId: "u8", username: "william.s", password: "123", employeeCode: "1009", nationalId: "", email: "william.s@donexsolution.com" },
    { id: "u10", name: "Noah Martin", role: "specialist", posId: "senior_software_developer", teamId: "t1", deptId: "d1", managerId: "u4", username: "noah.m", password: "123", employeeCode: "1010", nationalId: "", email: "noah.m@donexsolution.com" },
    { id: "u11", name: "Ethan Brown", role: "specialist", posId: "software_developer", teamId: "t1", deptId: "d1", managerId: "u4", username: "ethan.b", password: "123", employeeCode: "1011", nationalId: "", email: "ethan.b@donexsolution.com" },
    { id: "u12", name: "Olivia Nguyen", role: "specialist", posId: "ux_ui_designer", teamId: "t1", deptId: "d1", managerId: "u4", username: "olivia.n", password: "123", employeeCode: "1012", nationalId: "", email: "olivia.n@donexsolution.com" },
    { id: "u13", name: "Lucas Gagnon", role: "specialist", posId: "data_analyst", teamId: "t1", deptId: "d1", managerId: "u4", username: "lucas.g", password: "123", employeeCode: "1013", nationalId: "", email: "lucas.g@donexsolution.com" },
    { id: "u14", name: "Chloé Anderson", role: "specialist", posId: "qa_specialist", teamId: "t1", deptId: "d1", managerId: "u4", username: "chloe.a", password: "123", employeeCode: "1014", nationalId: "", email: "chloe.a@donexsolution.com" },
    { id: "u15", name: "Gabriel Pelletier", role: "specialist", posId: "business_development_specialist", teamId: "t2", deptId: "d2", managerId: "u6", username: "gabriel.p", password: "123", employeeCode: "1015", nationalId: "", email: "gabriel.p@donexsolution.com" },
    { id: "u16", name: "Emily Clark", role: "specialist", posId: "customer_success_specialist", teamId: "t2", deptId: "d2", managerId: "u6", username: "emily.c", password: "123", employeeCode: "1016", nationalId: "", email: "emily.c@donexsolution.com" },
    { id: "u17", name: "Nathan Lee", role: "specialist", posId: "customer_support_specialist", teamId: "t2", deptId: "d2", managerId: "u6", username: "nathan.l", password: "123", employeeCode: "1017", nationalId: "", email: "nathan.l@donexsolution.com" },
    { id: "u18", name: "Benjamin Taylor", role: "specialist", posId: "digital_marketing_specialist", teamId: "t3", deptId: "d2", managerId: "u7", username: "benjamin.t", password: "123", employeeCode: "1018", nationalId: "", email: "benjamin.t@donexsolution.com" },
    { id: "u19", name: "Amélie Fortin", role: "specialist", posId: "hr_administration_specialist", teamId: "t4", deptId: "d3", managerId: "u9", username: "amelie.f", password: "123", employeeCode: "1019", nationalId: "", email: "amelie.f@donexsolution.com" },
    { id: "u20", name: "Jacob Miller", role: "specialist", posId: "finance_administration_analyst", teamId: "t4", deptId: "d3", managerId: "u9", username: "jacob.m", password: "123", employeeCode: "1020", nationalId: "", email: "jacob.m@donexsolution.com" },
];

const INIT_TEAMS = [
    { id: "t1", label: "Product & Technology", deptId: "d1" },
    { id: "t2", label: "Sales & Customer Success", deptId: "d2" },
    { id: "t3", label: "Marketing", deptId: "d2" },
    { id: "t4", label: "Operations & Administration", deptId: "d3" },
];

const INIT_DEPARTMENTS = [
    { id: "d1", label: "Product & Technology" },
    { id: "d2", label: "Revenue & Growth" },
    { id: "d3", label: "Corporate Services" },
];

const specialistPosition = (id, label) => ({ id, label, wTeam: 70, wDept: 20, wOrg: 10, wNonRoutine: 42, wRoutine: 58, wPeerFeedback: 15, wResponsibilities: 60, wGoals: 25 });
const managerPosition = (id, label) => ({ id, label, wTeam: 40, wDept: 35, wOrg: 25, wNonRoutine: 50, wRoutine: 50, wPeerFeedback: 30, wResponsibilities: 50, wGoals: 20 });
const directorPosition = (id, label) => ({ id, label, wTeam: 25, wDept: 35, wOrg: 40, wNonRoutine: 50, wRoutine: 50, wPeerFeedback: 15, wResponsibilities: 40, wGoals: 45 });

const INIT_POSITIONS = [
    directorPosition("ceo", "CEO"),
    specialistPosition("admin_operations_support", "Administrator / Operations Support"),
    directorPosition("director_product_technology", "Director, Product & Technology"),
    managerPosition("product_technology_manager", "Product & Technology Manager"),
    directorPosition("director_revenue_growth", "Director, Revenue & Growth"),
    managerPosition("sales_customer_success_manager", "Sales & Customer Success Manager"),
    managerPosition("marketing_manager", "Marketing Lead / Manager"),
    directorPosition("director_corporate_services", "Director, Corporate Services"),
    managerPosition("operations_administration_manager", "Operations & Administration Manager"),
    specialistPosition("senior_software_developer", "Senior Software Developer"),
    specialistPosition("software_developer", "Software Developer"),
    specialistPosition("ux_ui_designer", "UX/UI Designer"),
    specialistPosition("data_analyst", "Data Analyst"),
    specialistPosition("qa_specialist", "QA Specialist"),
    specialistPosition("business_development_specialist", "Business Development Specialist"),
    specialistPosition("customer_success_specialist", "Customer Success Specialist"),
    specialistPosition("customer_support_specialist", "Customer Support Specialist"),
    specialistPosition("digital_marketing_specialist", "Digital Marketing Specialist"),
    specialistPosition("hr_administration_specialist", "HR & Administration Specialist"),
    specialistPosition("finance_administration_analyst", "Finance & Administration Analyst"),
];

const INIT_ACTIVITY_GROUPS = [
    {
        id: "learning", label: "Learning & Development", maxScore: 25, icon: "activity",
        subs: ["Professional certification", "Internal training", "Industry conference", "Independent learning", "Webinar"]
    },
    {
        id: "project", label: "Projects & Delivery", maxScore: 30, icon: "target",
        subs: ["Cross-functional project", "Critical issue response", "Team coverage", "Delivery above agreed standard"]
    },
    {
        id: "innovation", label: "Innovation & Improvement", maxScore: 25, icon: "performance",
        subs: ["Implemented process improvement", "Improvement proposal", "New internal tool", "Knowledge documentation"]
    },
    {
        id: "impact", label: "Team Enablement", maxScore: 20, icon: "team",
        subs: ["Peer coaching", "Knowledge-sharing session", "New-hire mentoring", "Training resource"]
    },
];

const INIT_IMPACT_LEVELS = [
    { id: "very_high", label: "Very High", score: 4 },
    { id: "high", label: "High", score: 3 },
    { id: "medium", label: "Medium", score: 2 },
    { id: "low", label: "Low", score: 1 },
];

// ── بخش روتین (ارزیابی ماهانه‌ی مدیر) ──────────────────────────────────────
// سطوح کیفیِ پاسخ به سؤالات وظایف مستمر — مشابه سطوح تأثیر، ولی مستقل و قابل‌ویرایش
const INIT_ROUTINE_LEVELS = [
    { id: "r_excellent", label: "Excellent", score: 5 },
    { id: "r_good", label: "Good", score: 4 },
    { id: "r_medium", label: "Meets expectations", score: 3 },
    { id: "r_weak", label: "Needs improvement", score: 2 },
    { id: "r_poor", label: "Unsatisfactory", score: 1 },
];

const INIT_ORG_SETTINGS = {
    wNonRoutine: 25,
    wRoutine: 35,
    wPeerFeedback: 15,
    name: "Donex Solutions Inc.",
    logo: null,
    defaultLanguage: "en",
    accentColor: "#00C8E6",
    timezone: "America/Toronto",
    dateFormat: "YYYY-MM-DD",
    calendar: "gregorian"
};

// بانک مشترک سؤالات بازخورد همکاران و تنظیم ارزیاب‌های هر فرد
const INIT_PEER_QUESTIONS = [
    { id: "pq1", label: "Collaboration and professional behaviour", weight: 25 },
    { id: "pq2", label: "Reliability and ownership", weight: 25 },
    { id: "pq3", label: "Communication and information sharing", weight: 25 },
    { id: "pq4", label: "Problem solving and continuous improvement", weight: 25 },
];
const INIT_PEER_CONFIGS = [
    { userId: "u10", enabled: true, reviewerIds: ["u11", "u12", "u13"], visibility: "anonymous", minResponses: 2 },
];
const INIT_PEER_REVIEWS = [];

// بانک سؤالات وظایف مستمر — به ازای هر تیم، ۳ سؤال برای هر ماه از فصل (month: 1|2|3)
// وزن هر سؤال به‌صورت درصد است؛ جمع وزن ۹ سؤالِ یک تیم باید ۱۰۰ شود.
const INIT_ROUTINE_QUESTIONS = INIT_TEAMS.flatMap((team, teamIndex) =>
    [1, 2, 3].flatMap((month) => [
        { id: `rq_${team.id}_${month}_1`, teamId: team.id, month, label: "Delivers recurring responsibilities on time", weight: month === 3 ? 12 : 12 },
        { id: `rq_${team.id}_${month}_2`, teamId: team.id, month, label: "Maintains quality and accuracy in day-to-day work", weight: 11 },
        { id: `rq_${team.id}_${month}_3`, teamId: team.id, month, label: "Communicates and coordinates effectively with the team", weight: month === 3 ? 11 : 10 },
    ])
);

const INIT_ROUTINE_ANSWERS = [
    { id: "ra1", questionId: "rq_t1_1_1", userId: "u10", periodId: "Q3-1404", levelId: "r_good", date: "2026-07-31" },
    { id: "ra2", questionId: "rq_t1_1_2", userId: "u10", periodId: "Q3-1404", levelId: "r_excellent", date: "2026-07-31" },
    { id: "ra3", questionId: "rq_t1_1_3", userId: "u10", periodId: "Q3-1404", levelId: "r_good", date: "2026-07-31" },
];

const INIT_DEPT_ROUTINE_QUESTIONS = INIT_DEPARTMENTS.flatMap(dept =>
    [1, 2, 3].flatMap(month => [
        { id: `dq_${dept.id}_${month}_1`, deptId: dept.id, month, label: "Keeps team objectives and priorities on track", weight: 12 },
        { id: `dq_${dept.id}_${month}_2`, deptId: dept.id, month, label: "Provides clear and timely management reporting", weight: 11 },
        { id: `dq_${dept.id}_${month}_3`, deptId: dept.id, month, label: "Coordinates effectively across teams and functions", weight: month === 3 ? 11 : 10 },
    ])
);

const INIT_DEPT_ROUTINE_ANSWERS = [];

const INIT_ORG_ROUTINE_QUESTIONS = [1, 2, 3].flatMap(month => [
    { id: `oq_${month}_1`, month, label: "Aligns business-unit priorities with company strategy", weight: 12 },
    { id: `oq_${month}_2`, month, label: "Provides decision-ready information to executive leadership", weight: 11 },
    { id: `oq_${month}_3`, month, label: "Enables effective cross-functional collaboration", weight: month === 3 ? 11 : 10 },
]);

const INIT_ORG_ROUTINE_ANSWERS = [];

const INIT_SCHEDULE_TEMPLATES = [
    { id: "schedule_monthly", label: "Standard monthly reporting", frequency: "monthly", openOffsetDays: 0, submitAfterDays: 5, approveAfterDays: 3, lockAfterDays: 10, reminderDays: 3 },
    { id: "schedule_quarterly", label: "Standard quarterly reporting", frequency: "quarterly", openOffsetDays: 0, submitAfterDays: 7, approveAfterDays: 4, lockAfterDays: 14, reminderDays: 3 },
    { id: "schedule_annual", label: "Standard annual reporting", frequency: "annual", openOffsetDays: 0, submitAfterDays: 10, approveAfterDays: 5, lockAfterDays: 20, reminderDays: 5 },
];
const INIT_SCHEDULE_EXCEPTIONS = [];

// لایه‌ی ۱: تعریف شاخص — یک‌بار ساخته می‌شه، بین دوره‌ها ثابت می‌مونه (بدون هدف/واقعی)
const INIT_INDICATOR_DEFS = [
    { id: "i1", label: "Client Satisfaction", level: "org", unit: "%", weight: 4, ownerId: "u1", registrarId: "u2", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i2", label: "Revenue Growth", level: "org", unit: "%", weight: 3, ownerId: "u1", registrarId: "u2", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i3", label: "Project Delivery Reliability", level: "org", unit: "%", weight: 3, ownerId: "u1", registrarId: "u2", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i4", label: "Product Quality", level: "org", unit: "%", weight: 3, ownerId: "u1", registrarId: "u2", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i5", label: "Organizational Collaboration", level: "org", unit: "%", weight: 2, ownerId: "u1", registrarId: "u2", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i6", label: "Sprint Delivery", level: "org_unit", orgUnitId: "legacy_team_t1", unit: "%", weight: 4, ownerId: "u4", registrarId: "u10", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i7", label: "Product Quality", level: "org_unit", orgUnitId: "legacy_team_t1", unit: "%", weight: 4, ownerId: "u4", registrarId: "u11", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i8", label: "Feature Adoption", level: "org_unit", orgUnitId: "legacy_team_t1", unit: "%", weight: 3, ownerId: "u4", registrarId: "u12", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i9", label: "Technical Reliability", level: "org_unit", orgUnitId: "legacy_team_t1", unit: "%", weight: 3, ownerId: "u4", registrarId: "u13", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i10", label: "Cross-team Collaboration", level: "org_unit", orgUnitId: "legacy_team_t1", unit: "/5", weight: 2, ownerId: "u4", registrarId: "u14", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i11", label: "New Revenue", level: "org_unit", orgUnitId: "legacy_team_t2", unit: "CAD", weight: 4, ownerId: "u6", registrarId: "u15", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i12", label: "Customer Retention", level: "org_unit", orgUnitId: "legacy_team_t2", unit: "%", weight: 4, ownerId: "u6", registrarId: "u16", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i13", label: "Lead Conversion", level: "org_unit", orgUnitId: "legacy_team_t2", unit: "%", weight: 3, ownerId: "u6", registrarId: "u15", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i14", label: "Customer Satisfaction", level: "org_unit", orgUnitId: "legacy_team_t2", unit: "/5", weight: 3, ownerId: "u6", registrarId: "u16", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i15", label: "CRM & Follow-up Quality", level: "org_unit", orgUnitId: "legacy_team_t2", unit: "%", weight: 2, ownerId: "u6", registrarId: "u17", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i16", label: "Qualified Leads", level: "org_unit", orgUnitId: "legacy_team_t3", unit: "#", weight: 4, ownerId: "u7", registrarId: "u18", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i17", label: "Campaign Performance", level: "org_unit", orgUnitId: "legacy_team_t3", unit: "%", weight: 3, ownerId: "u7", registrarId: "u18", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i18", label: "Website Engagement", level: "org_unit", orgUnitId: "legacy_team_t3", unit: "%", weight: 3, ownerId: "u7", registrarId: "u18", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i19", label: "Content Delivery", level: "org_unit", orgUnitId: "legacy_team_t3", unit: "%", weight: 2, ownerId: "u7", registrarId: "u18", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i20", label: "Sales Alignment", level: "org_unit", orgUnitId: "legacy_team_t3", unit: "/5", weight: 2, ownerId: "u7", registrarId: "u18", approverId: "u5", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i21", label: "On-time Project Support", level: "org_unit", orgUnitId: "legacy_team_t4", unit: "%", weight: 4, ownerId: "u9", registrarId: "u19", approverId: "u8", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i22", label: "Process Compliance", level: "org_unit", orgUnitId: "legacy_team_t4", unit: "%", weight: 3, ownerId: "u9", registrarId: "u19", approverId: "u8", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i23", label: "Administrative Accuracy", level: "org_unit", orgUnitId: "legacy_team_t4", unit: "%", weight: 3, ownerId: "u9", registrarId: "u20", approverId: "u8", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
    { id: "i24", label: "Internal Service Quality", level: "org_unit", orgUnitId: "legacy_team_t4", unit: "/5", weight: 3, ownerId: "u9", registrarId: "u20", approverId: "u8", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
    { id: "i25", label: "Process Improvement", level: "org_unit", orgUnitId: "legacy_team_t4", unit: "#", weight: 2, ownerId: "u9", registrarId: "u19", approverId: "u8", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: false },
];

const DEMO_INDICATOR_TARGETS = {
    i1: 90, i2: 12, i3: 92, i4: 95, i5: 4.4,
    i6: 90, i7: 94, i8: 62, i9: 99.5, i10: 4.3,
    i11: 180000, i12: 92, i13: 24, i14: 4.5, i15: 90,
    i16: 120, i17: 7.5, i18: 18, i19: 95, i20: 4.2,
    i21: 94, i22: 96, i23: 98, i24: 4.5, i25: 8,
};
const DEMO_PERIOD_FACTORS = {
    "Q1-1404": 0.91,
    "Q2-1404": 0.97,
    "Q3-1404": 1.01,
};
const INIT_INDICATOR_DATA = INIT_INDICATOR_DEFS.flatMap((indicator, index) =>
    Object.entries(DEMO_PERIOD_FACTORS).map(([periodId, factor]) => {
        const target = DEMO_INDICATOR_TARGETS[indicator.id] ?? 100;
        const isCurrent = periodId === "Q3-1404";
        const completedCurrent = index < 6;
        const actual = isCurrent && !completedCurrent ? null : Number((target * (factor + ((index % 5) - 2) * 0.012)).toFixed(target < 10 ? 1 : 0));
        return {
            id: `${indicator.id}_${periodId}`,
            periodId,
            indicatorId: indicator.id,
            target,
            actual,
            status: isCurrent && !completedCurrent ? "draft" : "approved",
            submittedBy: indicator.registrarId,
            approvedBy: isCurrent && !completedCurrent ? null : indicator.approverId,
            history: actual == null ? [] : [{ type: "approved", date: isCurrent ? "2026-10-04" : (periodId === "Q2-1404" ? "2026-10-02" : "2026-07-08"), by: "Donex reviewer", note: "Value approved" }],
        };
    })
);

const INIT_PERIODS = [
    { id: "Q1-1404", label: "Q2 2026", start: "2026-04-01", end: "2026-06-30", active: false, status: "locked" },
    { id: "Q2-1404", label: "Q3 2026", start: "2026-07-01", end: "2026-09-30", active: false, status: "locked" },
    { id: "Q3-1404", label: "Q4 2026", start: "2026-10-01", end: "2026-12-31", active: true, status: "active" },
];

const INIT_ACTIVITIES = [
    { id: "a1", userId: "u10", periodId: "Q3-1404", groupId: "project", sub: "Cross-functional project", title: "Release readiness automation", desc: "Automated release-readiness checks and reduced manual verification time for the product team.", doc: "release-readiness.pdf", status: "approved", impactLevel: "very_high", score: 4, managerNote: "", date: "2026-10-03", blockerCategory: "EXTERNAL_DEPENDENCY", blockerText: "Waiting on client-side API validation for one integration.", blockerStatus: "in_progress" },
    { id: "a2", userId: "u15", periodId: "Q3-1404", groupId: "project", sub: "Cross-functional project", title: "Enterprise pilot proposal", desc: "Coordinated a cross-functional pilot proposal for a 70-employee prospect.", doc: "", status: "approved", impactLevel: "high", score: 3, managerNote: "", date: "2026-10-03", blockerCategory: "MANAGEMENT_DECISION", blockerText: "Pricing exception requires executive approval.", blockerStatus: "open" },
    { id: "a3", userId: "u18", periodId: "Q3-1404", groupId: "innovation", sub: "Implemented process improvement", title: "Campaign attribution cleanup", desc: "Standardized campaign attribution and reporting across website and CRM sources.", doc: "attribution-notes.pdf", status: "approved", impactLevel: "medium", score: 2, managerNote: "", date: "2026-10-03", blockerCategory: "RESOURCE_LIMITATION", blockerText: "Limited design capacity is slowing the next campaign iteration.", blockerStatus: "open" },
    { id: "a4", userId: "u19", periodId: "Q3-1404", groupId: "innovation", sub: "Implemented process improvement", title: "Approval workflow simplification", desc: "Reduced internal purchase approval handoffs and documented the new process.", doc: "", status: "pending_review", impactLevel: null, score: 0, managerNote: "", date: "2026-10-03", blockerCategory: "PROCESS_ISSUE", blockerText: "Two legacy approval steps still require manual confirmation.", blockerStatus: "in_progress" },
    { id: "a5", userId: "u11", periodId: "Q3-1404", groupId: "learning", sub: "Professional certification", title: "Cloud security certification", desc: "Completed a role-relevant cloud security certification and shared key practices with the team.", doc: "cloud-security.pdf", status: "approved", impactLevel: "high", score: 3, managerNote: "", date: "2026-10-03" },
    { id: "a6", userId: "u16", periodId: "Q3-1404", groupId: "impact", sub: "Peer coaching", title: "Customer onboarding playbook", desc: "Built and coached the team on a consistent onboarding playbook for pilot clients.", doc: "", status: "approved", impactLevel: "high", score: 3, managerNote: "", date: "2026-10-03" },
];

// نمودار روند درآمد تیم (صرفاً نمایشی/دکوراتیو، بخشی از داده‌ی قابل‌ویرایش ادمین نیست)

// روند تاریخی CVI برای دوره‌های قدیمی که داده‌ی شاخص برایشان نداریم (صرفاً دکوراتیو)
const HISTORY_CVI = [
    {
        periodId: "Q1-1404", label: "Q2 2026", individual: 79, org: 77, cvi: 78,
        nonRoutine: 78, routine: 80,
        activityGroupScores: { learning: 18, project: 21, innovation: 17, impact: 16 },
        activityCounts: { learning: 2, project: 2, innovation: 1, impact: 1 },
        routineCriteria: [
            { label: "On-time delivery", score: 80, weight: 35 },
            { label: "Quality and accuracy", score: 78, weight: 35 },
            { label: "Team coordination", score: 79, weight: 30 },
        ]
    },
    {
        periodId: "Q2-1404", label: "Q3 2026", individual: 83, org: 81, cvi: 82,
        nonRoutine: 82, routine: 84,
        activityGroupScores: { learning: 20, project: 24, innovation: 20, impact: 18 },
        activityCounts: { learning: 2, project: 3, innovation: 2, impact: 2 },
        routineCriteria: [
            { label: "On-time delivery", score: 85, weight: 35 },
            { label: "Quality and accuracy", score: 84, weight: 35 },
            { label: "Team coordination", score: 82, weight: 30 },
        ]
    },
];

const DEMO_HISTORY_OVERRIDES = {
    u10: { "Q1-1404": { individual: 81, org: 77, cvi: 79 }, "Q2-1404": { individual: 86, org: 82, cvi: 84 } },
    u6:  { "Q1-1404": { individual: 88, org: 74, cvi: 81 }, "Q2-1404": { individual: 92, org: 78, cvi: 85 } },
    u9:  { "Q1-1404": { individual: 75, org: 82, cvi: 78 }, "Q2-1404": { individual: 77, org: 86, cvi: 81 } },
    u18: { "Q1-1404": { individual: 69, org: 71, cvi: 70 }, "Q2-1404": { individual: 71, org: 74, cvi: 72 } },
};

// Snapshot نهایی دوره: پس از قفل‌شدن، تغییر فعالیت‌ها/فرمول‌ها روی این داده اثر نمی‌گذارد.
const INIT_PERIOD_SNAPSHOTS = INIT_USERS
    .filter(u => !["admin", "ceo"].includes(u.role))
    .flatMap(u => HISTORY_CVI.map(p => {
        const override = DEMO_HISTORY_OVERRIDES[u.id]?.[p.periodId] || {};
        return {
            ...p,
            ...override,
            id: `snapshot_${p.periodId}_${u.id}`,
            userId: u.id,
            status: "final",
            locked: true,
            formulaVersion: "CVI-1.0",
            weights: { individual: 50, organizational: 50, nonRoutine: 25, routine: 35, peerFeedback: 15, goals: 25 },
            calculatedAt: `End of ${p.label}`,
            version: 1,
        };
    }));

const INIT_PERIOD_AUDIT = INIT_PERIODS.filter(p => p.status === "locked").map(p => ({
    id: `period_audit_${p.id}`, periodId: p.id, type: "locked", by: "System",
    date: `End of ${p.label}`, note: "Period finalized and snapshot locked"
}));

function latestPeriodSnapshotsForUser(snapshots, userId) {
    return Object.values(snapshots.filter(s => s.userId === userId).reduce((latest, s) => {
        const current = latest[s.periodId];
        if (!current || Number(s.version || 1) >= Number(current.version || 1)) latest[s.periodId] = s;
        return latest;
    }, {}));
}

// ── DATA CONTEXT (منبع واحد — همه‌ی پنل‌ها از همینجا می‌خونن/می‌نویسن) ─────────
const DataContext = createContext(null);
function useData() { return useContext(DataContext); }
// بسته‌ی کامل داده‌های روتین (سه سطح: تیم/معاونت/سازمان) — برای محاسبه‌ی امتیاز فردیِ هر نقش لازم است
function useRoutineCtx() {
    const { routineAnswers, routineQuestions, deptRoutineAnswers, deptRoutineQuestions,
        orgRoutineAnswers, orgRoutineQuestions, routineLevels,
        peerQuestions, peerConfigs, peerReviews, peerResult, positions } = useData();
    return {
        routineAnswers, routineQuestions, deptRoutineAnswers, deptRoutineQuestions,
        orgRoutineAnswers, orgRoutineQuestions, routineLevels, peerQuestions, peerConfigs, peerReviews, peerResult, positions
    };
}

function DataProvider({ children }) {
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [orgUnits, setOrgUnits] = useState([]);
    const [positions, setPositions] = useState([]);
    const [activityGroups, setActivityGroups] = useState([]);
    const [impactLevels, setImpactLevels] = useState([]);
    const [indicatorDefs, setIndicatorDefs] = useState([]);
    const [indicatorData, setIndicatorData] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [activities, setActivities] = useState([]);
    const [routineLevels, setRoutineLevels] = useState([]);
    const [routineQuestions, setRoutineQuestions] = useState([]);
    const [routineAnswers, setRoutineAnswers] = useState([]);
    const [deptRoutineQuestions, setDeptRoutineQuestions] = useState([]);
    const [deptRoutineAnswers, setDeptRoutineAnswers] = useState([]);
    const [orgRoutineQuestions, setOrgRoutineQuestions] = useState([]);
    const [orgRoutineAnswers, setOrgRoutineAnswers] = useState([]);
    const [orgSettings, setOrgSettings] = useState(INIT_ORG_SETTINGS);
    const [periodSnapshots, setPeriodSnapshots] = useState([]);
    const [periodAudit, setPeriodAudit] = useState([]);
    const [scheduleTemplates, setScheduleTemplates] = useState([]);
    const [scheduleExceptions, setScheduleExceptions] = useState(INIT_SCHEDULE_EXCEPTIONS);
    const [peerQuestions, setPeerQuestions] = useState([]);
    const [peerConfigs, setPeerConfigs] = useState([]);
    const [peerReviews, setPeerReviews] = useState([]);
    const [peerAssignments, setPeerAssignments] = useState([]);
    const [peerResult, setPeerResult] = useState(null);
    const [peerLoading, setPeerLoading] = useState(false);
    const [peerError, setPeerError] = useState("");
    const [baseDataLoading, setBaseDataLoading] = useState(false);
    const [baseDataError, setBaseDataError] = useState("");
    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [activitiesError, setActivitiesError] = useState("");
    const [routineTargets, setRoutineTargets] = useState([]);
    const [routineLoading, setRoutineLoading] = useState(false);
    const [routineError, setRoutineError] = useState("");
    const [indicatorLoading, setIndicatorLoading] = useState(false);
    const [indicatorError, setIndicatorError] = useState("");

    const normalizeApiActivity = (activity) => {
        const firstAttachment = activity.attachments?.[0] || null;
        const activityDate = activity.submittedAt || activity.createdAt || null;

        return {
            id: activity.id,
            userId: activity.userId,
            periodId: activity.periodId,
            groupId: activity.groupId,
            sub: activity.subtype?.name || activity.subtype?.label || "",
            subtypeId: activity.subtypeId || null,
            title: activity.title || "",
            desc: activity.description || "",
            doc: firstAttachment?.fileName || "",
            docDataUrl: "",
            status: String(activity.status || "").toLowerCase(),
            impactLevel: activity.impactLevelId || null,
            score: Number(activity.score) || 0,
            managerNote: activity.managerNote || "",
            date: activityDate ? jalaliDateFromGregorian(new Date(activityDate)) : "",
            submittedAt: activity.submittedAt || null,
            revisionRequestedAt: activity.revisionRequestedAt || null,
            resubmittedAt: activity.resubmittedAt || null,
            approvedAt: activity.approvedAt || null,
            rejectedAt: activity.rejectedAt || null,
            // Challenge / support fields were returned by the API but never mapped, so
            // "Support needs" and "Support follow-ups" always showed zero.
            blockerCategory: activity.blockerCategory || null,
            blockerText: activity.blockerText || null,
            supportNeed: activity.supportNeed || null,
            suggestedAction: activity.suggestedAction || null,
            blockerStatus: String(activity.blockerStatus || "none").toLowerCase(),
            attachments: activity.attachments || [],
            history: (activity.history || []).map(event => ({
                id: event.id,
                type: String(event.type || event.eventType || "").toLowerCase(),
                eventType: String(event.eventType || event.type || "").toLowerCase(),
                date: event.createdAt ? jalaliDateFromGregorian(new Date(event.createdAt)) : "",
                createdAt: event.createdAt || null,
                actorName: event.actor?.name || null,
                by: event.actor?.name || "System",
                actorId: event.actorId || event.actor?.id || null,
                actorRole: event.actor?.role ? String(event.actor.role).toLowerCase() : null,
                fromStatus: event.fromStatus ? String(event.fromStatus).toLowerCase() : null,
                toStatus: event.toStatus ? String(event.toStatus).toLowerCase() : null,
                note: event.note || "",
            })),
        };
    };

    // Clear all session-bound data before switching identities.
    const invalidateSessionData = () => {
        setUsers([]); setTeams([]); setDepartments([]); setOrgUnits([]);
        setPositions([]); setActivityGroups([]); setImpactLevels([]);
        setIndicatorDefs([]); setIndicatorData([]); setPeriods([]); setActivities([]);
        setRoutineLevels([]); setRoutineQuestions([]); setRoutineAnswers([]);
        setDeptRoutineQuestions([]); setDeptRoutineAnswers([]);
        setOrgRoutineQuestions([]); setOrgRoutineAnswers([]);
        setRoutineTargets([]); setPeriodSnapshots([]); setPeriodAudit([]);
        setScheduleTemplates([]); setScheduleExceptions(INIT_SCHEDULE_EXCEPTIONS);
        setPeerQuestions([]); setPeerConfigs([]); setPeerReviews([]);
        setPeerAssignments([]); setPeerResult(null);
        setOrgSettings(INIT_ORG_SETTINGS);
        setBaseDataError(""); setActivitiesError(""); setRoutineError("");
        setIndicatorError(""); setPeerError("");
    };

    const handleUnauthorized = () => {
        invalidateSessionData();
        sessionStorage.removeItem("cvi_user");
        sessionStorage.removeItem("cvi_token");
        window.dispatchEvent(new Event("cvi-session-expired"));
    };

    const uploadActivityFileApi = async (activityId, file) => {
        if (!file) return null;
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(`${API_BASE_URL}/api/activities/${activityId}/files`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });
        if (response.status === 401) handleUnauthorized();
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || `File upload failed with status ${response.status}.`);
        return payload;
    };

    const activityApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");

        const response = await fetch(`${API_BASE_URL}/api/activities${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });

        if (response.status === 401) {
            handleUnauthorized();
            throw new Error("Session expired.");
        }

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload.message || `Activity request failed with status ${response.status}.`);
        }
        return payload;
    };

    const loadActivities = async () => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) {
            setActivities([]);
            setActivitiesLoading(false);
            setActivitiesError("");
            return;
        }

        setActivitiesLoading(true);
        setActivitiesError("");

        try {
            const rows = await activityApiRequest("");
            setActivities(rows.map(normalizeApiActivity));
        } catch (error) {
            console.error("Activities load failed:", error);
            setActivitiesError("Contributions could not be loaded. Please try again.");
        } finally {
            setActivitiesLoading(false);
        }
    };

    const resolveSubtypeId = (groupId, subtypeLabel) => {
        if (!groupId || !subtypeLabel) return null;
        const group = activityGroups.find(item => item.id === groupId);
        const index = group?.subs?.findIndex(label => label === subtypeLabel) ?? -1;
        return index >= 0 ? `${groupId}_sub_${index + 1}` : null;
    };

    const activityPayload = (form, { classification = true } = {}) => ({
        ...(classification ? { groupId: form.groupId, subtypeId: resolveSubtypeId(form.groupId, form.sub) } : {}),
        title: form.title,
        description: form.desc,
        blockerCategory: form.blockerCategory || null,
        blockerText: form.blockerText || null,
        supportNeed: form.supportNeed || null,
        suggestedAction: form.suggestedAction || null,
        blockerStatus: form.blockerStatus || "none",
    });
    const storeActivity = (row) => {
        const normalized = normalizeApiActivity(row);
        setActivities(prev => prev.some(item => item.id === normalized.id)
            ? prev.map(item => item.id === normalized.id ? normalized : item)
            : [normalized, ...prev]);
        return normalized;
    };
    // Re-reads one of the user's contributions after a step that changed it on the server
    // (an uploaded file adds an attachment and a history entry).
    const refreshMyActivity = async (activityId, fallback) => {
        try {
            const rows = await activityApiRequest("/mine");
            const row = (Array.isArray(rows) ? rows : []).find(item => item.id === activityId);
            return row ? storeActivity(row) : fallback;
        } catch {
            return fallback;
        }
    };
    const submitActivityApi = async (activityId) => storeActivity(await activityApiRequest(`/${activityId}/submit`, { method: "POST", body: JSON.stringify({}) }));

    // Creates the contribution as a draft, attaches the file, then submits it unless `submit` is
    // false. If a later step fails the draft is kept and the error says so (`savedAsDraft`).
    const createActivityApi = async (form, periodId, { submit = true } = {}) => {
        const created = await activityApiRequest("", {
            method: "POST",
            body: JSON.stringify({ periodId, ...activityPayload(form), submit: false }),
        });
        let current = storeActivity(created);
        try {
            if (form._file) {
                await uploadActivityFileApi(created.id, form._file);
                if (!submit) current = await refreshMyActivity(created.id, current);
            }
            if (submit) current = await submitActivityApi(created.id);
            return current;
        } catch (error) {
            error.savedAsDraft = true;
            error.activity = current;
            throw error;
        }
    };

    // Saves changes to a draft or to a contribution returned for revision, optionally submitting it.
    const updateActivityApi = async (activityId, form, { submit = false, classification = true } = {}) => {
        const updated = await activityApiRequest(`/${activityId}`, {
            method: "PATCH",
            body: JSON.stringify(activityPayload(form, { classification })),
        });
        let current = storeActivity(updated);
        try {
            if (form._file) {
                await uploadActivityFileApi(activityId, form._file);
                if (!submit) current = await refreshMyActivity(activityId, current);
            }
            if (submit) current = await submitActivityApi(activityId);
            return current;
        } catch (error) {
            error.savedChanges = true;
            error.activity = current;
            throw error;
        }
    };

    const reviewActivityApi = async (activityId, decision, impactLevelId, note) => {
        const reviewed = await activityApiRequest(`/${activityId}/review`, {
            method: "POST",
            body: JSON.stringify({
                decision,
                impactLevelId: impactLevelId || null,
                note: note?.trim() || null,
            }),
        });

        const normalized = normalizeApiActivity(reviewed);
        setActivities(prev => prev.map(item => item.id === normalized.id ? normalized : item));
        return normalized;
    };

    const normalizeRoutineQuestion = (question) => ({
        id: question.id,
        scope: String(question.scope || "").toLowerCase(),
        teamId: question.teamId ?? null,
        deptId: question.departmentId ?? question.deptId ?? null,
        departmentId: question.departmentId ?? question.deptId ?? null,
        orgUnitId: question.orgUnitId ?? null,
        month: Number(question.month) || 1,
        label: question.label || question.text || "",
        text: question.text || question.label || "",
        weight: Number(question.weight) || 0,
        isActive: question.isActive !== false,
        sortOrder: Number(question.sortOrder) || 0,
    });

    const normalizeRoutineLevel = (level) => ({
        id: level.id,
        label: level.label || level.name || "",
        name: level.name || level.label || "",
        score: Number(level.score) || 0,
        isActive: level.isActive !== false,
        sortOrder: Number(level.sortOrder) || 0,
    });

    const normalizeRoutineAnswer = (answer) => ({
        id: answer.id,
        questionId: answer.questionId,
        userId: answer.subjectUserId || answer.userId,
        subjectUserId: answer.subjectUserId || answer.userId,
        evaluatorId: answer.evaluatorId || null,
        periodId: answer.periodId,
        levelId: answer.levelId,
        note: answer.note || "",
        answeredAt: answer.answeredAt || null,
        date: answer.answeredAt ? jalaliDateFromGregorian(new Date(answer.answeredAt)) : "",
        question: answer.question ? normalizeRoutineQuestion(answer.question) : null,
        level: answer.level ? normalizeRoutineLevel(answer.level) : null,
    });

    const routineApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");

        const response = await fetch(`${API_BASE_URL}/api/routine${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });

        if (response.status === 401) {
            handleUnauthorized();
            throw new Error("Session expired.");
        }

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload.message || `Routine request failed with status ${response.status}.`);
        }
        return payload;
    };

    const setRoutineQuestionsForScope = (scope, rows) => {
        const normalized = rows.map(normalizeRoutineQuestion);
        if (scope === "team") setRoutineQuestions(normalized);
        if (scope === "department") setDeptRoutineQuestions(normalized);
        if (scope === "org_unit") {
            const current = JSON.parse(sessionStorage.getItem("cvi_user") || "null");
            if (String(current?.role || "").toLowerCase() === "deputy") setDeptRoutineQuestions(normalized);
            else setRoutineQuestions(normalized);
        }
        if (scope === "organization") setOrgRoutineQuestions(normalized);
    };

    const setRoutineAnswersForScope = (scope, rows) => {
        const normalized = rows.map(normalizeRoutineAnswer);
        if (scope === "team") setRoutineAnswers(normalized);
        if (scope === "department") setDeptRoutineAnswers(normalized);
        if (scope === "org_unit") {
            const current = JSON.parse(sessionStorage.getItem("cvi_user") || "null");
            if (String(current?.role || "").toLowerCase() === "deputy") setDeptRoutineAnswers(normalized);
            else setRoutineAnswers(normalized);
        }
        if (scope === "organization") setOrgRoutineAnswers(normalized);
    };

    const loadRoutineData = async () => {
        const token = sessionStorage.getItem("cvi_token");
        const active = periods.find(period => period.active && period.status === "active") || null;

        if (!token) {
            setRoutineTargets([]);
            setRoutineLoading(false);
            setRoutineError("");
            return;
        }

        let sessionUser = null;
        try {
            sessionUser = JSON.parse(sessionStorage.getItem("cvi_user") || "null");
        } catch { }
        const directoryUser = users.find(item => item.id === sessionUser?.id);
        if (directoryUser) sessionUser = { ...sessionUser, ...directoryUser };

        const role = String(sessionUser?.role || "").toLowerCase();
        setRoutineLoading(true);
        setRoutineError("");

        try {
            const levels = await routineApiRequest("/levels");
            setRoutineLevels(levels.map(normalizeRoutineLevel));

            if (role === "admin") {
                const adminQuestions = await adminApiRequest("/routine-questions");
                const normalizedQuestions = adminQuestions.map(normalizeRoutineQuestion);
                setRoutineQuestions(normalizedQuestions.filter(question => question.scope === "org_unit"));
                setDeptRoutineQuestions([]);
                setOrgRoutineQuestions(normalizedQuestions.filter(question => question.scope === "organization"));
                setRoutineAnswers([]);
                setDeptRoutineAnswers([]);
                setOrgRoutineAnswers([]);
                setRoutineTargets([]);
                return;
            }

            if (!active) {
                setRoutineTargets([]);
                return;
            }

            const evaluatorScope = sessionUser?.orgUnitId && ["manager","deputy"].includes(role) ? "org_unit"
                : role === "manager" ? "team"
                    : role === "deputy" ? "department"
                        : null; // The CEO has no routine-evaluation scope on the server (403).

            if (evaluatorScope) {
                const [questions, targets, answers] = await Promise.all([
                    routineApiRequest("/questions"),
                    routineApiRequest("/targets"),
                    routineApiRequest(`/answers?periodId=${encodeURIComponent(active.id)}`),
                ]);

                setRoutineQuestionsForScope(evaluatorScope, questions);
                setRoutineAnswersForScope(evaluatorScope, answers);
                setRoutineTargets(targets.map(target => ({
                    id: target.id,
                    name: target.name,
                    role: String(target.role || "").toLowerCase(),
                    teamId: target.teamId ?? null,
                    deptId: target.departmentId ?? null,
                    departmentId: target.departmentId ?? null,
                    orgUnitId: target.orgUnitId ?? null,
                    managerId: target.managerId ?? null,
                })));
            } else {
                setRoutineTargets([]);
            }

            const mine = await routineApiRequest(`/mine?periodId=${encodeURIComponent(active.id)}`);
            const myAnswers = (mine.answers || []).map(normalizeRoutineAnswer);
            const myQuestions = myAnswers.map(answer => answer.question).filter(Boolean);
            const myScope = myQuestions[0]?.scope || null;

            if (myScope) {
                if (myScope !== evaluatorScope) {
                    setRoutineQuestionsForScope(myScope, myQuestions);
                    setRoutineAnswersForScope(myScope, myAnswers);
                } else if (!evaluatorScope) {
                    setRoutineQuestionsForScope(myScope, myQuestions);
                    setRoutineAnswersForScope(myScope, myAnswers);
                }
            }
        } catch (error) {
            console.error("Routine data load failed:", error);
            setRoutineError("Core responsibilities data could not be loaded. Please try again.");
        } finally {
            setRoutineLoading(false);
        }
    };

    const saveRoutineAnswerApi = async (questionId, subjectUserId, levelId, note = "") => {
        const active = periods.find(period => period.active && period.status === "active") || null;
        if (!active) throw new Error("No active period.");

        const saved = await routineApiRequest("/answers", {
            method: "POST",
            body: JSON.stringify({
                questionId,
                subjectUserId,
                periodId: active.id,
                levelId,
                note: note?.trim() || null,
            }),
        });

        const normalized = normalizeRoutineAnswer(saved);
        const scope = normalized.question?.scope || null;
        let updater = scope === "team" ? setRoutineAnswers
            : scope === "department" ? setDeptRoutineAnswers
                : scope === "organization" ? setOrgRoutineAnswers : null;
        if (scope === "org_unit") {
            let current = null;
            try { current = JSON.parse(sessionStorage.getItem("cvi_user") || "null"); } catch { current = null; }
            updater = String(current?.role || "").toLowerCase() === "deputy" ? setDeptRoutineAnswers : setRoutineAnswers;
        }

        if (updater) {
            updater(prev => {
                const exists = prev.some(answer => answer.id === normalized.id || (
                    answer.questionId === normalized.questionId &&
                    answer.userId === normalized.userId &&
                    answer.periodId === normalized.periodId
                ));
                if (!exists) return [...prev, normalized];
                return prev.map(answer =>
                    answer.id === normalized.id || (
                        answer.questionId === normalized.questionId &&
                        answer.userId === normalized.userId &&
                        answer.periodId === normalized.periodId
                    ) ? normalized : answer
                );
            });
        }

        return normalized;
    };

    const normalizePeerQuestion = (question) => ({
        id: question.id,
        label: question.label || question.text || "",
        text: question.text || question.label || "",
        weight: Number(question.weight) || 0,
        isActive: question.isActive !== false,
        sortOrder: Number(question.sortOrder) || 0,
    });

    const normalizePeerConfig = (config) => ({
        id: config.id,
        userId: config.userId || config.subjectUserId,
        subjectUserId: config.subjectUserId || config.userId,
        enabled: Boolean(config.enabled),
        visibility: String(config.visibility || "anonymous").toLowerCase() === "identified" ? "identified" : "anonymous",
        minResponses: Math.max(1, Number(config.minResponses) || 3),
        reviewerIds: Array.isArray(config.reviewerIds) ? config.reviewerIds : [],
    });

    const normalizePeerAssignment = (assignment) => ({
        id: assignment.id,
        periodId: assignment.periodId,
        subjectUserId: assignment.subjectUserId,
        reviewerId: assignment.reviewerId,
        status: String(assignment.status || "").toLowerCase(),
        assignedAt: assignment.assignedAt || null,
        completedAt: assignment.completedAt || null,
        subjectUser: assignment.subjectUser ? {
            id: assignment.subjectUser.id,
            name: assignment.subjectUser.name,
            role: String(assignment.subjectUser.role || "").toLowerCase(),
            teamId: assignment.subjectUser.teamId ?? null,
            deptId: assignment.subjectUser.departmentId ?? null,
            departmentId: assignment.subjectUser.departmentId ?? null,
        } : null,
        period: assignment.period ? {
            id: assignment.period.id,
            label: assignment.period.name,
            name: assignment.period.name,
            status: String(assignment.period.status || "").toLowerCase(),
        } : null,
        review: assignment.review ? {
            id: assignment.review.id,
            submittedAt: assignment.review.submittedAt || null,
            answers: (assignment.review.answers || []).map(answer => ({
                id: answer.id,
                questionId: answer.questionId,
                score: Number(answer.score),
                note: answer.note || "",
            })),
        } : null,
    });

    const normalizePeerResult = (result) => result ? ({
        period: result.period ? {
            id: result.period.id,
            label: result.period.name,
            name: result.period.name,
            status: String(result.period.status || "").toLowerCase(),
        } : null,
        subjectUserId: result.subjectUserId,
        config: result.config ? {
            enabled: Boolean(result.config.enabled),
            visibility: String(result.config.visibility || "anonymous").toLowerCase(),
            minResponses: Number(result.config.minResponses) || 1,
        } : null,
        summary: {
            assignedCount: Number(result.summary?.assignedCount) || 0,
            responseCount: Number(result.summary?.responseCount) || 0,
            enoughResponses: Boolean(result.summary?.enoughResponses),
            overallScore: result.summary?.overallScore == null ? null : Number(result.summary.overallScore),
        },
        questions: (result.questions || []).map(question => ({
            questionId: question.questionId,
            text: question.text || "",
            label: question.text || "",
            weight: Number(question.weight) || 0,
            responseCount: Number(question.responseCount) || 0,
            averageScore: question.averageScore == null ? null : Number(question.averageScore),
        })),
    }) : null;

    const peerApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");

        const response = await fetch(`${API_BASE_URL}/api/peer${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });

        if (response.status === 401) {
            handleUnauthorized();
            throw new Error("Session expired.");
        }

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload.message || `Peer feedback request failed with status ${response.status}.`);
        }
        return payload;
    };

    const loadPeerData = async () => {
        const token = sessionStorage.getItem("cvi_token");
        const active = periods.find(period => period.active && period.status === "active") || null;

        if (!token) {
            setPeerAssignments([]);
            setPeerResult(null);
            setPeerLoading(false);
            setPeerError("");
            return;
        }

        setPeerLoading(true);
        setPeerError("");

        try {
            const questions = await peerApiRequest("/questions");
            setPeerQuestions(questions.map(normalizePeerQuestion));

            let sessionUser = null;
            try { sessionUser = JSON.parse(sessionStorage.getItem("cvi_user") || "null"); } catch { sessionUser = null; }
            if (String(sessionUser?.role || "").toLowerCase() === "admin") {
                const configs = await peerApiRequest("/admin/configs");
                setPeerConfigs(configs.map(normalizePeerConfig));
            }

            if (!active) {
                setPeerAssignments([]);
                setPeerResult(null);
                return;
            }

            const [assignments, mine] = await Promise.all([
                peerApiRequest(`/assignments?periodId=${encodeURIComponent(active.id)}`),
                peerApiRequest(`/mine?periodId=${encodeURIComponent(active.id)}`),
            ]);

            setPeerAssignments(assignments.map(normalizePeerAssignment));
            setPeerResult(normalizePeerResult(mine));
        } catch (error) {
            console.error("Peer feedback load failed:", error);
            setPeerError("Peer feedback data could not be loaded. Please try again.");
        } finally {
            setPeerLoading(false);
        }
    };

    const submitPeerReviewApi = async (assignmentId, answers) => {
        const payload = Object.entries(answers).map(([questionId, score]) => ({
            questionId,
            score: Number(score),
        }));

        const saved = await peerApiRequest(`/assignments/${assignmentId}/submit`, {
            method: "POST",
            body: JSON.stringify({ answers: payload }),
        });

        const normalized = normalizePeerAssignment(saved);
        setPeerAssignments(prev => prev.map(item => item.id === normalized.id ? normalized : item));

        const active = periods.find(period => period.active && period.status === "active") || null;
        if (active) {
            const mine = await peerApiRequest(`/mine?periodId=${encodeURIComponent(active.id)}`);
            setPeerResult(normalizePeerResult(mine));
        }

        return normalized;
    };

    const normalizeScheduleTemplate = (template) => ({
        id: template.id,
        label: template.label || template.name || "",
        name: template.name || template.label || "",
        frequency: String(template.frequency || "").toLowerCase(),
        openOffsetDays: Number(template.openOffsetDays) || 0,
        submitAfterDays: Number(template.submitAfterDays) || 0,
        approveAfterDays: Number(template.approveAfterDays) || 0,
        lockAfterDays: Number(template.lockAfterDays) || 0,
        reminderDays: Number(template.reminderDays) || 0,
        isActive: template.isActive !== false,
    });

    const normalizeApiIndicator = (indicator) => ({
        id: indicator.id,
        label: indicator.label || indicator.name || "",
        name: indicator.name || indicator.label || "",
        level: String(indicator.level || "").toLowerCase() === "department" ? "dept"
            : String(indicator.level || "").toLowerCase() === "organization" ? "org"
                : String(indicator.level || "").toLowerCase(),
        teamId: indicator.teamId ?? indicator.team?.id ?? null,
        deptId: indicator.departmentId ?? indicator.department?.id ?? null,
        departmentId: indicator.departmentId ?? indicator.department?.id ?? null,
        orgUnitId: indicator.orgUnitId ?? indicator.orgUnit?.id ?? null,
        orgUnitLabel: indicator.orgUnit?.name || indicator.orgUnit?.label || "",
        orgUnit: indicator.orgUnit || null,
        unit: indicator.unit || "",
        weight: Number(indicator.weight) || 0,
        ownerId: indicator.ownerId,
        registrarId: indicator.registrarId,
        approverId: indicator.approverId,
        scheduleTemplateId: indicator.scheduleTemplateId,
        aggregationMethod: String(indicator.aggregationMethod || "").toLowerCase(),
        formulaTemplateId: indicator.formulaTemplateId || "standard:higher",
        requiresEvidence: Boolean(indicator.requiresEvidence),
        isActive: indicator.isActive !== false,
        owner: indicator.owner || null,
        registrar: indicator.registrar || null,
        approver: indicator.approver || null,
        team: indicator.team || null,
        department: indicator.department || null,
        scheduleTemplate: indicator.scheduleTemplate ? normalizeScheduleTemplate(indicator.scheduleTemplate) : null,
    });

    const normalizeApiIndicatorValue = (value) => {
        const firstAttachment = value.attachments?.[0] || null;
        const latestRevision = (value.history || []).slice().reverse().find(item =>
            String(item.toStatus || "").toLowerCase() === "revision_requested" ||
            String(item.eventType || "").toLowerCase() === "revision_requested");
        return {
            id: value.id,
            indicatorId: value.indicatorId,
            periodId: value.periodId,
            occurrenceId: value.occurrenceId === "period" ? null : (value.occurrenceId ?? null),
            target: value.target == null ? null : Number(value.target),
            actual: value.actual == null ? null : Number(value.actual),
            status: String(value.status || "not_started").toLowerCase(),
            submittedBy: value.submittedById ?? value.submittedBy?.id ?? null,
            approvedBy: value.approvedById ?? value.approvedBy?.id ?? null,
            note: value.note || "",
            deviationReason: value.deviationReason || "",
            correctiveAction: value.correctiveAction || "",
            reviewNote: latestRevision?.note || "",
            scheduleSnapshot: value.scheduleSnapshot || null,
            submittedAt: value.submittedAt || null,
            approvedAt: value.approvedAt || null,
            lockedAt: value.lockedAt || null,
            doc: firstAttachment?.fileName || "",
            docDataUrl: "",
            attachments: value.attachments || [],
            history: (value.history || []).map(item => ({
                id: item.id,
                type: String(item.eventType || item.toStatus || "").toLowerCase(),
                eventType: String(item.eventType || "").toLowerCase(),
                fromStatus: item.fromStatus ? String(item.fromStatus).toLowerCase() : null,
                toStatus: item.toStatus ? String(item.toStatus).toLowerCase() : null,
                oldValue: item.oldValue,
                newValue: item.newValue,
                note: item.note || "",
                actorId: item.actorId || item.actor?.id || null,
                actorRole: item.actor?.role ? String(item.actor.role).toLowerCase() : null,
                by: item.actor?.name || "System",
                actorName: item.actor?.name || null,
                createdAt: item.createdAt || null,
                date: item.createdAt ? jalaliDateFromGregorian(new Date(item.createdAt)) : "",
            })),
        };
    };

    const indicatorApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}/api/indicators${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });
        if (response.status === 401) {
            handleUnauthorized();
            throw new Error("Session expired.");
        }
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || `Indicator request failed with status ${response.status}.`);
        return payload;
    };

    const uploadIndicatorFileApi = async (valueId, file) => {
        if (!file) return null;
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(`${API_BASE_URL}/api/indicators/values/${valueId}/files`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });
        if (response.status === 401) handleUnauthorized();
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || `Evidence upload failed with status ${response.status}.`);
        return payload;
    };

    const mergeIndicatorValue = (value) => {
        const normalized = normalizeApiIndicatorValue(value);
        setIndicatorData(prev => {
            const exists = prev.some(item => item.id === normalized.id);
            return exists ? prev.map(item => item.id === normalized.id ? normalized : item) : [...prev, normalized];
        });
        return normalized;
    };

    const loadIndicatorData = async () => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) {
            setIndicatorDefs([]);
            setIndicatorData([]);
            setScheduleTemplates([]);
            setIndicatorLoading(false);
            setIndicatorError("");
            return;
        }
        setIndicatorLoading(true);
        setIndicatorError("");
        try {
            const [definitions, templates, values] = await Promise.all([
                indicatorApiRequest("/"),
                indicatorApiRequest("/schedule-templates"),
                indicatorApiRequest("/values/list"),
            ]);
            setIndicatorDefs(definitions.map(normalizeApiIndicator));
            setScheduleTemplates(templates.map(normalizeScheduleTemplate));
            setIndicatorData(values.map(normalizeApiIndicatorValue));
        } catch (error) {
            console.error("Indicator data load failed:", error);
            setIndicatorError("Collective Contribution indicators could not be loaded. Please try again.");
        } finally {
            setIndicatorLoading(false);
        }
    };

    const indicatorDefinitionPayload = (form) => ({
        name: String(form.label || form.name || "").trim(),
        level: form.level === "dept" ? "department" : form.level === "org" ? "organization" : form.level,
        teamId: form.level === "team" ? (form.teamId || null) : null,
        departmentId: form.level === "dept" ? (form.deptId || form.departmentId || null) : null,
        orgUnitId: form.level === "org_unit" ? (form.orgUnitId || null) : null,
        unit: String(form.unit || "").trim(),
        weight: Number(form.weight) || 0,
        ownerId: form.ownerId,
        registrarId: form.registrarId,
        approverId: form.approverId,
        scheduleTemplateId: form.scheduleTemplateId,
        aggregationMethod: form.aggregationMethod,
        requiresEvidence: form.requiresEvidence === true || String(form.requiresEvidence) === "true",
    });

    const createIndicatorApi = async (form) => {
        const saved = await indicatorApiRequest("/", { method: "POST", body: JSON.stringify(indicatorDefinitionPayload(form)) });
        const normalized = normalizeApiIndicator(saved);
        setIndicatorDefs(prev => [...prev.filter(item => item.id !== normalized.id), normalized]);
        return normalized;
    };

    const updateIndicatorApi = async (id, form) => {
        const saved = await indicatorApiRequest(`/${id}`, { method: "PATCH", body: JSON.stringify(indicatorDefinitionPayload(form)) });
        const normalized = normalizeApiIndicator(saved);
        setIndicatorDefs(prev => prev.map(item => item.id === id ? normalized : item));
        return normalized;
    };

    const archiveIndicatorApi = async (id) => {
        const current = indicatorDefs.find(item => item.id === id);
        if (!current) return null;
        const saved = await indicatorApiRequest(`/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ ...indicatorDefinitionPayload(current), isActive: false }),
        });
        setIndicatorDefs(prev => prev.filter(item => item.id !== id));
        return normalizeApiIndicator(saved);
    };

    const createScheduleTemplateApi = async (form) => {
        const saved = await indicatorApiRequest("/schedule-templates", {
            method: "POST",
            body: JSON.stringify({
                name: String(form.label || form.name || "").trim(),
                frequency: form.frequency,
                openOffsetDays: Number(form.openOffsetDays) || 0,
                submitAfterDays: Number(form.submitAfterDays) || 0,
                approveAfterDays: Number(form.approveAfterDays) || 0,
                lockAfterDays: Number(form.lockAfterDays) || 0,
                reminderDays: Number(form.reminderDays) || 0,
            }),
        });
        const normalized = normalizeScheduleTemplate(saved);
        setScheduleTemplates(prev => [...prev.filter(item => item.id !== normalized.id), normalized]);
        return normalized;
    };

    const setIndicatorTargetApi = async (indicatorId, periodId, target, occurrenceId = "period") => {
        const saved = await indicatorApiRequest(`/${indicatorId}/periods/${periodId}/target`, {
            method: "PUT",
            body: JSON.stringify({ target: Number(target), occurrenceId: occurrenceId || "period" }),
        });
        return mergeIndicatorValue(saved);
    };

    const saveIndicatorValueApi = async (indicatorId, periodId, form, submit = false, occurrenceId = "period") => {
        const savedDraft = await indicatorApiRequest(`/${indicatorId}/periods/${periodId}/actual`, {
            method: "PUT",
            body: JSON.stringify({
                actual: Number(form.actual),
                occurrenceId: occurrenceId || "period",
                note: form.note?.trim() || null,
                deviationReason: form.deviationReason?.trim() || null,
                correctiveAction: form.correctiveAction?.trim() || null,
            }),
        });
        let normalized = mergeIndicatorValue(savedDraft);
        if (form._file) await uploadIndicatorFileApi(savedDraft.id, form._file);
        if (submit) {
            const submitted = await indicatorApiRequest(`/values/${savedDraft.id}/submit`, {
                method: "POST",
                body: JSON.stringify({ note: form.note?.trim() || null }),
            });
            normalized = mergeIndicatorValue(submitted);
        }
        return normalized;
    };

    const reviewIndicatorValueApi = async (valueId, decision, note = "") => {
        const path = decision === "approved" ? "approve" : "revision";
        const saved = await indicatorApiRequest(`/values/${valueId}/${path}`, {
            method: "POST",
            body: JSON.stringify({ note: note?.trim() || null }),
        });
        return mergeIndicatorValue(saved);
    };

    const periodApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}/api/periods${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });
        let payload = null;
        try { payload = await response.json(); } catch { payload = null; }
        if (response.status === 401) {
            sessionStorage.removeItem("cvi_user");
            sessionStorage.removeItem("cvi_token");
            window.dispatchEvent(new Event("cvi-session-expired"));
        }
        if (!response.ok) {
            const error = new Error(payload?.message || `Period request failed with status ${response.status}.`);
            error.status = response.status;
            error.blockers = payload?.blockers || null;
            throw error;
        }
        return payload;
    };

    const normalizePeriodSnapshot = (snapshot) => ({
        id: snapshot.id,
        periodId: snapshot.periodId,
        label: snapshot.label,
        userId: snapshot.userId,
        ...normalizeSnapshotScores(snapshot),
        nonRoutine: snapshot.nonRoutineScore == null ? null : Number(snapshot.nonRoutineScore),
        routine: snapshot.routineScore == null ? null : Number(snapshot.routineScore),
        peerFeedback: snapshot.peerFeedbackScore == null ? null : Number(snapshot.peerFeedbackScore),
        activityGroupScores: snapshot.activityGroupScores || {},
        activityCounts: snapshot.activityCounts || {},
        routineCriteria: snapshot.routineCriteria || [],
        status: String(snapshot.status || "final").toLowerCase(),
        locked: Boolean(snapshot.locked),
        formulaVersion: snapshot.formulaVersion,
        weights: snapshot.weights || {},
        calculatedAt: snapshot.calculatedAt,
        version: Number(snapshot.version) || 1,
    });

    const loadPeriodLifecycleData = async (periodList = periods) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token || !periodList?.length) {
            setPeriodSnapshots([]);
            setPeriodAudit([]);
            return;
        }
        let sessionUser = null;
        try { sessionUser = JSON.parse(sessionStorage.getItem("cvi_user") || "null"); } catch { sessionUser = null; }
        const role = String(sessionUser?.role || "").toLowerCase();
        const canSeeAll = ["admin", "ceo"].includes(role);
        try {
            const snapshotGroups = await Promise.all(periodList.map(period =>
                periodApiRequest(`/${period.id}/snapshots${canSeeAll ? "" : "/me"}`)
                    .catch(error => error.status === 404 ? [] : Promise.reject(error))
            ));
            setPeriodSnapshots(snapshotGroups.flat().map(normalizePeriodSnapshot));

            if (canSeeAll) {
                const auditGroups = await Promise.all(periodList.map(period =>
                    periodApiRequest(`/${period.id}/audit`).catch(error => error.status === 404 ? [] : Promise.reject(error))
                ));
                setPeriodAudit(auditGroups.flat());
            } else {
                setPeriodAudit([]);
            }
        } catch (error) {
            console.error("Period lifecycle data load failed:", error);
        }
    };

    const adminApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}/api/admin${path}`, {
            ...options,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        if (response.status === 401) { handleUnauthorized(); throw new Error("Session expired."); }
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) { const error = new Error(payload.message || `Admin request failed with status ${response.status}.`); error.blockers = payload.blockers || null; throw error; }
        return payload;
    };

    const reloadAdminBaseData = async () => { await Promise.all([loadBaseData({ silent: true }), loadRoutineData()]); };

    const updateOrganizationSettingsApi = async (patch) => {
        const saved = await adminApiRequest("/organization/settings", { method: "PATCH", body: JSON.stringify(patch) });
        setOrgSettings(prev => ({
            ...prev,
            name: patch.organizationName ?? prev.name,
            logo: patch.logoUrl !== undefined ? patch.logoUrl : prev.logo,
            defaultLanguage: saved.defaultLanguage ?? patch.defaultLanguage ?? prev.defaultLanguage,
            accentColor: saved.accentColor ?? patch.accentColor ?? prev.accentColor,
            timezone: saved.timezone ?? patch.timezone ?? prev.timezone,
            dateFormat: saved.dateFormat ?? patch.dateFormat ?? prev.dateFormat,
            calendar: saved.calendar ?? patch.calendar ?? prev.calendar,
        }));
        return saved;
    };

    const createDepartmentApi = async f => { await adminApiRequest("/departments", { method: "POST", body: JSON.stringify({ name: f.label }) }); await loadBaseData({ silent: true }); };
    const updateDepartmentApi = async (id, f) => { await adminApiRequest(`/departments/${id}`, { method: "PATCH", body: JSON.stringify({ name: f.label }) }); await loadBaseData({ silent: true }); };
    const archiveDepartmentApi = async id => { await adminApiRequest(`/departments/${id}`, { method: "DELETE" }); await loadBaseData({ silent: true }); };
    const createTeamApi = async f => { await adminApiRequest("/teams", { method: "POST", body: JSON.stringify({ name: f.label, departmentId: f.deptId }) }); await loadBaseData({ silent: true }); };
    const updateTeamApi = async (id, f) => { await adminApiRequest(`/teams/${id}`, { method: "PATCH", body: JSON.stringify({ name: f.label, departmentId: f.deptId }) }); await loadBaseData({ silent: true }); };
    const archiveTeamApi = async id => { await adminApiRequest(`/teams/${id}`, { method: "DELETE" }); await loadBaseData({ silent: true }); };

    const positionPayload = f => ({ name: f.label, teamWeight: Number(f.wTeam) || 0, departmentWeight: Number(f.wDept) || 0, organizationWeight: Number(f.wOrg) || 0, tasksActivitiesWeight: Number(f.wTasksActivities) || 0, nonRoutineWeight: Number(f.wNonRoutine) || 0, routineWeight: Number(f.wRoutine) || 0, peerFeedbackWeight: Number(f.wPeerFeedback) || 0, responsibilityWeight: 100, individualGoalWeight: Number(f.wGoals) || 0 });
    const createPositionApi = async f => { const row = await adminApiRequest("/positions", { method: "POST", body: JSON.stringify(positionPayload(f)) }); await loadBaseData({ silent: true }); return row; };
    const updatePositionApi = async (id, f) => { const row = await adminApiRequest(`/positions/${id}`, { method: "PATCH", body: JSON.stringify(positionPayload(f)) }); await loadBaseData({ silent: true }); return row; };
    const archivePositionApi = async id => { await adminApiRequest(`/positions/${id}`, { method: "DELETE" }); await loadBaseData({ silent: true }); };

    const userPayload = f => ({ name: f.name, employeeCode: f.employeeCode, nationalId: f.nationalId, email: f.email, username: f.username, password: f.password || undefined, role: f.role, positionId: f.posId || null, orgUnitId: f.orgUnitId || null, departmentId: f.orgUnitId ? null : (f.deptId || null), teamId: f.orgUnitId ? null : (f.teamId || null), managerId: f.managerId || null });
    const createUserApi = async f => { await adminApiRequest("/users", { method: "POST", body: JSON.stringify(userPayload(f)) }); await loadBaseData({ silent: true }); };
    const updateUserApi = async (id, f) => { await adminApiRequest(`/users/${id}`, { method: "PATCH", body: JSON.stringify(userPayload(f)) }); await loadBaseData({ silent: true }); };
    const archiveUserApi = async id => { await adminApiRequest(`/users/${id}`, { method: "DELETE" }); await loadBaseData({ silent: true }); };

    const groupPayload = f => ({ name: f.label, maxScore: Number(f.maxScore) || 0, icon: f.icon || null, subtypes: (f.subsText || "").split(",").map(x => x.trim()).filter(Boolean) });
    const createActivityGroupApi = async f => { await adminApiRequest("/activity-groups", { method: "POST", body: JSON.stringify(groupPayload(f)) }); await loadBaseData({ silent: true }); };
    const updateActivityGroupApi = async (id, f) => { await adminApiRequest(`/activity-groups/${id}`, { method: "PATCH", body: JSON.stringify(groupPayload(f)) }); await loadBaseData({ silent: true }); };
    const archiveActivityGroupApi = async id => { await adminApiRequest(`/activity-groups/${id}`, { method: "DELETE" }); await loadBaseData({ silent: true }); };
    const createImpactLevelApi = async f => { await adminApiRequest("/impact-levels", { method: "POST", body: JSON.stringify({ name: f.label, score: Number(f.score) || 0 }) }); await loadBaseData({ silent: true }); };
    const updateImpactLevelApi = async (id, f) => { await adminApiRequest(`/impact-levels/${id}`, { method: "PATCH", body: JSON.stringify({ name: f.label, score: Number(f.score) || 0 }) }); await loadBaseData({ silent: true }); };
    const archiveImpactLevelApi = async id => { await adminApiRequest(`/impact-levels/${id}`, { method: "DELETE" }); await loadBaseData({ silent: true }); };
    const createRoutineLevelApi = async f => { await adminApiRequest("/routine-levels", { method: "POST", body: JSON.stringify({ name: f.label, score: Number(f.score) || 0 }) }); await loadRoutineData(); };
    const updateRoutineLevelApi = async (id, f) => { await adminApiRequest(`/routine-levels/${id}`, { method: "PATCH", body: JSON.stringify({ name: f.label, score: Number(f.score) || 0 }) }); await loadRoutineData(); };
    const archiveRoutineLevelApi = async id => { await adminApiRequest(`/routine-levels/${id}`, { method: "DELETE" }); await loadRoutineData(); };

    // silent: refresh after an admin save without replacing the app with the loading skeleton
    // (the skeleton unmounts the Admin Panel and returned the administrator to Overview).
    const loadBaseData = async ({ silent = false } = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) {
            setUsers([]);
            setTeams([]);
            setDepartments([]);
            setOrgUnits([]);
            setPositions([]);
            setPeriods([]);
            setBaseDataLoading(false);
            setBaseDataError("");
            return;
        }

        if (!silent) setBaseDataLoading(true);
        setBaseDataError("");

        try {
            const headers = { Authorization: `Bearer ${token}` };
            const endpoints = ["users", "teams", "departments", "org-units", "positions", "periods", "organization", "activity-groups", "impact-levels"];
            const responses = await Promise.all(
                endpoints.map(endpoint => fetch(`${API_BASE_URL}/api/${endpoint}`, { headers }))
            );

            const unauthorized = responses.some(response => response.status === 401);
            if (unauthorized) {
                sessionStorage.removeItem("cvi_user");
                sessionStorage.removeItem("cvi_token");
                window.dispatchEvent(new Event("cvi-session-expired"));
                throw new Error("Session expired.");
            }

            const failed = responses.find(response => !response.ok);
            if (failed) throw new Error(`Base data request failed with status ${failed.status}.`);

            const [apiUsers, apiTeams, apiDepartments, apiOrgUnitsResponse, apiPositions, apiPeriods, apiOrganization, apiActivityGroups, apiImpactLevels] =
                await Promise.all(responses.map(response => response.json()));
            const apiOrgUnits = Array.isArray(apiOrgUnitsResponse) ? apiOrgUnitsResponse : (apiOrgUnitsResponse?.items || []);

            setUsers(apiUsers.map(user => ({
                id: user.id,
                name: user.name,
                role: String(user.role || "").toLowerCase(),
                posId: user.position?.id ?? null,
                teamId: user.team?.id ?? null,
                deptId: user.department?.id ?? null,
                orgUnitId: user.orgUnit?.id ?? null,
                orgUnit: user.orgUnit ?? null,
                managerId: user.managerId ?? null,
                username: user.username,
                employeeCode: user.employeeCode ?? "",
                nationalId: user.nationalId ?? "",
                email: user.email,
                avatarUrl: user.avatarUrl ?? null,
                preferredLanguage: user.preferredLanguage ?? null,
            })));

            setTeams(apiTeams.map(team => ({
                id: team.id,
                label: team.name,
                deptId: team.departmentId ?? team.department?.id ?? null,
            })));

            setDepartments(apiDepartments.map(department => ({
                id: department.id,
                label: department.name,
            })));

            setOrgUnits(apiOrgUnits.map(unit => ({
                id: unit.id,
                label: unit.name,
                name: unit.name,
                type: unit.type || "",
                parentId: unit.parentId ?? null,
                managerUserId: unit.managerUserId ?? unit.manager?.id ?? null,
                isActive: unit.isActive !== false,
            })));

            setPositions(apiPositions.map(position => ({
                id: position.id,
                label: position.name,
                wTeam: Number(position.teamWeight) || 0,
                wDept: Number(position.departmentWeight) || 0,
                wOrg: Number(position.organizationWeight) || 0,
                wTasksActivities: Number(position.tasksActivitiesWeight ?? 0),
                wNonRoutine: Number(position.nonRoutineWeight) || 0,
                wRoutine: Number(position.routineWeight) || 0,
                wPeerFeedback: Number(position.peerFeedbackWeight) || 0,
                wResponsibilities: 100,
                wGoals: Number(position.individualGoalWeight ?? 0),
                // Templates returned by /api/positions; the backend uses them before the position's own weights.
                collectiveProfile: position.collectiveContributionProfile ? {
                    teamWeight: Number(position.collectiveContributionProfile.teamWeight),
                    upstreamWeight: Number(position.collectiveContributionProfile.upstreamWeight),
                    organizationWeight: Number(position.collectiveContributionProfile.organizationWeight),
                } : null,
                individualProfile: position.individualPerformanceProfile ? {
                    tasksWeight: Number(position.individualPerformanceProfile.tasksWeight),
                    goalsWeight: Number(position.individualPerformanceProfile.goalsWeight),
                    peerFeedbackWeight: Number(position.individualPerformanceProfile.peerFeedbackWeight),
                    routineWeight: Number(position.individualPerformanceProfile.routineWeight),
                    nonRoutineWeight: Number(position.individualPerformanceProfile.nonRoutineWeight),
                } : null,
            })));

            setPeriods(apiPeriods.map(period => ({
                id: period.id,
                label: period.name,
                start: jalaliDateFromGregorian(new Date(period.startDate)),
                end: jalaliDateFromGregorian(new Date(period.endDate)),
                active: Boolean(period.isActive),
                status: String(period.status || "").toLowerCase(),
                closedAt: period.closedAt ?? null,
                reopenedAt: period.reopenedAt ?? null,
            })));

            setActivityGroups(apiActivityGroups.map(group => ({ id: group.id, label: group.name, maxScore: Number(group.maxScore) || 0, icon: group.icon || "⭐", subs: (group.subtypes || []).map(sub => sub.name) })));
            setImpactLevels(apiImpactLevels.map(level => ({ id: level.id, label: level.name, score: Number(level.score) || 0 })));

            if (apiOrganization?.settings) {
                setOrgSettings(prev => ({
                    ...prev,
                    wNonRoutine: Number(apiOrganization.settings.defaultNonRoutineWeight) || 0,
                    wRoutine: Number(apiOrganization.settings.defaultRoutineWeight) || 0,
                    wPeerFeedback: Number(apiOrganization.settings.defaultPeerFeedbackWeight) || 0,
                    name: apiOrganization.name || prev.name || "",
                    logo: apiOrganization.logoUrl ?? prev.logo,
                    defaultLanguage: apiOrganization.settings.defaultLanguage || prev.defaultLanguage || "en",
                    accentColor: apiOrganization.settings.accentColor || prev.accentColor || "#00C8E6",
                    timezone: apiOrganization.settings.timezone || prev.timezone || "UTC",
                    dateFormat: apiOrganization.settings.dateFormat || prev.dateFormat || "YYYY-MM-DD",
                    calendar: apiOrganization.settings.calendar || prev.calendar || "gregorian",
                }));
            }
        } catch (error) {
            console.error("Base data load failed:", error);
            // A failed background refresh keeps the current data instead of replacing the app with
            // the error screen; session expiry is still handled above.
            if (!silent || error?.message === "Session expired.") setBaseDataError("Data could not be loaded. Please try again.");
        } finally {
            setBaseDataLoading(false);
        }
    };

    useEffect(() => {
        loadBaseData();
        const handleAuthChanged = () => loadBaseData();
        window.addEventListener("cvi-auth-changed", handleAuthChanged);
        return () => window.removeEventListener("cvi-auth-changed", handleAuthChanged);
    }, []);

    useEffect(() => {
        loadActivities();
        const handleAuthChanged = () => loadActivities();
        window.addEventListener("cvi-auth-changed", handleAuthChanged);
        return () => window.removeEventListener("cvi-auth-changed", handleAuthChanged);
    }, []);

    useEffect(() => {
        loadRoutineData();
    }, [periods, users]);

    useEffect(() => {
        loadPeerData();
    }, [periods]);

    useEffect(() => {
        loadIndicatorData();
        const handleAuthChanged = () => loadIndicatorData();
        window.addEventListener("cvi-auth-changed", handleAuthChanged);
        return () => window.removeEventListener("cvi-auth-changed", handleAuthChanged);
    }, []);

    useEffect(() => {
        loadPeriodLifecycleData(periods);
    }, [periods]);

    const activePeriod = periods.find(p => p.active && p.status === "active") || null;

    useEffect(() => {
        const today = todayJalaliStr();
        setIndicatorData(prev => {
            let changed = false;
            const next = prev.map(data => {
                if (data.status !== "approved") return data;
                const def = indicatorDefs.find(i => i.id === data.indicatorId), period = periods.find(p => p.id === data.periodId);
                const computed = data.scheduleSnapshot || indicatorScheduleOccurrences(def, period, scheduleTemplates, scheduleExceptions)
                    .find(o => o.occurrenceId === data.occurrenceId) || indicatorScheduleOccurrences(def, period, scheduleTemplates, scheduleExceptions)[0];
                if (!computed?.lockDate || daysBetweenJalali(computed.lockDate, today) < 0) return data;
                changed = true;
                return {
                    ...data, status: "locked", lockedAt: currentLocalDateTime(), scheduleSnapshot: data.scheduleSnapshot || computed,
                    history: [...(data.history || []), {
                        type: "locked", toStatus: "locked", date: currentLocalDateTime(), by: "System",
                        oldValue: data.actual, newValue: data.actual, note: "Locked automatically by the indicator schedule"
                    }]
                };
            });
            if (activePeriod) {
                indicatorDefs.flatMap(def => indicatorScheduleOccurrences(def, activePeriod, scheduleTemplates, scheduleExceptions)
                    .map(schedule => ({ def, schedule }))).forEach(({ def, schedule }) => {
                        const exists = next.some(d => d.indicatorId === def.id && d.periodId === activePeriod.id &&
                            (d.occurrenceId === schedule.occurrenceId || (!d.occurrenceId && schedule.occurrenceId.endsWith(":1"))));
                        if (!exists && daysBetweenJalali(schedule.lockDate, today) >= 0) {
                            changed = true; next.push({
                                id: `${def.id}_${schedule.occurrenceId}_missed`, indicatorId: def.id, periodId: activePeriod.id,
                                occurrenceId: schedule.occurrenceId, status: "closed_without_data", actual: null, target: null,
                                scheduleSnapshot: schedule, history: [{
                                    type: "closed_without_data", toStatus: "closed_without_data",
                                    date: currentLocalDateTime(), by: "System", note: "Submission window closed without data"
                                }]
                            });
                        }
                    });
            }
            return changed ? next : prev;
        });
    }, [indicatorDefs, periods, scheduleTemplates, scheduleExceptions]);

    const closePeriod = async (periodId) => {
        const period = periods.find(p => p.id === periodId);
        if (!period || period.status === "locked") return false;

        const status = await periodApiRequest(`/${periodId}/finalization-status`);
        if (!status.ready) {
            const error = new Error("Period has incomplete items and cannot be finalized.");
            error.blockers = status.blockers;
            throw error;
        }

        await periodApiRequest(`/${periodId}/finalize`, { method: "POST", body: JSON.stringify({}) });
        await Promise.all([loadBaseData({ silent: true }), loadIndicatorData()]);
        await loadPeriodLifecycleData(periods);
        return true;
    };

    const reopenPeriod = async (periodId, actorName = "Administrator", reason = "") => {
        if (!reason.trim()) return false;
        await periodApiRequest(`/${periodId}/reopen`, {
            method: "POST",
            body: JSON.stringify({ reason: reason.trim() }),
        });
        await Promise.all([loadBaseData({ silent: true }), loadIndicatorData()]);
        await loadPeriodLifecycleData(periods);
        return true;
    };

    const createPeriodApi = async (form) => {
        const row = await adminApiRequest("/periods", {
            method: "POST",
            body: JSON.stringify({ name: form.label, startDate: form.start, endDate: form.end }),
        });
        await loadBaseData({ silent: true });
        return row;
    };
    const activatePeriodApi = async (id) => {
        const row = await adminApiRequest(`/periods/${id}/activate`, { method: "POST" });
        await loadBaseData({ silent: true });
        return row;
    };
    const createRoutineQuestionApi = async (form) => {
        const row = await adminApiRequest("/routine-questions", { method: "POST", body: JSON.stringify(form) });
        await loadRoutineData();
        return row;
    };
    const updateRoutineQuestionApi = async (id, form) => {
        const row = await adminApiRequest(`/routine-questions/${id}`, { method: "PATCH", body: JSON.stringify(form) });
        await loadRoutineData();
        return row;
    };
    const archiveRoutineQuestionApi = async (id) => {
        const row = await adminApiRequest(`/routine-questions/${id}`, { method: "DELETE" });
        await loadRoutineData();
        return row;
    };
    const createPeerQuestionAdminApi = async (form) => {
        const row = await peerApiRequest("/admin/questions", { method: "POST", body: JSON.stringify(form) });
        await loadPeerData();
        return row;
    };
    const updatePeerQuestionAdminApi = async (id, form) => {
        const row = await peerApiRequest(`/admin/questions/${id}`, { method: "PATCH", body: JSON.stringify(form) });
        await loadPeerData();
        return row;
    };
    const archivePeerQuestionAdminApi = async (id) => {
        const row = await peerApiRequest(`/admin/questions/${id}`, { method: "DELETE" });
        await loadPeerData();
        return row;
    };
    const savePeerConfigAdminApi = async (subjectUserId, config) => {
        const visibility = config.visibility === "identified" ? "IDENTIFIED" : "ANONYMOUS";
        const row = await peerApiRequest(`/admin/subjects/${subjectUserId}/config`, {
            method: "PUT",
            body: JSON.stringify({
                enabled: Boolean(config.enabled), visibility,
                minResponses: Math.max(1, Number(config.minResponses) || 1),
                reviewerIds: config.reviewerIds || [],
            }),
        });
        await loadPeerData();
        return row;
    };
    const createPeerAssignmentsAdminApi = async (subjectUserId, periodId, reviewerIds) => {
        const row = await peerApiRequest(`/admin/subjects/${subjectUserId}/assignments`, {
            method: "POST",
            body: JSON.stringify({ periodId, reviewerIds }),
        });
        await loadPeerData();
        return row;
    };

    const value = {
        users, setUsers, teams, setTeams, departments, setDepartments, orgUnits, setOrgUnits,
        positions, setPositions, activityGroups, setActivityGroups,
        impactLevels, setImpactLevels, indicatorDefs, setIndicatorDefs,
        indicatorData, setIndicatorData, periods, setPeriods, activePeriod,
        activities, setActivities,
        routineLevels, setRoutineLevels, routineQuestions, setRoutineQuestions,
        routineAnswers, setRoutineAnswers, orgSettings, setOrgSettings,
        deptRoutineQuestions, setDeptRoutineQuestions, deptRoutineAnswers, setDeptRoutineAnswers,
        orgRoutineQuestions, setOrgRoutineQuestions, orgRoutineAnswers, setOrgRoutineAnswers,
        periodSnapshots, setPeriodSnapshots, periodAudit, closePeriod, reopenPeriod,
        scheduleTemplates, setScheduleTemplates, scheduleExceptions, setScheduleExceptions,
        peerQuestions, setPeerQuestions, peerConfigs, setPeerConfigs, peerReviews, setPeerReviews,
        peerAssignments, peerResult, peerLoading, peerError, reloadPeerData: loadPeerData, submitPeerReviewApi,
        invalidateSessionData,
        baseDataLoading, baseDataError, reloadBaseData: loadBaseData,
        activitiesLoading, activitiesError, reloadActivities: loadActivities,
        createActivityApi, updateActivityApi, submitActivityApi, reviewActivityApi,
        routineTargets, routineLoading, routineError, reloadRoutineData: loadRoutineData,
        saveRoutineAnswerApi,
        indicatorLoading, indicatorError, reloadIndicatorData: loadIndicatorData,
        createIndicatorApi, updateIndicatorApi, archiveIndicatorApi, createScheduleTemplateApi,
        setIndicatorTargetApi, saveIndicatorValueApi, reviewIndicatorValueApi,
        updateOrganizationSettingsApi,
        createDepartmentApi, updateDepartmentApi, archiveDepartmentApi, createTeamApi, updateTeamApi, archiveTeamApi,
        createPositionApi, updatePositionApi, archivePositionApi, createUserApi, updateUserApi, archiveUserApi,
        createActivityGroupApi, updateActivityGroupApi, archiveActivityGroupApi, createImpactLevelApi, updateImpactLevelApi, archiveImpactLevelApi,
        createRoutineLevelApi, updateRoutineLevelApi, archiveRoutineLevelApi,
        createPeriodApi, activatePeriodApi,
        createRoutineQuestionApi, updateRoutineQuestionApi, archiveRoutineQuestionApi,
        createPeerQuestionAdminApi, updatePeerQuestionAdminApi, archivePeerQuestionAdminApi,
        savePeerConfigAdminApi, createPeerAssignmentsAdminApi,
    };
    return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

// ── SHARED CALC (منبع واحد محاسبه CVI) ────────────────────────────────────────
function groupLabel(activityGroups, groupId) {
    return activityGroups.find(g => g.id === groupId)?.label || groupId;
}

const ACTIVITY_STATUS = {
    DRAFT: "draft",
    PENDING_REVIEW: "pending_review",
    REVISION_REQUESTED: "revision_requested",
    RESUBMITTED: "resubmitted",
    APPROVED: "approved",
    REJECTED: "rejected",
};

function isReviewableActivity(activity) {
    return [ACTIVITY_STATUS.PENDING_REVIEW, ACTIVITY_STATUS.RESUBMITTED].includes(activity.status);
}

// فقط مدیر مستقیم ثبت‌کننده مجاز به تصمیم‌گیری درباره فعالیت است.
function canReviewActivity(evaluator, activity, users) {
    const owner = users.find(u => u.id === activity.userId);
    return Boolean(owner && owner.managerId === evaluator.id);
}

// Timestamp shown in the UI for client-side events (draft saved, automatic lock). Canadian
// locale and Gregorian calendar, following the active interface language.
function currentLocalDateTime() {
    const lang = typeof document !== "undefined" && document.documentElement.lang === "fr" ? "fr-CA" : "en-CA";
    try {
        return new Intl.DateTimeFormat(lang, {
            year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
        }).format(new Date());
    } catch {
        return new Date().toLocaleString(lang);
    }
}

// Core-responsibility score preview used while a manager answers the evaluation form. It follows the
// backend rule (weighted average of answered questions, scaled to the highest rating level) so the
// evaluator sees the effect of each answer; the stored CVI always comes from the server.
function calcScopedRoutineScore(answers, questions, routineLevels, userId, scopeKey, scopeValue, periodId) {
    const qs = scopeKey ? questions.filter(q => q[scopeKey] === scopeValue) : questions;
    if (!qs.length) return null;
    const maxLevelScore = routineLevels.length ? Math.max(...routineLevels.map(l => l.score)) : 0;
    if (!maxLevelScore) return null;
    let t = 0, w = 0;
    qs.forEach(q => {
        const ans = answers.find(a => a.questionId === q.id && a.userId === userId && a.periodId === periodId);
        if (!ans) return;
        const lvl = routineLevels.find(l => l.id === ans.levelId);
        if (!lvl) return;
        const pct = Math.min(100, (lvl.score / maxLevelScore) * 100);
        t += pct * (Number(q.weight) || 0);
        w += (Number(q.weight) || 0);
    });
    return w > 0 ? t / w : null;
}

function calcRoutineCompletionForUser(user, routineCtx, periodId) {
    if (!user || !periodId) return { percent: null, answered: 0, total: 0 };
    let questions = [], answers = [];
    if (user.role === "specialist") {
        questions = user.orgUnitId
            ? (routineCtx.routineQuestions || []).filter(q => q.orgUnitId === user.orgUnitId)
            : (routineCtx.routineQuestions || []).filter(q => q.teamId === user.teamId);
        answers = routineCtx.routineAnswers || [];
    } else if (user.role === "manager") {
        questions = user.orgUnitId
            ? (routineCtx.deptRoutineQuestions || []).filter(q => q.orgUnitId === user.orgUnitId)
            : (routineCtx.deptRoutineQuestions || []).filter(q => q.deptId === user.deptId);
        answers = routineCtx.deptRoutineAnswers || [];
    } else if (user.role === "deputy") {
        questions = user.orgUnitId
            ? (routineCtx.orgRoutineQuestions || []).filter(q => q.orgUnitId === user.orgUnitId)
            : (routineCtx.orgRoutineQuestions || []);
        answers = routineCtx.orgRoutineAnswers || [];
    }
    const total = questions.length;
    if (!total) return { percent: null, answered: 0, total: 0 };
    const answered = questions.filter(q => answers.some(a =>
        a.questionId === q.id && a.userId === user.id && a.periodId === periodId && a.levelId
    )).length;
    return { percent: Math.round((answered / total) * 100), answered, total };
}

// Configured strategic-level weights of a person's position. Configuration only: it carries no
// scores, so dashboards can label the levels while every score comes from the server.
function positionLevelWeights(user, positions) {
    const pos = positions.find(p => p.id === user?.posId);
    return { team: null, dept: null, org: null, wTeam: pos?.wTeam ?? 0, wDept: pos?.wDept ?? 0, wOrg: pos?.wOrg ?? 0, total: null };
}

// ── تبدیل تقویم میلادی به شمسی (بدون نیاز به کتابخانه‌ی خارجی) ────────────────
function gregorianToJalali(gy, gm, gd) {
    const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    let jy = (gy <= 1600) ? 0 : 979;
    gy -= (gy <= 1600) ? 621 : 1600;
    const gy2 = (gm > 2) ? (gy + 1) : gy;
    let days = (365 * gy) + Math.floor((gy2 + 3) / 4) - Math.floor((gy2 + 99) / 100) + Math.floor((gy2 + 399) / 400)
        - 80 + gd + g_d_m[gm - 1];
    jy += 33 * Math.floor(days / 12053); days %= 12053;
    jy += 4 * Math.floor(days / 1461); days %= 1461;
    jy += Math.floor((days - 1) / 365);
    if (days > 365) days = (days - 1) % 365;
    const jm = (days < 186) ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
    const jd = 1 + ((days < 186) ? (days % 31) : ((days - 186) % 30));
    return [jy, jm, jd];
}
function jalaliToGregorian(jy, jm, jd) {
    let gy = (jy <= 979) ? 621 : 1600;
    jy -= (jy <= 979) ? 0 : 979;
    const days = 365 * jy + Math.floor(jy / 33) * 8 + Math.floor(((jy % 33) + 3) / 4)
        + 78 + jd + ((jm < 7) ? (jm - 1) * 31 : ((jm - 7) * 30 + 186));
    gy += 400 * Math.floor(days / 146097); let d2 = days % 146097;
    if (d2 > 36524) { gy += 100 * Math.floor(--d2 / 36524); d2 %= 36524; if (d2 >= 365) d2++; }
    gy += 4 * Math.floor(d2 / 1461); d2 %= 1461;
    if (d2 > 365) { gy += Math.floor((d2 - 1) / 365); d2 = (d2 - 1) % 365; }
    let gd = d2 + 1;
    const sal_a = [0, 31, (((gy % 4 === 0) && (gy % 100 !== 0)) || (gy % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let gm = 0;
    while (gm < 13 && gd > sal_a[gm]) { gd -= sal_a[gm]; gm++; }
    return new Date(gy, gm - 1, gd);
}
// Display-only: schedule and activity dates are stored as Jalali "YYYY/MM/DD" strings for the
// schedule arithmetic above. Canadian users see them as Gregorian dates in their language.
function displayDate(value) {
    if (value == null || value === "") return value;
    const text = String(value);
    const lang = typeof document !== "undefined" && document.documentElement.lang === "fr" ? "fr-CA" : "en-CA";
    const format = date => new Intl.DateTimeFormat(lang, { year: "numeric", month: "short", day: "numeric" }).format(date);
    const jalali = text.match(/^(1[2-4]\d{2})\/(\d{1,2})\/(\d{1,2})$/);
    if (jalali) return format(jalaliToGregorian(Number(jalali[1]), Number(jalali[2]), Number(jalali[3])));
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) { const date = new Date(text); if (!Number.isNaN(date.getTime())) return format(date); }
    return text;
}
function todayJalaliStr() {
    const now = new Date();
    const [jy, jm, jd] = gregorianToJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());
    return `${jy}/${String(jm).padStart(2, "0")}/${String(jd).padStart(2, "0")}`;
}
export function jalaliDateFromGregorian(date) {
    const [jy, jm, jd] = gregorianToJalali(date.getFullYear(), date.getMonth() + 1, date.getDate());
    return `${jy}/${String(jm).padStart(2, "0")}/${String(jd).padStart(2, "0")}`;
}
function addDaysToJalali(dateStr, days = 0) {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("/").map(Number);
    const date = jalaliToGregorian(y, m, d); date.setDate(date.getDate() + Number(days || 0));
    return jalaliDateFromGregorian(date);
}
function daysBetweenJalali(fromStr, toStr) {
    if (!fromStr || !toStr) return null;
    const parse = s => { const [y, m, d] = s.split("/").map(Number); return jalaliToGregorian(y, m, d).getTime(); };
    return Math.ceil((parse(toStr) - parse(fromStr)) / 86400000);
}
export function indicatorScheduleOccurrences(def, period, templates, exceptions = []) {
    if (!def || !period) return [];
    const template = templates.find(t => t.id === def.scheduleTemplateId) || templates.find(t => t.frequency === "quarterly");
    if (!template) return [];
    // Period bounds are kept as internal "YYYY/MM/DD" strings; occurrence boundaries follow the
    // Canadian (Gregorian) calendar: monthly = last day of each calendar month, annual = December year-end.
    const toDate = value => { const [y, m, d] = value.split("/").map(Number); return jalaliToGregorian(y, m, d); };
    const periodStart = toDate(period.start), periodEnd = toDate(period.end);
    let ends = [];
    if (template.frequency === "monthly") {
        let cursor = new Date(periodStart.getFullYear(), periodStart.getMonth() + 1, 0);
        while (cursor <= periodEnd) {
            ends.push(jalaliDateFromGregorian(cursor));
            cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0);
        }
        // A period that stops mid-month still closes its last partial month on the period end.
        if (!ends.length || ends[ends.length - 1] !== period.end) ends.push(period.end);
    } else if (template.frequency === "annual") {
        if (periodEnd.getMonth() === 11) ends = [period.end];
    } else ends = [period.end];
    return ends.map((rangeEnd, index) => {
        const occurrenceId = `${period.id}:${template.frequency}:${index + 1}`;
        const exception = exceptions.find(e => e.indicatorId === def.id && e.periodId === period.id &&
            (!e.occurrenceId || e.occurrenceId === occurrenceId));
        const openDate = exception?.openDate || addDaysToJalali(rangeEnd, template.openOffsetDays);
        const submitDeadline = exception?.submitDeadline || addDaysToJalali(rangeEnd, template.submitAfterDays);
        const approvalDeadline = exception?.approvalDeadline || approvalDeadlineFromPeriodEnd(rangeEnd, template.approveAfterDays, addDaysToJalali);
        const lockDate = exception?.lockDate || addDaysToJalali(rangeEnd, template.lockAfterDays);
        return {
            occurrenceId, rangeEnd, openDate, submitDeadline, approvalDeadline, lockDate,
            label: template.frequency === "monthly" ? `Month ${index + 1}` : template.frequency === "annual" ? "Annual" : "Quarterly",
            reminderDays: Number(template.reminderDays) || 3, templateId: template.id, exception: exception || null
        };
    });
}
function daysRemainingInPeriod(period) {
    if (!period?.end) return null;
    const [ey, em, ed] = period.end.split("/").map(Number);
    const endGregorian = jalaliToGregorian(ey, em, ed);
    const now = new Date();
    const diffMs = endGregorian.setHours(23, 59, 59, 999) - now.getTime();
    return Math.ceil(diffMs / 86400000);
}

// یک شاخص تعریف‌شده را با هدف/واقعیِ دوره‌ی مشخص ترکیب می‌کنه (برای نمایش در پنل مدیر)
function withPeriodData(defs, indicatorData, periodId) {
    return defs.map(def => {
        const d = indicatorData.find(x => x.periodId === periodId && x.indicatorId === def.id);
        const approved = d && ["approved", "locked"].includes(d.status);
        return {
            ...def, target: d?.target ?? 0, actual: approved ? (d.actual ?? 0) : 0,
            dataStatus: d?.status || "not_started"
        };
    });
}

function useIsMobile() {
    const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : true);
    useEffect(() => {
        const h = () => setM(window.innerWidth < 768);
        window.addEventListener("resize", h);
        return () => window.removeEventListener("resize", h);
    }, []);
    return m;
}

// ── SHARED ATOMS ────────────────────────────────────────────────────────────
// Pill for labels, weights and counts. Uses the shared workspace tones so every badge in the app
// draws from one palette; statuses use StatusBadge, which adds the status dot.
const BADGE_TONE = { gray: "neutral", blue: "info", teal: "success", green: "success", purple: "purple", amber: "warning", red: "danger", cyan: "info" };
function Badge({ color = "gray", children, small }) {
    return <span className={`cvi-ws-status is-${BADGE_TONE[color] || "neutral"} is-plain${small ? " is-sm" : ""}`}>{children}</span>;
}

function ProfessionalTabs({ items, value, active, onChange, ariaLabel = "Page sections" }) {
    const selectedValue = value ?? active ?? items?.[0]?.id;
    return (
        <div role="tablist" aria-label={ariaLabel} style={{
            display: "flex", alignItems: "flex-end", gap: 22,
            borderBottom: `1px solid ${C.border}`, overflowX: "auto", overflowY: "hidden", marginBottom: 16,
            padding: "0 2px", scrollbarWidth: "thin"
        }}>
            {items.map(item => {
                const isActive = item.id === selectedValue;
                return <button key={item.id} role="tab" aria-selected={isActive} onClick={() => onChange(item.id)}
                    style={{
                        position: "relative", display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap",
                        padding: "10px 2px 11px", border: "none", background: "transparent", fontFamily: "inherit",
                        fontSize: 12.5, fontWeight: isActive ? 600 : 400, color: isActive ? C.navy : C.muted, cursor: "pointer"
                    }}>
                    {item.icon && <span style={{ display: "inline-flex", alignItems: "center", color: "currentColor" }}><UiIcon name={item.icon} size={15} /></span>}<span>{item.label}</span>
                    {item.count != null && <span style={{
                        minWidth: 18, height: 18, padding: "0 5px", boxSizing: "border-box",
                        borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9.5,
                        background: isActive ? C.cyanBg : C.bg, color: isActive ? C.navy : C.hint
                    }}>{item.count}</span>}
                    {isActive && <span style={{
                        position: "absolute", right: 0, left: 0, bottom: -1, height: 3,
                        background: C.cyan, borderRadius: "3px 3px 0 0"
                    }} />}
                </button>;
            })}
        </div>
    );
}

function UserAvatar({ user, size = 30 }) {
    const { users } = useData();
    const currentUser = users.find(u => u.id === user.id) || user;
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%", overflow: "hidden",
            background: C.cyan, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: size <= 30 ? 12 : 16, fontWeight: 700, color: C.navy, flexShrink: 0
        }}>
            {currentUser.photoDataUrl
                ? <img src={currentUser.photoDataUrl} alt={currentUser.name || ""}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : currentUser.name?.[0]}
        </div>
    );
}


function CviHelpCenter() {
    const { language } = useI18n();
    const isFrench = language === "fr";
    const topics = isFrench ? [
        {
            id: "start", title: "Qu’est-ce que le CVI?", keywords: "CVI valeur contribution contribution individuelle contribution collective",
            body: "Le CVI (Indice de valeur de contribution) offre une vue intégrée de la façon dont une personne contribue à la réussite de l’organisation. Il combine la contribution individuelle et la contribution collective.",
            points: ["Contribution individuelle : responsabilités principales, contributions additionnelles, objectifs et rétroaction des collègues.", "Contribution collective : lien entre la personne et les résultats de son équipe, des unités parentes et de l’organisation."]
        },
        {
            id: "tasks", title: "Responsabilités et contributions", keywords: "responsabilités principales contributions additionnelles autoévaluation",
            body: "Cette section distingue le travail attendu dans le cadre du rôle des contributions additionnelles qui dépassent les responsabilités courantes.",
            points: ["Responsabilités principales : activités récurrentes et attentes normales du poste.", "Contributions additionnelles : projets, améliorations, apprentissage ou soutien ponctuel.", "L’autoévaluation soutient la réflexion et la discussion; l’évaluation officielle demeure distincte."]
        },
        {
            id: "goals", title: "Objectifs individuels", keywords: "objectif cible échéance période évaluation",
            body: "Les objectifs individuels définissent des résultats précis à atteindre pendant une période d’évaluation.",
            points: ["Chaque objectif peut inclure un résultat attendu, une mesure de réussite, une pondération et une échéance.", "Les objectifs peuvent être réutilisés d’une période à l’autre sans transférer les résultats antérieurs.", "Le gestionnaire peut suivre et confirmer l’avancement selon le processus établi."]
        },
        {
            id: "peer", title: "Rétroaction des collègues", keywords: "rétroaction collègues répondants évaluateurs confidentialité",
            body: "La rétroaction des collègues recueille le point de vue de plusieurs pairs sur la collaboration et les comportements de travail.",
            points: ["Le gestionnaire immédiat est exclu des affectations de rétroaction entre collègues.", "L’affichage du nom des répondants dépend des paramètres de confidentialité de l’organisation.", "Le résultat contribue à la contribution individuelle selon la pondération configurée."]
        },
        {
            id: "indicators", title: "Indicateurs", keywords: "indicateur performance cible valeur réelle responsable saisie approbateur",
            body: "Les indicateurs suivent les résultats d’une unité ou de l’ensemble de l’organisation et alimentent la contribution collective.",
            points: ["Responsable de l’indicateur : assure le suivi du résultat.", "Responsable de saisie : soumet la valeur réelle.", "Approbateur : vérifie et approuve la valeur soumise.", "Processus habituel : saisie du résultat → soumission → approbation ou demande de correction."]
        },
        {
            id: "collective", title: "Contribution collective", keywords: "contribution collective équipe unités organisation",
            body: "La contribution collective reflète la part des résultats communs attribuable au contexte organisationnel d’une personne.",
            points: ["Elle complète la contribution individuelle; elle ne la remplace pas.", "La pondération des niveaux dépend du poste et du modèle de contribution collective attribué.", "Lorsqu’un niveau hiérarchique ne s’applique pas, sa pondération est redistribuée selon les règles du modèle."]
        },
        {
            id: "period", title: "Périodes d’évaluation", keywords: "période planification cible activer finaliser verrouiller",
            body: "Une période d’évaluation regroupe les cibles, les soumissions et les résultats utilisés pour calculer le CVI.",
            points: ["Préparez les cibles avant d’activer la période.", "Suivez les soumissions et les approbations pendant la période.", "Finalisez et verrouillez la période lorsque les résultats sont complets.", "Rouvrez une période uniquement lorsqu’une correction contrôlée est nécessaire."]
        },
        {
            id: "score", title: "Comment le CVI est-il calculé?", keywords: "calcul pondération score résultat performance rendement contribution",
            body: "Le CVI combine la contribution individuelle et la contribution collective selon les modèles attribués au poste et à l’organisation.",
            points: ["Les pondérations sont normalisées selon la configuration applicable.", "Les détails techniques sont gérés dans les paramètres avancés.", "Les résultats d’une période finalisée sont conservés comme historique verrouillé."]
        },
    ] : [
        {
            id: "start", title: "What is CVI?", keywords: "CVI contribution value individual performance collective contribution",
            body: "CVI (Contribution Value Index) provides an integrated view of how a person contributes to organizational success. It combines Individual Contribution and Collective Contribution.",
            points: ["Individual Contribution: core responsibilities, additional contributions, goals, and peer feedback.", "Collective Contribution: the connection between a person and the results of their team, parent units and organization."]
        },
        {
            id: "tasks", title: "Responsibilities & Contributions", keywords: "core responsibilities additional contributions self assessment",
            body: "This area separates expected role responsibilities from additional contributions that go beyond day-to-day work.",
            points: ["Core responsibilities: recurring work and normal expectations of the position.", "Additional contributions: projects, improvements, learning, or occasional support.", "Self-assessment supports reflection and discussion; the formal evaluation remains separate."]
        },
        {
            id: "goals", title: "Individual Goals", keywords: "goal target deadline review period",
            body: "Individual goals define specific outcomes to achieve during a review period.",
            points: ["Each goal can include an expected result, success measure, weight, and due date.", "Goals can be reused across periods without carrying forward previous results.", "The manager can track and confirm progress according to the organization’s process."]
        },
        {
            id: "peer", title: "Peer Feedback", keywords: "peer feedback providers reviewers confidentiality",
            body: "Peer feedback gathers perspectives from several colleagues on collaboration and workplace behaviours.",
            points: ["The direct manager is excluded from peer feedback assignments.", "Whether feedback provider names are shown follows the organization’s confidentiality settings.", "The result counts toward Individual Contribution according to the configured weight."]
        },
        {
            id: "indicators", title: "Indicators", keywords: "indicator KPI performance target actual owner data submitter approver",
            body: "Indicators track results for an organizational unit or the organization as a whole and feed into Collective Contribution.",
            points: ["Indicator owner: accountable for following the result.", "Data submitter: enters and submits the actual value.", "Approver: reviews and approves the submitted value.", "Typical workflow: enter result → submit → approve or request a correction."]
        },
        {
            id: "collective", title: "Collective Contribution", keywords: "collective contribution team units organization",
            body: "Collective Contribution reflects the shared results connected to a person’s organizational context.",
            points: ["It complements Individual Contribution rather than replacing it.", "Level weights depend on the position and the assigned collective contribution template.", "When a hierarchy level does not apply, its weight is redistributed according to the model rules."]
        },
        {
            id: "period", title: "Review Periods", keywords: "review period planning target activate finalize lock",
            body: "A review period groups the targets, submissions and results used to calculate CVI.",
            points: ["Set targets before activating the period.", "Monitor submissions and approvals during the period.", "Finalize and lock the period when results are complete.", "Reopen a period only when a controlled correction is required."]
        },
        {
            id: "score", title: "How is CVI calculated?", keywords: "calculation weighting score performance contribution",
            body: "CVI combines Individual Contribution and Collective Contribution using the templates assigned to the position and organization.",
            points: ["Weights are normalized according to the applicable configuration.", "Technical details are managed in Advanced Settings.", "Finalized-period results are retained as locked historical records."]
        },
    ];
    const copy = isFrench ? {
        eyebrow: "Centre d’aide CVI",
        title: "Aide CVI",
        subtitle: "Consultez rapidement les concepts, rôles et processus principaux.",
        prompt: "Sur quel sujet souhaitez-vous de l’aide?",
        placeholder: "Ex. : indicateurs, rétroaction, périodes d’évaluation…",
        noMatch: "Aucun sujet ne correspond à votre recherche.",
        tryAgain: "Essayez un autre terme de recherche.",
    } : {
        eyebrow: "CVI Help Centre",
        title: "CVI Help",
        subtitle: "Quick guidance on the main CVI concepts, roles, and workflows.",
        prompt: "What would you like help with?",
        placeholder: "For example: indicators, peer feedback, review periods…",
        noMatch: "No help topics match your search.",
        tryAgain: "Try another search term.",
    };
    const [query,setQuery]=useState("");
    const [openId,setOpenId]=useState("start");
    const q=query.trim().toLowerCase();
    const visible=q ? topics.filter(t => (t.title+" "+t.keywords+" "+t.body+" "+t.points.join(" ")).toLowerCase().includes(q)) : topics;
    const selected=topics.find(x=>x.id===openId && visible.some(v=>v.id===x.id)) || visible[0];
    return <div className="cvi-dashboard-page">
        <DashboardPageIntro eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} />
        <Card style={{marginBottom:14}}><div style={{padding:18}}><div style={{fontSize:12.5,fontWeight:600,marginBottom:8}}>{copy.prompt}</div><input value={query} onChange={e=>setQuery(e.target.value)} placeholder={copy.placeholder} style={{width:"100%",boxSizing:"border-box",padding:"11px 12px",border:"1.5px solid #E4E4EC",borderRadius:9,fontFamily:"inherit",fontSize:12.5,outline:"none"}}/></div></Card>
        <div className="cvi-help-grid" style={{display:"grid",gridTemplateColumns:"minmax(230px,300px) minmax(0,1fr)",gap:14,alignItems:"start"}}>
            <Card><div style={{padding:8}}>{visible.length===0?<div style={{padding:20,textAlign:"center",fontSize:11.5,color:C.muted}}>{copy.noMatch}</div>:visible.map(t=><button key={t.id} onClick={()=>setOpenId(t.id)} style={{width:"100%",border:"none",borderRadius:8,padding:"11px 12px",marginBottom:3,textAlign:"left",fontFamily:"inherit",cursor:"pointer",background:openId===t.id?C.cyanBg:"transparent",color:openId===t.id?C.navy:C.text,fontSize:12.5,fontWeight:openId===t.id?600:450}}>{t.title}</button>)}</div></Card>
            {selected ? <Card><div style={{padding:22}}><h2 style={{fontSize:17,margin:"0 0 12px",color:C.navy}}>{selected.title}</h2><p style={{fontSize:13,lineHeight:1.75,color:"#44495A",margin:"0 0 16px"}}>{selected.body}</p><div style={{display:"grid",gap:8}}>{selected.points.map((point,i)=><div key={i} style={{display:"flex",gap:9,alignItems:"flex-start",background:"#F8FAFC",border:"1px solid #ECEFF4",borderRadius:9,padding:"10px 12px",fontSize:12,lineHeight:1.65}}><span style={{color:C.teal,marginTop:1}}><UiIcon name="check" size={14}/></span><span>{point}</span></div>)}</div></div></Card> : <Card><div style={{padding:30,textAlign:"center",color:C.muted}}>{copy.tryAgain}</div></Card>}
        </div>
    </div>;
}

function AppSkeleton() {
    return <div className="cvi-app-skeleton" aria-label="Loading">
        <div className="cvi-skeleton-header" />
        <div className="cvi-skeleton-body">
            <div className="cvi-skeleton-sidebar" />
            <div className="cvi-skeleton-content">
                <div className="cvi-skeleton-line wide" />
                <div className="cvi-skeleton-line" />
                <div className="cvi-skeleton-grid">{[0,1,2,3].map(i => <div key={i} className="cvi-skeleton-card" />)}</div>
                <div className="cvi-skeleton-panel" />
            </div>
        </div>
    </div>;
}

function TabTransitionContent({ transitionKey, children }) {
    // Keep the wrapper identity stable across local tab changes.
    // A key tied to the active tab forces React to unmount/remount the whole subtree,
    // which resets child state and re-runs mount-only data loading effects.
    return <div className="cvi-tab-content">{children}</div>;
}

function UserMenu({ user, onLogout, onSettings }) {
    const { language } = useI18n();
    return <AccountMenu user={user} language={language} avatar={<UserAvatar user={user} size={30} />}
        onLogout={onLogout} onSettings={onSettings} />;
}

function StatusBadge({ status }) {
    // Labels stay in the legacy source language so the page translator localizes them; the tone
    // comes from the shared status system.
    const labels = {
        draft: "Draft", approved: "Approved", pending_review: "Pending review", revision_requested: "Revision required",
        resubmitted: "Resubmitted", rejected: "Rejected", submitted: "Submitted", locked: "Locked", active: "Active",
        inactive: "Inactive", pending: "Pending", completed: "Completed",
    };
    const tone = { active: "success", inactive: "neutral" }[status] || STATUS_TONE[status] || "neutral";
    return <span className={`cvi-ws-status is-${tone}`}><i aria-hidden="true" />{labels[status] || status}</span>;
}

function ScoreRing({ value, size = 72, color = C.navy }) {
    const r = (size - 8) / 2;
    const circ = 2 * Math.PI * r;
    const missing = value == null || !Number.isFinite(Number(value));
    const dash = missing ? 0 : (Math.min(100, Math.max(0, Number(value))) / 100) * circ;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.bg} strokeWidth={6} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6}
                strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`} />
            <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
                fontSize={size < 60 ? 11 : 14} fontWeight={500} fill={color}>
                {missing ? "—" : Math.round(Number(value))}
            </text>
        </svg>
    );
}

function MiniBar({ value, max = 100, color = C.navy, height = 5 }) {
    return (
        <div style={{ height, background: C.bg, borderRadius: height / 2, overflow: "hidden", marginTop: 6 }}>
            <div style={{
                width: `${Math.min(100, (value / max) * 100)}%`, height: "100%",
                background: color, borderRadius: height / 2
            }} />
        </div>
    );
}

function Card({ children, style }) {
    return (
        <div className="cvi-card" style={style}>
            {children}
        </div>
    );
}

// نمای کلی امتیاز (لایه اول drill-down) — روی داشبورد اصلیِ هر نقش استفاده می‌شه.
// امتیاز فردی به «روتین/غیر روتین» و امتیاز سازمانی به «سازمانی/معاونتی/تیمی» تفکیک می‌شه؛
// با کلیک روی هرکدوم، لایه دوم (جزئیات کامل) باز می‌شه.
function DashboardPageIntro({ title, subtitle, period, eyebrow, actions, periodOptions = [], onPeriodSelect = null, periodIsHistorical = false }) {
    const [periodOpen, setPeriodOpen] = useState(false);
    const { language } = useI18n();
    const selectablePeriods = (periodOptions || []).filter(Boolean);
    const hasPeriodMenu = Boolean(onPeriodSelect && selectablePeriods.length > 1);
    return (
        <div className="cvi-dashboard-intro">
            <div className="cvi-dashboard-intro-copy">
                {eyebrow && <div className="cvi-dashboard-eyebrow">{eyebrow}</div>}
                <h1 className="cvi-dashboard-title">{title}</h1>
                {subtitle && <div className="cvi-dashboard-subtitle">{subtitle}</div>}
            </div>
            <div className="cvi-dashboard-intro-actions">
                {period && <div className="cvi-dashboard-period-wrap">
                    <button type="button" className={`cvi-dashboard-period${hasPeriodMenu ? " is-selectable" : ""}`}
                        onClick={() => hasPeriodMenu && setPeriodOpen(v => !v)}
                        aria-expanded={hasPeriodMenu ? periodOpen : undefined}>
                        <UiIcon name="history" size={14} />
                        <span><small>{periodIsHistorical ? (language === "fr" ? "Période sélectionnée" : "Selected period") : (language === "fr" ? "Période en cours" : "Current period")}</small><strong>{localizePeriodLabel(period, language)}</strong></span>
                        {hasPeriodMenu && <span className="cvi-dashboard-period-chevron">⌄</span>}
                    </button>
                    {periodOpen && hasPeriodMenu && <div className="cvi-dashboard-period-menu">
                        <div className="cvi-dashboard-period-menu-label">View another period</div>
                        {selectablePeriods.slice().reverse().map(item => {
                            const current = item.label === period;
                            return <button key={item.periodId || item.id || item.label} type="button"
                                className={current ? "is-current" : ""}
                                onClick={() => {
                                    setPeriodOpen(false);
                                    if (!current) onPeriodSelect(item);
                                }}>
                                <span>{localizePeriodLabel(item.label, language)}</span>
                                <small>{current ? (language === "fr" ? "Sélectionnée" : "Selected") : (language === "fr" ? "Afficher le tableau de bord" : "View dashboard")}</small>
                            </button>;
                        })}
                    </div>}
                </div>}
                {periodIsHistorical && <span className="adm-badge is-plain" data-cvi-i18n="managed" title={language === "fr" ? "Période finalisée en lecture seule" : "Finalized period, read-only"}>{language === "fr" ? "Finalisée · Lecture seule" : "Finalized · Read-only"}</span>}
                {actions}
            </div>
        </div>
    );
}

function DashboardMetricCard({ label, value, helper, tone = "navy", icon, onClick }) {
    const toneMap = {
        navy: [C.navy, C.cyanBg], teal: [C.teal, C.tealLt], purple: [C.purple, C.purpleLt],
        amber: [C.amber, C.amberLt], red: [C.red, C.redLt], cyan: ["#007A8C", C.cyanLt],
    };
    const [color, soft] = toneMap[tone] || toneMap.navy;
    return (
        <div className={`cvi-dashboard-metric${onClick ? " is-clickable" : ""}`} onClick={onClick}
            role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}>
            <div className="cvi-dashboard-metric-top">
                <span className="cvi-dashboard-metric-label">{label}</span>
                {icon && <span className="cvi-dashboard-metric-icon" style={{ color, background: soft }}>{icon}</span>}
            </div>
            <div className="cvi-dashboard-metric-value" style={{ color }}>{value}</div>
            {helper && <div className="cvi-dashboard-metric-helper">{helper}</div>}
        </div>
    );
}

function DashboardSection({ title, action, children, className = "" }) {
    return (
        <section className={`cvi-dashboard-section ${className}`.trim()}>
            <div className="cvi-dashboard-section-head">
                <h2>{title}</h2>
                {action}
            </div>
            {children}
        </section>
    );
}

function DashboardLinkButton({ children, onClick, primary = false }) {
    return <button type="button" onClick={onClick}
        className={`cvi-dashboard-link-button${primary ? " is-primary" : ""}`}>{children}</button>;
}

function ScoreOverview({ user, orgBreakdown,
    onOpenNonRoutine, onOpenRoutine, onOpenTasksActivities, onOpenGoals, onOpenPeerFeedback, onOpenOrgLevel,
    locale = "fa", onContextPanelChange = null }) {
    const { orgSettings, positions, peerResult, activePeriod } = useData();
    const routineCtx = useRoutineCtx();
    const en = locale === "en";
    const { language: uiLanguage } = useI18n();
    const fr = en && uiLanguage === "fr";
    const individualWeights = positions.find(p => p.id === user?.posId) || orgSettings;
    const [goalProgressSummary, setGoalProgressSummary] = useState({ percent: null, activeCount: 0 });
    const { data: liveCvi } = useLiveCvi(user?.id, activePeriod?.id);
    // Scores come only from the server result (live calculation or final snapshot). When it is
    // unavailable every score shows "—"; configured weights may still fall back to the position.
    const serverScores = liveCvi?.scores || null;
    const serverScore = key => (serverScores ? cviValue(serverScores[key]) : null);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [explainOpen, setExplainOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const loadGoalScore = async () => {
            if (!user?.id || !activePeriod?.id) { setGoalProgressSummary({ percent: null, activeCount: 0 }); return; }
            try {
                const token = sessionStorage.getItem("cvi_token");
                const response = await fetch(`${API_BASE_URL}/api/goals/?periodId=${encodeURIComponent(activePeriod.id)}`, { headers: { Authorization: `Bearer ${token}` } });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.message || "Goal score load failed");
                const personalGoals = (payload.goals || []).filter(goal =>
                    goal.subjectUserId === user.id && ["ACTIVE", "COMPLETED"].includes(String(goal.status).toUpperCase())
                );

                const progressGoals = personalGoals.filter(goal => Number(goal.weight) > 0);
                const progressWeight = progressGoals.reduce((sum, goal) => sum + Number(goal.weight || 0), 0);
                const progress = progressWeight
                    ? progressGoals.reduce((sum, goal) => sum + Math.max(0, Math.min(100, Number(goal.progress || 0))) * Number(goal.weight || 0), 0) / progressWeight
                    : null;
                if (!cancelled) {
                    setGoalProgressSummary({ percent: progress == null ? null : Math.round(progress), activeCount: personalGoals.length });
                }
            } catch {
                if (!cancelled) {
                    setGoalProgressSummary({ percent: null, activeCount: 0 });
                }
            }
        };
        loadGoalScore();
        return () => { cancelled = true; };
    }, [user?.id, activePeriod?.id]);

    const scoreText = value => value == null || !Number.isFinite(Number(value)) ? "—" : Number(value).toFixed(0);
    const weightText = value => value == null || !Number.isFinite(Number(value)) ? "—" : Number(value).toFixed(0);


    const components = [
        { id:"tasks", label: "Responsibilities & contributions", value: serverScore("tasksActivities"), weight: liveCvi?.weights?.individualComponents?.tasksActivities ?? individualWeights?.wTasksActivities ?? 50, subLabel: `Core ${liveCvi?.weights?.tasksActivitiesSplit?.continuous ?? individualWeights?.wRoutine ?? 60}% · Additional ${liveCvi?.weights?.tasksActivitiesSplit?.nonContinuous ?? individualWeights?.wNonRoutine ?? 40}%`, onOpenFull: onOpenTasksActivities },
        { id:"goals", label: "Individual Goals", value: serverScore("individualGoals"), weight: liveCvi?.weights?.individualComponents?.individualGoals ?? individualWeights?.wGoals ?? 30, onOpenFull: onOpenGoals },
        { id:"peer", label: "Peer feedback", value: serverScore("peerFeedback"), weight: liveCvi?.weights?.individualComponents?.peerFeedback ?? individualWeights?.wPeerFeedback ?? 20, onOpenFull: onOpenPeerFeedback },
    ];
    const displayedIndividualScore = serverScore("individual");
    const displayedOrgScore = serverScore("organizational");
    const displayedCviScore = serverScore("cvi");

    const liveOrg = liveCvi?.breakdown?.organizational;
    const strategic = [
        { id:"team", label: "Team", value: cviValue(liveOrg?.levels?.team?.score), weight: liveOrg?.levels?.team?.effectiveWeight ?? liveOrg?.levels?.team?.weight ?? orgBreakdown?.wTeam, level:"team", onOpenFull:()=>onOpenOrgLevel?.("team") },
        { id:"upstream", label: "Parent units", value: cviValue(liveOrg?.levels?.upstream?.score ?? liveOrg?.levels?.department?.score), weight: liveOrg?.levels?.upstream?.effectiveWeight ?? liveOrg?.levels?.upstream?.weight ?? liveOrg?.levels?.department?.effectiveWeight ?? liveOrg?.levels?.department?.weight ?? orgBreakdown?.wUpstream ?? orgBreakdown?.wDept, level:"dept", onOpenFull:()=>onOpenOrgLevel?.("dept") },
        { id:"org", label: "Organization", value: cviValue(liveOrg?.levels?.organization?.score), weight: liveOrg?.levels?.organization?.effectiveWeight ?? liveOrg?.levels?.organization?.weight ?? orgBreakdown?.wOrg, level:"org", onOpenFull:()=>onOpenOrgLevel?.("org") },
    ];
    const cviProgress = displayedCviScore == null ? 0 : Math.max(0, Math.min(100, Number(displayedCviScore)));
    // Data coverage: how many weighted components actually have a score this cycle. The server keeps
    // "no data" as null and a real result (including 0) as a number, so this never re-labels a valid zero.
    const coverageOf = rows => {
        const applicable = rows.filter(item => Number(item.weight) > 0);
        const assessed = applicable.filter(item => item.value != null && Number.isFinite(Number(item.value)));
        return { assessed: assessed.length, total: applicable.length };
    };
    const individualCoverage = coverageOf(components);
    const collectiveCoverage = coverageOf(strategic);
    const coverageLabel = (coverage, score) => {
        if (score == null || !coverage.assessed) return { tone: "is-none", text: fr ? "Pas encore évalué" : "Not assessed yet", hint: "" };
        const counted = fr ? `${coverage.assessed} composante${coverage.assessed > 1 ? "s" : ""} sur ${coverage.total}` : `${coverage.assessed} of ${coverage.total} components`;
        if (coverage.assessed >= coverage.total) return { tone: "is-complete", text: counted, hint: fr ? "Toutes les composantes ont un résultat" : en ? "Every component has a score" : "" };
        return { tone: "is-partial", text: counted, hint: fr ? "Données partielles : les composantes sans données ne sont pas comptées" : en ? "Partial data: components without data are not counted" : "" };
    };
    const individualCoverageLabel = coverageLabel(individualCoverage, displayedIndividualScore);
    const collectiveCoverageLabel = coverageLabel(collectiveCoverage, displayedOrgScore);
    // The active cycle's score is provisional until the cycle is finalized (server: live_calculation).
    const isProvisional = liveCvi ? liveCvi.source === "live_calculation" : Boolean(activePeriod && activePeriod.status === "active");
    const responsibilityProgress = calcRoutineCompletionForUser(user, routineCtx, activePeriod?.id);
    const peerSummary = peerResult?.subjectUserId === user.id && peerResult?.period?.id === activePeriod?.id
        ? peerResult.summary
        : null;
    const feedbackReceived = Number(peerSummary?.responseCount || 0);
    const feedbackAssigned = Number(peerSummary?.assignedCount || 0);

    const publishContext = payload => {
        if (onContextPanelChange) onContextPanelChange(payload);
    };

    const openDriver = item => {
        setSelectedDriver(item);
        setExplainOpen(false);
        publishContext({
            eyebrow: "SCORE DETAILS",
            title: item.label,
            content: <div className="cvi-personal-context-body">
                <div className="cvi-personal-quick-facts">
                    <div><span>{"Current score"}</span><strong>{scoreText(item.value)}</strong></div>
                    <div><span>{"Weight"}</span><strong>{item.weight == null ? "—" : `${weightText(item.weight)}%`}</strong></div>
                </div>
                {item.subLabel && <p className="cvi-personal-detail-note">{item.subLabel}</p>}
                {item.onOpenFull && <button type="button" className="cvi-score-full-detail" onClick={item.onOpenFull}>{"Open full details →"}</button>}
            </div>
        });
    };

    const DriverList = ({ rows }) => <div className="cvi-personal-driver-list">
        {rows.map(item => <button type="button" key={item.id} className={selectedDriver?.id === item.id ? "is-selected" : ""}
            onClick={() => openDriver(item)}>
            <span className="cvi-personal-driver-main">
                <b>{item.label}</b>
                {item.subLabel && <small>{item.subLabel}</small>}
            </span>
            <span className="cvi-personal-driver-weight">{"Weight"} {`${weightText(item.weight)}%`}</span>
            <strong>{scoreText(item.value)}</strong>
            <span className="cvi-personal-driver-chevron">›</span>
        </button>)}
    </div>;

    const selectedRows = selectedDriver?.id === "individual-summary" ? components : selectedDriver?.id === "collective-summary" ? strategic : null;
    const selectedWeight = selectedDriver?.weight != null ? weightText(selectedDriver.weight) : null;

    const openSummary = id => {
        const isIndividual = id === "individual-summary";
        const summary = { id, label: isIndividual ? ("Individual Contribution") : ("Collective Contribution") };
        const rows = isIndividual ? components : strategic;
        setSelectedDriver(summary);
        setExplainOpen(false);
        publishContext({
            eyebrow: "SCORE COMPONENTS",
            title: summary.label,
            content: <DriverList rows={rows} />
        });
    };

    const cviExplainerContent = <div className="cvi-cvi-explainer">
        <p className="cvi-cvi-explainer-intro">
            {"CVI gives a balanced view of your contribution by combining your Individual Contribution with your Collective Contribution."}
        </p>
        <div className="cvi-cvi-explainer-pair">
            <div>
                <span className="cvi-cvi-explainer-icon"><UiIcon name="performance" size={16} /></span>
                <strong>{"Individual Contribution"}</strong>
                <small>{"Your direct results through responsibilities, goals, and contributions."}</small>
            </div>
            <div>
                <span className="cvi-cvi-explainer-icon collective"><UiIcon name="team" size={16} /></span>
                <strong>{"Collective Contribution"}</strong>
                <small>{"How your role supports the success of your team and organization."}</small>
            </div>
        </div>
        <div className="cvi-cvi-explainer-why">
            <span>{"WHY IT MATTERS"}</span>
            <p>{"Strong personal results matter, but so does contributing to shared outcomes. CVI combines both dimensions using a geometric mean, so a very low result in one area is not fully hidden by a high result in the other."}</p>
        </div>
    </div>;

    const openCviExplanation = () => {
        setExplainOpen(true);
        setSelectedDriver(null);
        if (onContextPanelChange) publishContext(null);
    };

    return (
        <section className="cvi-personal-performance">
            {onContextPanelChange && en && <div className="cvi-personal-head" data-cvi-i18n="managed">
                <div>
                    <h2>{fr ? "Indice de valeur de contribution" : "Contribution Value Index"}</h2>
                    <small>
                        {activePeriod?.label ? `${localizePeriodLabel(activePeriod.label, uiLanguage)} · ` : ""}
                        {isProvisional
                            ? (fr ? "Provisoire jusqu’à la finalisation de la période" : "Provisional until the period is finalized")
                            : (fr ? "Résultat finalisé" : "Finalized result")}
                    </small>
                </div>
                <button type="button" className="cvi-personal-head-link" onClick={openCviExplanation} aria-expanded={explainOpen}>
                    <UiIcon name="book" size={14} />{fr ? "Mode de calcul" : "How it’s calculated"}
                </button>
            </div>}
            <div className={`cvi-personal-hero${explainOpen && onContextPanelChange ? " is-explaining" : ""}`}>
                <button type="button" className="cvi-personal-side-score" onClick={() => openSummary("individual-summary")}>
                    <span>{"Individual Contribution"}</span>
                    <strong>{scoreText(displayedIndividualScore)}</strong>
                    <small>{"out of 100"}</small>
                    <span className={`cvi-personal-coverage ${individualCoverageLabel.tone}`} data-cvi-i18n="managed" title={individualCoverageLabel.hint || undefined}><i aria-hidden="true" />{individualCoverageLabel.text}{individualCoverageLabel.hint && <span className="cvi-xp-sr"> — {individualCoverageLabel.hint}</span>}</span>
                    <em>{"View components"}</em>
                </button>

                <div className="cvi-personal-cvi-zone">
                    <button type="button" className="cvi-personal-cvi-button" onClick={openCviExplanation}
                        aria-label={fr ? `CVI actuel ${displayedCviScore == null ? "non disponible" : Number(displayedCviScore).toFixed(1)}${isProvisional ? " (provisoire)" : ""}. Comprendre ce que reflète votre CVI` : `Current CVI ${displayedCviScore == null ? "not available" : Number(displayedCviScore).toFixed(1)}${isProvisional ? " (provisional)" : ""}. Learn what your CVI reflects`}>
                        <div className="cvi-personal-cvi-ring" style={{ "--cvi-progress": `${cviProgress * 3.6}deg` }}>
                            <div className="cvi-personal-cvi-ring-content">
                                <span>{"Current CVI"} <i className="cvi-cvi-info">i</i></span>
                                <strong>{displayedCviScore == null ? "—" : Number(displayedCviScore).toFixed(1)}</strong>
                                <small>{"out of 100"}</small>
                                {isProvisional && displayedCviScore != null && <em data-cvi-i18n="managed">{fr ? "Provisoire" : "Provisional"}</em>}
                            </div>
                        </div>
                    </button>
                </div>

                <button type="button" className="cvi-personal-side-score collective" onClick={() => openSummary("collective-summary")}>
                    <span>{"Collective Contribution"}</span>
                    <strong>{scoreText(displayedOrgScore)}</strong>
                    <small>{"out of 100"}</small>
                    <span className={`cvi-personal-coverage ${collectiveCoverageLabel.tone}`} data-cvi-i18n="managed" title={collectiveCoverageLabel.hint || undefined}><i aria-hidden="true" />{collectiveCoverageLabel.text}{collectiveCoverageLabel.hint && <span className="cvi-xp-sr"> — {collectiveCoverageLabel.hint}</span>}</span>
                    <em>{"View components"}</em>
                </button>

                {explainOpen && onContextPanelChange && <div className="cvi-cvi-definition-overlay">
                    <div className="cvi-cvi-definition-head">
                        <div>
                            <span>{"UNDERSTANDING YOUR CVI"}</span>
                            <h3>{"What your CVI represents"}</h3>
                        </div>
                        <button type="button" onClick={() => setExplainOpen(false)} aria-label={"Close CVI explanation"}>×</button>
                    </div>
                    {cviExplainerContent}
                </div>}
            </div>

            <div className="cvi-personal-insight-strip">
                <div>
                    <span>{"Manager evaluation"}</span>
                    <strong className={responsibilityProgress.percent == null ? "is-empty" : ""}>{responsibilityProgress.percent == null ? "—" : `${responsibilityProgress.percent}%`}</strong>
                    <small>{responsibilityProgress.total
                        ? (fr ? `${responsibilityProgress.answered} sur ${responsibilityProgress.total} responsabilités principales évaluées` : `${responsibilityProgress.answered} of ${responsibilityProgress.total} core responsibilities evaluated`)
                        : ("Not yet evaluated by your manager")}</small>
                </div>
                <div>
                    <span>{"Goal progress"}</span>
                    <strong className={goalProgressSummary.percent == null ? "is-empty" : ""}>{goalProgressSummary.percent == null ? "—" : `${goalProgressSummary.percent}%`}</strong>
                    <small>{goalProgressSummary.activeCount
                        ? (`${goalProgressSummary.activeCount} active goal${goalProgressSummary.activeCount === 1 ? "" : "s"}`)
                        : ("No active goals")}</small>
                </div>
                <div>
                    <span>{"Feedback coverage"}</span>
                    <strong className={feedbackAssigned ? "" : "is-empty"}>{feedbackAssigned ? `${feedbackReceived}/${feedbackAssigned}` : "—"}</strong>
                    <small>{feedbackAssigned
                        ? ("responses received")
                        : ("Peer feedback not configured")}</small>
                </div>
            </div>

            {!onContextPanelChange && (selectedDriver || explainOpen) && <div className="cvi-personal-detail-panel">
                <div className="cvi-personal-detail-head">
                    <div>
                        <span>{explainOpen ? ("CVI CALCULATION") : ("SCORE DETAILS")}</span>
                        <h3>{explainOpen ? ("How your CVI is calculated") : selectedDriver?.label}</h3>
                    </div>
                    <button type="button" onClick={() => { setSelectedDriver(null); setExplainOpen(false); }} aria-label={"Close details"}>×</button>
                </div>
                {explainOpen ? <>
                    <div className="cvi-personal-formula">
                        <div><span>{"Individual Contribution"}</span><strong>{scoreText(displayedIndividualScore)}</strong></div>
                        <b>×</b>
                        <div><span>{"Collective Contribution"}</span><strong>{scoreText(displayedOrgScore)}</strong></div>
                        <em>√</em>
                        <div><span>CVI</span><strong>{displayedCviScore == null ? "—" : Number(displayedCviScore).toFixed(1)}</strong></div>
                    </div>
                    <p className="cvi-personal-detail-note">{"CVI combines Individual Contribution and Collective Contribution using a geometric mean. Only eligible data available for the current period is included."}</p>
                </> : selectedRows ? <DriverList rows={selectedRows} /> : <>
                    <div className="cvi-personal-quick-facts">
                        <div><span>{"Current score"}</span><strong>{scoreText(selectedDriver?.value)}</strong></div>
                        <div><span>{"Weight"}</span><strong>{selectedWeight == null ? "—" : `${selectedWeight}%`}</strong></div>
                    </div>
                    {selectedDriver?.subLabel && <p className="cvi-personal-detail-note">{selectedDriver.subLabel}</p>}
                    {selectedDriver?.onOpenFull && <button type="button" className="cvi-score-full-detail" onClick={() => { const fn=selectedDriver.onOpenFull; setSelectedDriver(null); fn(); }}>{"Open full details →"}</button>}
                </>}
            </div>}
        </section>
    );
}

function GlobalResponsiveStyles() {
    return <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        html, body, #root { width: 100%; min-height: 100%; margin: 0; }
        body { overflow-x: hidden; }
        button, input, select, textarea { max-width: 100%; }
        table { max-width: 100%; }
        /* Canada Admin — restrained B2B visual system */
        .cvi-admin-shell { background: #F5F7FA !important; color: #202234; }
        .cvi-admin-content { padding: 26px 28px 34px; }
        .cvi-admin-content-inner { width: 100%; max-width: 1480px; margin: 0 auto; }
        .cvi-admin-shell .cvi-dashboard-intro { margin-bottom: 22px; }
        .cvi-admin-shell .cvi-dashboard-title { font-size: 28px; line-height: 1.2; letter-spacing: -0.45px; font-weight: 720; }
        .cvi-admin-shell .cvi-dashboard-subtitle { font-size: 13.5px; line-height: 1.65; max-width: 760px; color: #6E7487; }
        .cvi-admin-shell .cvi-card,
        .cvi-admin-shell .cvi-dashboard-section { border-color: #E2E6EE; border-radius: 14px; box-shadow: 0 5px 18px rgba(25,34,72,.045); }
        .cvi-admin-shell .cvi-card { background: #FFFFFF; }
        .cvi-admin-shell [role="tablist"] { margin-bottom: 22px !important; }
        .cvi-admin-shell [role="tab"] { font-size: 13px !important; min-height: 42px; }
        .cvi-admin-shell input,
        .cvi-admin-shell select,
        .cvi-admin-shell textarea { min-height: 42px; border-color: #DCE1EA !important; border-radius: 9px !important; }
        .cvi-admin-shell input:focus,
        .cvi-admin-shell select:focus,
        .cvi-admin-shell textarea:focus { border-color: #7A86C8 !important; box-shadow: 0 0 0 3px rgba(82,96,181,.10); outline: none; }
        .cvi-admin-shell .cvi-button-primary,
        .cvi-admin-shell .cvi-button-secondary { min-height: 38px; border-radius: 9px; font-weight: 600; }
        .cvi-admin-shell .cvi-admin-table thead th { height: 46px; font-size: 11.5px !important; color: #6E7482 !important; background: #F8F9FB !important; }
        .cvi-admin-shell .cvi-admin-table tbody td { height: 56px; font-size: 12.5px !important; }
        .cvi-admin-shell .cvi-admin-table-row { transition: background .14s ease; }
        .cvi-admin-shell .cvi-admin-table-row:hover { background: #FAFBFD; }
        .cvi-admin-shell .cvi-admin-filter-grid { padding: 14px !important; gap: 10px !important; }
        .cvi-admin-shell .cvi-settings-intro { margin-bottom: 14px; }
        .cvi-admin-shell .cvi-settings-intro strong { font-size: 13.5px; }
        .cvi-admin-shell .cvi-settings-intro span { font-size: 11.5px; line-height: 1.65; }
        @media (max-width: 1100px) {
            .cvi-admin-filter-grid { grid-template-columns: 1fr 1fr !important; }
            .cvi-org-structure-grid { grid-template-columns: 1fr !important; }
            .cvi-org-detail-panel { position: static !important; }
            .cvi-position-template-row { grid-template-columns: 1fr 1fr !important; }
            .cvi-position-template-row > :first-child { grid-column: 1 / -1; }
        }
        @media (max-width: 720px) {
            .cvi-admin-content { padding: 16px 14px 24px; }
            .cvi-admin-shell .cvi-dashboard-title { font-size: 24px; }
            .cvi-admin-filter-grid { grid-template-columns: 1fr !important; }
            .cvi-help-grid { grid-template-columns: 1fr !important; }
            .cvi-position-template-row { grid-template-columns: 1fr !important; }
            .cvi-position-template-row > * { grid-column: auto !important; }

            button { min-height: 36px; }
            input, select, textarea { font-size: 16px !important; }
            .cvi-workflow-modal { width: calc(100vw - 20px) !important; max-height: 82vh !important; }
            .cvi-workflow-grid { grid-template-columns: 1fr !important; }
            .cvi-attachment-row { align-items: flex-start !important; flex-wrap: wrap !important; }
        }
        @media (max-width: 420px) {
            .cvi-hide-xs { display: none !important; }
        }
    `}</style>;
}

async function protectedFileDownload(url, fileName) {
    const token = sessionStorage.getItem("cvi_token");
    if (!token) throw new Error("Authentication required.");
    const response = await fetch(`${API_BASE_URL}${url}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) throw new Error(response.status === 404 ? "This file is no longer available." : "The file could not be downloaded. Please try again.");
    const blob = await response.blob();
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = fileName || "attachment";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(href);
}

function ProtectedAttachmentList({ module, entityId, attachments = [], canDelete = false }) {
    const { t } = useI18n();
    const [rows, setRows] = useState(attachments);
    const [busyId, setBusyId] = useState(null);
    useEffect(() => setRows(attachments), [attachments]);
    if (!entityId || !rows?.length) return null;
    const pathFor = (attachmentId, download = false) => module === "indicator"
        ? `/api/indicators/values/${entityId}/files/${attachmentId}${download ? "/download" : ""}`
        : `/api/activities/${entityId}/files/${attachmentId}${download ? "/download" : ""}`;
    const remove = async attachment => {
        if (!canDelete || busyId) return;
        setBusyId(attachment.id);
        try {
            const token = sessionStorage.getItem("cvi_token");
            const response = await fetch(`${API_BASE_URL}${pathFor(attachment.id)}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) throw new Error("delete failed");
            setRows(current => current.filter(item => item.id !== attachment.id));
        } catch (error) {
            console.error("Attachment delete failed:", error);
            uxNotify(t("file.failed"));
        } finally { setBusyId(null); }
    };
    return <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
        {rows.map(attachment => <div key={attachment.id} className="cvi-attachment-row" style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 9px", background: C.bg, borderRadius: 7, fontSize: 11 }}>
            <span aria-hidden="true"><UiIcon name="paperclip" size={14} /></span>
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachment.fileName}</span>
            {attachment.sizeBytes != null && <span style={{ color: C.hint, fontSize: 9 }}>{Math.max(1, Math.round(Number(attachment.sizeBytes) / 1024))} KB</span>}
            <button onClick={() => protectedFileDownload(pathFor(attachment.id, true), attachment.fileName).catch(() => uxNotify(t("file.failed")))} style={{ border: `1px solid ${C.border}`, background: "#fff", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}>{t("file.download")}</button>
            {canDelete && <button disabled={busyId === attachment.id} onClick={() => remove(attachment)} style={{ border: `1px solid ${C.red}`, color: C.red, background: C.redLt, borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}>{busyId === attachment.id ? t("file.deleting") : t("file.delete")}</button>}
        </div>)}
    </div>;
}

// Where a task's actionTarget leads inside a role shell. Shells only navigate to destinations they
// render, so a call to action never opens a page the role does not have.
const NAV_TARGETS = {
    "routine-self": "self-assessment", activities: "activities", "review-inbox": "inbox", "peer-feedback": "peer-feedback",
    indicators: "my-indicators", "routine-evaluation": "employee-evaluation", goals: "goals", "check-ins": "check-ins",
    "team-challenges": "team-challenges", recognition: "recognition", learning: "learning", privacy: "privacy",
    challenges: "dashboard", "review-cycle": "dashboard",
};
const WorkspaceNavContext = createContext(null);

function makeShellNavigator({ navs, setNav, setView, overrides = {} }) {
    const resolve = target => overrides[target] || NAV_TARGETS[target] || target;
    return {
        can: target => Boolean(target) && navs.includes(resolve(target)),
        open: target => {
            const nav = resolve(target);
            if (!navs.includes(nav)) return false;
            setView?.(null);
            setNav(nav);
            return true;
        },
    };
}

function WorkflowCenterButton({ hidden = false }) {
    const { activePeriod } = useData();
    const { language } = useI18n();
    const navigator = useContext(WorkspaceNavContext);
    const token = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("cvi_token") : null;
    if (hidden || !token) return null;
    return <WorkCenter language={language} periodId={activePeriod?.id} navigator={navigator} />;
}

// Reuse the same duty ledger as the bell Work Center, with a full-page destination.
function EvaluationDutiesPage() {
    const { activePeriod } = useData();
    const { language } = useI18n();
    const navigator = useContext(WorkspaceNavContext);
    const duties = useEvaluationDuties(activePeriod?.id, true);
    const copy = evaluationResponsibilityCopy(language);
    const [filter, setFilter] = useState("all");
    const items = duties.data?.items || [];
    const counts = {
        all: items.length,
        open: items.filter(item => ["OPEN", "DUE_SOON", "OVERDUE"].includes(item.state)).length,
        done: items.filter(item => ["ON_TIME", "COMPLETED_LATE"].includes(item.state)).length,
        closed: items.filter(item => ["EXEMPT", "CANCELLED", "TRANSFERRED"].includes(item.state)).length,
    };
    const subset = filter === "all" ? items : items.filter(item => (
        filter === "open" ? ["OPEN", "DUE_SOON", "OVERDUE"].includes(item.state)
            : filter === "done" ? ["ON_TIME", "COMPLETED_LATE"].includes(item.state)
            : ["EXEMPT", "CANCELLED", "TRANSFERRED"].includes(item.state)
    ));
    const filteredData = duties.data ? { ...duties.data, items: subset } : null;
    const tabs = [
        { id: "all", name: language === "fr" ? "Toutes" : "All", count: counts.all },
        { id: "open", name: copy.groups.open, count: counts.open },
        { id: "done", name: copy.groups.done, count: counts.done },
        { id: "closed", name: copy.groups.closed, count: counts.closed },
    ];
    return <main className="cvi-workflow-page" style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px", width: "100%" }}>
        <header style={{ marginBottom: 20 }}>
            <h1 style={{ margin: "0 0 8px" }}>{copy.ledger.tab}</h1>
            <p style={{ margin: 0, color: "var(--cvi-text-secondary, #667085)" }}>{copy.ledger.intro}</p>
        </header>
        {duties.data && <div role="group" aria-label={copy.ledger.tab} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
            {tabs.map(tab => <button type="button" key={tab.id} aria-pressed={filter === tab.id}
                className={`cvi-ws-btn is-sm ${filter === tab.id ? "is-primary" : "is-secondary"}`}
                onClick={() => setFilter(tab.id)}>{tab.name} ({tab.count})</button>)}
        </div>}
        <EvaluationDutiesView language={language} data={filteredData} error={duties.error}
            onRetry={duties.reload} navigator={navigator} />
    </main>;
}

// TopBar واحد: هم برای هدر اصلی و هم صفحات فرعی
function HeaderQuickSearch() {
    const { language } = useI18n();
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onKey = event => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                ref.current?.focus();
                setOpen(true);
            }
            if (event.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    // Destinations come from the navigation of the signed-in role, so every result actually opens a page.
    const [navItems, setNavItems] = useState(() => (typeof window !== "undefined" && window.__cviNavItems) || []);
    useEffect(() => {
        const update = event => setNavItems(event.detail || []);
        window.addEventListener("cvi-nav-items", update);
        return () => window.removeEventListener("cvi-nav-items", update);
    }, []);
    const options = navItems.map(item => [item.id, translateCanadaEnglishUiText(item.label, language)]);
    const matching = options.filter(([id,label]) => (id+" "+label).toLowerCase().includes(query.toLowerCase().trim()));
    return <div style={{position:"relative",flex:"1 1 160px",maxWidth:300,minWidth:125}}>
        <input ref={ref} value={query} onFocus={()=>setOpen(true)} onBlur={()=>setTimeout(()=>setOpen(false),120)}
            onChange={e=>{setQuery(e.target.value);setOpen(true)}} onKeyDown={e=>{if(e.key==="Enter"&&matching[0]){window.dispatchEvent(new CustomEvent("cvi-quick-nav",{detail:{id:matching[0][0]}}));setOpen(false);setQuery("");e.currentTarget.blur();}}} aria-label={language==="fr"?"Rechercher une page":"Find a page"}
            placeholder={language==="fr"?"Rechercher…":"Find a page…"}
            style={{width:"100%",border:"1px solid #63709c",background:"#17214e",color:"#fff",borderRadius:9,padding:"8px 12px"}}/>
        {open&&query.trim()&&<div role="listbox" style={{position:"absolute",top:"100%",left:0,right:0,zIndex:150,background:"#fff",boxShadow:"0 10px 30px #0003",borderRadius:8}}>
            {matching.map(([id,label])=><button key={id} type="button" className="cvi-quick-search-option" style={{width:"100%",padding:"9px 12px",textAlign:"left",background:"transparent",border:0,color:"#18244d",cursor:"pointer"}}
              onMouseDown={e=>e.preventDefault()} onClick={()=>{window.dispatchEvent(new CustomEvent("cvi-quick-nav",{detail:{id}}));setOpen(false);setQuery("")}}>{label}</button>)}
            {matching.length===0&&<div style={{padding:10,color:"#555"}}>{language==="fr"?"Aucun résultat":"No results"}</div>}
        </div>}
    </div>;
}

function TopBar({ onBack, title, right, stepDots }) {
    const { orgSettings, activePeriod } = useData();
    const { t, language } = useI18n();
    const daysLeft = !onBack ? daysRemainingInPeriod(activePeriod) : null;
    const periodStatusText = daysLeft == null ? ""
        : language === "fr" ? (daysLeft > 0 ? `${daysLeft} jours restants` : daysLeft === 0 ? "dernier jour aujourd’hui" : "terminée")
            : (daysLeft > 0 ? `${daysLeft} days left` : daysLeft === 0 ? "last day today" : "ended");

    return (
        <div className="cvi-shell-header">
            <div className="cvi-brand">
                {onBack ? (
                    <>
                        <button className="cvi-icon-button" onClick={onBack} aria-label={t("common.back")} style={{ width: "auto", paddingInline: 11, fontSize: 12 }}>
                            ← {t("common.back")}
                        </button>
                        <span style={{ color: "#fff", fontSize: 14, fontWeight: 650, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</span>
                    </>
                ) : (
                    <>
                        {orgSettings?.logo ? (
                            <img className="cvi-brand-logo" src={orgSettings.logo} alt="CVI" />
                        ) : (
                            <div className="cvi-brand-mark">CVI</div>
                        )}
                        <div style={{ minWidth: 0 }}>
                            <div className="cvi-brand-title">{"Contribution Value Index"}</div>
                            <div className="cvi-brand-meta">{language === "fr" ? "Gestion de la performance" : "Performance management"}</div>
                        </div>
                        {orgSettings?.name && (
                            <div className="cvi-org-pill cvi-hide-xs" title={orgSettings.name}>
                                <UiIcon name="building" size={13} />
                                <span>{orgSettings.name}</span>
                            </div>
                        )}
                        {activePeriod && (
                            <div className="cvi-period-pill cvi-hide-xs">
                                <span aria-hidden="true">●</span>
                                <span>{localizePeriodLabel(activePeriod.label, language)}</span>
                                {daysLeft != null && <span>· {periodStatusText}</span>}
                            </div>
                        )}
                    </>
                )}
            </div>
            {!onBack && <HeaderQuickSearch />}
            <div className="cvi-header-actions">
                {stepDots && (
                    <div style={{ display: "flex", gap: 4 }}>
                        {[0, 1, 2].map(i => (
                            <div key={i} style={{
                                height: 6, borderRadius: 3,
                                background: i === stepDots - 1 ? C.cyan : i < stepDots - 1 ? "rgba(0,200,230,0.5)" : "rgba(255,255,255,0.2)",
                                width: i === stepDots - 1 ? 16 : 6, transition: "all 0.2s"
                            }} />
                        ))}
                    </div>
                )}
                <WorkflowCenterButton hidden={Boolean(onBack)} />
                {right}
            </div>
        </div>
    );
}

function SubBar({ title, onBack }) {
    const { t } = useI18n();
    const ref = useRef(null);
    // A drill-down opens at the top of the page, not at the scroll position of the previous view.
    useEffect(() => {
        const scroller = ref.current?.closest(".cvi-role-scroll, .cvi-admin-content");
        if (scroller) scroller.scrollTop = 0;
    }, [title]);
    return (
        <nav ref={ref} className="cvi-subbar" aria-label={title}>
            <button type="button" className="adm-btn adm-btn--sm" onClick={onBack}>← {t("common.back")}</button>
            <span className="cvi-subbar-sep" aria-hidden="true">/</span>
            <strong>{title}</strong>
        </nav>
    );
}

// ── APP LAYOUT (مشترک بین همه‌ی پنل‌ها — دسکتاپ: Sidebar / موبایل: BottomNav) ──
// items: [{ id, label, icon, badge? }]
// Publishes the signed-in role's destinations to the header quick search.
function usePublishNavItems(items) {
    const signature = items.map(item => `${item.id}:${item.label}:${item.disabled ? 1 : 0}`).join("|");
    useEffect(() => {
        const flat = items.flatMap(item => item.children?.length ? item.children : [item])
            .filter(item => !item.disabled && item.id !== "more")
            .map(({ id, label }) => ({ id, label }));
        window.__cviNavItems = flat;
        window.dispatchEvent(new CustomEvent("cvi-nav-items", { detail: flat }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);
}

function Sidebar({ items, nav, onNav, onLogout }) {
    const { language } = useI18n();
    usePublishNavItems(items);
    useEffect(() => {
        const navigate = event => {
            const target = items.find(item => item.id === event.detail?.id ||
               (event.detail?.id === "dashboard" && ["overview","my-performance"].includes(item.id)));
            if (target && !target.disabled) onNav(target.id);
        };
        window.addEventListener("cvi-quick-nav",navigate);
        return () => window.removeEventListener("cvi-quick-nav",navigate);
    }, [items,onNav]);
    const groupNames = {
        en: { overview: "Overview", performance: "Performance", workflow: "Evaluation & Reviews", people: "People", account: "Account" },
        fr: { overview: "Aperçu", performance: "Performance", workflow: "Évaluations et suivis", people: "Équipe", account: "Compte" },
    }[language] || { overview: "Overview", performance: "Performance", workflow: "Evaluation & Reviews", people: "People", account: "Account" };

    const classify = item => {
        if (item.section) return item.section;
        const id = String(item.id || "").toLowerCase();
        if (id.includes("dashboard") || id === "home" || id === "company" || id === "dept" || id === "overview") return "overview";
        if (id.includes("member") || id.includes("people") || id.includes("user")) return "people";
        if (id.includes("history") || id.includes("review") || id.includes("inbox")) return "workflow";
        if (id === "help") return "account";
        if (id.includes("profile") || id.includes("setting")) return "account";
        return "performance";
    };

    let groups;
    if (items.some(item => item.group)) {
        const seen = [];
        items.forEach(item => { if (item.group && !seen.includes(item.group)) seen.push(item.group); });
        groups = seen.map(label => ({ id: label, label, items: items.filter(item => item.group === label) }));
    } else {
        const order = ["overview", "performance", "workflow", "people", "account"];
        groups = order.map(id => ({ id, label: groupNames[id], items: items.filter(item => classify(item) === id) })).filter(group => group.items.length);
    }

    return (
        <aside className="cvi-sidebar" aria-label="Primary navigation">
            {groups.map(group => (
                <div className="cvi-sidebar-group" key={group.id}>
                    <div className="cvi-sidebar-label">{group.label}</div>
                    {group.items.map(item => <NavigationItem key={item.id} item={item} active={nav} onNav={onNav} />)}
                </div>
            ))}
            <div style={{ flex: 1, minHeight: 18 }} />
            <button onClick={onLogout} className="cvi-sidebar-logout"><UiIcon name="logout" size={16} />{language === "fr" ? "Déconnexion" : "Sign out"}</button>
        </aside>
    );
}

// items بدون آیتم خروج داده می‌شن — این کامپوننت خودش دکمه‌ی خروج رو به انتها اضافه می‌کنه
export function AppBottomNav({ items, active, onNav, onLogout }) {
    const [openMenu, setOpenMenu] = useState(null);
    usePublishNavItems(items);
    useEffect(() => {
        const navigate = event => {
            const target = items.find(item => item.id === event.detail?.id);
            if (target && !target.disabled) { setOpenMenu(null); onNav(target.id); }
        };
        window.addEventListener("cvi-quick-nav", navigate);
        return () => window.removeEventListener("cvi-quick-nav", navigate);
    }, [items, onNav]);
    const hierarchical = items.some(item => item.id === "tasks-activities");
    const performanceItems = items.filter(item => item.section === "performance");
    const primary = items.filter(item => ["dashboard", "company", "inbox"].includes(item.id));
    const moreItems = items.filter(item => !primary.includes(item) && !performanceItems.includes(item));
    const allItems = hierarchical ? [
        ...primary.slice(0, 1),
        { id: "performance-menu", label: "Performance", icon: "performance", children: performanceItems, badge: performanceItems.reduce((sum, item) => sum + (item.badge || 0), 0) },
        ...primary.slice(1),
        { id: "more-menu", label: "More", icon: "more", children: [...moreItems, { id: "logout", label: "Sign out", icon: "logout" }] },
    ] : [...items, { id: "logout", label: "Sign out", icon: "logout" }];
    const menu = allItems.find(item => item.id === openMenu);
    const navigate = id => { setOpenMenu(null); id === "logout" ? onLogout() : onNav(id); };
    return (
        <>
        {menu && <section className="cvi-mobile-navigation-panel" aria-label={menu.label}>
            <button type="button" className="cvi-navigation-close" onClick={() => setOpenMenu(null)}>Close</button>
            {menu.children.map(item => <NavigationItem key={item.id} item={item} active={active} onNav={navigate} />)}
        </section>}
        <nav className="cvi-bottom-nav" aria-label="Mobile navigation">
            {allItems.map(item => (
                <button key={item.id}
                    onClick={() => item.children ? setOpenMenu(openMenu === item.id ? null : item.id) : navigate(item.id)}
                    aria-expanded={item.children ? openMenu === item.id : undefined}
                    className={`cvi-bottom-nav-item${isNavigationActive(item, active) ? " is-active" : ""}`}
                    aria-current={active === item.id ? "page" : undefined}>
                    {item.badge > 0 && (
                        <span style={{
                            position: "absolute", top: 6, insetInlineEnd: "calc(50% - 18px)",
                            minWidth: 8, height: 8, background: C.red, borderRadius: 10, border: "2px solid #fff"
                        }} />
                    )}
                    <span className="cvi-bottom-nav-icon" aria-hidden="true"><UiIcon name={item.icon} size={19} /></span>
                    <span className="cvi-bottom-nav-label">{item.label}</span>
                </button>
            ))}
        </nav>
        </>
    );
}

// ── LOGIN PAGE (واحد برای همه نقش‌ها) ──────────────────────────────────────────
function LoginPage({ onLogin }) {
    const { t, language, setLanguage } = useI18n();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const selectLoginLanguage = (nextLanguage) => {
        if (!SUPPORTED_LANGUAGES[nextLanguage]) return;
        setLanguage(nextLanguage);
        try {
            localStorage.setItem("cvi_language", nextLanguage);
            sessionStorage.setItem("cvi_login_language", nextLanguage);
        } catch {}
    };

    const handle = async () => {
        if (loading) return;

        const cleanUsername = username.trim();
        if (!cleanUsername || !password) {
            setError(t("login.required"));
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username: cleanUsername, password }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                setError(response.status === 401 ? t("login.invalid") : t("login.failed"));
                return;
            }

            if (!data?.user || !data?.token) {
                setError(t("login.invalidResponse"));
                return;
            }

            const apiUser = data.user;
            const normalizedUser = {
                ...apiUser,
                role: String(apiUser.role || "").toLowerCase(),
                deptId: apiUser.department?.id ?? null,
                teamId: apiUser.team?.id ?? null,
                posId: apiUser.position?.id ?? null,
            };

            const resolvedLanguage = SUPPORTED_LANGUAGES[apiUser.preferredLanguage]
                ? apiUser.preferredLanguage
                : language;
            setLanguage(resolvedLanguage);
            normalizedUser.preferredLanguage = apiUser.preferredLanguage || resolvedLanguage;

            onLogin(normalizedUser, data.token);
        } catch (err) {
            console.error("Login request failed:", err);
            setError(t("login.connectionError"));
        } finally {
            setLoading(false);
        }
    };

    const isFrench = language === "fr";
    const heroTitle = isFrench
        ? "Votre impact va au-delà\nde vos résultats individuels."
        : "Your impact goes beyond\nindividual results.";
    const brandLabel = isFrench ? "Indice de valeur de contribution" : "Contribution Value Index";

    return (
        <main className="cvi-login-page">
            <style>{`
                html, body, #root {
                    height: 100%;
                    margin: 0;
                    overflow: hidden;
                }
                * { box-sizing: border-box; }
                .cvi-login-page {
                    height: 100vh;
                    overflow: hidden;
                    display: grid;
                    grid-template-columns: minmax(0, 1.05fr) minmax(440px, .95fr);
                    background: #fff;
                    color: #111827;
                    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
                    direction: inherit;
                }
                .cvi-login-brand-panel {
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    min-height: 100vh;
                    overflow: hidden;
                    padding: clamp(36px, 5vw, 72px);
                    overflow: hidden;
                    background:
                        radial-gradient(circle at 16% 12%, rgba(0,200,230,.11), transparent 32%),
                        linear-gradient(145deg, #F7FBFF 0%, #F8FAFC 56%, #F4F7FB 100%);
                    border-right: 1px solid #E8EDF4;
                }
                .cvi-login-brand-panel::after {
                    content: "";
                    position: absolute;
                    width: 420px;
                    height: 420px;
                    border-radius: 50%;
                    right: -210px;
                    bottom: -220px;
                    border: 1px solid rgba(0,0,100,.07);
                    box-shadow: 0 0 0 58px rgba(0,200,230,.025), 0 0 0 116px rgba(0,0,100,.018);
                    pointer-events: none;
                }
                .cvi-login-brand {
                    display: inline-flex;
                    align-items: center;
                    gap: 12px;
                    width: fit-content;
                    position: relative;
                    z-index: 1;
                }
                .cvi-login-logo {
                    width: 42px;
                    height: 42px;
                    display: grid;
                    place-items: center;
                    border-radius: 11px;
                    background: #000064;
                    color: #fff;
                    font-size: 13px;
                    font-weight: 800;
                    letter-spacing: .03em;
                    box-shadow: 0 8px 24px rgba(0,0,100,.12);
                }
                .cvi-login-brand-copy strong {
                    display: block;
                    color: #000064;
                    font-size: 15px;
                    font-weight: 700;
                    line-height: 1.2;
                }
                .cvi-login-brand-copy span {
                    display: block;
                    margin-top: 3px;
                    color: #778197;
                    font-size: 11px;
                    letter-spacing: .06em;
                    text-transform: uppercase;
                }
                .cvi-login-hero {
                    position: relative;
                    z-index: 1;
                    max-width: 500px;
                    margin: auto 0;
                    padding: 56px 0;
                }
                .cvi-login-eyebrow {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    color: #0B6F79;
                    font-size: 10.5px;
                    font-weight: 700;
                    letter-spacing: .10em;
                    text-transform: uppercase;
                    margin-bottom: 14px;
                }
                .cvi-login-eyebrow::before {
                    content: "";
                    width: 22px;
                    height: 2px;
                    border-radius: 99px;
                    background: #00C8E6;
                }
                .cvi-login-hero h1 {
                    margin: 0;
                    max-width: 450px;
                    color: #07184A;
                    font-size: clamp(28px, 2.25vw, 36px);
                    line-height: 1.18;
                    letter-spacing: -.022em;
                    font-weight: 650;
                    white-space: pre-line;
                    text-wrap: balance;
                }
                .cvi-login-hero p {
                    max-width: 500px;
                    margin: 20px 0 0;
                    color: #59677E;
                    font-size: 15px;
                    line-height: 1.65;
                }
                .cvi-login-brand-footer {
                    display: none;
                }
                .cvi-login-form-panel {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 100vh;
                    overflow: hidden;
                    padding: 48px 40px 32px;
                    background: #fff;
                }
                .cvi-login-form-wrap {
                    width: min(100%, 360px);
                }
                .cvi-login-form-top {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    min-height: 28px;
                    margin-bottom: 26px;
                }
                .cvi-login-language {
                    display: inline-flex;
                    align-items: center;
                    gap: 14px;
                    padding: 0;
                    border: 0;
                    background: transparent;
                }
                .cvi-login-language button {
                    min-width: auto;
                    height: auto;
                    padding: 0;
                    border: 0;
                    border-radius: 0;
                    background: transparent;
                    color: #98A2B3;
                    font: 600 12px/1 inherit;
                    cursor: pointer;
                    transition: color .15s ease;
                }
                .cvi-login-language button:hover {
                    color: #475467;
                }
                .cvi-login-language button.is-active {
                    color: #000064;
                    font-weight: 750;
                    box-shadow: none;
                }
                .cvi-login-form h2 {
                    margin: 0;
                    color: #101828;
                    font-size: 24px;
                    line-height: 1.2;
                    letter-spacing: -.025em;
                    font-weight: 700;
                }
                .cvi-login-form-subtitle {
                    margin: 8px 0 32px;
                    color: #7A8599;
                    font-size: 14px;
                    line-height: 1.55;
                }
                .cvi-login-field {
                    margin-bottom: 18px;
                }
                .cvi-login-field label {
                    display: block;
                    margin-bottom: 7px;
                    color: #344054;
                    font-size: 12px;
                    font-weight: 650;
                }
                .cvi-login-input {
                    box-sizing: border-box;
                    width: 100%;
                    height: 46px;
                    padding: 0 13px;
                    border: 1px solid #D8DEE8;
                    border-radius: 9px;
                    background: #fff;
                    color: #101828;
                    outline: none;
                    font: 400 14px/1 inherit;
                    transition: border-color .15s ease, box-shadow .15s ease;
                }
                .cvi-login-input::placeholder { color: #B0B8C6; }
                .cvi-login-input:focus {
                    border-color: #00AFC9;
                    box-shadow: 0 0 0 3px rgba(0,200,230,.10);
                }
                .cvi-login-error {
                    margin: -4px 0 14px;
                    padding: 9px 11px;
                    border-radius: 8px;
                    background: #FFF5F5;
                    color: #C33A3A;
                    font-size: 12px;
                    line-height: 1.45;
                }
                .cvi-login-submit {
                    width: 100%;
                    height: 46px;
                    margin-top: 4px;
                    border: 0;
                    border-radius: 9px;
                    background: #000064;
                    color: #fff;
                    font: 700 13px/1 inherit;
                    cursor: pointer;
                    transition: transform .15s ease, background .15s ease, box-shadow .15s ease;
                    box-shadow: 0 6px 18px rgba(0,0,100,.12);
                }
                .cvi-login-submit:hover:not(:disabled) {
                    background: #080878;
                    transform: translateY(-1px);
                    box-shadow: 0 8px 22px rgba(0,0,100,.16);
                }
                .cvi-login-submit:disabled {
                    opacity: .65;
                    cursor: wait;
                }
                .cvi-login-what {
                    margin-top: 22px;
                    text-align: center;
                }
                .cvi-login-what button {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 0;
                    border: 0;
                    background: transparent;
                    color: #667085;
                    font: 600 12px/1.2 inherit;
                    cursor: pointer;
                    transition: color .15s ease;
                }
                .cvi-login-what button:hover { color: #000064; }
                .cvi-login-what-icon {
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    display: inline-grid;
                    place-items: center;
                    background: #EEF4FF;
                    color: #000064;
                    font-size: 11px;
                    font-weight: 800;
                }
                @media (max-width: 900px) {
                    html, body, #root { overflow: auto; }
                    .cvi-login-page { grid-template-columns: 1fr; height: auto; min-height: 100vh; overflow: visible; }
                    .cvi-login-brand-panel {
                        min-height: auto;
                        padding: 28px 28px 34px;
                        border-right: 0;
                        border-bottom: 1px solid #E8EDF4;
                    }
                    .cvi-login-hero { padding: 56px 0 20px; }
                    .cvi-login-hero h1 { font-size: clamp(34px, 8vw, 48px); }
                    .cvi-login-brand-footer { display: none; }
                    .cvi-login-form-panel { min-height: auto; padding: 34px 24px 52px; }
                    .cvi-login-form-top { margin-bottom: 34px; }
                }
                @media (max-width: 560px) {
                    .cvi-login-brand-panel { padding: 24px 20px 28px; }
                    .cvi-login-brand-copy span { display: none; }
                    .cvi-login-hero { padding: 44px 0 10px; }
                    .cvi-login-hero h1 { font-size: 34px; }
                    .cvi-login-hero p { font-size: 14px; margin-top: 18px; }
                    .cvi-login-form-panel { padding: 28px 20px 44px; align-items: flex-start; }
                    .cvi-login-form-top { margin-bottom: 30px; }
                }
            `}</style>

            <section className="cvi-login-brand-panel" aria-label={brandLabel}>
                <div className="cvi-login-brand">
                    <div className="cvi-login-logo">CVI</div>
                    <div className="cvi-login-brand-copy">
                        <strong>CVI</strong>
                        <span>{brandLabel}</span>
                    </div>
                </div>

                <div className="cvi-login-hero">
                    <div className="cvi-login-eyebrow">
                        {isFrench ? "Performance et impact" : "Performance & impact"}
                    </div>
                    <h1>{heroTitle}</h1>
                </div>

                <div className="cvi-login-brand-footer">CVI · Contribution Value Index</div>
            </section>

            <section className="cvi-login-form-panel">
                <div className="cvi-login-form-wrap">
                    <div className="cvi-login-form-top">
                        <div className="cvi-login-language" aria-label={isFrench ? "Choix de langue" : "Language selection"}>
                            {[{ id: "en", label: "EN" }, { id: "fr", label: "FR" }].map(option => (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={language === option.id ? "is-active" : ""}
                                    aria-pressed={language === option.id}
                                    onClick={() => selectLoginLanguage(option.id)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="cvi-login-form">
                        <h2>{t("login.title")}</h2>
                        <p className="cvi-login-form-subtitle">{t("login.subtitle")}</p>

                        <div className="cvi-login-field">
                            <label htmlFor="cvi-login-username">{t("login.username")}</label>
                            <input
                                id="cvi-login-username"
                                className="cvi-login-input"
                                type="text"
                                autoComplete="username"
                                value={username}
                                onChange={e => { setUsername(e.target.value); setError(""); }}
                                placeholder={isFrench ? "nom@entreprise.ca ou nom d’utilisateur" : "name@company.ca or username"}
                                onKeyDown={e => e.key === "Enter" && handle()}
                            />
                        </div>

                        <div className="cvi-login-field">
                            <label htmlFor="cvi-login-password">{t("login.password")}</label>
                            <input
                                id="cvi-login-password"
                                className="cvi-login-input"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={e => { setPassword(e.target.value); setError(""); }}
                                placeholder="••••••••"
                                onKeyDown={e => e.key === "Enter" && handle()}
                            />
                        </div>

                        {error && <div className="cvi-login-error" role="alert">{error}</div>}

                        <button className="cvi-login-submit" onClick={handle} disabled={loading}>
                            {loading ? t("login.submitting") : t("login.submit")}
                        </button>

                        <div className="cvi-login-what">
                            <button
                                type="button"
                                onClick={() => {
                                    const overlay = document.querySelector("#cvi-login-guide-root .cvi-guide-overlay");
                                    if (overlay) {
                                        overlay.classList.add("is-open");
                                        document.body.style.overflow = "hidden";
                                        overlay.querySelector(".cvi-guide-close")?.focus();
                                    }
                                }}
                            >
                                <span className="cvi-login-what-icon">i</span>
                                <span>{isFrench ? "Qu’est-ce que le CVI?" : "What is CVI?"}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}

// The reviewer's note on a contribution, shown with who wrote it and when.
function ManagerFeedbackNote({ activity, compact = false }) {
    const { language } = useI18n();
    const f = workspaceCopy(language).feedback;
    if (!activity?.managerNote) return null;
    const source = [...(activity.history || [])].reverse().find(event => event.note && event.note === activity.managerNote)
        || [...(activity.history || [])].reverse().find(event => ["revision_requested", "approved", "rejected"].includes(event.type));
    const when = source?.createdAt || activity.revisionRequestedAt || activity.approvedAt || activity.rejectedAt || null;
    return <div data-cvi-i18n="managed" style={{ marginBottom: compact ? 0 : 12, marginTop: compact ? 10 : 0 }}>
        <FeedbackItem compact={compact} language={language} author={source?.actorName || f.fromManager}
            context={f.managerFeedback} date={when ? formatStamp(when, language, { time: !compact }) : null}
            status={activity.status} statusText={statusLabel(activity.status, language)}>
            <p>{activity.managerNote}</p>
        </FeedbackItem>
    </div>;
}

// ── ADDITIONAL CONTRIBUTIONS: new contribution, detail and editing ────────────────
// The direct manager is the only reviewer the server accepts, so it is the person a pending step waits on.
function contributionPeople(users, activity, user) {
    const ownerId = activity?.userId || user?.id;
    const owner = users.find(u => u.id === ownerId) || (user?.id === ownerId ? user : null);
    const manager = owner?.managerId ? users.find(u => u.id === owner.managerId) : null;
    return { ownerName: owner?.name || null, reviewerName: manager?.name || null, reviewerId: owner?.managerId || null };
}

// Evidence files of one contribution: protected download, and removal while it is editable.
function ContributionAttachments({ activity, canDelete = false }) {
    const { language } = useI18n();
    const c = contributionCopy(language);
    const [rows, setRows] = useState(activity?.attachments || []);
    const [busyId, setBusyId] = useState(null);
    useEffect(() => setRows(activity?.attachments || []), [activity?.attachments]);
    if (!activity?.id || !rows.length) return null;
    const path = (attachmentId, download = false) => `/api/activities/${activity.id}/files/${attachmentId}${download ? "/download" : ""}`;
    const remove = async attachment => {
        if (!canDelete || busyId) return;
        setBusyId(attachment.id);
        try {
            const token = sessionStorage.getItem("cvi_token");
            const response = await fetch(`${API_BASE_URL}${path(attachment.id)}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) throw new Error("delete failed");
            setRows(current => current.filter(item => item.id !== attachment.id));
        } catch (error) {
            console.error("Attachment delete failed:", error);
            uxNotify(c.form.errors.generic, "error");
        } finally { setBusyId(null); }
    };
    const size = bytes => bytes == null ? null : `${Math.max(1, Math.round(Number(bytes) / 1024))} ${language === "fr" ? "Ko" : "KB"}`;
    return <ul className="cvi-contrib-attachments">{rows.map(attachment => <li key={attachment.id}>
        <span className="cvi-contrib-file-icon" aria-hidden="true"><UiIcon name="paperclip" size={15} /></span>
        <div><strong>{attachment.fileName}</strong>{size(attachment.sizeBytes) && <small>{size(attachment.sizeBytes)}</small>}</div>
        <button type="button" className="adm-btn adm-btn--sm" onClick={() => protectedFileDownload(path(attachment.id, true), attachment.fileName).catch(() => uxNotify(c.detail.downloadFailed, "error"))}>
            {language === "fr" ? "Télécharger" : "Download"}</button>
        {canDelete && <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" disabled={busyId === attachment.id} onClick={() => remove(attachment)}>
            {c.form.upload.remove}</button>}
    </li>)}</ul>;
}

function NewContributionPage({ user, activePeriod, onClose, onOpenActivity }) {
    const { activityGroups, users, createActivityApi } = useData();
    const { language } = useI18n();
    const c = contributionCopy(language);
    const { reviewerName } = contributionPeople(users, null, user);
    if (!activePeriod) return <div className="adm-page cvi-contrib" data-cvi-i18n="managed" lang={language}>
        <nav className="cvi-contrib-crumbs"><button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" onClick={onClose}><UiIcon name="chevronLeft" size={14} />{c.common.back}</button></nav>
        <h1 className="adm-title">{c.form.title.create}</h1>
        <div className="adm-callout is-neutral" role="note"><UiIcon name="schedule" size={15} /><div>{c.form.errors.noPeriod}</div></div>
    </div>;
    const save = submit => async form => {
        const created = await createActivityApi(form, activePeriod.id, { submit });
        uxNotify(submit ? c.form.saved.submitted : c.form.saved.draft, "success");
        onOpenActivity(created);
    };
    return <ContributionForm mode="create" language={language} activityGroups={activityGroups} orgLabel={text => translateCanadaEnglishUiText(text, language)}
        periodLabel={localizePeriodLabel(activePeriod.label, language)} reviewerName={reviewerName}
        draftKey={`activity-draft-${user.id}`} onCancel={onClose} onSaveDraft={save(false)} onSubmit={save(true)}
        onError={error => {
            console.error("Contribution creation failed:", error);
            // The draft exists on the server: open it rather than keep a form that would create a second one.
            if (error?.activity) { uxNotify(c.form.errors.savedAsDraft, "error"); onOpenActivity(error.activity); }
        }} />;
}

function ContributionDetailPage({ activity, user, onBack, onChange }) {
    const { activityGroups, impactLevels, users, periods, updateActivityApi, submitActivityApi } = useData();
    const { language } = useI18n();
    const c = contributionCopy(language);
    const [mode, setMode] = useState(null);
    const orgLabel = text => translateCanadaEnglishUiText(text, language);
    const isOwner = activity.userId === user.id;
    const { ownerName, reviewerName, reviewerId } = contributionPeople(users, activity, user);
    const workflow = buildWorkflow(activity, { ownerName, reviewerName, reviewerId, currentUserId: user.id });
    const period = periods.find(p => p.id === activity.periodId);
    const periodLabel = period?.label ? localizePeriodLabel(period.label, language) : null;
    const editable = isOwner && [ACTIVITY_STATUS.DRAFT, ACTIVITY_STATUS.REVISION_REQUESTED].includes(activity.status);
    const attachments = <ContributionAttachments activity={activity} canDelete={editable} />;

    if (mode && editable) {
        const finish = (submit, message) => async form => {
            const updated = await updateActivityApi(activity.id, form, { submit, classification: mode === "draft" });
            onChange(updated);
            uxNotify(message, "success");
            setMode(null);
        };
        return <ContributionForm mode={mode} activity={activity} language={language} activityGroups={activityGroups} orgLabel={orgLabel}
            periodLabel={periodLabel} reviewerName={reviewerName} feedback={workflow.feedback}
            existingAttachments={(activity.attachments || []).length ? attachments : null}
            onCancel={() => setMode(null)}
            onSaveDraft={mode === "draft" ? finish(false, c.form.saved.draft) : null}
            onSubmit={finish(true, mode === "revise" ? c.form.saved.resubmitted : c.form.saved.submitted)}
            onError={error => { console.error("Contribution update failed:", error); if (error?.activity) onChange(error.activity); }} />;
    }
    return <ContributionDetail activity={activity} language={language} activityGroups={activityGroups} impactLevels={impactLevels} orgLabel={orgLabel}
        periodLabel={periodLabel} ownerName={ownerName} reviewerName={reviewerName} reviewerId={reviewerId} currentUserId={user.id} isOwner={isOwner}
        attachments={(activity.attachments || []).length ? attachments : null} onBack={onBack}
        onEdit={editable ? () => setMode("draft") : null}
        onRevise={editable ? () => setMode("revise") : null}
        onSubmitDraft={editable && activity.status === ACTIVITY_STATUS.DRAFT ? async () => {
            const updated = await submitActivityApi(activity.id);
            onChange(updated);
            uxNotify(c.form.saved.submitted, "success");
        } : null} />;
}

// ── SPECIALIST DETAIL VIEWS (جزئیات فعالیت / دوره / نمودار CVI — تمام‌صفحه با دکمه بازگشت) ──
function SpecialistDetailView({ view, setView, selectedAct, setSelectedAct,
    selectedPeriod, setSelectedPeriod, periodDetailOrigin, setPeriodDetailOrigin, myActs, allPeriods, activityGroups, impactLevels,
    user, positions, indicatorDefs, indicatorData, activePeriod,
    routineCtx, selectedOrgLevel }) {
    const { orgSettings } = useData();
    const [expandedGroupId, setExpandedGroupId] = useState(null);

    if (view === "period-detail" && selectedPeriod) {
        const periodActs = myActs.filter(a => a.periodId === selectedPeriod.periodId);
        const approvedActs = periodActs.filter(a => a.status === "approved");
        // Every score here comes from the period's server result (final snapshot or live calculation).
        // Missing values are shown as "—"; nothing is recalculated in the browser.
        const groupScoreMap = selectedPeriod.activityGroupScores || null;
        const groupRows = activityGroups.map(g => {
            const acts = approvedActs.filter(a => a.groupId === g.id);
            return {
                ...g,
                score: groupScoreMap ? cviValue(groupScoreMap[g.id]) ?? 0 : null,
                count: selectedPeriod.activityCounts?.[g.id] ?? acts.length
            };
        });
        const nonRoutine = cviValue(selectedPeriod.nonRoutine);
        const routine = cviValue(selectedPeriod.routine);
        const peerFeedback = cviValue(selectedPeriod.peerFeedback);
        const periodRoleWeights = positions.find(p => p.id === user.posId) || orgSettings;
        const periodIndex = allPeriods.findIndex(p => p.periodId === selectedPeriod.periodId);
        const previousPeriod = periodIndex > 0 ? allPeriods[periodIndex - 1] : null;
        const delta = previousPeriod && cviValue(previousPeriod.cvi) != null && cviValue(selectedPeriod.cvi) != null ? selectedPeriod.cvi - previousPeriod.cvi : null;
        // Snapshots store the server's question rows (text / normalizedScore); live entries use label / score.
        const routineCriteria = (selectedPeriod.routineCriteria || [])
            .map(c => ({ label: c.label ?? c.text, score: cviValue(c.score ?? c.normalizedScore), weight: c.weight }))
            .filter(c => c.score != null);
        const improvementItems = [
            ...groupRows.filter(g => g.score != null).map(g => ({ label: g.label, score: g.maxScore ? (g.score / g.maxScore) * 100 : 0 })),
            ...routineCriteria.map(c => ({ label: c.label, score: Number(c.score) }))
        ];
        const strongest = improvementItems.length
            ? improvementItems.reduce((a, b) => a.score > b.score ? a : b) : null;
        const weakest = improvementItems.length
            ? improvementItems.reduce((a, b) => a.score < b.score ? a : b) : null;
        const changeText = delta == null ? "First recorded period"
            : Math.abs(delta) < 0.05 ? "No change" : formatPointsChange(delta, "en");

        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "system-ui,sans-serif", direction: "inherit"
            }}>
                <TopBar onBack={() => { setView(periodDetailOrigin === "history" ? null : "cvi-detail"); setSelectedPeriod(null); }}
                    title={selectedPeriod.label} />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                        {[["CVI", formatScore(selectedPeriod.cvi, 1), C.cyan],
                        ["Individual Contribution", formatScore(selectedPeriod.individual, 0), C.navy],
                        ["Collective Contribution", formatScore(selectedPeriod.org, 1), C.purple]].map(([l, v, c]) => (
                            <div key={l} style={{
                                background: "#fff", border: `1px solid ${C.border}`,
                                borderRadius: 10, padding: "10px", textAlign: "center"
                            }}>
                                <div style={{ fontSize: 10, color: C.hint, marginBottom: 4 }}>{l}</div>
                                <div style={{ fontSize: 18, fontWeight: 500, color: c }}>{v}</div>
                            </div>
                        ))}
                    </div>

                    <Card style={{ marginBottom: 12 }}>
                        <div style={{ padding: 14 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                <span style={{ fontSize: 12, fontWeight: 500 }}>Compare with previous period</span>
                                <span style={{
                                    fontSize: 11, fontWeight: 500,
                                    color: delta == null ? C.muted : delta >= 0 ? C.teal : C.red
                                }}>{changeText}</span>
                            </div>
                            {previousPeriod && (
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                                    {[["CVI", selectedPeriod.cvi, previousPeriod.cvi],
                                    ["Individual", selectedPeriod.individual, previousPeriod.individual],
                                    ["Organization", selectedPeriod.org, previousPeriod.org]].map(([label, current, previous]) => (
                                        <div key={label} style={{ background: C.bg, borderRadius: 8, padding: 8, textAlign: "center" }}>
                                            <div style={{ fontSize: 9.5, color: C.hint }}>{label}</div>
                                            <div style={{ fontSize: 12, fontWeight: 500, marginTop: 3 }}>
                                                {formatScore(current, 1)}
                                                {cviValue(current) != null && cviValue(previous) != null && <span style={{ fontSize: 9.5, color: current >= previous ? C.teal : C.red, marginRight: 4 }}>
                                                    ({formatPointsChange(current - previous, "en")})
                                                </span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Individual Contribution breakdown</div>
                    <Card style={{ marginBottom: 12 }}>
                        <div style={{ padding: 14 }}>
                            {[["Responsibilities & Contributions", nonRoutine, selectedPeriod.weights?.nonRoutine ?? periodRoleWeights?.wNonRoutine, C.cyan],
                            ["Core Responsibilities", routine, selectedPeriod.weights?.routine ?? periodRoleWeights?.wRoutine, C.purple],
                            ["Peer Feedback", peerFeedback, selectedPeriod.weights?.peerFeedback ?? periodRoleWeights?.wPeerFeedback, C.amber]].map(([label, score, weight, color], i) => (
                                <div key={label} style={{ marginBottom: i < 2 ? 14 : 0 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                                        <span style={{ fontSize: 11.5, fontWeight: 500 }}>{label}</span>
                                        <span style={{ fontSize: 11, color }}>
                                            {score == null ? "Not assessed" : `${Number(score).toFixed(0)} out of 100`} · Weight {`${weight}%`}
                                        </span>
                                    </div>
                                    <MiniBar value={score ?? 0} color={color} height={6} />
                                </div>
                            ))}
                        </div>
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Additional Contributions by category</div>
                    <Card style={{ marginBottom: 12 }}>
                        <div style={{ padding: 14 }}>
                            {groupRows.map((g, i) => {
                                const pct = g.maxScore && g.score != null ? Math.min(100, (g.score / g.maxScore) * 100) : 0;
                                return (
                                    <div key={g.id} style={{ marginBottom: i < groupRows.length - 1 ? 13 : 0 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                                            <span style={{ fontSize: 11.5 }}>{g.icon} {g.label}</span>
                                            <span style={{ fontSize: 10.5, color: C.muted }}>
                                                {g.score == null ? "—" : g.score} out of {g.maxScore} · {g.count} contributions
                                            </span>
                                        </div>
                                        <MiniBar value={pct} color={pct >= 70 ? C.teal : pct >= 50 ? C.amber : C.red} height={5} />
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Core Responsibilities criteria</div>
                    <Card style={{ marginBottom: 12 }}>
                        {routineCriteria.length === 0 ? (
                            <div style={{ padding: 16, textAlign: "center", color: C.muted, fontSize: 11.5 }}>
                                No Core Responsibilities ratings have been recorded for this period
                            </div>
                        ) : routineCriteria.map((c, i) => (
                            <div key={`${c.label}-${i}`} style={{ padding: 14, borderTop: i > 0 ? `1px solid ${C.bg}` : "none" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                    <span style={{ fontSize: 11.5, fontWeight: 500 }}>{c.label}</span>
                                    <span style={{ fontSize: 10.5, color: c.score >= 70 ? C.teal : c.score >= 50 ? C.amber : C.red }}>
                                        {Number(c.score).toFixed(0)} out of 100 · Weight {`${c.weight}%`}
                                    </span>
                                </div>
                                <MiniBar value={c.score} color={c.score >= 70 ? C.teal : c.score >= 50 ? C.amber : C.red} height={5} />
                            </div>
                        ))}
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Period highlights</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                        <div style={{ background: C.tealLt, borderRadius: 10, padding: 12 }}>
                            <div style={{ fontSize: 10, color: C.teal, marginBottom: 4 }}>Strength</div>
                            <div style={{ fontSize: 11.5, fontWeight: 500 }}>{strongest?.label || "—"}</div>
                        </div>
                        <div style={{ background: "#FFF1F0", borderRadius: 10, padding: 12 }}>
                            <div style={{ fontSize: 10, color: C.red, marginBottom: 4 }}>Needs attention</div>
                            <div style={{ fontSize: 11.5, fontWeight: 500 }}>{weakest?.label || "—"}</div>
                        </div>
                    </div>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Contributions recorded in this period</div>
                    <Card>
                        {periodActs.length === 0 ? (
                            <div style={{ padding: 16, textAlign: "center", color: C.muted, fontSize: 12 }}>
                                Activity details for this period are not available
                            </div>
                        ) : periodActs.map((a, i) => (
                            <div key={a.id} onClick={() => { setSelectedAct(a); setView("act-detail"); }}
                                style={{
                                    display: "flex", justifyContent: "space-between", alignItems: "center",
                                    padding: "11px 14px", borderBottom: i < periodActs.length - 1 ? `1px solid ${C.bg}` : "none", cursor: "pointer"
                                }}>
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 500 }}>{a.title}</div>
                                    <div style={{ fontSize: 10, color: C.hint, marginTop: 1 }}>
                                        {groupLabel(activityGroups, a.groupId)} · Score: {a.score || 0}
                                    </div>
                                </div>
                                <StatusBadge status={a.status} />
                            </div>
                        ))}
                    </Card>
                </div>
            </div>
        );
    }

    if (view === "tasks-activities-detail") {
        const options = [
            { id: "routine-detail", title: "Core Responsibilities", desc: "View the evaluation details for your core responsibilities", color: C.purple },
            { id: "nonroutine-detail", title: "Additional Contributions", desc: "View the additional contributions recorded this period", color: C.cyan },
        ];
        return <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "system-ui,sans-serif", direction: "inherit" }}>
            <TopBar onBack={() => setView(null)} title="Responsibilities & Contributions" />
            <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>This section combines two components: core responsibilities and additional contributions.</div>
                <div style={{ display: "grid", gap: 10 }}>
                    {options.map(option => <button key={option.id} type="button" onClick={() => setView(option.id)} style={{ width: "100%", textAlign: "right", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 15, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ width: 10, height: 36, borderRadius: 6, background: option.color, flexShrink: 0 }} />
                        <span style={{ flex: 1 }}><strong style={{ display: "block", fontSize: 13, color: C.text, marginBottom: 4 }}>{option.title}</strong><span style={{ fontSize: 10.5, color: C.muted }}>{option.desc}</span></span>
                        <span style={{ color: C.hint, fontSize: 16 }}>←</span>
                    </button>)}
                </div>
            </div>
        </div>;
    }

    if (view === "cvi-detail") {
        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "system-ui,sans-serif", direction: "inherit"
            }}>
                <TopBar onBack={() => setView(null)} title="Period chart" />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <Card style={{ marginBottom: 12 }}>
                        <div style={{ padding: 14 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>CVI trend — recent periods</div>
                            <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                                {[[C.cyan, "CVI"], [C.navy, "Individual Contribution"], [C.purple, "Collective Contribution"]].map(([c, l]) => (
                                    <div key={l} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#666" }}>
                                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
                                        {l}
                                    </div>
                                ))}
                            </div>
                            <svg width="100%" viewBox="0 0 300 130" preserveAspectRatio="none" style={{ overflow: "visible" }}>
                                {[20, 55, 90, 125].map(y => (
                                    <line key={y} x1="0" y1={y} x2="300" y2={y} stroke={C.bg} strokeWidth="1" />
                                ))}
                                {allPeriods.map((p, i) => {
                                    const x = 40 + i * 110;
                                    const yi = (ind) => 125 - (ind / 100) * 105;
                                    return <g key={p.periodId}>
                                        {i > 0 && <>
                                            {cviValue(allPeriods[i - 1].individual) != null && cviValue(p.individual) != null && <line x1={40 + (i - 1) * 110} y1={yi(allPeriods[i - 1].individual)} x2={x} y2={yi(p.individual)}
                                                stroke={C.navy} strokeWidth="2" fill="none" />}
                                            {cviValue(allPeriods[i - 1].org) != null && cviValue(p.org) != null && <line x1={40 + (i - 1) * 110} y1={yi(allPeriods[i - 1].org)} x2={x} y2={yi(p.org)}
                                                stroke={C.purple} strokeWidth="2" strokeDasharray="5 3" fill="none" />}
                                            {cviValue(allPeriods[i - 1].cvi) != null && cviValue(p.cvi) != null && <line x1={40 + (i - 1) * 110} y1={yi(allPeriods[i - 1].cvi)} x2={x} y2={yi(p.cvi)}
                                                stroke={C.cyan} strokeWidth="2.5" fill="none" />}
                                        </>}
                                        {cviValue(p.cvi) != null && <circle cx={x} cy={yi(p.cvi)} r={i === allPeriods.length - 1 ? 5 : 4}
                                            fill={C.cyan} stroke="#fff" strokeWidth="2" />}
                                        {cviValue(p.individual) != null && <circle cx={x} cy={yi(p.individual)} r="3.5" fill={C.navy} stroke="#fff" strokeWidth="2" />}
                                        {cviValue(p.org) != null && <circle cx={x} cy={yi(p.org)} r="3.5" fill={C.purple} stroke="#fff" strokeWidth="2" />}
                                        {i === allPeriods.length - 1 && <>
                                            {cviValue(p.cvi) != null && <text x={x + 7} y={yi(p.cvi) + 4} fontSize="9" fill={C.cyan}>{formatScore(p.cvi, 1)}</text>}
                                            {cviValue(p.individual) != null && <text x={x + 7} y={yi(p.individual) + 4} fontSize="9" fill={C.navy}>{formatScore(p.individual, 0)}</text>}
                                            {cviValue(p.org) != null && <text x={x + 7} y={yi(p.org) + 4} fontSize="9" fill={C.purple}>{formatScore(p.org, 1)}</text>}
                                        </>}
                                    </g>;
                                })}
                            </svg>
                            <div style={{
                                display: "flex", justifyContent: "space-around",
                                marginTop: 8, fontSize: 9, color: C.hint
                            }}>
                                {allPeriods.map(p => (
                                    <span key={p.periodId} style={{ textAlign: "center" }}>{p.label}</span>
                                ))}
                            </div>
                        </div>
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Period details</div>
                    {allPeriods.map((p, i) => (
                        <div key={p.periodId} onClick={() => { setSelectedPeriod(p); setPeriodDetailOrigin?.("cvi-detail"); setView("period-detail"); }}
                            style={{ marginBottom: 8, cursor: "pointer" }}>
                            <Card>
                                <div style={{
                                    background: i === allPeriods.length - 1 ? C.navy : "#1a1a4a",
                                    padding: "10px 14px", display: "flex", justifyContent: "space-between"
                                }}>
                                    <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>
                                        {p.label}
                                        {i === allPeriods.length - 1 && (
                                            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginRight: 6 }}>Current</span>
                                        )}
                                    </span>
                                    <span style={{ color: C.cyan, fontSize: 16, fontWeight: 500 }}>{formatScore(p.cvi, 1)}</span>
                                </div>
                                <div style={{ padding: "0 14px" }}>
                                    {[["Individual score", formatScore(p.individual, 0)], ["Collective score", formatScore(p.org, 1)]].map(([l, v]) => (
                                        <div key={l} style={{
                                            display: "flex", justifyContent: "space-between",
                                            padding: "8px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 12
                                        }}>
                                            <span style={{ color: C.muted }}>{l}</span>
                                            <span style={{ fontWeight: 500 }}>{v}</span>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (view === "nonroutine-detail") {
        const approvedActs = myActs.filter(a => a.status === "approved");
        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "system-ui,sans-serif", direction: "inherit"
            }}>
                <TopBar onBack={() => setView(null)} title="Additional contributions — category details" />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 14, lineHeight: 1.7 }}>
                        The additional contributions score is built from your approved contributions, grouped by category. Select a category to see its contributions.
                    </div>
                    {activityGroups.map(g => {
                        const groupActs = approvedActs.filter(a => a.groupId === g.id);
                        const earned = groupActs.reduce((s, a) => s + a.score, 0);
                        const capped = Math.min(earned, g.maxScore);
                        const isOpen = expandedGroupId === g.id;
                        return (
                            <Card key={g.id} style={{ marginBottom: 10 }}>
                                <div onClick={() => setExpandedGroupId(isOpen ? null : g.id)}
                                    style={{ padding: 14, cursor: "pointer" }}>
                                    <div style={{
                                        display: "flex", justifyContent: "space-between",
                                        alignItems: "center", marginBottom: 7
                                    }}>
                                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                                            {g.icon} {g.label}
                                        </span>
                                        <span style={{ fontSize: 14, fontWeight: 600, color: C.cyan }}>
                                            {capped} / {g.maxScore}
                                        </span>
                                    </div>
                                    <MiniBar value={capped} max={g.maxScore} color={C.cyan} height={6} />
                                    <div style={{ fontSize: 10.5, color: C.hint, marginTop: 8 }}>
                                        {groupActs.length} approved contributions {isOpen ? "▲" : "▼"}
                                    </div>
                                </div>
                                {isOpen && (
                                    <div style={{ borderTop: `1px solid ${C.bg}` }}>
                                        {groupActs.length === 0 ? (
                                            <div style={{ padding: 14, textAlign: "center", color: C.muted, fontSize: 11.5 }}>
                                                No approved activities have been recorded in this group
                                            </div>
                                        ) : groupActs.map((a, i) => (
                                            <div key={a.id} onClick={() => { setSelectedAct(a); setView("act-detail"); }}
                                                style={{
                                                    display: "flex", justifyContent: "space-between", alignItems: "center",
                                                    padding: "10px 14px", borderTop: i > 0 ? `1px solid ${C.bg}` : "none", cursor: "pointer"
                                                }}>
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 500 }}>{a.title}</div>
                                                    <div style={{ fontSize: 10, color: C.hint, marginTop: 1 }}>{a.sub}</div>
                                                </div>
                                                <Badge color="cyan" small>{a.score} Score</Badge>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (view === "routine-detail") {
        let scopedQuestions, scopedAnswers, evaluatorLabel;
        if (user.role === "manager") {
            scopedQuestions = routineCtx.deptRoutineQuestions.filter(q => q.deptId === user.deptId);
            scopedAnswers = routineCtx.deptRoutineAnswers;
            evaluatorLabel = "Business Unit Lead";
        } else if (user.role === "deputy") {
            scopedQuestions = routineCtx.orgRoutineQuestions;
            scopedAnswers = routineCtx.orgRoutineAnswers;
            evaluatorLabel = "Executive";
        } else {
            scopedQuestions = routineCtx.routineQuestions.filter(q => q.teamId === user.teamId);
            scopedAnswers = routineCtx.routineAnswers;
            evaluatorLabel = "Manager";
        }
        const routineLevels = routineCtx.routineLevels;
        const byMonth = { 1: [], 2: [], 3: [] };
        scopedQuestions.forEach(q => byMonth[q.month]?.push(q));
        const answerFor = (qId) => scopedAnswers.find(a => a.questionId === qId && a.userId === user.id && a.periodId === activePeriod?.id);
        const maxLevelScore = routineLevels.length ? Math.max(...routineLevels.map(l => l.score)) : 0;

        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "system-ui,sans-serif", direction: "inherit"
            }}>
                <TopBar onBack={() => setView(null)} title="Core responsibilities — question details" />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 14, lineHeight: 1.7 }}>
                        These questions cover your recurring core responsibilities and are answered by your {evaluatorLabel} at the end of each month.
                    </div>
                    {scopedQuestions.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                            No Core Responsibilities criteria have been defined for you yet.
                        </div>
                    ) : [1, 2, 3].map(month => byMonth[month].length > 0 && (
                        <div key={month} style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>
                                {month === 1 ? "Month 1" : month === 2 ? "Month 2" : "Month 3"}
                            </div>
                            <Card>
                                {byMonth[month].map((q, i) => {
                                    const ans = answerFor(q.id);
                                    const lvl = ans ? routineLevels.find(l => l.id === ans.levelId) : null;
                                    const pct = lvl && maxLevelScore ? Math.min(100, (lvl.score / maxLevelScore) * 100) : 0;
                                    return (
                                        <div key={q.id} style={{
                                            padding: 14,
                                            borderTop: i > 0 ? `1px solid ${C.bg}` : "none"
                                        }}>
                                            <div style={{
                                                display: "flex", justifyContent: "space-between",
                                                alignItems: "flex-start", gap: 8, marginBottom: 8
                                            }}>
                                                <span style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.6 }}>{q.label}</span>
                                                <Badge color="gray" small>Weight {`${q.weight}%`}</Badge>
                                            </div>
                                            {lvl ? (
                                                <>
                                                    <div style={{
                                                        display: "flex", justifyContent: "space-between",
                                                        fontSize: 11, marginBottom: 5
                                                    }}>
                                                        <span style={{ color: C.purple, fontWeight: 500 }}>{lvl.label}</span>
                                                        <span style={{ color: C.muted }}>{pct.toFixed(0)} / 100</span>
                                                    </div>
                                                    <MiniBar value={pct} color={C.purple} height={5} />
                                                </>
                                            ) : (
                                                <div style={{ fontSize: 11, color: C.hint }}>Not yet answered by {evaluatorLabel}</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </Card>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (view === "org-level-detail") {
        const level = selectedOrgLevel;
        const scopeId = level === "team" ? user.teamId : level === "dept" ? user.deptId : null;
        const defs = indicatorDefs.filter(i => {
            if (i.level !== level) return false;
            if (level === "org") return true;
            if (level === "team") return i.teamId === scopeId;
            if (level === "dept") return i.deptId === scopeId;
            return false;
        });
        const withData = withPeriodData(defs, indicatorData, activePeriod?.id);
        const titleMap = { org: "Organization indicators", dept: "Business Unit indicators", team: "Team indicators" };
        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "system-ui,sans-serif", direction: "inherit"
            }}>
                <TopBar onBack={() => setView(null)} title={titleMap[level] || "Indicators"} />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    {withData.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                            No indicators have been defined for this level.
                        </div>
                    ) : (
                        <Card>
                            {withData.map((ind, i) => {
                                const pct = ind.target ? Math.min(100, (ind.actual / ind.target) * 100) : null;
                                return (
                                    <div key={ind.id} style={{
                                        padding: 14,
                                        borderTop: i > 0 ? `1px solid ${C.bg}` : "none"
                                    }}>
                                        <div style={{
                                            display: "flex", justifyContent: "space-between",
                                            alignItems: "flex-start", gap: 8, marginBottom: 8
                                        }}>
                                            <span style={{ fontSize: 12, fontWeight: 500 }}>{ind.label}</span>
                                            <Badge color="gray" small>Weight {`${ind.weight}%`}</Badge>
                                        </div>
                                        <div style={{
                                            display: "flex", justifyContent: "space-between",
                                            fontSize: 11, marginBottom: 5
                                        }}>
                                            <span style={{ color: C.muted }}>Target: {ind.target || "—"} · Actual: {ind.actual || "—"}</span>
                                            <span style={{ color: pct == null ? C.hint : C.teal, fontWeight: 500 }}>
                                                {pct == null ? "No data" : `${pct.toFixed(0)}%`}
                                            </span>
                                        </div>
                                        <MiniBar value={pct ?? 0} color={C.teal} height={5} />
                                    </div>
                                );
                            })}
                        </Card>
                    )}
                </div>
            </div>
        );
    }

    return null;
}

function PerformanceTrendChart({ periods = [], compact = false, onOpenHistory = null, locale = "fa", embedded = false }) {
    const { language } = useI18n();
    const [hoverIndex, setHoverIndex] = useState(null);
    const visible = (periods || []).filter(p => cviValue(p?.cvi) != null).slice(compact ? -4 : -8);
    const en = locale === "en";
    const periodLabel = period => localizePeriodLabel(period?.label, language);
    const fr = language === "fr";
    const hasProvisional = visible.some(period => period.provisional);
    if (!visible.length) return <Card><div className="cvi-trend-empty">{"Trend data will appear after the first completed period."}</div></Card>;

    const width = 760, height = compact ? 210 : 250;
    const pad = { left: 44, right: 36, top: 22, bottom: 40 };
    const chartW = width - pad.left - pad.right;
    const chartH = height - pad.top - pad.bottom;
    const x = i => visible.length === 1 ? pad.left + chartW / 2 : pad.left + (i / (visible.length - 1)) * chartW;
    const y = value => pad.top + (1 - Math.max(0, Math.min(100, Number(value) || 0)) / 100) * chartH;
    // Finalized cycles are drawn solid; the step into a provisional (active) cycle is dashed.
    const lastFinal = visible.reduce((acc, period, i) => (period.provisional ? acc : i), -1);
    const segments = (key, from, to) => consecutiveScoreRuns(visible.slice(from, to + 1), key)
        .map(run => run.map(point => `${x(from + point.index)},${y(point.value)}`).join(' '));
    const line = (key, cls) => <g key={key}>
        {lastFinal >= 1 && segments(key, 0, lastFinal).map((points, i) =>
            <polyline key={`final-${i}`} points={points} className={`cvi-trend-line ${cls}`} />)}
        {lastFinal >= 0 && lastFinal < visible.length - 1 && segments(key, lastFinal, visible.length - 1).map((points, i) =>
            <polyline key={`provisional-${i}`} points={points} className={`cvi-trend-line ${cls} is-provisional`} />)}
        {lastFinal < 0 && visible.length > 1 && segments(key, 0, visible.length - 1).map((points, i) =>
            <polyline key={`active-${i}`} points={points} className={`cvi-trend-line ${cls} is-provisional`} />)}
    </g>;
    const latest = visible[visible.length - 1];
    const previous = visible.length > 1 ? visible[visible.length - 2] : null;
    const delta = previous ? Number(latest.cvi) - Number(previous.cvi) : null;
    const latestProvisional = Boolean(latest.provisional);
    const finalized = visible.filter(period => !period.provisional);
    const best = (finalized.length ? finalized : visible).reduce((a, b) => Number(a.cvi) >= Number(b.cvi) ? a : b);
    const ticks = [100, 75, 50, 25, 0];
    const text = fr
        ? { current: latestProvisional ? "Plus récente · provisoire" : "Dernière disponible", change: "Variation", pending: "À la finalisation", best: "Meilleure période finalisée" }
        : { current: latestProvisional ? "Latest · provisional" : "Latest available", change: "Change", pending: "After finalization", best: "Best finalized period" };
    // A provisional result reflects only the components assessed so far; comparing it with a
    // finalized period would show a change that does not exist. The change appears once locked.
    const deltaClass = delta == null || latestProvisional ? '' : delta >= 0 ? 'is-up' : 'is-down';
    const deltaText = latestProvisional ? text.pending : delta == null ? '—' : formatPointsChange(delta, fr ? "fr" : "en");
    const hovered = hoverIndex != null ? visible[hoverIndex] : null;
    const hoverLabels = fr ? ["CVI", "Contribution individuelle", "Contribution collective"] : ["CVI", "Individual Contribution", "Collective Contribution"];
    const hoverRows = hovered ? [
        { label: hoverLabels[0], value: hovered.cvi, color: C.navy },
        { label: hoverLabels[1], value: hovered.individual, color: C.cyan },
        { label: hoverLabels[2], value: hovered.org, color: C.purple },
    ] : [];
    const pointTitle = period => `${periodLabel(period)}${period.provisional ? (fr ? " (provisoire)" : " (provisional)") : ""}: CVI ${Number(period.cvi).toFixed(1)} · ${fr ? "Individuelle" : "Individual"} ${formatScore(period.individual, 1)} · ${fr ? "Collective" : "Collective"} ${formatScore(period.org, 1)}`;

    return <Card>
        <div className="cvi-trend-card">
            <div className="cvi-trend-head">
                {!embedded && <div>
                    <div className="cvi-trend-title">{"CVI trend"}</div>
                    <div className="cvi-trend-subtitle">{compact ? "CVI across the last four periods" : "CVI, Individual Contribution, and Collective Contribution across recent periods"}</div>
                </div>}
                <div className="cvi-trend-summary" data-cvi-i18n={en ? "managed" : undefined}>
                    <div><span>{text.current}</span><strong>{Number(latest.cvi).toFixed(1)}</strong></div>
                    <div><span>{text.change}</span><strong className={deltaClass}>{deltaText}</strong></div>
                    {!compact && <div><span>{text.best}</span><strong>{periodLabel(best)}</strong></div>}
                </div>
            </div>
            <div className="cvi-trend-chart-wrap" style={{ position: "relative" }}>
                <svg className="cvi-trend-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={fr ? "Évolution du CVI par période" : "CVI score trend across periods"}>
                    {ticks.map(t => <g key={t}><line x1={pad.left} x2={width-pad.right} y1={y(t)} y2={y(t)} className="cvi-trend-gridline"/><text x={pad.left-12} y={y(t)+4} textAnchor="end" className="cvi-trend-axis-label">{t}</text></g>)}
                    {!compact && line('individual', 'individual')}
                    {!compact && line('org', 'strategic')}
                    {line('cvi', 'cvi')}
                    {hovered && <line x1={x(hoverIndex)} x2={x(hoverIndex)} y1={pad.top} y2={height - pad.bottom} stroke={C.navy} strokeWidth="1" strokeDasharray="4 5" opacity=".38" pointerEvents="none" />}
                    {visible.map((period, i) => <g key={period.periodId || i} className="cvi-trend-point">
                        {!compact && cviValue(period.individual) != null && <circle cx={x(i)} cy={y(period.individual)} r="3.5" className={`cvi-trend-dot individual${period.provisional ? " is-provisional" : ""}`}/>}
                        {!compact && cviValue(period.org) != null && <circle cx={x(i)} cy={y(period.org)} r="3.5" className={`cvi-trend-dot strategic${period.provisional ? " is-provisional" : ""}`}/>}
                        <circle cx={x(i)} cy={y(period.cvi)} r={hoverIndex === i ? 7 : compact ? 5 : 5.5} className={`cvi-trend-dot cvi${period.provisional ? " is-provisional" : ""}`}><title>{pointTitle(period)}</title></circle>
                        {i === visible.length - 1 && <text x={x(i)} y={Math.max(pad.top + 4, y(period.cvi) - 12)} textAnchor="middle" className="cvi-trend-value-label">{Number(period.cvi).toFixed(1)}</text>}
                        <text x={x(i)} y={height-14} textAnchor="middle" className={`cvi-trend-period-label${period.provisional ? " is-provisional" : ""}`}>{periodLabel(period)}{period.provisional ? " *" : ""}</text>
                    </g>)}
                    {visible.map((period, i) => {
                        const step = visible.length > 1 ? chartW / (visible.length - 1) : chartW;
                        const bandStart = Math.max(pad.left, x(i) - step / 2);
                        const bandEnd = Math.min(width - pad.right, x(i) + step / 2);
                        return <rect key={`period-hit-${period.periodId || i}`} x={bandStart} y={pad.top} width={bandEnd - bandStart} height={chartH + 22}
                            fill="transparent" role="button" tabIndex={0}
                            aria-label={pointTitle(period)}
                            onMouseEnter={() => setHoverIndex(i)}
                            onMouseLeave={() => setHoverIndex(null)}
                            onFocus={() => setHoverIndex(i)}
                            onBlur={() => setHoverIndex(null)}
                            onClick={() => setHoverIndex(current => current === i ? null : i)}
                            onKeyDown={event => { if (event.key === "Escape") setHoverIndex(null); if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setHoverIndex(current => current === i ? null : i); } }}
                            style={{ cursor: "pointer", outlineOffset: "-3px" }} />;
                    })}
                </svg>
                {hovered && <div role="tooltip" data-cvi-i18n="managed"
                    style={{
                        position: "absolute", zIndex: 5, top: 9,
                        left: `${Math.min(79, Math.max(21, x(hoverIndex) / width * 100))}%`,
                        transform: "translateX(-50%)", pointerEvents: "none",
                        background: "#fff", color: C.navy, border: `1px solid ${C.border}`,
                        boxShadow: "0 10px 28px rgba(14, 24, 67, .16)",
                        borderRadius: 9, padding: "9px 11px", width: 205, maxWidth: "80%",
                        fontSize: 11, lineHeight: 1.3
                    }}>
                    <div style={{ fontWeight: 750, fontSize: 12, marginBottom: 2 }}>{periodLabel(hovered)}</div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>
                        {hovered.provisional ? (fr ? "Résultat provisoire" : "Provisional result") : (fr ? "Résultat finalisé" : "Finalized result")}
                    </div>
                    {hoverRows.map(row => <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between", padding: "3px 0" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6, color: C.muted }}>
                            <i aria-hidden="true" style={{ display: "inline-block", background: row.color, height: 8, width: 8, borderRadius: "50%", flexShrink: 0 }} />{row.label}
                        </span>
                        <strong style={{ marginLeft: 8, whiteSpace: "nowrap" }}>{formatScore(cviValue(row.value), 1)}</strong>
                    </div>)}
                </div>}
            </div>
            <div className="cvi-trend-footer">
                <div className="cvi-trend-legend"><span><i className="cvi"/>CVI</span>{!compact && <><span><i className="individual"/>{"Individual Contribution"}</span><span><i className="strategic"/>{"Collective Contribution"}</span></>}</div>
                {hasProvisional && <span className="cvi-trend-provisional-note" data-cvi-i18n="managed"><i aria-hidden="true" />{fr ? "* Période en cours : résultat provisoire, mis à jour jusqu’à la finalisation" : "* Current period: provisional result, updates until the period is finalized"}</span>}
                {onOpenHistory && <button className="cvi-dashboard-text-action" onClick={onOpenHistory}>{"View period history →"}</button>}
            </div>
        </div>
    </Card>;
}

// ── SPECIALIST TABS (محتوای هر تب — TopBar/BottomNav در پوسته‌ی اصلی‌ان) ──
function SpecialistHome({ user, myActs, allPeriods, indivScore, nonRoutineScore, routineScore,
    orgScore, orgBreakdown, cviScore, team, pos, activityGroups, setView, setSelectedAct, setNav,
    setSelectedOrgLevel }) {
    const approved = myActs.filter(a => a.status === ACTIVITY_STATUS.APPROVED || a.status === "approved").length;
    const needsAction = myActs.filter(a => a.status === ACTIVITY_STATUS.REVISION_REQUESTED || a.status === "revision_requested").length;
    return (
        <div className="cvi-dashboard-page">
            <DashboardPageIntro title={user.name}
                subtitle={`${pos?.label || "—"} · ${team?.label || "—"}`}
                eyebrow="My dashboard" />

            <ScoreOverview user={user} cviScore={cviScore} indivScore={indivScore}
                nonRoutineScore={nonRoutineScore} routineScore={routineScore}
                orgScore={orgScore} orgBreakdown={orgBreakdown}
                onOpenNonRoutine={() => setView("nonroutine-detail")}
                onOpenRoutine={() => setView("routine-detail")}
                onOpenTasksActivities={() => setView("tasks-activities-detail")}
                onOpenGoals={() => setNav("goals")}
                onOpenPeerFeedback={() => setNav("peer-feedback")}
                onOpenOrgLevel={(level) => { setSelectedOrgLevel(level); setView("org-level-detail"); }} />

            <DashboardSection title="Performance trend">
                <PerformanceTrendChart periods={allPeriods} onOpenHistory={() => setNav("history")} />
            </DashboardSection>

            <div className="cvi-dashboard-kpi-grid compact">
                <DashboardMetricCard label="My contributions" value={myActs.length} helper={`Approved: ${approved}`} tone="navy" icon={<UiIcon name="performance" size={16} />} onClick={() => setNav("activities")} />
                <DashboardMetricCard label="Action Required" value={needsAction} helper={needsAction ? "contributions need changes" : "Nothing to follow up"} tone={needsAction ? "amber" : "teal"} icon={<UiIcon name="warning" size={16} />} onClick={() => setNav("activities")} />
            </div>

            <div className="cvi-dashboard-quick-actions">
                <DashboardLinkButton primary onClick={() => setNav("new")}>Log a contribution</DashboardLinkButton>
            </div>

            <DashboardSection title="Recent contributions"
                action={<button className="cvi-dashboard-text-action" onClick={() => setNav("activities")}>View all</button>}>
                <Card>
                    {myActs.length === 0 ? (
                        <div className="cvi-dashboard-empty">You have not recorded any contributions yet</div>
                    ) : myActs.slice(0, 4).map((a, i, arr) => {
                        const group = activityGroups.find(g => g.id === a.groupId);
                        return (
                            <button type="button" key={a.id} onClick={() => { setSelectedAct(a); setView("act-detail"); }}
                                className="cvi-dashboard-list-row" style={{ borderBottom: i < arr.length - 1 ? `1px solid ${C.bg}` : "none" }}>
                                <span className="cvi-dashboard-row-icon" aria-hidden="true"><UiIcon name="activity" size={17} /></span>
                                <span className="cvi-dashboard-row-main">
                                    <strong>{a.title}</strong><small>{group?.label || "—"} · {displayDate(a.date)}</small>
                                </span>
                                <StatusBadge status={a.status} />
                            </button>
                        );
                    })}
                </Card>
            </DashboardSection>
        </div>
    );
}

export function TasksActivitiesPage({ nav, setNav, routineContent, activityContent }) {
    const tab = nav === "activities" ? "activities" : "self-assessment";
    return <section className="cvi-tasks-activities-page adm-page">
        <AdminPageHeader title="Responsibilities & Contributions"
            description="Rate your core responsibilities each month and record the additional contributions you make beyond your role." />
        <ProfessionalTabs items={[
            { id: "self-assessment", label: "Core Responsibilities" },
            { id: "activities", label: "Additional Contributions" },
        ]} value={tab} onChange={setNav} ariaLabel="Responsibilities & Contributions" />
        <div role="tabpanel" aria-label={tab === "activities" ? "Additional Contributions" : "Core Responsibilities"}>
            {tab === "activities" ? activityContent : routineContent}
        </div>
    </section>;
}

function SpecialistActivities({ myActs, activityGroups, setNav, setSelectedAct, setView }) {
    const open = activity => { setSelectedAct(activity); setView("act-detail"); };
    return <AdminPanel title="Additional Contributions" description="Achievements, projects and initiatives beyond your core responsibilities. Your manager reviews each submission."
        actions={myActs.length > 0 && <button type="button" className="adm-btn adm-btn--primary" onClick={() => setNav("new")}><UiIcon name="plus" size={15} />Log a contribution</button>} flush>
        {myActs.length === 0 ? <AdminEmpty icon="activity" title="You have not recorded any additional contributions yet."
            description="Log a project, improvement, learning activity or support you provided. Your manager reviews it and it counts toward Individual Contribution."
            action={<button type="button" className="adm-btn adm-btn--primary" onClick={() => setNav("new")}><UiIcon name="plus" size={15} />Log a contribution</button>} />
            : <ul className="adm-list">{myActs.map(a => <li key={a.id}>
                <button type="button" className="adm-list-item" onClick={() => open(a)} style={{ gridTemplateColumns: "minmax(0,1fr) auto auto" }}>
                    <span>
                        <strong>{a.title}</strong>
                        <small>{[groupLabel(activityGroups, a.groupId), a.sub, a.status === ACTIVITY_STATUS.DRAFT ? "Draft · not submitted" : displayDate(a.date)].filter(Boolean).join(" · ")}</small>
                        {a.status === ACTIVITY_STATUS.REVISION_REQUESTED && <ManagerFeedbackNote activity={a} compact />}
                    </span>
                    <StatusBadge status={a.status} />
                    <span className="adm-list-go" aria-hidden="true"><UiIcon name="chevronDown" size={15} /></span>
                </button>
            </li>)}</ul>}
    </AdminPanel>;
}

function SpecialistHistory({ allPeriods, user, setView, setSelectedPeriod, setPeriodDetailOrigin }) {
    const openSummary = (period) => {
        setSelectedPeriod(period);
        setPeriodDetailOrigin("history");
        setView("period-detail");
    };
    const { language } = useI18n();
    return <HistoryArchivePage allPeriods={allPeriods} user={user} onOpenSummary={openSummary} language={language} />;
}

// پروفایل عمومی — برای همه‌ی نقش‌ها (کارشناس/مدیر/معاون/مدیرعامل/ادمین) یکسان است
export function ProfileTabs({ header, personal, organization, account, preferences }) {
    const [tab, setTab] = useState("personal");
    const tabs = [
        { id: "personal", label: "Personal details" },
        { id: "organization", label: "Organization" },
        { id: "account", label: "Account" },
        { id: "preferences", label: "Preferences" },
    ];
    const content = { personal, organization, account, preferences };
    return <div id="cvi-profile" className="cvi-profile-page">
        <div className="cvi-profile-content">
            <div className="cvi-profile-intro"><h1>Profile and settings</h1><p>Your personal details, account and preferences</p></div>
            <div className="cvi-profile-header">{header}</div>
            <ProfessionalTabs items={tabs} value={tab} onChange={setTab} ariaLabel="Profile and settings" />
            <div role="tabpanel" aria-label={tabs.find(item => item.id === tab).label} className="cvi-profile-panel">
                {content[tab]}
            </div>
        </div>
    </div>;
}

function PersonProfile({ user }) {
    const { users, setUsers, teams, positions, departments, orgSettings } = useData();
    const { language, languages, t } = useI18n();
    // The directory record can be a reduced view for non-admin roles; the signed-in account fills the gaps.
    const directoryUser = users.find(u => u.id === user.id) || {};
    const profileUser = { ...user, ...Object.fromEntries(Object.entries(directoryUser).filter(([, value]) => value != null && value !== "")) };
    const team = teams.find(t => t.id === profileUser.teamId);
    const pos = positions.find(p => p.id === profileUser.posId);
    const dept = departments.find(d => d.id === (team?.deptId ?? profileUser.deptId));
    const orgUnit = profileUser.orgUnit || null;
    const roleLabelMap = { specialist: "Employee", manager: "Manager", deputy: "Director", ceo: "Executive", admin: "Administrator" };

    const rows = [
        ["Employee ID", profileUser.employeeCode || "—"],
        ...(profileUser.nationalId ? [["Internal ID", profileUser.nationalId]] : []),
        ["Email", profileUser.email || "—"],
        ["Username", profileUser.username || "—"],
        ["Access Level", roleLabelMap[profileUser.role] || profileUser.role || "—"],
        ["Position", pos?.label || profileUser.position?.name || "—"],
        ["Organization Unit", orgUnit?.name || team?.label || dept?.label || "—"],
        ["Direct Manager", users.find(u => u.id === profileUser.managerId)?.name || "—"],
    ];

    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [pwError, setPwError] = useState("");
    const [pwSuccess, setPwSuccess] = useState(false);
    const [pwSaving, setPwSaving] = useState(false);
    const [photoError, setPhotoError] = useState("");
    const [languageStatus, setLanguageStatus] = useState("");
    const [languageSaving, setLanguageSaving] = useState(false);
    const [draftLanguage, setDraftLanguage] = useState(language);

    useEffect(() => {
        setDraftLanguage(language);
    }, [language]);



    const savePreferredLanguage = async () => {
        const nextLanguage = draftLanguage;
        if (!SUPPORTED_LANGUAGES[nextLanguage] || languageSaving || nextLanguage === language) return;
        setLanguageStatus("");
        setLanguageSaving(true);
        const applyAndReload = () => {
            setUsers(prev => prev.map(item => item.id === user.id ? { ...item, preferredLanguage: nextLanguage } : item));
            try {
                const storedUser = JSON.parse(sessionStorage.getItem("cvi_user") || "null");
                if (storedUser?.id === user.id) sessionStorage.setItem("cvi_user", JSON.stringify({ ...storedUser, preferredLanguage: nextLanguage }));
                localStorage.setItem("cvi_language", nextLanguage);
            } catch { }
            window.location.reload();
        };
        const token = sessionStorage.getItem("cvi_token");
        if (!token) {
            applyAndReload();
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/api/me/preferences`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ preferredLanguage: nextLanguage }),
            });
            if (response.status === 401) {
                sessionStorage.removeItem("cvi_user");
                sessionStorage.removeItem("cvi_token");
                window.dispatchEvent(new Event("cvi-session-expired"));
                return;
            }
            if (!response.ok && response.status !== 404 && response.status !== 405) {
                setLanguageStatus(t("language.saveFailed"));
                return;
            }
            applyAndReload();
        } catch {
            setLanguageStatus(t("language.saveFailed"));
        } finally {
            setLanguageSaving(false);
        }
    };

    const submitPasswordChange = async () => {
        if (pwSaving) return;
        setPwSuccess(false);
        if (!currentPw) return setPwError("Enter your current password.");
        if (newPw.length < 6) return setPwError("The new password must be at least 6 characters.");
        if (newPw !== confirmPw) return setPwError("The new password and its confirmation do not match.");
        setPwSaving(true); setPwError("");
        try {
            const response = await fetch(`${API_BASE_URL}/api/me/password`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionStorage.getItem("cvi_token")}` },
                body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                const messages = {
                    PASSWORD_CURRENT_INVALID: "The current password is incorrect.",
                    PASSWORD_UNCHANGED: "The new password must be different from the current password.",
                    PASSWORD_TOO_SHORT: "The new password must be at least 6 characters.",
                    PASSWORD_RATE_LIMITED: "Too many attempts. Please try again later.",
                };
                setPwError(messages[payload.code] || "Your password could not be changed. Please try again.");
                return;
            }
            setCurrentPw(""); setNewPw(""); setConfirmPw("");
            setPwSuccess(true);
        } catch (error) {
            console.error("Password change failed:", error);
            setPwError("Your password could not be changed. Please try again.");
        } finally {
            setPwSaving(false);
        }
    };

    const uploadProfilePhoto = (file) => {
        setPhotoError("");
        if (!file) return;
        if (!file.type.startsWith("image/")) return setPhotoError("Only image files can be uploaded");
        if (file.size > 3 * 1024 * 1024) return setPhotoError("The image must be smaller than 3 MB");
        const reader = new FileReader();
        reader.onload = () => setUsers(prev => prev.map(u => u.id === user.id
            ? { ...u, photoDataUrl: String(reader.result || "") } : u));
        reader.onerror = () => setPhotoError("Image upload failed; please try again");
        reader.readAsDataURL(file);
    };

    const renderRows = entries => <Card><dl className="cvi-profile-fields">
        {entries.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
    </dl></Card>;
    return <ProfileTabs
        header={<>                    <div className="cvi-profile-identity">
                        <div style={{
                            width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
                            background: C.cyanBg, border: `2px solid ${C.cyan}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 22, fontWeight: 600, color: C.navy, flexShrink: 0
                        }}>
                            {profileUser.photoDataUrl
                                ? <img src={profileUser.photoDataUrl} alt={profileUser.name || ""}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                : profileUser.name?.[0] || "?"}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 22, fontWeight: 650 }}>{profileUser.name}</div>
                            <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>{[pos?.label || profileUser.position?.name, roleLabelMap[profileUser.role] || profileUser.role].filter(Boolean).join(" · ")}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <label style={{
                                    padding: "5px 10px", border: `1px solid ${C.borderMd}`,
                                    borderRadius: 7, fontSize: 10.5, color: C.navy, cursor: "pointer", background: "#fff"
                                }}>
                                    {profileUser.photoDataUrl ? "Change photo" : "Upload photo"}
                                    <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }}
                                        onChange={e => { uploadProfilePhoto(e.target.files?.[0]); e.target.value = ""; }} />
                                </label>
                                {profileUser.photoDataUrl && <button onClick={() => setUsers(prev => prev.map(u => u.id === user.id
                                    ? { ...u, photoDataUrl: null } : u))} style={{
                                        border: "none", background: "transparent",
                                        color: C.red, fontSize: 10.5, cursor: "pointer", fontFamily: "inherit"
                                    }}>Remove photo</button>}
                            </div>
                            {photoError && <div style={{ color: C.red, fontSize: 10, marginTop: 5 }}>{photoError}</div>}
                        </div>
                    </div>
</>}
        personal={renderRows(rows.slice(0, 3))}
        organization={renderRows(rows.slice(3))}
        account={<AdminPanel title="Change password" description="Use at least 6 characters. You stay signed in on this device after the change."
            footer={<button type="button" className="adm-btn adm-btn--primary" onClick={submitPasswordChange} disabled={pwSaving}>{pwSaving ? "Saving…" : "Update password"}</button>}>
            <form className="adm-form-grid" style={{ maxWidth: 640 }} onSubmit={event => { event.preventDefault(); submitPasswordChange(); }}>
                <label className="adm-field is-wide"><span>Current password</span>
                    <input type="password" autoComplete="current-password" value={currentPw} onChange={e => { setCurrentPw(e.target.value); setPwError(""); setPwSuccess(false); }} /></label>
                <label className="adm-field"><span>New password</span>
                    <input type="password" autoComplete="new-password" value={newPw} onChange={e => { setNewPw(e.target.value); setPwError(""); setPwSuccess(false); }} /></label>
                <label className="adm-field"><span>Confirm new password</span>
                    <input type="password" autoComplete="new-password" value={confirmPw} onChange={e => { setConfirmPw(e.target.value); setPwError(""); setPwSuccess(false); }} /></label>
                <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
            </form>
            {pwError && <div className="adm-callout is-danger" role="alert" style={{ marginTop: 14 }}><div>{pwError}</div></div>}
            {pwSuccess && <div className="adm-callout is-success" role="status" style={{ marginTop: 14 }}><div>Your password has been updated.</div></div>}
        </AdminPanel>}
        preferences={<>      <Card style={{ marginBottom: 12 }}>
        <div style={{ padding: 15 }}>
          <div style={{ fontSize: 15, fontWeight: 650, marginBottom: 5, color: "var(--adm-ink)" }}>{t("language.display")}</div>
          <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.7, marginBottom: 12 }}>{t("language.description")}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 10, color: C.hint, marginBottom: 3 }}>{t("settings.currentLanguage")}</div>
              <div style={{ fontSize: 11.5, fontWeight: 500 }}>{languages[language]?.label || language}</div>
            </div>
            <LanguageSelector value={draftLanguage} onChange={(nextLanguage) => { setDraftLanguage(nextLanguage); setLanguageStatus(""); }} />
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: C.hint }}>
            {profileUser.preferredLanguage ? `Your preference: ${languages[profileUser.preferredLanguage]?.label || profileUser.preferredLanguage}` : `Organization default: ${languages[orgSettings?.defaultLanguage || "en"]?.label || orgSettings?.defaultLanguage || "en"}`}
          </div>
          <button onClick={savePreferredLanguage} disabled={languageSaving || draftLanguage === language}
            style={{ marginTop: 12, padding: "8px 16px", background: draftLanguage === language ? C.borderMd : C.navy, color: draftLanguage === language ? C.hint : "#fff", border: "none", borderRadius: 8, fontSize: 11.5, fontWeight: 500, cursor: languageSaving || draftLanguage === language ? "default" : "pointer", fontFamily: "inherit" }}>
            {languageSaving ? t("common.loading") : ({ en: "Apply language", fr: "Appliquer la langue",    }[language] || "Apply language")}
          </button>
          {languageSaving && <div style={{ marginTop: 8, fontSize: 10.5, color: C.muted }}>{t("common.loading")}</div>}
          {!languageSaving && languageStatus && <div style={{ marginTop: 8, fontSize: 10.5, color: C.teal }}>{languageStatus}</div>}
        </div>
      </Card></>}
    />;
}

// ── PEER FEEDBACK ─────────────────────────────────────────────────────────────
function peerFeedbackPendingCount(peerAssignments, periodId) {
    if (!periodId) return 0;
    return (peerAssignments || []).filter(assignment => assignment.periodId === periodId && assignment.status === "pending").length;
}

function PeerFeedbackPage({ user }) {
    const { t, language } = useI18n();
    const { peerQuestions, peerAssignments, peerResult, peerLoading, peerError, submitPeerReviewApi, activePeriod } = useData();
    return <PeerFeedbackWorkspace language={language} userId={user.id} periodId={activePeriod?.id} periodLabel={activePeriod?.label || null}
        questions={peerQuestions} assignments={peerAssignments || []} result={peerResult} loading={peerLoading} error={peerError}
        ratingLabel={score => t(`rating.${score}`)} submitReview={submitPeerReviewApi}
        messages={{ allRequired: t("peer.allRequired"), submitFailed: t("peer.submitFailed"), submit: t("peer.submit"), submitting: t("peer.submitting") }} />;
}

// ── SPECIALIST APP SHELL (ریسپانسیو — دسکتاپ Sidebar / موبایل BottomNav) ──────
function SpecialistApp({ user, onLogout }) {
    const { activities, setActivities, activityGroups, impactLevels,
        positions, indicatorDefs, indicatorData, teams, departments, users, activePeriod,
        periodSnapshots,
        scheduleTemplates, scheduleExceptions, peerAssignments , periods } = useData();
    const { language: experienceLanguage } = useI18n();
    const routineCtx = useRoutineCtx();
    const isMobile = useIsMobile();
    const [nav, setNav] = useState("dashboard");
    const [view, setView] = useState(null);
    const [selectedAct, setSelectedAct] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [periodDetailOrigin, setPeriodDetailOrigin] = useState("cvi-detail");
    const [selectedOrgLevel, setSelectedOrgLevel] = useState(null);

    const myActs = activities.filter(a => a.userId === user.id);
    const pendingCount = myActs.filter(isReviewableActivity).length;
    const revisionCount = myActs.filter(a => a.status === ACTIVITY_STATUS.REVISION_REQUESTED).length;
    const badgeCount = pendingCount + revisionCount;
    const peerPending = peerFeedbackPendingCount(peerAssignments, activePeriod?.id);
    const indicatorActionCount = activePeriod ? indicatorDefs.filter(d => d.registrarId === user.id)
        .flatMap(d => indicatorScheduleOccurrences(d, activePeriod, scheduleTemplates, scheduleExceptions).map(schedule => ({ d, schedule })))
        .filter(({ d, schedule }) => {
            const data = indicatorData.find(x => x.indicatorId === d.id && x.periodId === activePeriod.id &&
                (x.occurrenceId === schedule.occurrenceId || (!x.occurrenceId && schedule.occurrenceId.endsWith(":1")))); return !data || ["draft", "revision_requested"].includes(data.status);
        }).length : 0;

    const team = teams.find(t => t.id === user.teamId);
    const pos = positions.find(p => p.id === user.posId);
    const directReports = users.filter(u => u.managerId === user.id && u.isActive !== false);
    const reportingMembers = reportingSubtreeUsers(users, user.id);
    const hasTeamScope = directReports.length > 0;

    const { data: liveCviPayload } = useLiveCvi(user.id, activePeriod?.id);
    const allPeriods = buildPerformanceHistory({
        snapshots: periodSnapshots, userId: user.id, periods, activePeriod,
        liveEntry: liveEntryFromServer(liveCviPayload, activePeriod),
    });


    // A contribution page is shown inside the shell; leaving Additional Contributions closes it.
    useEffect(() => {
        if (nav !== "activities" && view === "act-detail") { setView(null); setSelectedAct(null); }
    }, [nav, view]);

    if (view && view !== "act-detail") {
        return (
            <SpecialistDetailView view={view} setView={setView}
                selectedAct={selectedAct} setSelectedAct={setSelectedAct}
                selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod}
                periodDetailOrigin={periodDetailOrigin} setPeriodDetailOrigin={setPeriodDetailOrigin}
                myActs={myActs} allPeriods={allPeriods}
                activityGroups={activityGroups} impactLevels={impactLevels}
                user={user} positions={positions} indicatorDefs={indicatorDefs}
                indicatorData={indicatorData} activePeriod={activePeriod}
                routineCtx={routineCtx} selectedOrgLevel={selectedOrgLevel} />
        );
    }

    const renderContent = () => {
        if (nav === "new") return <NewContributionPage user={user} activePeriod={activePeriod} onClose={() => setNav("activities")}
            onOpenActivity={activity => { setSelectedAct(activity); setView("act-detail"); setNav("activities"); }} />;
        if (nav === "activities" && view === "act-detail" && selectedAct) return <ContributionDetailPage key={selectedAct.id} activity={selectedAct} user={user}
            onBack={() => { setView(null); setSelectedAct(null); }} onChange={setSelectedAct} />;
        if (nav === "dashboard") return (
            <ManagerDashboard user={user} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={reportingMembers} directReports={directReports}
                teams={teams} allPeriods={allPeriods} onNavigate={setNav}
                setView={setView} setSelectedOrgLevel={setSelectedOrgLevel}
                onOpenPeriod={(period) => { setSelectedPeriod(period); setPeriodDetailOrigin("dashboard"); setView("period-detail"); }}
                navigator={shellNavigator}
                showTeam={hasTeamScope} />
        );
        if (nav === "teamdash" && hasTeamScope) return (
            <TeamDashboard user={user} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={reportingMembers} teams={teams} onNavigate={setNav} />
        );

        if (["tasks-activities", "self-assessment", "activities"].includes(nav)) return (
            <TasksActivitiesPage nav={nav} setNav={setNav}
                routineContent={<RoutineSelfAssessment activePeriod={activePeriod} user={user} />}
                activityContent={<SpecialistActivities myActs={myActs} activityGroups={activityGroups} setNav={setNav}
                    setSelectedAct={setSelectedAct} setView={setView} />} />
        );
        if (nav === "my-indicators") return <MyIndicators user={user} />;
        if (nav === "history") return <SpecialistHistory user={user} allPeriods={allPeriods}
            setView={setView} setSelectedPeriod={setSelectedPeriod} setPeriodDetailOrigin={setPeriodDetailOrigin} />;
        if (nav === "peer-feedback") return <PeerFeedbackPage user={user} />;
        if (nav === "evaluation-duties") return <EvaluationDutiesPage />;
        if (nav === "goals") return <GoalsPage user={user} />;
        if (nav === "privacy") return <PrivacyPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "check-ins") return <CheckInsPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "recognition") return <RecognitionPage language={experienceLanguage} users={users} currentUserId={user.id} onBack={() => setNav("dashboard")} />;
        if (nav === "learning") return <LearningPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "team-challenges") return <TeamChallengesPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "help") return <CviHelpCenter />;

        if (nav === "profile") return <PersonProfile user={user} />;
    };

    const sidebarItems = organizePerformanceNavigation([
        { id: "dashboard", label: "Dashboard", icon: "dashboard" },
        { id: "self-assessment", label: "Core Responsibilities", icon: "self" },
        { id: "goals", label: "Individual Goals", icon: "target" },
        { id: "activities", label: "Additional Contributions", icon: "activity", badge: badgeCount },
        { id: "my-indicators", label: "My Indicators", icon: "target", badge: indicatorActionCount },
        { id: "history", label: "History", icon: "history" },
        { id: "peer-feedback", label: "Peer Feedback", icon: "peer", badge: peerPending },
        { id: "help", label: "Help", icon: "help", section: "account" },
        { id: "privacy", label: "Privacy & data rights", icon: "shield", section: "account" },
        { id: "profile", label: "Profile", icon: "profile" },
    ]);

    const shellNavigator = makeShellNavigator({ navs: ["evaluation-duties", "dashboard", "self-assessment", "activities", "my-indicators", "history", "peer-feedback", "goals", "privacy", "check-ins", "recognition", "learning", "team-challenges"], setNav, setView });
    return (
        <div className="cvi-admin-shell" style={{
            display: "flex", flexDirection: "column", height: "100vh",
            fontFamily: "system-ui,sans-serif", direction: "ltr", textAlign: "left", background: C.bg,
            maxWidth: isMobile ? 420 : "100%", margin: isMobile ? "0 auto" : 0,
            border: isMobile ? `1px solid ${C.border}` : "none",
            boxShadow: isMobile ? "0 4px 32px rgba(0,0,64,0.08)" : "none"
        }}>

            <WorkspaceNavContext.Provider value={shellNavigator}><TopBar right={
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} />
                </div>
            } /></WorkspaceNavContext.Provider>

            <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
                {!isMobile && <Sidebar items={sidebarItems} nav={nav === "teamdash" ? "dashboard" : nav} onNav={setNav} onLogout={onLogout} />}
                <div className="cvi-role-scroll">
                    <TabTransitionContent transitionKey={nav}>{renderContent()}</TabTransitionContent>
                </div>
            </div>

            {isMobile && <AppBottomNav items={sidebarItems} active={nav} onNav={setNav} onLogout={onLogout} />}
        </div>
    );
}

function orgUnitSubtreeIds(orgUnits, rootId) {
    if (!rootId) return [];
    const ids = new Set([rootId]);
    let changed = true;
    while (changed) {
        changed = false;
        for (const unit of orgUnits || []) {
            if (unit.parentId && ids.has(unit.parentId) && !ids.has(unit.id)) {
                ids.add(unit.id);
                changed = true;
            }
        }
    }
    return [...ids];
}

function orgUnitChildren(orgUnits, parentId) {
    return (orgUnits || []).filter(unit => unit.parentId === parentId && unit.isActive !== false);
}

function reportingSubtreeUsers(users, managerId) {
    if (!managerId) return [];
    const children = new Map();
    (users || []).filter(u => u.isActive !== false).forEach(user => {
        if (!user.managerId) return;
        if (!children.has(user.managerId)) children.set(user.managerId, []);
        children.get(user.managerId).push(user);
    });
    const result = [];
    const seen = new Set();
    const queue = [...(children.get(managerId) || [])];
    while (queue.length) {
        const user = queue.shift();
        if (!user || seen.has(user.id)) continue;
        seen.add(user.id);
        result.push(user);
        queue.push(...(children.get(user.id) || []));
    }
    return result;
}


function DashboardScopeSwitch({ active, onChange, showTeam = true }) {
    return (
        <div className="cvi-dashboard-scope-switch" role="tablist" aria-label="Dashboard view">
            <button type="button" role="tab" aria-selected={active === "performance"}
                className={active === "performance" ? "is-active" : ""}
                onClick={() => onChange("performance")}>
                <UiIcon name="performance" size={15} />
                <span>My Performance</span>
            </button>
            {showTeam && <button type="button" role="tab" aria-selected={active === "team"}
                className={active === "team" ? "is-active" : ""}
                onClick={() => onChange("team")}>
                <UiIcon name="team" size={15} />
                <span>My Team</span>
            </button>}
        </div>
    );
}

// ── MANAGER DASHBOARD ──────────────────────────────────────────────────────────
function ManagerDashboard({ user, activities, activityGroups, positions, indicatorDefs,
    indicatorData, activePeriod, members, directReports = members, teams, allPeriods = [], onNavigate, setView, setSelectedOrgLevel,
    onOpenPeriod = null, showTeam = directReports.length > 0, navigator = null }) {
    const { orgUnits, peerAssignments, scheduleTemplates, scheduleExceptions } = useData();
    const { language } = useI18n();
    const orgBreakdown = positionLevelWeights(user, positions);
    // Historical dashboards only use the selected period's saved result; workflows stay active-period scoped.
    const [dashboardPeriodId, setDashboardPeriodId] = useState(null);
    const selectedHistoricalPeriod = (allPeriods || []).find(p => String(p.periodId || p.id) === String(dashboardPeriodId));
    const isHistorical = Boolean(dashboardPeriodId && String(dashboardPeriodId) !== String(activePeriod?.id));
    const dashboardPeriodLabel = isHistorical ? (selectedHistoricalPeriod?.label || "Historical period") : (activePeriod?.label || "No active period");

    const myActivities = activities.filter(a => a.userId === user.id);
    // Fallback counts, shown only until the server step list loads (or if it fails). Scoped to the
    // active cycle like the step list, so the tiles never jump between two different definitions.
    const itemsToComplete = myActivities.filter(a =>
        (!activePeriod?.id || a.periodId === activePeriod.id) &&
        [ACTIVITY_STATUS.DRAFT, ACTIVITY_STATUS.REVISION_REQUESTED].includes(a.status)
    );
    const peerFeedbackDue = peerFeedbackPendingCount(peerAssignments, activePeriod?.id);
    const indicatorActions = activePeriod ? indicatorDefs
        .filter(def => def.registrarId === user.id || def.ownerId === user.id)
        .flatMap(def => indicatorScheduleOccurrences(def, activePeriod, scheduleTemplates, scheduleExceptions)
            .map(schedule => ({ def, schedule })))
        .filter(({ def, schedule }) => {
            const data = indicatorData.find(row =>
                row.indicatorId === def.id &&
                row.periodId === activePeriod.id &&
                (row.occurrenceId === schedule.occurrenceId || (!row.occurrenceId && schedule.occurrenceId.endsWith(":1")))
            );
            return !data || ["draft", "revision_requested"].includes(data.status);
        }).length : 0;
    const personalSupportNeeds = myActivities.filter(a =>
        ["open", "in_progress"].includes(String(a.blockerStatus || "").toLowerCase())
    );

    const team = teams.find(t => t.id === user.teamId);
    const orgUnit = orgUnits.find(unit => unit.id === user.orgUnitId);
    const position = positions.find(p => p.id === user.posId);
    const unitLabel = orgUnit?.label || team?.label || "Team";
    const fallbackPositionLabel = user.posId
        ? String(user.posId).replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())
        : positionsLabelForEmployee(user);
    const positionLabel = position?.label || fallbackPositionLabel;
    const score = value => value == null || !Number.isFinite(Number(value)) ? "—" : Number(value).toFixed(0);
    const attentionCount = itemsToComplete.length + peerFeedbackDue + indicatorActions + personalSupportNeeds.length;
    const [scoreContextPanel, setScoreContextPanel] = useState(null);
    const dashboardPeriods = Array.from(new Map(
        [
            ...(allPeriods || []),
            ...(activePeriod?.id ? [{ ...activePeriod, periodId: activePeriod.id }] : [])
        ].map(item => [String(item.periodId || item.id || item.label), item])
    ).values());


    // Where a dashboard task or tracker action leads. Destinations stay within the role's existing navigation.
    const openTarget = target => {
        const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (target === "challenges") return scrollTo("my-challenges");
        if (target === "review-cycle") return scrollTo("xp-cycle-title");
        onNavigate(NAV_TARGETS[target] || target || "activities");
    };
    const myCycleActivities = activities.filter(a => a.userId === user.id && (!activePeriod?.id || a.periodId === activePeriod.id));
    const [nextStepCount, setNextStepCount] = useState(null);

    return (
        <div className="cvi-dashboard-page cvi-manager-dashboard">
            <DashboardPageIntro
                title="My Performance"
                subtitle={`${user.name} · ${positionLabel}`}
                period={dashboardPeriodLabel}
                periodIsHistorical={isHistorical}
                periodOptions={dashboardPeriods}
                onPeriodSelect={period => setDashboardPeriodId(String(period.periodId || period.id))}
                actions={showTeam ? <DashboardScopeSwitch active="performance" showTeam onChange={(scope) => scope === "team" && onNavigate("teamdash")} /> : null}
            />

            {isHistorical ? (() => {
                const p = selectedHistoricalPeriod;
                const idx = allPeriods.findIndex(row => String(row.periodId || row.id) === String(dashboardPeriodId));
                const previous = idx > 0 ? allPeriods[idx - 1] : null;
                const scoreValue = key => cviValue(p?.[key]);
                const fmt = key => formatScore(scoreValue(key), 1);
                const change = scoreValue("cvi") != null && cviValue(previous?.cvi) != null
                    ? scoreValue("cvi") - Number(previous.cvi) : null;
                const cycleActivities = activities.filter(activity =>
                    String(activity.periodId) === String(dashboardPeriodId) && String(activity.userId) === String(user.id));
                const approved = cycleActivities.filter(activity => ["approved", "locked"].includes(String(activity.status).toLowerCase()));
                const groups = activityGroups.map(group => ({
                    ...group,
                    score: cviValue(p?.activityGroupScores?.[group.id]),
                    count: p?.activityCounts?.[group.id] ?? null,
                })).filter(group => group.score != null || group.count != null);
                const criteria = (p?.routineCriteria || []).map(item => ({
                    label: item.label || item.text,
                    score: cviValue(item.score ?? item.normalizedScore),
                    weight: cviValue(item.weight),
                })).filter(item => item.label);
                const comparison = (key, label) => {
                    const current = scoreValue(key), prior = cviValue(previous?.[key]);
                    const diff = current != null && prior != null ? current - prior : null;
                    return <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                        <span style={{ color: C.muted }}>{label}</span>
                        <strong>{formatScore(current, 1)}{diff == null ? "" : <span style={{ color: diff >= 0 ? C.teal : C.red, fontSize: 12, marginLeft: 9 }}>
                            ({formatPointsChange(diff, language)})
                        </span>}</strong>
                    </div>;
                };
                const panel = (heading, subtitle, body) => <section key={heading} className="adm-panel" style={{ padding: 22, minWidth: 0 }}>
                    <h2 style={{ margin: "0 0 5px", fontSize: 18, color: C.navy }}>{heading}</h2>
                    {subtitle && <p style={{ margin: "0 0 16px", fontSize: 12, color: C.muted }}>{subtitle}</p>}
                    {body}
                </section>;
                const missing = <p style={{ color: C.muted, fontSize: 13 }}>{language === "fr" ? "Détails indisponibles dans l’instantané finalisé." : "Details not available in the finalized snapshot."}</p>;
                return <section className="cvi-manager-historical" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 18, minWidth: 0 }} aria-label={language === "fr" ? "Résultats historiques" : "Historical results"}>
                    <div className="adm-callout is-neutral" role="status"><div>{language === "fr"
                        ? "Résultats finalisés · lecture seule. Les valeurs proviennent uniquement des données de cette période."
                        : "Finalized results · read-only. Values shown belong exclusively to this period."}</div></div>
                    <div className="cvi-dashboard-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 18 }}>
                        <DashboardMetricCard label={language === "fr" ? "Contribution individuelle" : "Individual Contribution"} value={fmt("individual")} tone="navy" />
                        <DashboardMetricCard label="CVI" value={fmt("cvi")} tone="cyan" />
                        <DashboardMetricCard label={language === "fr" ? "Contribution collective" : "Collective Contribution"} value={fmt("org")} tone="purple" />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 16 }}>
                        {panel(language === "fr" ? "Contribution individuelle" : "Individual Contribution",
                            language === "fr" ? "Composantes calculées au moment de la finalisation" : "Components recorded at finalization",
                            <div>
                                {[
                                    ["nonRoutine", language === "fr" ? "Contributions additionnelles" : "Additional Contributions", p?.weights?.nonRoutine],
                                    ["routine", language === "fr" ? "Responsabilités principales" : "Core Responsibilities", p?.weights?.routine],
                                    ["peerFeedback", language === "fr" ? "Rétroaction des collègues" : "Peer Feedback", p?.weights?.peerFeedback],
                                ].map(([key, label, weight]) => <div key={key} style={{ padding: "11px 0", borderBottom: `1px solid ${C.border}` }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                        <span>{label}{weight != null ? <small style={{ color: C.muted }}> · {weight}%</small> : null}</span>
                                        <strong>{fmt(key)}</strong>
                                    </div>
                                    {scoreValue(key) != null && <MiniBar value={scoreValue(key)} color={C.cyan} height={6} />}
                                </div>)}
                            </div>)}
                        {panel(language === "fr" ? "Contribution collective" : "Collective Contribution",
                            language === "fr" ? "Résultat organisationnel finalisé" : "Finalized organizational result",
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0" }}>
                                    <span>{language === "fr" ? "Contribution collective" : "Collective score"}</span><strong>{fmt("org")}</strong>
                                </div>
                                {scoreValue("org") != null && <MiniBar value={scoreValue("org")} color={C.purple} height={8} />}
                                <p style={{ color: C.muted, fontSize: 12, marginTop: 15 }}>{language === "fr"
                                    ? "Les résultats détaillés des unités ne sont pas déduits du résultat global."
                                    : "Unit-level scores are not inferred from the overall collective score."}</p>
                            </div>)}
                        {panel(language === "fr" ? "Comparaison avec la période précédente" : "Compared with previous period",
                            previous ? (previous.label || "") : null,
                            previous ? <div>
                                {comparison("cvi", "CVI")}
                                {comparison("individual", language === "fr" ? "Contribution individuelle" : "Individual Contribution")}
                                {comparison("org", language === "fr" ? "Contribution collective" : "Collective Contribution")}
                                {change != null && <p style={{ color: change >= 0 ? C.teal : C.red, fontWeight: 600 }}>
                                    {formatPointsChange(change, language)}
                                </p>}
                            </div> : missing)}
                        {panel(language === "fr" ? "Contributions enregistrées" : "Recorded Contributions",
                            language === "fr" ? "Contributions approuvées de la période sélectionnée" : "Approved contributions from the selected period",
                            <div>
                                <strong style={{ fontSize: 24, color: C.navy }}>{approved.length}</strong>
                                <span style={{ marginLeft: 8, color: C.muted }}>{language === "fr" ? "contributions approuvées disponibles" : "approved contributions available"}</span>
                                {approved.slice(0, 6).map(item => <div key={item.id} style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                                    <strong style={{ display: "block", fontSize: 13 }}>{item.title}</strong>
                                    <small style={{ color: C.muted }}>{activityGroups.find(group => group.id === item.groupId)?.label || ""}</small>
                                </div>)}
                                {!cycleActivities.length && missing}
                            </div>)}
                        {panel(language === "fr" ? "Contributions par catégorie" : "Contributions by Category",
                            language === "fr" ? "Résultats et volumes archivés" : "Archived category scores and counts",
                            groups.length ? <div>{groups.map(group => <div key={group.id} style={{ padding: "11px 0", borderBottom: `1px solid ${C.border}` }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                    <span>{group.label}</span><strong>{group.score == null ? "—" : formatScore(group.score, 1)}{group.maxScore != null ? ` / ${group.maxScore}` : ""}</strong>
                                </div>
                                {group.count != null && <small style={{ color: C.muted }}>{group.count} {language === "fr" ? "activités" : "activities"}</small>}
                                {group.score != null && group.maxScore > 0 && <MiniBar value={group.score} max={group.maxScore} color={C.cyan} height={5} />}
                            </div>)}</div> : missing)}
                        {panel(language === "fr" ? "Évaluation des responsabilités principales" : "Core Responsibilities Evaluation",
                            language === "fr" ? "Critères enregistrés dans l’instantané" : "Criteria recorded in the finalized snapshot",
                            criteria.length ? <div>{criteria.map((item, index) => <div key={index} style={{ padding: "11px 0", borderBottom: `1px solid ${C.border}` }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                    <span>{item.label}{item.weight != null && <small style={{ color: C.muted }}> · {item.weight}%</small>}</span>
                                    <strong>{formatScore(item.score, 1)}</strong>
                                </div>
                                {item.score != null && <MiniBar value={item.score} color={C.purple} height={5} />}
                            </div>)}</div> : missing)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button type="button" className="adm-btn" onClick={() => onNavigate("history")}>
                            {language === "fr" ? "Consulter les archives détaillées" : "Open detailed history"}
                        </button>
                    </div>
                    <EvaluationAccountabilitySection language={language} periodId={dashboardPeriodId} periodLabel={dashboardPeriodLabel} navigator={navigator} />
                </section>;
            })() : <>
            <div className="cvi-manager-primary-grid">
                <div className="cvi-manager-cvi-shell">
                    <ScoreOverview user={user} orgBreakdown={orgBreakdown} locale="en"
                        onContextPanelChange={setScoreContextPanel}
                        onOpenNonRoutine={() => setView("nonroutine-detail")}
                        onOpenRoutine={() => setView("routine-detail")}
                        onOpenTasksActivities={() => setView("tasks-activities-detail")}
                        onOpenGoals={() => onNavigate("goals")}
                        onOpenPeerFeedback={() => onNavigate("peer-feedback")}
                        onOpenOrgLevel={(level) => { setSelectedOrgLevel(level); setView("org-level-detail"); }} />
                </div>

                {scoreContextPanel ? (
                    <section className="cvi-manager-attention-card cvi-score-context-card">
                        <div className="cvi-manager-section-head cvi-score-context-head">
                            <div>
                                <span>{scoreContextPanel.eyebrow}</span>
                                <h2>{scoreContextPanel.title}</h2>
                            </div>
                            <button type="button" className="cvi-score-context-close" onClick={() => setScoreContextPanel(null)} aria-label="Close score details">×</button>
                        </div>
                        <div className="cvi-score-context-content">{scoreContextPanel.content}</div>
                        <button type="button" className="cvi-score-context-back" onClick={() => setScoreContextPanel(null)}>← Back to workflow</button>
                    </section>
                ) : (
                    <section className="cvi-manager-attention-card">
                    <div className="cvi-manager-section-head">
                        <div>
                            <h2 data-cvi-i18n="managed">{language === "fr" ? "Ce qui demande mon attention" : "Needs my attention"}</h2>
                        </div>
                        {(nextStepCount ?? attentionCount) > 0 && <b className="cvi-manager-attention-count" data-cvi-i18n="managed"
                            title={experienceCopy(language).nextSteps.badge(nextStepCount ?? attentionCount)}
                            aria-label={experienceCopy(language).nextSteps.badge(nextStepCount ?? attentionCount)}>{nextStepCount ?? attentionCount}</b>}
                    </div>

                    <NextStepsList language={language} periodId={activePeriod?.id} onOpenTarget={openTarget} onCount={setNextStepCount} summary={[
                        { id: "items", icon: "activity", label: language === "fr" ? "À terminer" : "To complete", count: itemsToComplete.length, onClick: () => onNavigate("activities") },
                        { id: "feedback", icon: "peer", label: language === "fr" ? "Rétroaction" : "Feedback", count: peerFeedbackDue, onClick: () => onNavigate("peer-feedback") },
                        { id: "indicators", icon: "target", label: language === "fr" ? "Indicateurs" : "Indicators", count: indicatorActions, onClick: () => onNavigate("my-indicators") },
                        { id: "support", icon: "warning", label: language === "fr" ? "Soutien" : "Support", count: personalSupportNeeds.length, onClick: () => openTarget("challenges") },
                    ]} />
                </section>
                )}
            </div>

            <ReviewCycleTracker language={language} periodId={activePeriod?.id} onOpenTarget={openTarget} />

            <div className="cvi-xp-row cvi-xp-row-2">
                <GoalsPanel language={language} periodId={activePeriod?.id} onOpenGoals={() => onNavigate("goals")} />
                <ContributionsPanel language={language} activities={myCycleActivities} onView={() => onNavigate("activities")} onAdd={() => onNavigate("new")} />
            </div>

            <section className="cvi-manager-trend-feature">
                <div className="cvi-manager-trend-copy">
                    <span>PERFORMANCE OVER TIME</span>
                    <h2>Your CVI trajectory</h2>
                    <p>See how your CVI, Individual Contribution, and Collective Contribution change from one period to the next.</p>
                </div>
                <PerformanceTrendChart periods={allPeriods} onOpenHistory={() => onNavigate("history")} locale="en" />
            </section>

            {/* Separate from the CVI contribution results above: obligations, not a score. */}
            <EvaluationAccountabilitySection language={language} periodId={activePeriod?.id} periodLabel={dashboardPeriodLabel} navigator={navigator} />

            <div className="cvi-xp-row cvi-xp-row-auto">
                <CheckInPanel language={language} onOpen={() => onNavigate("check-ins")} />
                <RecognitionPanel language={language} onOpen={() => onNavigate("recognition")} />
                <LearningPanel language={language} onOpen={() => onNavigate("learning")} />
            </div>

            <ChallengesSection language={language} scope="mine" onRecord={() => onNavigate("new")} />

            <PrivacyFooterLink language={language} onOpen={() => onNavigate("privacy")} />
            </>}
        </div>
    );
}

const INDICATOR_STATUS_LABELS = {
    not_started: "Not submitted", draft: "Draft", submitted: "Submitted",
    revision_requested: "Revision required", approved: "Approved", locked: "Locked",
    closed_without_data: "Closed without data"
};
function indicatorStatusLabel(status) { return INDICATOR_STATUS_LABELS[status] || INDICATOR_STATUS_LABELS.not_started; }

function IndicatorAudit({ data }) {
    const rows = data?.history || [];
    return <Card><div style={{ padding: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>Change history</div>
        {!rows.length ? <div style={{ fontSize: 11, color: C.hint }}>No events have been recorded yet.</div> :
            rows.slice().reverse().map((e, i) => <div key={`${e.date}_${i}`} style={{ padding: "8px 0", borderBottom: `1px solid ${C.bg}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 500 }}>{indicatorStatusLabel(e.toStatus || e.type)} · {e.by}</div>
                <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{displayDate(e.date)}{e.note ? ` — ${e.note}` : ""}</div>
                {(e.oldValue != null || e.newValue != null) && <div style={{ fontSize: 10, color: C.hint, marginTop: 2 }}>
                    Previous value: {e.oldValue ?? "—"} → New value: {e.newValue ?? "—"}
                </div>}
            </div>)}
    </div></Card>;
}

function MyIndicators({ user }) {
    const { indicatorDefs, indicatorData, activePeriod, users, periods,
        scheduleTemplates, scheduleExceptions, saveIndicatorValueApi, indicatorLoading, indicatorError } = useData();
    const [tab, setTab] = useState("action");
    const [savingStatus, setSavingStatus] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [selectedOccurrence, setSelectedOccurrence] = useState(null);
    const [form, setForm] = useState({ actual: "", note: "", deviationReason: "", correctiveAction: "", doc: "", docDataUrl: "", _file: null });
    const assigned = indicatorDefs.filter(d => [d.ownerId, d.registrarId, d.approverId].includes(user.id));
    const activeRows = activePeriod ? assigned.flatMap(def => indicatorScheduleOccurrences(def, activePeriod, scheduleTemplates, scheduleExceptions)
        .map(schedule => ({ def, schedule }))) : [];
    const getData = (id, occurrenceId) => tab === "history" ? indicatorData.filter(d => d.indicatorId === id && ["approved", "locked", "closed_without_data"].includes(d.status)).slice(-1)[0]
        : activePeriod ? indicatorData.find(d => d.indicatorId === id && d.periodId === activePeriod.id &&
            (d.occurrenceId === occurrenceId || (!d.occurrenceId && occurrenceId?.endsWith(":1")))) : indicatorData.filter(d => d.indicatorId === id).slice(-1)[0];
    const statusOf = row => getData(row.def.id, row.schedule?.occurrenceId)?.status || "not_started";
    const inTab = row => tab === "action" ? ((row.def.registrarId === user.id || row.def.ownerId === user.id) && ["not_started", "draft", "revision_requested"].includes(statusOf(row)))
        : tab === "submitted" ? ["submitted"].includes(statusOf(row))
            : ["approved", "locked", "closed_without_data"].includes(statusOf(row));
    const rows = (tab === "history" ? assigned.map(def => ({ def, schedule: null })) : activeRows).filter(inTab);
    const selected = indicatorDefs.find(d => d.id === selectedId);
    const current = selected ? getData(selected.id, selectedOccurrence?.occurrenceId) : null;
    const today = todayJalaliStr();
    const registrationOpen = !selectedOccurrence || daysBetweenJalali(selectedOccurrence.openDate, today) >= 0;
    const beforeLock = !selectedOccurrence || daysBetweenJalali(today, selectedOccurrence.lockDate) >= 0;
    const beforeSubmitDeadline = !selectedOccurrence || daysBetweenJalali(today, selectedOccurrence.submitDeadline) >= 0 || current?.status === "revision_requested";
    const canEdit = selected?.registrarId === user.id && activePeriod?.status === "active" && registrationOpen && beforeSubmitDeadline && beforeLock && !["approved", "locked"].includes(current?.status);
    const activeStatus = row => indicatorData.find(d => d.indicatorId === row.def.id && d.periodId === activePeriod?.id &&
        (d.occurrenceId === row.schedule?.occurrenceId || (!d.occurrenceId && row.schedule?.occurrenceId?.endsWith(":1"))))?.status || "not_started";
    const tabItems = [
        { id: "action", label: "Action required", icon: "inbox", count: activeRows.filter(r => (r.def.registrarId === user.id || r.def.ownerId === user.id) && ["not_started", "draft", "revision_requested"].includes(activeStatus(r))).length },
        { id: "submitted", label: "Submitted", icon: "send", count: activeRows.filter(r => activeStatus(r) === "submitted").length },
        { id: "history", label: "History", icon: "history", count: assigned.filter(def => indicatorData.some(d => d.indicatorId === def.id && ["approved", "locked", "closed_without_data"].includes(d.status))).length },
    ];
    const actualNum = Number(form.actual);
    const achievement = current?.target ? actualNum / current.target * 100 : null;
    const needsDeviation = achievement != null && achievement < 80;
    const open = row => {
        const d = row.def, data = getData(d.id, row.schedule?.occurrenceId); setSelectedId(d.id); setSelectedOccurrence(row.schedule); setForm({
            actual: data?.actual ?? "", note: data?.note || "",
            deviationReason: data?.deviationReason || "", correctiveAction: data?.correctiveAction || "", doc: data?.doc || "", docDataUrl: data?.docDataUrl || "", _file: null
        });
    };
    const save = async status => {
        if (!selected || !canEdit || form.actual === "" || savingStatus) return;
        if (status === "submitted" && selected.requiresEvidence && !form.doc) {
            uxNotify("Attach a supporting document before submitting this value.");
            return;
        }
        if (status === "submitted" && needsDeviation && (!form.deviationReason.trim() || !form.correctiveAction.trim())) return;
        setSavingStatus(status);
        try {
            await saveIndicatorValueApi(
                selected.id,
                activePeriod.id,
                form,
                status === "submitted",
                selectedOccurrence?.occurrenceId || "period"
            );
            setSelectedId(null);
            setSelectedOccurrence(null);
            uxNotify(status === "submitted" ? "Value submitted for approval." : "Draft saved.", "success");
        } catch (error) {
            console.error("Indicator value save failed:", error);
            uxNotify(error.message || "The indicator value could not be saved");
        } finally {
            setSavingStatus("");
        }
    };
    if (selected) return <div className="cvi-workflow-page">
        <button type="button" className="adm-btn adm-btn--sm" style={{ marginBottom: 14 }} onClick={() => { setSelectedId(null); setSelectedOccurrence(null); }}>← Back to My Indicators</button>
        <Card style={{ marginBottom: 12 }}><div style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><div><div style={{ fontSize: 15, fontWeight: 500 }}>{selected.label}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{activePeriod?.label || periods.find(p => p.id === current?.periodId)?.label || "Unknown period"}{selectedOccurrence ? ` · ${selectedOccurrence.label}` : ""}</div></div>
                <Badge color={current?.status === "approved" ? "teal" : current?.status === "revision_requested" ? "amber" : "gray"}>{indicatorStatusLabel(current?.status)}</Badge></div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 14, fontSize: 11 }}>
                <div>Owner<br /><b>{users.find(u => u.id === selected.ownerId)?.name || "—"}</b></div>
                <div>Data Submitter<br /><b>{users.find(u => u.id === selected.registrarId)?.name || "—"}</b></div>
                <div>Approver<br /><b>{users.find(u => u.id === selected.approverId)?.name || "—"}</b></div>
            </div>
            <div style={{ marginTop: 14, fontSize: 12 }}>Period target: <b>{current?.target ?? "Not set yet"} {selected.unit}</b></div>
            {selectedOccurrence && <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginTop: 12, fontSize: 10.5 }}>
                <div>Submission start<br /><b>{displayDate(selectedOccurrence.openDate)}</b></div><div>Submission deadline<br /><b>{displayDate(selectedOccurrence.submitDeadline)}</b></div>
                <div>Approval deadline<br /><b>{displayDate(selectedOccurrence.approvalDeadline)}</b></div><div>Automatic lock<br /><b>{displayDate(selectedOccurrence.lockDate)}</b></div>
            </div>}
            {!registrationOpen && <div style={{ marginTop: 10, padding: 8, background: C.cyanBg, borderRadius: 7, fontSize: 11 }}>The submission window is not open yet.</div>}
            {!beforeLock && <div style={{ marginTop: 10, padding: 8, background: C.redLt, color: C.red, borderRadius: 7, fontSize: 11 }}>This occurrence was locked automatically.</div>}
            {current?.reviewNote && <div style={{ marginTop: 10, padding: 9, background: C.amberLt, color: C.amber, borderRadius: 8, fontSize: 11 }}>Revision requested: {current.reviewNote}</div>}
        </div></Card>
        <Card style={{ marginBottom: 12 }}><div style={{ padding: 16 }}>
            <label style={{ fontSize: 11, color: C.muted }}>Actual value *</label>
            <input type="number" disabled={!canEdit} value={form.actual} onChange={e => setForm(f => ({ ...f, actual: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: 10, border: `1px solid ${C.borderMd}`, borderRadius: 8, margin: "5px 0 10px" }} />
            <label style={{ fontSize: 11, color: C.muted }}>Description or data source</label>
            <textarea disabled={!canEdit} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2}
                style={{ width: "100%", boxSizing: "border-box", padding: 10, border: `1px solid ${C.borderMd}`, borderRadius: 8, margin: "5px 0 10px", fontFamily: "inherit" }} />
            {needsDeviation && <div style={{ background: C.redLt, padding: 10, borderRadius: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: C.red, marginBottom: 7 }}>Achievement is below 80%. Briefly explain the variance and the planned corrective action.</div>
                <textarea disabled={!canEdit} placeholder="Reason for variance" value={form.deviationReason} onChange={e => setForm(f => ({ ...f, deviationReason: e.target.value }))} rows={2} style={{ width: "100%", boxSizing: "border-box", marginBottom: 6 }} />
                <textarea disabled={!canEdit} placeholder="Corrective action" value={form.correctiveAction} onChange={e => setForm(f => ({ ...f, correctiveAction: e.target.value }))} rows={2} style={{ width: "100%", boxSizing: "border-box" }} />
            </div>}
            {canEdit && <label style={{ display: "inline-block", padding: "8px 12px", border: `1px dashed ${C.borderMd}`, borderRadius: 8, cursor: "pointer", fontSize: 11 }}>
                📎 {form.doc || "Upload document"}<input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => { const file = e.target.files?.[0]; if (!file || file.size > 5 * 1024 * 1024) return; const reader = new FileReader(); reader.onload = () => setForm(f => ({ ...f, doc: file.name, docDataUrl: String(reader.result || ""), _file: file })); reader.readAsDataURL(file); }} />
            </label>}
            <ProtectedAttachmentList module="indicator" entityId={current?.id}
                attachments={current?.attachments || []} canDelete={canEdit} />
            {selected.requiresEvidence && <span style={{ fontSize: 10, color: C.hint, marginRight: 8 }}>A supporting document is required for submission.</span>}
            {canEdit && <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end", flexWrap: "wrap" }}><button type="button" className="adm-btn" onClick={() => save("draft")} disabled={form.actual === "" || Boolean(savingStatus)}>{savingStatus === "draft" ? "Saving…" : "Save draft"}</button>
                <button type="button" className="adm-btn adm-btn--primary" onClick={() => save("submitted")} disabled={Boolean(savingStatus) || form.actual === "" || (selected.requiresEvidence && !form.doc) || (needsDeviation && (!form.deviationReason.trim() || !form.correctiveAction.trim()))}>{savingStatus === "submitted" ? "Submitting…" : "Submit for approval"}</button></div>}
        </div></Card><IndicatorAudit data={current} />
    </div>;
    const emptyCopy = {
        action: ["Nothing needs your action right now.", assigned.length ? "Indicator values you need to enter or correct for the active period appear here when their submission window opens." : "You are not the owner, data submitter or approver of any indicator. Your administrator assigns these roles in Collective Contribution."],
        submitted: ["No values are awaiting approval.", "Values you submit stay here until the approver reviews them."],
        history: ["No approved values yet.", "Approved and locked indicator values from past submissions appear here."],
    }[tab];
    return <div className="adm-page cvi-my-indicators">
        <AdminPageHeader title="My Indicators" description="Collective Contribution indicators for which you are the owner, data submitter or approver." />
        {indicatorLoading && <div className="adm-callout is-neutral" role="status"><div>Loading indicators…</div></div>}
        {indicatorError && <div className="adm-callout is-danger" role="alert"><div>{indicatorError}</div></div>}
        {tab === "action" && rows.some(r => { const days = r.schedule ? daysBetweenJalali(today, r.schedule.submitDeadline) : null; return days != null && days <= r.schedule.reminderDays; }) && <div className="adm-callout is-warning" role="status"><div>{rows.filter(r => { const days = r.schedule ? daysBetweenJalali(today, r.schedule.submitDeadline) : null; return days != null && days <= r.schedule.reminderDays; }).length} submission(s) are close to or past their deadline.</div>
        </div>}
        <ProfessionalTabs items={tabItems} value={tab} onChange={setTab} ariaLabel="My indicator status" />
        {!activePeriod && tab !== "history" ? <div className="adm-panel"><AdminEmpty icon="planning" title="There is no active period." description="Indicator submissions open once an administrator activates a period." /></div> : rows.length === 0 ? <div className="adm-panel"><AdminEmpty icon="target" title={emptyCopy[0]} description={emptyCopy[1]} /></div> : rows.map(row => { const d = row.def, data = getData(d.id, row.schedule?.occurrenceId); const days = row.schedule ? daysBetweenJalali(today, row.schedule.submitDeadline) : null; return <Card key={`${d.id}_${row.schedule?.occurrenceId || "history"}`} style={{ marginBottom: 8, cursor: "pointer" }}><div onClick={() => open(row)} style={{ padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontSize: 13, fontWeight: 500 }}>{d.label}{row.schedule ? ` — ${row.schedule.label}` : ""}</div><div style={{ fontSize: 10.5, color: C.muted, marginTop: 3 }}>Your role: {d.registrarId === user.id ? "Data Submitter" : d.ownerId === user.id ? "Owner" : "Approver"}{row.schedule ? ` · Due ${displayDate(row.schedule.submitDeadline)}` : ""}</div>{tab === "action" && days != null && <div style={{ fontSize: 10, marginTop: 3, color: days < 0 ? C.red : days <= row.schedule.reminderDays ? C.amber : C.hint }}>{days < 0 ? `${Math.abs(days)} days overdue` : days === 0 ? "Today is the deadline" : `${days} days until submission deadline`}</div>}</div><Badge color={data?.status === "revision_requested" ? "amber" : ["approved", "locked"].includes(data?.status) ? "teal" : "gray"}>{indicatorStatusLabel(data?.status)}</Badge></div></Card>; })}
    </div>;
}

// ── REVIEW INBOX ───────────────────────────────────────────────────────────────
// Shared by every reviewing role. Which items a reviewer sees and may decide on is unchanged:
// contributions from people whose direct manager they are, and indicator values they approve.
function Inbox({ activities, impactLevels, evaluator, users }) {
    const { indicatorDefs, indicatorData, activePeriod, periods, activityGroups, reviewActivityApi, reviewIndicatorValueApi } = useData();
    const { language } = useI18n();
    const canReview = useCallback(activity => canReviewActivity(evaluator, activity, users), [evaluator, users]);
    return <ReviewInbox language={language} activities={activities} indicatorData={indicatorData} indicatorDefs={indicatorDefs}
        users={users} activityGroups={activityGroups} periods={periods} activePeriod={activePeriod} evaluator={evaluator}
        impactLevels={impactLevels} canReviewActivity={canReview}
        reviewActivity={reviewActivityApi} reviewIndicator={reviewIndicatorValueApi} notify={uxNotify} />;
}

// One strategic indicator as calculated by the CVI engine (formula template, cap, approved
// values only). The browser never derives achievement from actual / target; a missing engine
// value is shown as "No approved data", never as 0%.
const INDICATOR_BAND = { onTrack: 85, watch: 65 };
function engineIndicatorStatus(achievement) {
    if (achievement == null) return "no_data";
    return achievement >= INDICATOR_BAND.onTrack ? "on_track" : achievement >= INDICATOR_BAND.watch ? "watch" : "critical";
}
function EngineIndicatorRow({ label, unit, engine }) {
    const fr = typeof document !== "undefined" && document.documentElement.lang === "fr";
    const achievement = cviValue(engine?.achievement);
    const status = engineIndicatorStatus(achievement);
    const color = { on_track: C.teal, watch: C.amber, critical: C.red, no_data: C.muted }[status];
    const statusText = (fr
        ? { on_track: "Conforme", watch: "À surveiller", critical: "Sous la cible", no_data: "Aucune donnée approuvée" }
        : { on_track: "On track", watch: "Watch", critical: "Below target", no_data: "No approved data" })[status];
    const fmt = value => (value == null ? "—" : Number(value).toLocaleString(document.documentElement.lang === "fr" ? "fr-CA" : "en-CA", { maximumFractionDigits: 1 }));
    return <div className="cvi-dashboard-indicator-row">
        <div className="cvi-dashboard-indicator-meta">
            <strong>{label}</strong>
            <span style={{ color }}>{achievement == null ? "—" : `${achievement.toFixed(0)}%`}</span>
        </div>
        {achievement == null ? <div className="cvi-indicator-nodata" aria-hidden="true" /> : <MiniBar value={Math.min(100, achievement)} color={color} height={7} />}
        <small data-cvi-i18n="managed"><span className={`cvi-indicator-status is-${status}`}>{statusText}</span> · {fr ? "Réel" : "Actual"}{fr ? " : " : ": "}{fmt(engine?.actual)} {unit || engine?.unit || ""} · {fr ? "Cible" : "Target"}{fr ? " : " : ": "}{fmt(engine?.target)} {unit || engine?.unit || ""}</small>
    </div>;
}
function engineIndicatorSummary(defs, scopedCvi) {
    const counts = { on_track: 0, watch: 0, critical: 0, no_data: 0 };
    for (const def of defs) counts[engineIndicatorStatus(cviValue(scopedCvi.indicatorFor(def.id)?.achievement))] += 1;
    return { ...counts, total: defs.length, withData: defs.length - counts.no_data };
}

// ── TEAM DASHBOARD ─────────────────────────────────────────────────────────────
function TeamDashboard({ user, activities, activityGroups, positions, indicatorDefs,
    indicatorData, activePeriod, members, teams, onNavigate }) {
    const { orgUnits, periods } = useData();
    const [selectedTeamPeriodId, setSelectedTeamPeriodId] = useState(null);
    const chosenPeriod = periods.find(p => String(p.id) === String(selectedTeamPeriodId)) || activePeriod;
    const historicalTeam = Boolean(chosenPeriod?.id && String(chosenPeriod.id) !== String(activePeriod?.id));
    const { language: experienceLanguage } = useI18n();
    const fr = experienceLanguage === "fr";
    const L = (en, frText) => (fr ? frText : en);
    const teamInds = withPeriodData(indicatorDefs.filter(i =>
        user.orgUnitId ? (i.level === "org_unit" && i.orgUnitId === user.orgUnitId) : (i.level === "team" && i.teamId === user.teamId)
    ), indicatorData, chosenPeriod?.id);
    const team = teams.find(t => t.id === user.teamId);
    const orgUnit = orgUnits.find(unit => unit.id === user.orgUnitId);
    const unitLabel = orgUnit?.label || team?.label || L("Team", "Équipe");
    // Every CVI shown here is the server result for people the viewer may see (live, or the final
    // snapshot for a locked cycle). People without a result are shown as "—" and are not averaged as 0.
    const scopedCvi = useScopedCvi(chosenPeriod?.id);
    const memberScores = members.map(member => ({ member, cvi: cviValue(scopedCvi.scoresFor(member.id)?.cvi) }));
    const cviCoverage = averageScore(memberScores.map(row => row.cvi));
    const pendingCount = activities.filter(a => members.some(m => m.id === a.userId) && isReviewableActivity(a)).length;
    const supportNeeds = activities.filter(a =>
        members.some(m => m.id === a.userId) &&
        ["open", "in_progress"].includes(String(a.blockerStatus || "").toLowerCase())
    ).length;
    // The scoped endpoint returns the weighted KPI-only score from the backend engine.
    // Refuse cross-unit/inconsistent values rather than computing an invented average.
    const avgGoal = consistentTeamKpiScore(members, user, scopedCvi.kpis);
    // Rows for the Team Dashboard sections: server scores and engine indicator results for the
    // selected period; review counts only for the current period.
    const teamMemberRows = memberRows({
        members, historical: historicalTeam, scoresFor: id => scopedCvi.scoresFor(id),
        reviewsWaiting: id => activities.filter(a => a.userId === id && isReviewableActivity(a)).length,
        positionOf: member => positions.find(p => p.id === member.posId)?.label || null,
    });
    const teamIndicatorRows = teamIndicatorRowsFor({
        defs: teamInds, engineFor: id => scopedCvi.indicatorFor(id), records: indicatorData,
        periodId: chosenPeriod?.id, statusOf: engineIndicatorStatus,
    });

    return (
        <div className="cvi-dashboard-page cvi-team-dashboard">
            <DashboardPageIntro
                eyebrow={L("TEAM DASHBOARD", "TABLEAU DE BORD DE L’ÉQUIPE")}
                title="My Team"
                subtitle={L(`${unitLabel} · ${members.length} team member${members.length === 1 ? "" : "s"} · Manager: ${user.name}`, `${unitLabel} · ${members.length} membre${members.length === 1 ? "" : "s"} de l’équipe · Gestionnaire : ${user.name}`)}
                period={chosenPeriod?.label || "No active period"}
                periodOptions={periods.filter(p => p.status !== "DRAFT" && p.status !== "draft").map(p => ({ ...p, periodId: p.id }))}
                onPeriodSelect={p => setSelectedTeamPeriodId(String(p.periodId || p.id))}
                actions={<DashboardScopeSwitch active="team" onChange={(scope) => scope === "performance" && onNavigate("dashboard")} />}
            />

            <TeamDashboardView language={experienceLanguage} unitLabel={unitLabel} historical={historicalTeam}
                periodState={chosenPeriod?.status === "locked" ? "final" : "provisional"}
                loading={!scopedCvi.loaded} error={Boolean(scopedCvi.error)}
                teamCvi={cviCoverage} memberCount={members.length} attainment={avgGoal}
                indicatorTotal={teamIndicatorRows.length} indicatorWithData={teamIndicatorRows.filter(row => row.achievement != null).length}
                reviews={pendingCount} support={supportNeeds} onOpenInbox={() => onNavigate("inbox")}
                rows={teamMemberRows} indicators={teamIndicatorRows}
                renderAvatar={member => <UserAvatar user={member} size={32} />} />

            {!historicalTeam && <TeamReviewCycleCard language={experienceLanguage} periodId={activePeriod?.id} />}

            {!historicalTeam && <ChallengesSection language={experienceLanguage} scope="team" title={experienceLanguage === "fr" ? "Défis et soutien de l’équipe" : "Team challenges & support"} />}
        </div>
    );
}

// ── TEAM MEMBERS ───────────────────────────────────────────────────────────────
// نمای فقط‌خواندنی جزئیات سؤالات وظایف مستمر یک فرد — بانک درست را بر اساس نقش او انتخاب می‌کند
function PersonRoutineDetail({ user, routineCtx, activePeriod }) {
    let questions, answers;
    if (user.role === "manager") {
        questions = user.orgUnitId
            ? routineCtx.deptRoutineQuestions.filter(q => q.orgUnitId === user.orgUnitId)
            : routineCtx.deptRoutineQuestions.filter(q => q.deptId === user.deptId);
        answers = routineCtx.deptRoutineAnswers;
    } else if (user.role === "deputy") {
        questions = user.orgUnitId
            ? routineCtx.orgRoutineQuestions.filter(q => q.orgUnitId === user.orgUnitId || q.scope === "organization")
            : routineCtx.orgRoutineQuestions;
        answers = routineCtx.orgRoutineAnswers;
    } else if (user.role === "specialist") {
        questions = user.orgUnitId
            ? routineCtx.routineQuestions.filter(q => q.orgUnitId === user.orgUnitId)
            : routineCtx.routineQuestions.filter(q => q.teamId === user.teamId);
        answers = routineCtx.routineAnswers;
    } else {
        return null;
    }
    if (!questions.length) {
        return <div style={{ fontSize: 11, color: C.hint }}>No Core Responsibilities criteria have been defined for this person.</div>;
    }
    const byMonth = { 1: [], 2: [], 3: [] };
    questions.forEach(q => byMonth[q.month]?.push(q));
    const answerFor = (qId) => answers.find(a => a.questionId === qId && a.userId === user.id && a.periodId === activePeriod?.id);

    return (
        <div>
            {[1, 2, 3].map(month => byMonth[month].length > 0 && (
                <div key={month} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 500, color: C.muted, marginBottom: 6 }}>
                        {month === 1 ? "Month 1" : month === 2 ? "Month 2" : "Month 3"}
                    </div>
                    {byMonth[month].map(q => {
                        const ans = answerFor(q.id);
                        const lvl = ans ? routineCtx.routineLevels.find(l => l.id === ans.levelId) : null;
                        return (
                            <div key={q.id} style={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", gap: 8, padding: "7px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 11.5
                            }}>
                                <span style={{ color: "#444" }}>{q.label}</span>
                                {lvl ? <Badge color="purple" small>{lvl.label}</Badge> : <span style={{ color: C.hint, flexShrink: 0 }}>—</span>}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

function TeamView({ activities, activityGroups, positions, indicatorDefs,
    indicatorData, activePeriod, members, title = "Unit members",
    emptyMessage = "No members are assigned to this manager yet", enableOrgFilters = false, fixedDepartmentId = null }) {
    const { orgSettings, teams, departments, orgUnits } = useData();
    const routineCtx = useRoutineCtx();
    const [selected, setSelected] = useState(null);
    // Server results only: the scoped list for the table and the full result for the open member.
    const scopedCvi = useScopedCvi(activePeriod?.id);
    const selectedCvi = useMemberCvi(selected?.id, activePeriod?.id);
    const [departmentFilter, setDepartmentFilter] = useState(fixedDepartmentId || "all");
    const [teamFilter, setTeamFilter] = useState("all");
    const [orgUnitFilter, setOrgUnitFilter] = useState("all");
    const flexibleOrgActive = orgUnits.some(unit => unit.isActive !== false);
    const availableTeams = departmentFilter === "all"
        ? teams : teams.filter(t => t.deptId === departmentFilter);
    const visibleMembers = enableOrgFilters ? members.filter(m => {
        if (flexibleOrgActive) {
            if (orgUnitFilter === "all") return true;
            return orgUnitSubtreeIds(orgUnits, orgUnitFilter).includes(m.orgUnitId);
        }
        return (departmentFilter === "all" || m.deptId === departmentFilter ||
            teams.find(t => t.id === m.teamId)?.deptId === departmentFilter) &&
            (teamFilter === "all" || m.teamId === teamFilter);
    }) : members;

    if (selected) {
        const mActs = activities.filter(a => a.userId === selected.id);
        const result = selectedCvi.data;
        const scores = result?.scores || scopedCvi.scoresFor(selected.id) || {};
        const nonRoutine = cviValue(scores.nonRoutine);
        const routine = cviValue(scores.routine);
        const peerFeedback = cviValue(scores.peerFeedback);
        const positionWeights = positions.find(p => p.id === selected.posId) || orgSettings;
        // Weights the server actually applied (live result or snapshot); configured weights otherwise.
        const selectedWeights = {
            wNonRoutine: result?.weights?.tasksActivitiesSplit?.nonContinuous ?? positionWeights.wNonRoutine,
            wRoutine: result?.weights?.tasksActivitiesSplit?.continuous ?? positionWeights.wRoutine,
            wPeerFeedback: result?.weights?.individualComponents?.peerFeedback ?? positionWeights.wPeerFeedback,
        };
        const ind = cviValue(scores.individual);
        const org = cviValue(scores.organizational);
        const cvi = cviValue(scores.cvi);
        return (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <SubBar title={selected.name} onBack={() => setSelected(null)} />
                <div style={{padding:"8px 16px 0",overflowY:"auto",maxHeight:310}}><MemberCviDetails personId={selected.id} periodId={activePeriod?.id} language={typeof document !== "undefined" && document.documentElement.lang === "fr" ? "fr" : "en"} /></div>
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <div style={{
                        background: C.navy, borderRadius: 14, padding: 18,
                        display: "flex", alignItems: "center", gap: 16, marginBottom: 12
                    }}>
                        <ScoreRing value={cvi} size={72} color={C.cyan} />
                        <div style={{ flex: 1 }}>
                            <div style={{
                                fontSize: 10, color: "rgba(255,255,255,0.5)",
                                letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4
                            }}>
                                Current-period CVI
                            </div>
                            <div style={{ fontSize: 28, fontWeight: 500, color: "#fff", lineHeight: 1 }}>
                                {formatScore(cvi, 1)}
                            </div>
                            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>
                                √({formatScore(ind, 0)} × {formatScore(org, 1)})
                            </div>
                        </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                        {[["Individual", ind, C.navy], ["Organization", org, C.purple],
                        ["Approved", mActs.filter(a => a.status === "approved").length, C.teal]].map(([l, v, c]) => (
                            <div key={l} style={{
                                background: C.card, border: `1px solid ${C.border}`,
                                borderRadius: 10, padding: "12px 10px"
                            }}>
                                <div style={{ fontSize: 10, color: C.hint, marginBottom: 5 }}>{l}</div>
                                <div style={{ fontSize: 20, fontWeight: 500, color: c }}>
                                    {v == null ? "—" : typeof v === "number" ? v.toFixed(l === "Approved" ? 0 : 1) : v}
                                </div>
                                {typeof v === "number" && l !== "Approved" && <MiniBar value={v} color={c} />}
                            </div>
                        ))}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Individual Contribution breakdown</div>
                    <div style={{
                        background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 12, padding: 14, marginBottom: 12
                    }}>
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                                <span style={{ color: "#444", fontWeight: 500 }}>
                                    Responsibilities & Contributions <span style={{ color: C.hint, fontWeight: 400 }}>· Weight {selectedWeights.wNonRoutine}%</span>
                                </span>
                                <span style={{ color: C.muted }}>{nonRoutine == null ? "Not submitted" : `${nonRoutine.toFixed(0)} / 100`}</span>
                            </div>
                            <MiniBar value={nonRoutine ?? 0} color={C.cyan} height={5} />
                        </div>
                        <div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                                <span style={{ color: "#444", fontWeight: 500 }}>
                                    Core responsibilities performance <span style={{ color: C.hint, fontWeight: 400 }}>· Weight {selectedWeights.wRoutine}%</span>
                                </span>
                                <span style={{ color: C.muted }}>{routine == null ? "Not submitted" : `${routine.toFixed(0)} / 100`}</span>
                            </div>
                            <MiniBar value={routine ?? 0} color={C.purple} height={5} />
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                                <span style={{ color: "#444", fontWeight: 500 }}>
                                    Peer Feedback <span style={{ color: C.hint, fontWeight: 400 }}>· Weight {selectedWeights.wPeerFeedback ?? 0}%</span>
                                </span>
                                <span style={{ color: C.muted }}>{peerFeedback == null ? "Not submitted" : `${peerFeedback.toFixed(0)} / 100`}</span>
                            </div>
                            <MiniBar value={peerFeedback ?? 0} color={C.amber} height={5} />
                        </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Core Responsibilities criteria details</div>
                    <div style={{
                        background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 12, padding: 14, marginBottom: 12
                    }}>
                        <PersonRoutineDetail user={selected} routineCtx={routineCtx} activePeriod={activePeriod} />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Category scores</div>
                    <div style={{
                        background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 12, padding: 14, marginBottom: 12
                    }}>
                        {activityGroups.map((g, i) => {
                            // Category scores from the server result for this cycle (live: array, snapshot: map).
                            const serverGroups = result?.breakdown?.nonRoutine?.groups;
                            const capped = Array.isArray(serverGroups)
                                ? cviValue(serverGroups.find(row => row.groupId === g.id)?.score) ?? 0
                                : serverGroups ? cviValue(serverGroups[g.id]) ?? 0 : null;
                            return (
                                <div key={g.id} style={{ marginBottom: i < activityGroups.length - 1 ? 12 : 0 }}>
                                    <div style={{
                                        display: "flex", justifyContent: "space-between",
                                        fontSize: 11, marginBottom: 4
                                    }}>
                                        <span style={{ color: "#444", fontWeight: 500 }}>{g.label}</span>
                                        <span style={{ color: C.muted }}>{capped == null ? "—" : capped} / {g.maxScore}</span>
                                    </div>
                                    <MiniBar value={capped ?? 0} max={g.maxScore} color={C.cyan} height={5} />
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Recorded contributions</div>
                    <div style={{
                        background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 12, overflow: "hidden"
                    }}>
                        {mActs.length === 0 ? (
                            <div style={{ padding: 16, textAlign: "center", color: C.muted, fontSize: 12 }}>
                                No contributions have been recorded yet
                            </div>
                        ) : mActs.map((a, i) => (
                            <div key={a.id} style={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", padding: "10px 14px",
                                borderBottom: i < mActs.length - 1 ? `1px solid ${C.bg}` : "none"
                            }}>
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 500 }}>{a.title}</div>
                                    <div style={{ fontSize: 10, color: C.hint, marginTop: 1 }}>{groupLabel(activityGroups, a.groupId)}</div>
                                </div>
                                <StatusBadge status={a.status} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const avgCVI = formatScore(averageScore(visibleMembers.map(m => scopedCvi.scoresFor(m.id)?.cvi)).value, 1);

    return (
        <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
            <div style={{ marginBottom: 20 }}>
                <AdminPageHeader title={title}
                    description="Members you are authorized to see, with their current CVI. Select a person to open their detailed results."
                    meta={<span className="adm-meta-pill"><span>Average CVI</span><strong>{avgCVI}</strong></span>} />
            </div>
            {enableOrgFilters && <Card style={{ marginBottom: 12 }}>
                {flexibleOrgActive ? (
                    <div style={{ padding: 12 }}>
                        <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 5 }}>Organization Unit</label>
                        <select value={orgUnitFilter} onChange={e => setOrgUnitFilter(e.target.value)}
                            style={{
                                width: "100%", padding: "9px 10px", border: `1px solid ${C.borderMd}`,
                                borderRadius: 8, background: "#fff", fontFamily: "inherit", fontSize: 11.5
                            }}>
                            <option value="all">All organization units</option>
                            {orgUnits.filter(unit => unit.isActive !== false).map(unit =>
                                <option key={unit.id} value={unit.id}>{unit.label}</option>)}
                        </select>
                    </div>
                ) : (
                    <div style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10 }}>
                        {!fixedDepartmentId && <div>
                            <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 5 }}>Division</label>
                            <select value={departmentFilter} onChange={e => {
                                setDepartmentFilter(e.target.value); setTeamFilter("all");
                            }} style={{
                                width: "100%", padding: "9px 10px", border: `1px solid ${C.borderMd}`,
                                borderRadius: 8, background: "#fff", fontFamily: "inherit", fontSize: 11.5
                            }}>
                                <option value="all">All business units</option>
                                {departments.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                            </select>
                        </div>}
                        <div style={{ gridColumn: fixedDepartmentId ? "1 / -1" : "auto" }}>
                            <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 5 }}>Team</label>
                            <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)}
                                style={{
                                    width: "100%", padding: "9px 10px", border: `1px solid ${C.borderMd}`,
                                    borderRadius: 8, background: "#fff", fontFamily: "inherit", fontSize: 11.5
                                }}>
                                <option value="all">All teams</option>
                                {availableTeams.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                            </select>
                        </div>
                    </div>
                )}
            </Card>}
            {visibleMembers.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 12 }}>
                    {members.length === 0 ? emptyMessage : "No member matches this filter"}
                </div>
            ) : visibleMembers.map(m => {
                const memberScores = scopedCvi.scoresFor(m.id) || {};
                const ind = cviValue(memberScores.individual);
                const cvi = cviValue(memberScores.cvi);
                const mp = activities.filter(a => a.userId === m.id && isReviewableActivity(a)).length;
                return (
                    <div key={m.id} onClick={() => setSelected(m)}
                        style={{
                            background: C.card, border: `1px solid ${C.border}`,
                            borderRadius: 12, marginBottom: 8, cursor: "pointer"
                        }}>
                        <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: "50%", background: C.cyanBg,
                                border: `2px solid ${C.cyan}`, display: "flex", alignItems: "center",
                                justifyContent: "center", fontSize: 17, fontWeight: 600,
                                color: C.navy, flexShrink: 0
                            }}>{m.name[0]}</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{m.name}</div>
                                <div style={{ display: "flex", gap: 6 }}>
                                    {mp > 0 && <Badge color="amber" small>{mp} Pending</Badge>}
                                    <Badge color="gray" small>Individual Contribution: {formatScore(ind, 0)}</Badge>
                                </div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <ScoreRing value={cvi} size={52}
                                    color={cvi == null ? C.hint : cvi >= 70 ? C.teal : cvi >= 50 ? C.amber : C.red} />
                                <div style={{ fontSize: 9, color: C.hint, marginTop: 2 }}>CVI</div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ── داشبورد معاونت — مقایسه‌ی تیم‌های زیرمجموعه + شاخص‌های معاونت + drill-down به افراد ──
function DeptDashboard({ deptId, orgUnitId, activities, activityGroups, positions, indicatorDefs, indicatorData, activePeriod }) {
    const { teams, users, departments, orgUnits, periods } = useData();
    const [selectedDeptPeriodId, setSelectedDeptPeriodId] = useState(null);
    const chosenDeptPeriod = periods.find(p => String(p.id) === String(selectedDeptPeriodId)) || activePeriod;
    const historicalDept = Boolean(chosenDeptPeriod?.id && String(chosenDeptPeriod.id) !== String(activePeriod?.id));
    const { language } = useI18n();
    const fr = language === "fr";
    const L = (en, frText) => (fr ? frText : en);
    const [selectedChildId, setSelectedChildId] = useState(null);

    const flexible = Boolean(orgUnitId);
    const rootUnit = flexible ? orgUnits.find(unit => unit.id === orgUnitId) : null;
    const scopeIds = flexible ? orgUnitSubtreeIds(orgUnits, orgUnitId) : [];
    const childUnits = flexible ? orgUnitChildren(orgUnits, orgUnitId) : [];
    const dept = departments.find(d => d.id === deptId);
    const deptTeams = flexible ? [] : teams.filter(t => t.deptId === deptId);
    const scopeMembers = users.filter(u => {
        if (!["manager", "specialist"].includes(u.role)) return false;
        return flexible ? scopeIds.includes(u.orgUnitId) : u.deptId === deptId;
    });
    const scopeInds = withPeriodData(indicatorDefs.filter(i =>
        flexible ? (i.level === "org_unit" && i.orgUnitId === orgUnitId) : (i.level === "dept" && i.deptId === deptId)
    ), indicatorData, chosenDeptPeriod?.id);

    // Every CVI shown here is the server result for people the viewer may see (live, or the final
    // snapshot for a locked cycle). People without a result are shown as "—" and are not averaged as 0.
    const scopedCvi = useScopedCvi(chosenDeptPeriod?.id);
    const scoreOf = m => cviValue(scopedCvi.scoresFor(m.id)?.cvi);
    const scopeCvi = averageScore(scopeMembers.map(scoreOf));
    const scopeAvg = scopeCvi.value;
    const scopeIndividual = averageScore(scopeMembers.map(m => scopedCvi.scoresFor(m.id)?.individual));
    const scopeCollective = averageScore(scopeMembers.map(m => scopedCvi.scoresFor(m.id)?.organizational));
    const childStats = ids => averageScore(scopeMembers.filter(m => ids(m)).map(scoreOf));
    const indicatorSummary = engineIndicatorSummary(scopeInds, scopedCvi);

    if (selectedChildId) {
        if (flexible) {
            const child = orgUnits.find(unit => unit.id === selectedChildId);
            const ids = orgUnitSubtreeIds(orgUnits, selectedChildId);
            const members = scopeMembers.filter(member => ids.includes(member.orgUnitId));
            return <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <SubBar title={child?.label || "Sub-unit"} onBack={() => setSelectedChildId(null)} />
                <TeamView activities={activities} activityGroups={activityGroups} positions={positions} indicatorDefs={indicatorDefs}
                    indicatorData={indicatorData} activePeriod={chosenDeptPeriod} members={members}
                    title={`Members: ${child?.label || "Sub-unit"}`} />
            </div>;
        }
        const team = teams.find(t => t.id === selectedChildId);
        const members = scopeMembers.filter(m => m.teamId === selectedChildId);
        return <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <SubBar title={team?.label || "Team"} onBack={() => setSelectedChildId(null)} />
            <TeamView activities={activities} activityGroups={activityGroups} positions={positions} indicatorDefs={indicatorDefs}
                indicatorData={indicatorData} activePeriod={chosenDeptPeriod} members={members} />
        </div>;
    }

    const scopeLabel = flexible ? (rootUnit?.label || "Unit dashboard") : (dept?.label || "Business Unit dashboard");
    const children = flexible ? childUnits : deptTeams;

    return (
        <div className="cvi-dashboard-page">
            <DashboardPageIntro title={scopeLabel}
                subtitle={L(`${scopeMembers.length} ${scopeMembers.length === 1 ? "person" : "people"} · ${children.length} ${children.length === 1 ? "sub-unit" : "sub-units"}${chosenDeptPeriod?.status === "active" ? " · Provisional results until the period is locked" : ""}`,
                    `${scopeMembers.length} ${scopeMembers.length === 1 ? "personne" : "personnes"} · ${children.length} ${children.length === 1 ? "sous-unité" : "sous-unités"}${chosenDeptPeriod?.status === "active" ? " · Résultats provisoires jusqu’au verrouillage de la période" : ""}`)}
                eyebrow="Unit dashboard" period={chosenDeptPeriod?.label || "—"}
                periodOptions={periods.filter(p => !["draft", "DRAFT"].includes(String(p.status))).map(p => ({ ...p, periodId: p.id }))}
                onPeriodSelect={p => setSelectedDeptPeriodId(String(p.periodId || p.id))} />
            {historicalDept && <div className="adm-callout is-neutral" role="status"><div>{L(
                "Historical unit results · read-only; unavailable historical values are not replaced by current scores.",
                "Résultats historiques de l’unité · lecture seule; aucune substitution par les résultats actuels."
            )}</div></div>}
            {scopedCvi.error && <div className="adm-callout is-danger" role="alert" style={{ marginBottom: 16 }}><div>{L("CVI results could not be loaded. Values are shown as “—”.", "Les résultats CVI n’ont pas pu être chargés. Les valeurs sont affichées comme « — ».")}</div></div>}
            <div className="cvi-dashboard-kpi-grid cvi-unit-kpis">
                <DashboardMetricCard label={L("Unit CVI (mean)", "CVI de l’unité (moyenne)")} value={formatScore(scopeAvg, 1)} helper={L(`${scopeCvi.count} of ${scopeCvi.total} people with a CVI`, `${scopeCvi.count} personnes sur ${scopeCvi.total} avec un CVI`)} tone="cyan" icon={<UiIcon name="performance" size={16} />} />
                <DashboardMetricCard label="Individual Contribution" value={formatScore(scopeIndividual.value, 1)} helper={`${L("Mean", "Moyenne")} · n = ${scopeIndividual.count}`} tone="navy" icon={<UiIcon name="self" size={16} />} />
                <DashboardMetricCard label="Collective Contribution" value={formatScore(scopeCollective.value, 1)} helper={`${L("Mean", "Moyenne")} · n = ${scopeCollective.count}`} tone="navy" icon={<UiIcon name="team" size={16} />} />
                <DashboardMetricCard label={L("Indicators on track", "Indicateurs conformes")} value={scopeInds.length ? `${indicatorSummary.on_track} / ${scopeInds.length}` : "—"}
                    helper={scopeInds.length ? L(`${indicatorSummary.no_data} without approved data · engine-calculated`, `${indicatorSummary.no_data} sans donnée approuvée · calcul du moteur`) : L("No unit indicators", "Aucun indicateur d’unité")}
                    tone={!indicatorSummary.withData ? "navy" : indicatorSummary.critical ? "red" : indicatorSummary.watch ? "amber" : "teal"} icon={<UiIcon name="target" size={16} />} />
            </div>
            <DashboardSection title="Unit indicators">
                <Card><div className="cvi-dashboard-indicator-list">
                    {scopeInds.length === 0 ? <div className="cvi-dashboard-empty">No indicators have been defined for this unit</div>
                        : scopeInds.map(ind => <EngineIndicatorRow key={ind.id} label={ind.label} unit={ind.unit} engine={scopedCvi.indicatorFor(ind.id)} />)}
                </div></Card>
            </DashboardSection>
            <DashboardSection title="Sub-unit comparison">
                {children.length === 0 ? <div className="cvi-dashboard-empty standalone">No sub-units have been defined here</div>
                    : <div className="cvi-dashboard-comparison-grid">{children.map(child => {
                        const childIds = flexible ? orgUnitSubtreeIds(orgUnits, child.id) : null;
                        const stats = childStats(m => flexible ? childIds.includes(m.orgUnitId) : m.teamId === child.id);
                        const small = stats.count > 0 && stats.count < 3;
                        return <button type="button" key={child.id} className="cvi-dashboard-comparison-card" onClick={() => setSelectedChildId(child.id)}
                            aria-label={L(`${child.label}: CVI ${formatScore(stats.value, 1)}, ${stats.count} of ${stats.total} with a CVI. View members`, `${child.label} : CVI ${formatScore(stats.value, 1)}, ${stats.count} sur ${stats.total} avec un CVI. Voir les membres`)}>
                            <div><strong>{child.label}</strong><small>{L(`${stats.total} ${stats.total === 1 ? "person" : "people"} · ${stats.count} with a CVI`, `${stats.total} ${stats.total === 1 ? "personne" : "personnes"} · ${stats.count} avec un CVI`)}{small ? L(" · small sample", " · petit échantillon") : ""}</small></div>
                            <div className="cvi-dashboard-comparison-score"><span>CVI</span><strong>{formatScore(stats.value, 1)}</strong></div>
                        </button>;
                    })}</div>}
            </DashboardSection>
        </div>
    );
}

// ── داشبورد کل شرکت — خلاصه سازمانی + مقایسه‌ی معاونت‌ها و تیم‌ها + drill-down به افراد ──
// ── خوداظهاری فرد — بدون اثر مستقیم بر امتیاز CVI ────────────────────
function RoutineSelfAssessment({ activePeriod, user }) {
    const { routineLevels, periods, users } = useData();
    const { language } = useI18n();
    // The same endpoints as before: the self-assessment, its autosave and monthly submission, plus
    // the manager's recorded ratings for the signed-in person (GET /api/routine/mine).
    const api = useMemo(() => {
        const request = async (path, options = {}) => {
            const token = sessionStorage.getItem("cvi_token");
            if (!token) throw new Error("Authentication required.");
            const response = await fetch(`${API_BASE_URL}/api/routine${path}`, {
                ...options,
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(payload.message || "Core responsibilities request failed.");
            return payload;
        };
        return {
            loadSelf: periodId => request(`/self?periodId=${encodeURIComponent(periodId)}`),
            loadManager: periodId => request(`/mine?periodId=${encodeURIComponent(periodId)}`),
            saveSelf: body => request("/self/answers", { method: "POST", body: JSON.stringify(body) }),
            submitSelf: (periodId, month) => request("/self/submit", { method: "POST", body: JSON.stringify({ periodId, month }) }),
        };
    }, []);
    const nameOf = id => users.find(u => u.id === id)?.name || (user?.id === id ? user?.name : null) || null;
    return <CoreResponsibilitiesPage key={activePeriod?.id || "none"} api={api} activePeriod={activePeriod} periods={periods}
        routineLevels={routineLevels} language={language} currentUserId={user?.id || null} nameOf={nameOf}
        orgLabel={text => translateCanadaEnglishUiText(text, language)} periodLabel={label => localizePeriodLabel(label, language)}
        confirm={message => uxConfirm(message)} />;
}

// ── MANAGER APP SHELL (ریسپانسیو — دسکتاپ Sidebar / موبایل BottomNav) ─────────
// ── ارزیابی وظایف مستمر (پاسخ‌دهی ماهانه‌ی مدیر به سؤالات تیم برای هر عضو) ─────────
// ── ارزیابی وظایف مستمر عمومی — با هر بانک سؤالی (تیم/معاونت/سازمان) و حالت فقط‌خواندنی قابل استفاده است ──
function GenericRoutineEvaluation({ title, members, questions, answers, setAnswers, routineLevels,
    activePeriod, readOnly, noMembersMessage, noQuestionsMessage, subject = null, embedded = false }) {
    const { routineLoading, routineError, saveRoutineAnswerApi, users } = useData();
    const { language } = useI18n();
    const [selectedMember, setSelectedMember] = useState(subject);
    useEffect(() => { if (subject?.id) setSelectedMember(subject); }, [subject?.id]);
    const [savingKey, setSavingKey] = useState("");
    const [saveError, setSaveError] = useState("");
    const [selfReport, setSelfReport] = useState(null);
    const [selfLoading, setSelfLoading] = useState(false);
    // Evaluation Responsibility evidence for the selected person. A failed request stays an error:
    // the panel must not present missing evidence as "not applicable".
    const [responsibility, setResponsibility] = useState({ data: null, loading: false, error: null });
    const [responsibilityReload, setResponsibilityReload] = useState({ count: 0, silent: false });
    useEffect(() => {
        if (!selectedMember?.id || !activePeriod?.id) { setResponsibility({ data: null, loading: false, error: null }); return; }
        let cancelled = false;
        // A silent reload (after a saved rating) keeps the current evidence on screen until it is replaced.
        if (!responsibilityReload.silent) setResponsibility({ data: null, loading: true, error: null });
        const token = sessionStorage.getItem("cvi_token");
        fetch(`${API_BASE_URL}/api/evaluation-responsibility/${encodeURIComponent(selectedMember.id)}?periodId=${encodeURIComponent(activePeriod.id)}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(async response => {
            if (!response.ok) throw new Error("Evidence is unavailable");
            return response.json();
        }).then(payload => { if (!cancelled) setResponsibility({ data: payload, loading: false, error: null }); })
          .catch(error => { if (!cancelled && !responsibilityReload.silent) setResponsibility({ data: null, loading: false, error }); });
        return () => { cancelled = true; };
    }, [selectedMember?.id, activePeriod?.id, responsibilityReload]);
    const weightSum = questions.reduce((s, q) => s + (Number(q.weight) || 0), 0);

    useEffect(() => {
        if (!selectedMember?.id || !activePeriod?.id) { setSelfReport(null); return; }
        let cancelled = false;
        (async () => {
            setSelfLoading(true);
            try {
                const token = sessionStorage.getItem("cvi_token");
                const response = await fetch(`${API_BASE_URL}/api/routine/self/${selectedMember.id}?periodId=${encodeURIComponent(activePeriod.id)}`, { headers: { Authorization: `Bearer ${token}` } });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.message || "Self-assessment status failed.");
                if (!cancelled) setSelfReport(payload);
            } catch (error) {
                if (!cancelled) { setSelfReport({ status: "SELF_ASSESSMENT_PENDING", submittedMonths: [], answers: [] }); setSaveError(error.message || "Your self-assessment could not be loaded"); }
            } finally { if (!cancelled) setSelfLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [selectedMember?.id, activePeriod?.id]);
    const effectiveReadOnly = readOnly || !activePeriod || activePeriod.status !== "active";

    if (routineLoading) {
        return (
            <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 12 }}>{title}</div>
                <Card><div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 12 }}>
                    Loading...
                </div></Card>
            </div>
        );
    }

    if (!questions.length) {
        return (
            <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 12 }}>{title}</div>
                <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                    {noQuestionsMessage || "No Core Responsibilities criteria have been defined by the administrator yet."}
                </div>
            </div>
        );
    }

    const answerFor = (questionId, userId) =>
        answers.find(a => a.questionId === questionId && a.userId === userId && a.periodId === activePeriod?.id);

    const setAnswer = async (questionId, userId, levelId) => {
        if (effectiveReadOnly || !saveRoutineAnswerApi) return;
        const submittedSelfAnswer = selfReport?.answers?.find(answer => answer.questionId === questionId && answer.submittedAt);
        if (!submittedSelfAnswer) { setSaveError("The employee has not yet submitted a self-assessment for this item."); return; }
        const key = `${questionId}:${userId}`;
        setSavingKey(key);
        setSaveError("");
        try {
            await saveRoutineAnswerApi(questionId, userId, levelId);
        } catch (error) {
            console.error("Routine answer save failed:", error);
            setSaveError(error.message || "The Core Responsibilities rating could not be saved");
        } finally {
            setSavingKey("");
        }
    };

    const scoreFor = (userId) => calcScopedRoutineScore(answers, questions, routineLevels, userId, null, null, activePeriod?.id);

    if (selectedMember) {
        const subjectAnswers = answers.filter(a => a.userId === selectedMember.id && a.periodId === activePeriod?.id);
        const reviewReadOnly = !activePeriod || activePeriod.status !== "active" ? "closed" : readOnly ? "view" : null;
        const orgLabel = text => translateCanadaEnglishUiText(text, language);
        const nameOf = id => users.find(u => u.id === id)?.name || null;
        const saveComment = async (questionId, levelId, note) => {
            if (effectiveReadOnly || !saveRoutineAnswerApi) return;
            const key = `${questionId}:${selectedMember.id}`;
            setSavingKey(key); setSaveError("");
            try { await saveRoutineAnswerApi(questionId, selectedMember.id, levelId, note || ""); }
            catch (error) { console.error("Routine comment save failed:", error); setSaveError(error.message || "The Core Responsibilities rating could not be saved"); }
            finally { setSavingKey(""); }
        };
        return (
            <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: embedded ? "4px 0" : 16, background: embedded ? "transparent" : C.bg }}>
                <ManagerRoutineReview key={selectedMember.id} subject={selectedMember} questions={questions} answers={subjectAnswers}
                    routineLevels={routineLevels} selfReport={selfReport} selfLoading={selfLoading}
                    readOnly={reviewReadOnly}
                    score={scoreFor(selectedMember.id)} savingKey={savingKey} error={saveError || routineError}
                    onRate={(questionId, levelId) => setAnswer(questionId, selectedMember.id, levelId)} onComment={saveComment}
                    onBack={embedded ? null : () => setSelectedMember(null)} language={language}
                    orgLabel={orgLabel} nameOf={nameOf}>
                    {activePeriod && <EvaluationResponsibilityPanel key={selectedMember.id}
                        data={responsibility.data} loading={responsibility.loading} error={responsibility.error}
                        onRetry={() => setResponsibilityReload(r => ({ count: r.count + 1, silent: false }))}
                        readOnly={reviewReadOnly} routineLevels={routineLevels} language={language} orgLabel={orgLabel} nameOf={nameOf}
                        onRate={async levelId => {
                            const token = sessionStorage.getItem("cvi_token");
                            const response = await fetch(`${API_BASE_URL}/api/evaluation-responsibility/${encodeURIComponent(selectedMember.id)}/assessment`, {
                                method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                                body: JSON.stringify({ periodId: activePeriod.id, levelId }),
                            });
                            const payload = await response.json().catch(() => ({}));
                            if (!response.ok) throw new Error(payload.message || "Could not save Evaluation Responsibility");
                            // The saved rating matches the evidence it was recorded against; a silent reload then
                            // brings the stored record (evaluator, provenance) from the server.
                            setResponsibility(previous => ({ ...previous, data: { ...previous.data, assessment: { ...payload, evidenceCurrent: true } } }));
                            setResponsibilityReload(r => ({ count: r.count + 1, silent: true }));
                        }} />}
                </ManagerRoutineReview>
            </div>
        );
    }

    return (
        <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
            <RoutineMemberList title={translateCanadaEnglishUiText(title, language)} language={language} readOnly={Boolean(readOnly)} error={saveError || routineError}
                questionCount={questions.length} weightTotal={weightSum} onSelect={setSelectedMember}
                members={members.map(m => ({ id: m.id, name: m.name, raw: m, score: scoreFor(m.id), answered: questions.filter(q => answerFor(q.id, m.id)).length }))
                    .map(({ raw, ...rest }) => ({ ...raw, ...rest }))} />
        </div>
    );
}

// ارزیابی وظایف مستمر مدیر برای کارشناس‌های تیمش (سطح تیم)
function RoutineEvaluation({ user, members, activePeriod, subject = null }) {
    const { routineQuestions, routineAnswers, routineLevels, routineTargets } = useData();
    const unitQuestions = user.orgUnitId
        ? routineQuestions.filter(q => q.scope === "org_unit" && q.orgUnitId === (subject?.orgUnitId || user.orgUnitId))
        : routineQuestions.filter(q => q.teamId === user.teamId);
    const apiMembers = routineTargets;
    return (
        <GenericRoutineEvaluation title="Core Responsibilities review" members={apiMembers}
            questions={unitQuestions} answers={routineAnswers}
            routineLevels={routineLevels} activePeriod={activePeriod} subject={subject} embedded={Boolean(subject)}
            noMembersMessage="No members are assigned to this manager yet"
            noQuestionsMessage="No core responsibilities questions have been defined for this unit yet." />
    );
}

function EmployeeEvaluationWorkspace({ user, members, activePeriod }) {
    const { language } = useI18n();
    const { positions } = useData();
    return <EvaluationWorkspace members={members} activePeriod={activePeriod} language={language}
        periodLabel={label => localizePeriodLabel(label, language)}
        positionOf={member => { const label = positions.find(p => p.id === member.posId)?.label || member.position?.name; return label ? translateCanadaOrgText(label, language) : null; }}
        renderAvatar={(member, size) => <UserAvatar user={member} size={size} />}
        renderSection={(section, member) => section === "continuous"
            ? <RoutineEvaluation key={member.id} user={user} members={members} activePeriod={activePeriod} subject={member} />
            : <GoalsPage key={member.id} user={user} managerOnly selectedSubjectId={member.id} />} />;
}

// Org data (importance levels, positions, indicators) can still arrive as Persian server defaults;
// managed components receive it already in the interface language.
function translateCanadaOrgText(text, language) {
    if (text == null) return text;
    return translateCanadaEnglishUiText(/[\u0600-\u06FF]/.test(String(text)) ? translateLegacyUiText(text, language) : text, language);
}

function GoalsPage(props) {
    const { language } = useI18n();
    return <IndividualGoalsPage {...props} language={language} orgLabel={text => translateCanadaOrgText(text, language)} />;
}

function positionsLabelForEmployee(member) {
    return member?.role === "specialist" ? "Employee" : member?.role === "manager" ? "Manager" : member?.role === "deputy" ? "Business Unit Lead" : "Employee";
}

function ManagerApp({ user, onLogout }) {
    const { activities, setActivities, activityGroups, impactLevels, positions,
        indicatorDefs, indicatorData, activePeriod, teams, users, periodSnapshots,
        peerAssignments, periods } = useData();
    const { language: experienceLanguage } = useI18n();
    const routineCtx = useRoutineCtx();
    const isMobile = useIsMobile();
    const [nav, setNav] = useState("dashboard");
    const [view, setView] = useState(null);
    const [selectedAct, setSelectedAct] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [periodDetailOrigin, setPeriodDetailOrigin] = useState("cvi-detail");
    const [selectedOrgLevel, setSelectedOrgLevel] = useState(null);

    const directReports = users.filter(u => u.managerId === user.id && u.isActive !== false);
    const members = reportingSubtreeUsers(users, user.id);
    const hasTeamScope = directReports.length > 0;
    const pendingCount = activities.filter(a =>
        directReports.some(m => m.id === a.userId) && isReviewableActivity(a)).length;
    const indicatorReviewCount = indicatorData.filter(d => d.periodId === activePeriod?.id && d.status === "submitted" &&
        indicatorDefs.find(i => i.id === d.indicatorId)?.approverId === user.id).length;
    const peerPending = peerFeedbackPendingCount(peerAssignments, activePeriod?.id);
    const myActs = activities.filter(a => a.userId === user.id);
    const { data: liveCviPayload } = useLiveCvi(user.id, activePeriod?.id);
    const allPeriods = buildPerformanceHistory({
        snapshots: periodSnapshots, userId: user.id, periods, activePeriod,
        liveEntry: liveEntryFromServer(liveCviPayload, activePeriod),
    });

    // A contribution page is shown inside the shell; leaving Additional Contributions closes it.
    useEffect(() => {
        if (nav !== "activities" && view === "act-detail") { setView(null); setSelectedAct(null); }
    }, [nav, view]);

    if (view && view !== "act-detail") {
        return (
            <SpecialistDetailView view={view} setView={setView}
                selectedAct={selectedAct} setSelectedAct={setSelectedAct}
                selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod}
                periodDetailOrigin={periodDetailOrigin} setPeriodDetailOrigin={setPeriodDetailOrigin}
                myActs={myActs} allPeriods={allPeriods}
                activityGroups={activityGroups} impactLevels={impactLevels}
                user={user} positions={positions} indicatorDefs={indicatorDefs}
                indicatorData={indicatorData} activePeriod={activePeriod}
                routineCtx={routineCtx} selectedOrgLevel={selectedOrgLevel} />
        );
    }

    const renderContent = () => {
        if (nav === "new") return <NewContributionPage user={user} activePeriod={activePeriod} onClose={() => setNav("activities")}
            onOpenActivity={activity => { setSelectedAct(activity); setView("act-detail"); setNav("activities"); }} />;
        if (nav === "activities" && view === "act-detail" && selectedAct) return <ContributionDetailPage key={selectedAct.id} activity={selectedAct} user={user}
            onBack={() => { setView(null); setSelectedAct(null); }} onChange={setSelectedAct} />;
        if (nav === "dashboard") return (
            <ManagerDashboard user={user} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={members} directReports={directReports} teams={teams} allPeriods={allPeriods} onNavigate={setNav}
                setView={setView} setSelectedOrgLevel={setSelectedOrgLevel}
                onOpenPeriod={(period) => { setSelectedPeriod(period); setPeriodDetailOrigin("dashboard"); setView("period-detail"); }}
                navigator={shellNavigator}
                showTeam={hasTeamScope} />
        );
        if (nav === "teamdash" && !hasTeamScope) return (
            <ManagerDashboard user={user} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={members} directReports={directReports} teams={teams}
                allPeriods={allPeriods} onNavigate={setNav} setView={setView}
                setSelectedOrgLevel={setSelectedOrgLevel}
                onOpenPeriod={(period) => { setSelectedPeriod(period); setPeriodDetailOrigin("dashboard"); setView("period-detail"); }}
                navigator={shellNavigator}
                showTeam={false} />
        );
        if (nav === "inbox") return (
            <Inbox activities={activities} onUpdate={setActivities}
                isMobile={isMobile} members={directReports} impactLevels={impactLevels} evaluator={user} users={users} />
        );

        if (nav === "teamdash" && hasTeamScope) return (
            <TeamDashboard user={user} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={members} teams={teams} onNavigate={setNav} />
        );
        if (nav === "team") return (
            <TeamView activities={activities} activityGroups={activityGroups} positions={positions}
                indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={members} title="Team members" />
        );
        if (nav === "employee-evaluation") return (
            <EmployeeEvaluationWorkspace user={user} members={directReports} activePeriod={activePeriod} />
        );
        if (["tasks-activities", "self-assessment", "activities"].includes(nav)) return (
            <TasksActivitiesPage nav={nav} setNav={setNav}
                routineContent={<RoutineSelfAssessment activePeriod={activePeriod} user={user} />}
                activityContent={<SpecialistActivities myActs={myActs} activityGroups={activityGroups} setNav={setNav}
                    setSelectedAct={setSelectedAct} setView={setView} />} />
        );
        if (nav === "my-indicators") return <MyIndicators user={user} />;
        if (nav === "history") return <SpecialistHistory user={user} allPeriods={allPeriods}
            setView={setView} setSelectedPeriod={setSelectedPeriod} setPeriodDetailOrigin={setPeriodDetailOrigin} />;
        if (nav === "peer-feedback") return <PeerFeedbackPage user={user} />;
        if (nav === "evaluation-duties") return <EvaluationDutiesPage />;
        if (nav === "goals") return <GoalsPage user={user} />;
        if (nav === "privacy") return <PrivacyPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "check-ins") return <CheckInsPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "recognition") return <RecognitionPage language={experienceLanguage} users={users} currentUserId={user.id} onBack={() => setNav("dashboard")} />;
        if (nav === "learning") return <LearningPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "team-challenges") return <TeamChallengesPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "help") return <CviHelpCenter />;

        if (nav === "profile") return <PersonProfile user={user} />;
    };

    const sidebarItems = organizePerformanceNavigation([
        { id: "dashboard", label: "Dashboard", icon: "dashboard" },
        { id: "self-assessment", label: "Core Responsibilities", icon: "self" },
        { id: "goals", label: "Individual Goals", icon: "target" },
        { id: "inbox", label: "Inbox", icon: "inbox", badge: pendingCount + indicatorReviewCount },
        { id: "employee-evaluation", label: "Employee Reviews", icon: "routine" },
        { id: "activities", label: "Additional Contributions", icon: "activity" },
        { id: "my-indicators", label: "My Indicators", icon: "target" },
        { id: "history", label: "History", icon: "history" },
        { id: "peer-feedback", label: "Peer Feedback", icon: "peer", badge: peerPending },
        { id: "team", label: "Team members", icon: "members" },
        { id: "help", label: "Help", icon: "help", section: "account" },
        { id: "privacy", label: "Privacy & data rights", icon: "shield", section: "account" },
        { id: "profile", label: "Profile", icon: "profile" },
    ]);
    const bottomItems = [
        ...sidebarItems,
    ];

    const shellNavigator = makeShellNavigator({ navs: ["evaluation-duties", "dashboard", "self-assessment", "activities", "my-indicators", "history", "peer-feedback", "goals", "privacy", "check-ins", "recognition", "learning", "team-challenges", "inbox", "team", "employee-evaluation"], setNav, setView });
    return (
        <div className="cvi-role-viewport-shell cvi-management-shell" style={{
            display: "flex", flexDirection: "column", height: "100vh",
            fontFamily: "system-ui,sans-serif", direction: "inherit", background: C.bg,
            maxWidth: isMobile ? 420 : "100%", margin: isMobile ? "0 auto" : 0,
            border: isMobile ? `1px solid ${C.border}` : "none",
            boxShadow: isMobile ? "0 4px 32px rgba(0,0,64,0.1)" : "none"
        }}>

            <WorkspaceNavContext.Provider value={shellNavigator}><TopBar right={
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} />
                </div>
            } /></WorkspaceNavContext.Provider>

            <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
                {!isMobile && <Sidebar items={sidebarItems} nav={nav === "teamdash" ? "dashboard" : nav} onNav={setNav} onLogout={onLogout} />}
                <div className="cvi-role-scroll">
                    <TabTransitionContent transitionKey={nav}>{renderContent()}</TabTransitionContent>
                </div>
            </div>

            {isMobile && <AppBottomNav items={bottomItems} active={nav === "teamdash" ? "dashboard" : nav} onNav={setNav} onLogout={onLogout} />}
        </div>
    );
}

/* ════════════════════════════════════════════════════════════════════════
   Business Unit Lead workspace
   ════════════════════════════════════════════════════════════════════════ */

function DeputyOwnDashboard({ user, activities, activityGroups, positions, indicatorDefs, indicatorData,
    activePeriod, setView, setSelectedOrgLevel, onNavigate }) {
    const { departments, orgUnits } = useData();
    const orgBreakdown = positionLevelWeights(user, positions);
    const dept = departments.find(d => d.id === user.deptId);
    const orgUnit = orgUnits.find(unit => unit.id === user.orgUnitId);
    const scopeLabel = orgUnit?.label || dept?.label || "—";
    return (
        <div className="cvi-dashboard-page">
            <DashboardPageIntro title={user.name} subtitle={`Business Unit Lead · ${scopeLabel}`}
                eyebrow="My dashboard" period={activePeriod?.label || "No active period"} />
            <ScoreOverview user={user} orgBreakdown={orgBreakdown}
                onOpenNonRoutine={() => setView("nonroutine-detail")}
                onOpenRoutine={() => setView("routine-detail")}
                onOpenTasksActivities={() => setView("tasks-activities-detail")}
                onOpenGoals={() => onNavigate("goals")}
                onOpenPeerFeedback={() => onNavigate("peer-feedback")}
                onOpenOrgLevel={(level) => { setSelectedOrgLevel(level); setView("org-level-detail"); }} />
            <Card style={{ overflow: "visible" }}>
                <div className="cvi-dashboard-callout">
                    <div><span className="cvi-dashboard-callout-icon">◫</span><div><strong>Unit dashboard</strong><small>See the performance of this area’s sub-units and indicators in one place.</small></div></div>
                    <DashboardLinkButton primary onClick={() => onNavigate("dept")}>View unit dashboard →</DashboardLinkButton>
                </div>
            </Card>
        </div>
    );
}

function DeputyApp({ user, onLogout }) {
    const { activities, setActivities, activityGroups, impactLevels, positions, indicatorDefs, indicatorData,
        activePeriod, users, teams, orgUnits, deptRoutineQuestions, deptRoutineAnswers, routineTargets,
        routineLevels, periodSnapshots, peerAssignments, periods } = useData();
    const { language: experienceLanguage } = useI18n();
    const routineCtx = useRoutineCtx();
    const isMobile = useIsMobile();
    const [nav, setNav] = useState("dashboard");
    const [view, setView] = useState(null);
    const [selectedAct, setSelectedAct] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [periodDetailOrigin, setPeriodDetailOrigin] = useState("cvi-detail");
    const [selectedOrgLevel, setSelectedOrgLevel] = useState(null);

    const scopeUnitIds = user.orgUnitId ? orgUnitSubtreeIds(orgUnits, user.orgUnitId) : [];
    const visibleManagers = users.filter(u => u.role === "manager" && (
        user.orgUnitId ? (u.managerId === user.id || scopeUnitIds.includes(u.orgUnitId)) : u.deptId === user.deptId
    ));
    const directManagers = users.filter(u => u.role === "manager" && u.managerId === user.id && u.isActive !== false);
    const directReports = users.filter(u => u.managerId === user.id && u.isActive !== false);
    const reportingMembers = reportingSubtreeUsers(users, user.id);
    const hasTeamScope = directReports.length > 0;
    const managers = visibleManagers;
    const departmentMembers = users.filter(u => {
        if (["admin", "ceo"].includes(u.role) || u.id === user.id) return false;
        if (user.orgUnitId) return scopeUnitIds.includes(u.orgUnitId);
        const memberTeam = teams.find(t => t.id === u.teamId);
        return u.deptId === user.deptId || memberTeam?.deptId === user.deptId;
    });
    const deptQuestions = user.orgUnitId
        ? deptRoutineQuestions.filter(q => q.scope === "org_unit" && scopeUnitIds.includes(q.orgUnitId))
        : deptRoutineQuestions.filter(q => q.deptId === user.deptId);
    const pendingCount = activities.filter(a =>
        directManagers.some(m => m.id === a.userId) && isReviewableActivity(a)).length;
    const indicatorReviewCount = indicatorData.filter(d => d.periodId === activePeriod?.id && d.status === "submitted" &&
        indicatorDefs.find(i => i.id === d.indicatorId)?.approverId === user.id).length;
    const peerPending = peerFeedbackPendingCount(peerAssignments, activePeriod?.id);
    const myActs = activities.filter(a => a.userId === user.id);
    const { data: liveCviPayload } = useLiveCvi(user.id, activePeriod?.id);
    const allPeriods = buildPerformanceHistory({
        snapshots: periodSnapshots, userId: user.id, periods, activePeriod,
        liveEntry: liveEntryFromServer(liveCviPayload, activePeriod),
    });

    // A contribution page is shown inside the shell; leaving Additional Contributions closes it.
    useEffect(() => {
        if (nav !== "activities" && view === "act-detail") { setView(null); setSelectedAct(null); }
    }, [nav, view]);

    if (view && view !== "act-detail") {
        return (
            <SpecialistDetailView view={view} setView={setView}
                selectedAct={selectedAct} setSelectedAct={setSelectedAct}
                selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod}
                periodDetailOrigin={periodDetailOrigin} setPeriodDetailOrigin={setPeriodDetailOrigin}
                myActs={myActs} allPeriods={allPeriods}
                activityGroups={activityGroups} impactLevels={impactLevels}
                user={user} positions={positions} indicatorDefs={indicatorDefs}
                indicatorData={indicatorData} activePeriod={activePeriod}
                routineCtx={routineCtx} selectedOrgLevel={selectedOrgLevel} />
        );
    }

    const renderContent = () => {
        if (nav === "new") return <NewContributionPage user={user} activePeriod={activePeriod} onClose={() => setNav("activities")}
            onOpenActivity={activity => { setSelectedAct(activity); setView("act-detail"); setNav("activities"); }} />;
        if (nav === "activities" && view === "act-detail" && selectedAct) return <ContributionDetailPage key={selectedAct.id} activity={selectedAct} user={user}
            onBack={() => { setView(null); setSelectedAct(null); }} onChange={setSelectedAct} />;
        if (nav === "dashboard") return (
            <ManagerDashboard user={user} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={reportingMembers} directReports={directReports}
                teams={teams} allPeriods={allPeriods} onNavigate={setNav}
                setView={setView} setSelectedOrgLevel={setSelectedOrgLevel}
                onOpenPeriod={(period) => { setSelectedPeriod(period); setPeriodDetailOrigin("dashboard"); setView("period-detail"); }}
                navigator={shellNavigator}
                showTeam={hasTeamScope} />
        );
        if (nav === "teamdash" && hasTeamScope) return (
            <TeamDashboard user={user} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={reportingMembers} teams={teams} onNavigate={setNav} />
        );

        if (nav === "dept") return (
            <DeptDashboard deptId={user.deptId} orgUnitId={user.orgUnitId} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} />
        );
        if (nav === "inbox") return (
            <Inbox activities={activities} onUpdate={setActivities}
                isMobile={isMobile} members={directManagers} impactLevels={impactLevels} evaluator={user} users={users} />
        );
        if (nav === "routine") return (
            <GenericRoutineEvaluation title="Managers’ core responsibilities review" members={routineTargets}
                questions={deptQuestions} answers={deptRoutineAnswers}
                routineLevels={routineLevels} activePeriod={activePeriod}
                noMembersMessage="No managers have been assigned to this unit yet"
                noQuestionsMessage="No core responsibilities questions have been defined for this unit yet." />
        );
        if (["tasks-activities", "self-assessment", "activities"].includes(nav)) return (
            <TasksActivitiesPage nav={nav} setNav={setNav}
                routineContent={<RoutineSelfAssessment activePeriod={activePeriod} user={user} />}
                activityContent={<SpecialistActivities myActs={myActs} activityGroups={activityGroups} setNav={setNav}
                    setSelectedAct={setSelectedAct} setView={setView} />} />
        );
        if (nav === "my-indicators") return <MyIndicators user={user} />;
        if (nav === "history") return <SpecialistHistory user={user} allPeriods={allPeriods}
            setView={setView} setSelectedPeriod={setSelectedPeriod} setPeriodDetailOrigin={setPeriodDetailOrigin} />;
        if (nav === "peer-feedback") return <PeerFeedbackPage user={user} />;
        if (nav === "members") return (
            <TeamView activities={activities} activityGroups={activityGroups} positions={positions}
                indicatorDefs={indicatorDefs} indicatorData={indicatorData} activePeriod={activePeriod}
                members={departmentMembers} title="Unit members" emptyMessage="No members have been assigned to this unit yet"
                 />
        );
        if (nav === "evaluation-duties") return <EvaluationDutiesPage />;
        if (nav === "goals") return <GoalsPage user={user} />;
        if (nav === "privacy") return <PrivacyPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "check-ins") return <CheckInsPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "recognition") return <RecognitionPage language={experienceLanguage} users={users} currentUserId={user.id} onBack={() => setNav("dashboard")} />;
        if (nav === "learning") return <LearningPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "team-challenges") return <TeamChallengesPage language={experienceLanguage} onBack={() => setNav("dashboard")} />;
        if (nav === "help") return <CviHelpCenter />;

        if (nav === "profile") return <PersonProfile user={user} />;
    };

    const sidebarItems = organizePerformanceNavigation([
        { id: "dashboard", label: "Dashboard", icon: "dashboard" },
        { id: "self-assessment", label: "Core Responsibilities", icon: "self" },
        { id: "goals", label: "Individual Goals", icon: "target" },
        { id: "dept", label: "Unit dashboard", icon: "org" },
        { id: "inbox", label: "Inbox", icon: "inbox", badge: pendingCount + indicatorReviewCount },
        { id: "routine", label: "Core Responsibilities ratings", icon: "routine" },
        { id: "activities", label: "Additional Contributions", icon: "activity" },
        { id: "my-indicators", label: "My Indicators", icon: "target" },
        { id: "history", label: "History", icon: "history" },
        { id: "peer-feedback", label: "Peer Feedback", icon: "peer", badge: peerPending },
        { id: "members", label: "Unit members", icon: "members" },
        { id: "help", label: "Help", icon: "help", section: "account" },
        { id: "privacy", label: "Privacy & data rights", icon: "shield", section: "account" },
        { id: "profile", label: "Profile", icon: "profile" },
    ]);
    const bottomItems = [
        ...sidebarItems,
    ];

    const shellNavigator = makeShellNavigator({ navs: ["evaluation-duties", "dashboard", "self-assessment", "activities", "my-indicators", "history", "peer-feedback", "goals", "privacy", "check-ins", "recognition", "learning", "team-challenges", "inbox", "routine", "dept", "members"], setNav, setView, overrides: { "routine-evaluation": "routine" } });
    return (
        <div className="cvi-role-viewport-shell cvi-management-shell" style={{
            display: "flex", flexDirection: "column", height: "100vh",
            fontFamily: "system-ui,sans-serif", direction: "inherit", background: C.bg,
            maxWidth: isMobile ? 420 : "100%", margin: isMobile ? "0 auto" : 0,
            border: isMobile ? `1px solid ${C.border}` : "none",
            boxShadow: isMobile ? "0 4px 32px rgba(0,0,64,0.1)" : "none"
        }}>

            <WorkspaceNavContext.Provider value={shellNavigator}><TopBar right={
                <UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} />
            } /></WorkspaceNavContext.Provider>

            <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
                {!isMobile && <Sidebar items={sidebarItems} nav={nav} onNav={setNav} onLogout={onLogout} />}
                <div className="cvi-role-scroll">
                    <TabTransitionContent transitionKey={nav}>{renderContent()}</TabTransitionContent>
                </div>
            </div>

            {isMobile && <AppBottomNav items={bottomItems} active={nav} onNav={setNav} onLogout={onLogout} />}
        </div>
    );
}

/* ════════════════════════════════════════════════════════════════════════
   Executive workspace
   ════════════════════════════════════════════════════════════════════════ */

function CompanyHistory({ activities, activityGroups, positions, indicatorDefs, indicatorData, activePeriod, user }) {
    const { users, periodSnapshots } = useData();
    // CEO history must use the same complete archive used by the other roles.
    // Include every employee level that can have a CVI snapshot; CEO/admin are intentionally excluded.
    const members = users.filter(u => !["ceo", "admin"].includes(u.role));
    // The active cycle uses the server results of the people in scope; history uses final snapshots.
    const { language } = useI18n();
    const scopedCvi = useScopedCvi(activePeriod?.id);
    // Each organization value is the mean of the results that exist; a missing result is not counted.
    const liveMean = key => averageScore(members.map(m => scopedCvi.scoresFor(m.id)?.[key])).value;
    const currentCvi = liveMean("cvi");
    const historicalPeriods = [...new Set(periodSnapshots.map(s => s.periodId))].map(periodId => {
        const rows = members.map(m => latestPeriodSnapshotsForUser(periodSnapshots, m.id)
            .find(s => s.periodId === periodId)).filter(Boolean);
        return {
            periodId, label: rows[0]?.label || periodId, locked: true,
            cvi: historicalCviAverage(rows),
            individual: averageScore(rows.map(row => row.individual)).value,
            org: averageScore(rows.map(row => row.org)).value,
        };
    });
    const allPeriods = [...historicalPeriods,
        ...(activePeriod && currentCvi != null && !historicalPeriods.some(p => String(p.periodId) === String(activePeriod.id)) ? [{
            periodId: activePeriod.id,
            label: activePeriod.label,
            cvi: currentCvi,
            locked: false,
            individual: liveMean("individual"),
            org: liveMean("organizational"),
        }] : [])];

    return <HistoryArchivePage allPeriods={allPeriods} user={user} language={language} scope="organization" />;
}

function CeoApp({ user, onLogout }) {
    const { activities, setActivities, activityGroups, impactLevels, positions,
        indicatorDefs, indicatorData, activePeriod, users, orgRoutineQuestions,
        orgRoutineAnswers, routineLevels, routineTargets, peerAssignments,
        periods, orgUnits, departments, orgSettings } = useData();
    const { language: experienceLanguage } = useI18n();
    const isMobile = useIsMobile();
    const [nav, setNavState] = useState("company");
    // Drill-down from the executive dashboard into one top-level unit (existing unit dashboard).
    const [drillUnitId, setDrillUnitId] = useState(null);
    const setNav = next => { setDrillUnitId(null); setNavState(next); };
    const deputies = users.filter(u => u.role === "deputy");
    const companyMembers = users.filter(u => !["ceo", "admin"].includes(u.role));
    const pendingCount = activities.filter(a =>
        deputies.some(d => d.id === a.userId) && isReviewableActivity(a)).length;
    const indicatorReviewCount = indicatorData.filter(d => d.periodId === activePeriod?.id && d.status === "submitted" &&
        indicatorDefs.find(i => i.id === d.indicatorId)?.approverId === user.id).length;
    const peerPending = peerFeedbackPendingCount(peerAssignments, activePeriod?.id);

    const renderContent = () => {
        if (nav === "company" && drillUnitId) {
            const unit = orgUnits.find(item => item.id === drillUnitId);
            return <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <SubBar title={unit?.label || "Organizational unit"} onBack={() => setDrillUnitId(null)} />
                <DeptDashboard orgUnitId={drillUnitId} activities={activities} activityGroups={activityGroups}
                    positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                    activePeriod={activePeriod} />
            </div>;
        }
        if (nav === "company") return (
            <ExecutiveDashboard language={experienceLanguage} periods={periods} activePeriodId={activePeriod?.id || null}
                orgUnits={orgUnits} departments={departments} pendingDecisions={pendingCount + indicatorReviewCount}
                users={users} organizationName={orgSettings?.name || ""}
                onOpenUnit={setDrillUnitId} onOpenInbox={() => setNav("inbox")}
                onOpenHistory={() => setNav("history")} onOpenMembers={() => setNav("members")} />
        );
        if (nav === "inbox") return (
            <Inbox activities={activities} onUpdate={setActivities}
                isMobile={isMobile} members={deputies} impactLevels={impactLevels} evaluator={user} users={users} />
        );
        if (nav === "routine") return (
            <GenericRoutineEvaluation title="Business Unit Leads’ Core Responsibilities ratings" members={routineTargets}
                questions={orgRoutineQuestions} answers={orgRoutineAnswers}
                routineLevels={routineLevels} activePeriod={activePeriod}
                noMembersMessage="No business unit lead has been defined yet"
                noQuestionsMessage="No organization-level core responsibilities questions have been defined yet." />
        );
        if (nav === "history") return (
            <CompanyHistory activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} user={user} />
        );
        if (nav === "peer-feedback") return <PeerFeedbackPage user={user} />;
        if (nav === "members") return (
            <TeamView activities={activities} activityGroups={activityGroups} positions={positions}
                indicatorDefs={indicatorDefs} indicatorData={indicatorData} activePeriod={activePeriod}
                members={companyMembers} title="Organization members" emptyMessage="No members have been added to the organization yet."
                enableOrgFilters />
        );
        if (nav === "evaluation-duties") return <EvaluationDutiesPage />;
        if (nav === "goals") return <GoalsPage user={user} />;
        if (nav === "privacy") return <PrivacyPage language={experienceLanguage} onBack={() => setNav("company")} />;
        if (nav === "help") return <CviHelpCenter />;

        if (nav === "profile") return <PersonProfile user={user} />;
    };

    const sidebarItems = organizePerformanceNavigation([
        { id: "company", label: "Organization overview", icon: "company" },
        { id: "inbox", label: "Inbox", icon: "inbox", badge: pendingCount + indicatorReviewCount },
        { id: "history", label: "History", icon: "history" },
        { id: "peer-feedback", label: "Peer Feedback", icon: "peer", badge: peerPending },
        { id: "members", label: "Organization members", icon: "members" },
        { id: "help", label: "Help", icon: "help", section: "account" },
        { id: "privacy", label: "Privacy & data rights", icon: "shield", section: "account" },
        { id: "profile", label: "Profile", icon: "profile" },
    ]);
    const bottomItems = [
        ...sidebarItems,
    ];

    const shellNavigator = makeShellNavigator({ navs: ["evaluation-duties", "company", "inbox", "routine", "history", "peer-feedback", "members", "goals", "privacy"], setNav, overrides: { "routine-evaluation": "routine" } });
    return (
        <div className="cvi-role-viewport-shell cvi-management-shell" style={{
            display: "flex", flexDirection: "column", height: "100vh",
            fontFamily: "system-ui,sans-serif", direction: "inherit", background: C.bg,
            maxWidth: isMobile ? 420 : "100%", margin: isMobile ? "0 auto" : 0,
            border: isMobile ? `1px solid ${C.border}` : "none",
            boxShadow: isMobile ? "0 4px 32px rgba(0,0,64,0.1)" : "none"
        }}>

            <WorkspaceNavContext.Provider value={shellNavigator}><TopBar right={
                <UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} />
            } /></WorkspaceNavContext.Provider>

            <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
                {!isMobile && <Sidebar items={sidebarItems} nav={nav} onNav={setNav} onLogout={onLogout} />}
                <div className="cvi-role-scroll">
                    <TabTransitionContent transitionKey={`${nav}:${drillUnitId || ""}`}>{renderContent()}</TabTransitionContent>
                </div>
            </div>

            {isMobile && <AppBottomNav items={bottomItems} active={nav} onNav={setNav} onLogout={onLogout} />}
        </div>
    );
}

/* ════════════════════════════════════════════════════════════════════════
   Admin workspace
   ════════════════════════════════════════════════════════════════════════ */

// Small EN/FR helper for dynamic admin copy (sentences with numbers or names).
// Static strings stay in English and are translated through CANADA_EN_FR_COPY.
function useAdminText() {
    const { language } = useI18n();
    return (en, fr) => (language === "fr" ? fr : en);
}

// Generic CRUD table used across the Admin Panel (add dialog + edit dialog + deactivate).
// Field options: key, label, type, options, placeholder, tableVisible, computed, suffix,
// visible(form), clearsFields, section, hint (string | (form, mode) => string), required, wide, render(item).
function EditableList({ title, description, items, fields, onAdd, onUpdate, onDelete, validate, onView,
    addLabel, emptyTitle, emptyDescription, toolbar, renderPrimary, dialogTitle, headerExtra,
    editDisabledReason = "", deleteDisabledReason = "", notice = null }) {
    const { confirmAction } = useUx();
    const emptyForm = () => {
        const f = {};
        fields.forEach(fd => { f[fd.key] = ""; });
        return f;
    };
    const [newForm, setNewForm] = useState(emptyForm());
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [addError, setAddError] = useState("");
    const [editError, setEditError] = useState("");
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    // Snapshot of the form when its dialog opened; used only to warn before unsaved edits are discarded.
    const [formSnapshot, setFormSnapshot] = useState("");

    const fieldOptions = (fd, form) => typeof fd.options === "function" ? fd.options(form) : (fd.options || []);

    const renderField = (fd, form, setForm) => {
        if (fd.type === "icon") {
            const opts = fieldOptions(fd, form);
            return <select value={form[fd.key] || "activity"} onChange={e => setForm(f => ({ ...f, [fd.key]: e.target.value }))}>{opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
        }
        if (fd.type === "select") {
            const opts = fieldOptions(fd, form);
            return (
                <select value={form[fd.key] || ""} onChange={e => setForm(f => ({
                    ...f, [fd.key]: e.target.value,
                    ...(fd.clearsFields ? Object.fromEntries(fd.clearsFields.map(k => [k, ""])) : {})
                }))}>
                    <option value="">{fd.placeholder || "Select"}</option>
                    {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            );
        }
        if (fd.type === "number") {
            return <input type="number" value={form[fd.key] ?? ""} onChange={e => setForm(f => ({ ...f, [fd.key]: e.target.value }))} placeholder={fd.placeholder} />;
        }
        if (fd.type === "password") {
            return <input type="password" autoComplete="new-password" value={form[fd.key] ?? ""} onChange={e => setForm(f => ({ ...f, [fd.key]: e.target.value }))} placeholder={fd.placeholder} />;
        }
        return <input type="text" value={form[fd.key] || ""} onChange={e => setForm(f => ({ ...f, [fd.key]: e.target.value }))} placeholder={fd.placeholder} />;
    };

    const handleAdd = async () => {
        const err = validate ? validate(newForm) : null;
        if (err) { setAddError(err); return; }
        try {
            await onAdd(newForm);
            setNewForm(emptyForm());
            setAddError("");
            setShowAddForm(false);
        } catch (error) {
            setAddError(error.message || "Could not add the item.");
        }
    };
    const startEdit = (item) => { setEditingId(item.id); setEditForm({ ...item }); setFormSnapshot(JSON.stringify({ ...item })); setEditError(""); setShowEditForm(true); };
    const cancelEdit = () => { setEditingId(null); setEditError(""); setShowEditForm(false); };
    const saveEdit = async () => {
        const err = validate ? validate(editForm) : null;
        if (err) { setEditError(err); return; }
        try {
            await onUpdate(editingId, editForm);
            setEditingId(null);
            setEditError("");
            setShowEditForm(false);
        } catch (error) {
            setEditError(error.message || "Could not save the changes.");
        }
    };

    const displayValue = (fd, item) => {
        if (fd.render) return fd.render(item);
        let display = item[fd.key];
        if (fd.type === "icon") return <span style={{ display: "inline-flex", color: C.navy }}><UiIcon name={({ "📚":"activity", "📖":"activity", "🎯":"target", "💡":"performance", "🚀":"performance", "⭐":"performance", "🤝":"team", "👥":"team" }[String(item[fd.key] || "")] || String(item[fd.key] || "activity"))} size={17} /></span>;
        if (fd.type === "select") {
            const opts = fieldOptions(fd, item);
            const matched = opts.find(o => String(o.value) === String(item[fd.key]));
            if (matched) display = matched.label;
            else if (fd.key === "weight") {
                const fallbackImportance = { "5": "Very high", "4": "High", "3": "Moderate", "2": "Low", "1": "Very low" };
                display = fallbackImportance[String(item[fd.key])] || "—";
            } else display = display || "—";
        }
        if (fd.suffix && display !== "" && display != null) return <span dir="ltr" style={{unicodeBidi:"isolate"}}>{display}{fd.suffix}</span>;
        return display === "" || display == null ? "—" : display;
    };

    const visibleCols = fields.filter(fd => fd.type !== "password" && fd.tableVisible !== false);
    const alignClass = fd => fd.align === "end" || fd.computed || fd.type === "number" ? "is-num" : fd.align === "center" ? "is-center" : "";
    const openAdd = () => { setAddError(""); setFormSnapshot(JSON.stringify(newForm)); setShowAddForm(true); };
    const addButton = onAdd ? <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={openAdd}><UiIcon name="plus" size={14} />{addLabel || "Add item"}</button> : null;
    const itemName = title || "item";

    const renderForm = (form, setForm, mode) => {
        const formFields = fields.filter(fd => !fd.computed && (!fd.visible || fd.visible(form)));
        const sections = [];
        formFields.forEach(fd => {
            const key = fd.section || "";
            let section = sections.find(s => s.key === key);
            if (!section) { section = { key, fields: [] }; sections.push(section); }
            section.fields.push(fd);
        });
        return sections.map(section => <div key={section.key || "main"} className="adm-form-section">
            {section.key && <h3 className="adm-form-section-title">{section.key}</h3>}
            <div className="adm-form-grid">
                {section.fields.map(fd => {
                    const hint = typeof fd.hint === "function" ? fd.hint(form, mode) : fd.hint;
                    return <AdminField key={fd.key} label={fd.label} hint={hint} required={fd.required} wide={fd.wide || (formFields.length === 1)}>
                        {renderField(fd, form, setForm)}
                    </AdminField>;
                })}
            </div>
        </div>);
    };

    const dialog = (mode) => {
        const isEdit = mode === "edit";
        const form = isEdit ? editForm : newForm;
        const setForm = isEdit ? setEditForm : setNewForm;
        const error = isEdit ? editError : addError;
        const close = isEdit ? cancelEdit : () => { setShowAddForm(false); setNewForm(emptyForm()); setAddError(""); };
        const submit = isEdit ? saveEdit : handleAdd;
        return <AdminDialog size={fields.length > 4 ? "lg" : "md"} onClose={close} dirty={JSON.stringify(form) !== formSnapshot}
            title={isEdit ? `Edit ${dialogTitle || itemName}` : (addLabel || dialogTitle || title || "Add item")}
            description={isEdit ? "Update the information and save your changes." : "Complete the details for the new item."}
            footer={({ requestClose }) => <>
                <button type="button" className="adm-btn" onClick={requestClose}>Cancel</button>
                <button type="button" className="adm-btn adm-btn--primary" onClick={submit}>{isEdit ? "Save changes" : "Add"}</button>
            </>}>
            <form onSubmit={event => { event.preventDefault(); submit(); }} noValidate>
                {error && <div className="adm-form-error" role="alert"><UiIcon name="warning" size={15} /><span>{error}</span></div>}
                {renderForm(form, setForm, mode)}
                <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
            </form>
        </AdminDialog>;
    };

    return (
        <AdminPanel flush title={title} description={description}
            actions={(headerExtra || addButton) ? <>{headerExtra}{addButton}</> : null}>
            {notice && <div style={{ padding: "12px 16px 0" }}>{notice}</div>}
            {toolbar}
            {items.length === 0 ? (
                <AdminEmpty compact icon="activity" title={emptyTitle || "No items yet"}
                    description={emptyDescription || "Add the first item to get started."} />
            ) : (
                <div className="adm-table-wrap">
                    <table className="adm-table is-responsive">
                        <thead>
                            <tr>
                                <th scope="col" className="is-center" style={{ width: 48 }}>#</th>
                                {visibleCols.map(fd => <th key={fd.key} scope="col" className={alignClass(fd)} title={fd.columnLabel ? fd.label : undefined}>{fd.columnLabel || fd.label}</th>)}
                                <th scope="col" className="is-actions"><span className="adm-sr">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => (
                                <tr key={item.id}>
                                    <td className="is-center is-index adm-cell-muted">{i + 1}</td>
                                    {visibleCols.map((fd, ci) => (
                                        <td key={fd.key} data-label={ci === 0 ? undefined : fd.label}
                                            className={`${ci === 0 ? "is-primary" : ""} ${alignClass(fd)}`.trim() || undefined}>
                                            {ci === 0
                                                ? (renderPrimary ? renderPrimary(item) : <span className="adm-cell-primary">{displayValue(fd, item)}</span>)
                                                : <span className={fd.computed ? "adm-cell-muted" : undefined}>{displayValue(fd, item)}</span>}
                                        </td>
                                    ))}
                                    <td className="is-actions">
                                        <span className="adm-row-actions">
                                            {onView && <button type="button" className="adm-icon-btn" onClick={() => onView(item)} title="View" aria-label="View"><UiIcon name="view" size={16} /></button>}
                                            <button type="button" className="adm-icon-btn" onClick={() => startEdit(item)} disabled={Boolean(editDisabledReason)}
                                                title={editDisabledReason || "Edit"} aria-label="Edit"><UiIcon name="edit" size={16} /></button>
                                            <button type="button" className="adm-icon-btn is-danger" disabled={Boolean(deleteDisabledReason)}
                                                title={deleteDisabledReason || "Deactivate"} aria-label="Deactivate"
                                                onClick={() => confirmAction({title:"Deactivate item",message:"This item will be removed from the active list. Historical records will be retained.",confirmLabel:"Deactivate",tone:"danger",onConfirm:()=>onDelete(item.id)})}><UiIcon name="archive" size={16} /></button>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {showEditForm && dialog("edit")}
            {showAddForm && dialog("add")}
        </AdminPanel>
    );
}

function AdminOverview({ setNav }) {
    const { orgUnits, users, positions, indicatorDefs, indicatorData, activePeriod, routineQuestions, routineLevels } = useData();
    const { language } = useI18n();
    const tx = useAdminText();

    // Readiness mirrors how the backend resolves configuration (see components/admin/readiness.js).
    const positionCheck = positionReadiness(positions);
    const posWeightOk = positionCheck.ok;
    const targetsSet = activePeriod ? indicatorDefs.filter(def => {
        const d = indicatorData.find(x => x.periodId === activePeriod.id && x.indicatorId === def.id);
        return d && d.target != null;
    }).length : 0;
    const actualsSet = activePeriod ? indicatorDefs.filter(def => {
        const d = indicatorData.find(x => x.periodId === activePeriod.id && x.indicatorId === def.id);
        return d && d.actual != null && ["approved", "locked"].includes(d.status);
    }).length : 0;
    const activeOrgUnits = orgUnits.filter(unit => unit.isActive !== false);
    const routineCheck = unitRoutineReadiness({ orgUnits, users, routineQuestions, routineLevels });
    const rootOrgUnits = activeOrgUnits.filter(unit => !unit.parentId);
    const checks = [
        { ok: activeOrgUnits.length > 0, label: "Organizational Structure", detail: tx(`${activeOrgUnits.length} units · ${rootOrgUnits.length} top-level units`, `${activeOrgUnits.length} unités · ${rootOrgUnits.length} de premier niveau`), go: "org", icon: "org" },
        { ok: users.length > 0, label: "People & Access", detail: tx(`${users.length} people configured`, `${users.length} personnes configurées`), go: "people", icon: "people" },
        { ok: posWeightOk, label: "Job Positions", detail: posWeightOk ? tx(`${positions.length} job positions`, `${positions.length} postes`)
            : !positions.length ? "No job positions yet"
            : tx(`${positionCheck.notReady.length} of ${positions.length} positions have no usable weights: ${positionCheck.notReady.map(p => p.label).join(", ")}`, `${positionCheck.notReady.length} postes sur ${positions.length} sans pondération utilisable : ${positionCheck.notReady.map(p => p.label).join(", ")}`), go: "org", icon: "members" },
        { ok: routineCheck.ok, label: "Core Responsibilities by Unit", detail: !routineCheck.scaleReady ? "Define the Core Responsibility Rating Scale first."
            : tx(`${routineCheck.ready} of ${routineCheck.total} units with people have weighted questions`, `${routineCheck.ready} unités sur ${routineCheck.total} (avec personnel) ont des questions pondérées`), go: "individual-performance", icon: "routine" },
        { ok: indicatorDefs.length > 0, label: "Collective Contribution Indicators", detail: tx(`${indicatorDefs.length} active indicators`, `${indicatorDefs.length} indicateurs actifs`), go: "collective-contribution", icon: "target" },
        { ok: !!activePeriod && indicatorDefs.length > 0 && targetsSet === indicatorDefs.length, label: "Period Targets", detail: !activePeriod ? "No active period" : tx(`${targetsSet} of ${indicatorDefs.length} indicators have targets`, `${targetsSet} indicateurs sur ${indicatorDefs.length} ont une cible`), go: "cycle-management", icon: "planning" },
        { ok: !!activePeriod && indicatorDefs.length > 0 && actualsSet === indicatorDefs.length, label: "Period Performance", inProgress: !!activePeriod, detail: !activePeriod ? "No active period" : tx(`${actualsSet} of ${indicatorDefs.length} indicators approved`, `${actualsSet} indicateurs approuvés sur ${indicatorDefs.length}`), go: "cycle-management", icon: "performance" },
    ];
    const readyCount = checks.filter(item => item.ok).length;
    const readiness = Math.round((readyCount / checks.length) * 100);
    const attention = checks.filter(item => !item.ok);
    const activeMembers = users.filter(user => user.isActive !== false).length;
    const submittedIndicators = activePeriod ? indicatorData.filter(item => item.periodId === activePeriod.id && item.status === "submitted").length : 0;
    const daysLeft = daysRemainingInPeriod(activePeriod);
    const attentionTotal = attention.length + submittedIndicators;
    const periodMeta = activePeriod
        ? <span className="adm-meta-pill"><span className="adm-dot is-live" aria-hidden="true" /><strong>{localizePeriodLabel(activePeriod.label, language)}</strong>{daysLeft != null && <span>· {daysLeft > 0 ? tx(`${daysLeft} days left`, `${daysLeft} jours restants`) : "Ends"}</span>}</span>
        : <span className="adm-meta-pill"><span className="adm-dot is-warning" aria-hidden="true" />No active period</span>;

    return <AdminPage className="cvi-admin-overview-page">
        <AdminPageHeader eyebrow="Management" title="CVI Administration"
            description="A clear view of setup readiness, the active period, and what needs your attention." meta={periodMeta} />
        <div className="adm-stats">
            <AdminStat label="Setup Readiness" value={`${readiness}%`} helper={tx(`${readyCount} of ${checks.length} areas ready`, `${readyCount} domaines prêts sur ${checks.length}`)} tone={readiness === 100 ? "success" : "warning"} icon="check" />
            <AdminStat label="People" value={activeMembers} helper={tx(`${activeOrgUnits.length} organizational ${activeOrgUnits.length === 1 ? "unit" : "units"}`, `${activeOrgUnits.length} ${activeOrgUnits.length === 1 ? "unité organisationnelle" : "unités organisationnelles"}`)} icon="people" onClick={() => setNav("people")} />
            <AdminStat label="Active Period" value={activePeriod ? localizePeriodLabel(activePeriod.label, language) : "—"} helper={activePeriod ? (daysLeft > 0 ? tx(`${daysLeft} days left · ${actualsSet} of ${indicatorDefs.length} indicators approved`, `${daysLeft} jours restants · ${actualsSet} indicateurs approuvés sur ${indicatorDefs.length}`) : tx("Ends today", "Se termine aujourd’hui")) : "Activate a period to begin tracking performance."} tone="info" icon="planning" onClick={() => setNav("cycle-management")} />
            <AdminStat label="Needs Attention" value={attentionTotal} helper={submittedIndicators ? tx(`${submittedIndicators} indicators pending review`, `${submittedIndicators} indicateurs en attente de révision`) : tx("Setup items and pending reviews", "Configuration et révisions en attente")} tone={attentionTotal ? "danger" : "success"} icon={attentionTotal ? "warning" : "check"} />
        </div>

        <div className="adm-grid-2">
            <AdminPanel flush title="Needs Your Attention" description="Resolve these items to run a complete CVI period."
                actions={attentionTotal ? <AdminBadge tone="warning">{attentionTotal}</AdminBadge> : <AdminBadge tone="success">0</AdminBadge>}>
                {attentionTotal === 0 ? <AdminEmpty compact icon="check" title="Everything is ready" description="No configuration issues require attention." /> :
                    <ul className="adm-list">
                        {attention.map(item => <li key={item.label}><button type="button" className="adm-list-item" onClick={() => setNav(item.go)}>
                            <span className="adm-list-icon is-warning" aria-hidden="true"><UiIcon name="warning" size={15} /></span>
                            <span><strong>{item.label}</strong><small>{item.detail}</small></span>
                            <span className="adm-list-go">Review<UiIcon name="chevronDown" size={14} /></span>
                        </button></li>)}
                        {submittedIndicators > 0 && <li><button type="button" className="adm-list-item" onClick={() => setNav("cycle-management")}>
                            <span className="adm-list-icon is-info" aria-hidden="true"><UiIcon name="target" size={15} /></span>
                            <span><strong>Indicators Requiring Action</strong><small>{tx(`${submittedIndicators} values to review in the current period`, `${submittedIndicators} valeurs à examiner pour la période en cours`)}</small></span>
                            <span className="adm-list-go">Open<UiIcon name="chevronDown" size={14} /></span>
                        </button></li>}
                    </ul>}
            </AdminPanel>

            <AdminPanel flush title="Setup Progress" description="Ready to run a complete CVI period"
                actions={<span className="adm-count" style={{ height: 24, fontSize: 12 }}>{readiness}%</span>}>
                <div style={{ padding: "14px 20px 4px" }}><AdminProgress value={readiness} tone={readiness === 100 ? "success" : undefined} label="Setup Progress" /></div>
                <ul className="adm-list">
                    {checks.map((item, index) => <li key={item.label}><button type="button" className="adm-list-item" onClick={() => setNav(item.go)}>
                        <span className={`adm-check-mark${item.ok ? " is-done" : ""}`} aria-hidden="true">{item.ok ? <UiIcon name="check" size={12} strokeWidth={2.4} /> : <span style={{ fontSize: 10, color: "var(--adm-subtle)", fontWeight: 650 }}>{index + 1}</span>}</span>
                        <span><strong>{item.label}</strong><small>{item.detail}</small></span>
                        <AdminBadge tone={item.ok ? "success" : item.inProgress ? "info" : "warning"}>{item.ok ? "Ready" : item.inProgress ? "In progress" : "Needs setup"}</AdminBadge>
                    </button></li>)}
                </ul>
            </AdminPanel>
        </div>
    </AdminPage>;
}

function AdminPeople({ focusRequest = null }) {
    const { users, positions, orgUnits, createUserApi, updateUserApi, archiveUserApi } = useData();
    const tx = useAdminText();

    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [unitFilter, setUnitFilter] = useState("");
    // "View people" from Organization: show the selected unit (or everyone for the organization).
    useEffect(() => {
        if (!focusRequest) return;
        setSearch(""); setRoleFilter(""); setUnitFilter(focusRequest.unitId || "");
    }, [focusRequest]);
    const [viewUser, setViewUser] = useState(null);

    const positionOptions = positions.map(p => ({ value: p.id, label: p.label }));
    const orgUnitOptions = orgUnits
        .filter(unit => unit.isActive !== false)
        .map(unit => ({ value: unit.id, label: unit.label }));
    const roleOptions = [
        { value: "specialist", label: "Employee" }, { value: "manager", label: "Manager" },
        { value: "deputy", label: "Director" }, { value: "ceo", label: "Executive" }, { value: "admin", label: "Administrator" },
    ];

    const needsOrgUnit = (f) => ["specialist", "manager", "deputy"].includes(f.role);
    const needsManager = (f) => ["specialist", "manager", "deputy"].includes(f.role);
    const managerOptionsFor = (f) => {
        if (f.role === "specialist") {
            return users
                .filter(u => u.role === "manager" && (!f.orgUnitId || u.orgUnitId === f.orgUnitId) && u.id !== f.id)
                .map(u => ({ value: u.id, label: `${u.name}${u.orgUnit?.name ? ` · ${u.orgUnit.name}` : ""}` }));
        }
        if (f.role === "manager") {
            return users.filter(u => u.role === "deputy" && u.id !== f.id)
                .map(u => ({ value: u.id, label: `${u.name}${u.orgUnit?.name ? ` · ${u.orgUnit.name}` : ""}` }));
        }
        if (f.role === "deputy") {
            return users.filter(u => u.role === "ceo" && u.id !== f.id)
                .map(u => ({ value: u.id, label: u.name }));
        }
        return [];
    };

    const roleTone = role => ({ admin: "navy", ceo: "accent", deputy: "info", manager: "info", specialist: "neutral" }[role] || "neutral");
    const roleLabel = role => roleOptions.find(option => option.value === role)?.label || "—";
    const positionLabel = id => positionOptions.find(option => option.value === id)?.label || "—";
    const unitLabel = id => orgUnitOptions.find(option => option.value === id)?.label || "—";
    const managerLabel = id => users.find(user => user.id === id)?.name || "—";

    const identity = "Identity", signIn = "Sign-in", access = "Organization & access";
    const fields = [
        { key: "name", label: "Full name", placeholder: "e.g., Alex Morgan", section: identity, required: true },
        { key: "employeeCode", label: "Employee ID", placeholder: "1001", tableVisible: false, section: identity, required: true },
        { key: "nationalId", label: "Internal reference", placeholder: "e.g., HR-1048", tableVisible: false, section: identity, required: true, hint: "Letters, numbers, dots, dashes or underscores." },
        { key: "email", label: "Email", placeholder: "name@company.com", tableVisible: false, section: signIn, required: true },
        { key: "username", label: "Username", placeholder: "alex.m", tableVisible: false, section: signIn, required: true },
        { key: "password", label: "Password", type: "password", placeholder: "Minimum 6 characters", section: signIn,
            hint: (form, mode) => mode === "edit" ? "Leave blank to keep the current password." : "Minimum 6 characters" },
        {
            key: "role", label: "Access level", type: "select", options: roleOptions, placeholder: "Select access level",
            clearsFields: ["orgUnitId", "managerId"], section: access, required: true,
            render: item => <AdminBadge tone={roleTone(item.role)} plain>{roleLabel(item.role)}</AdminBadge>,
        },
        { key: "posId", label: "Job position", type: "select", options: positionOptions, placeholder: "Select job position", section: access },
        {
            key: "orgUnitId", label: "Organizational unit", type: "select", options: orgUnitOptions,
            placeholder: "Select organizational unit", visible: needsOrgUnit, clearsFields: ["managerId"], section: access, required: true
        },
        {
            key: "managerId", label: "Direct manager", type: "select", tableVisible: false,
            options: managerOptionsFor, placeholder: "Select direct manager", visible: needsManager, section: access, required: true
        },
    ];

    const normalizedSearch = search.trim().toLowerCase();
    const filteredUsers = users.filter(user => {
        const matchesSearch = !normalizedSearch || [user.name, user.email, user.username, user.employeeCode, user.nationalId]
            .some(value => String(value || "").toLowerCase().includes(normalizedSearch));
        return matchesSearch && (!roleFilter || user.role === roleFilter) && (!unitFilter || user.orgUnitId === unitFilter);
    });
    const hasFilters = Boolean(search || roleFilter || unitFilter);

    const normalizeUserForm = (f) => {
        const role = String(f.role || "").toLowerCase();
        if (["admin", "ceo"].includes(role)) {
            return { ...f, orgUnitId: null, deptId: null, teamId: null, managerId: null };
        }
        return {
            ...f,
            orgUnitId: f.orgUnitId || null,
            deptId: null,
            teamId: null,
            managerId: f.managerId || null,
        };
    };

    const counts = roleOptions.map(option => ({ ...option, count: users.filter(user => user.role === option.value && user.isActive !== false).length }));

    const toolbar = <div className="adm-toolbar">
        <div className="adm-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
            <input className="adm-control" type="search" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email, username, or employee ID…" aria-label="Search by name, email, username, or employee ID…" />
        </div>
        <select className="adm-control" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} aria-label="Access level"><option value="">All access levels</option>{roleOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
        <select className="adm-control" value={unitFilter} onChange={e => setUnitFilter(e.target.value)} aria-label="Organizational unit"><option value="">All organizational units</option>{orgUnitOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
        {hasFilters && <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => { setSearch(""); setRoleFilter(""); setUnitFilter(""); }}>Clear</button>}
        <span className="adm-toolbar-meta" aria-live="polite">{tx(`${filteredUsers.length} of ${users.length} people`, `${filteredUsers.length} personnes sur ${users.length}`)}</span>
    </div>;

    return (
        <AdminPage>
            <AdminPageHeader eyebrow="Management" title="People & Access"
                description="Connect people to positions, organizational units, and direct managers." />
            <div className="adm-stats is-compact is-5">
                {counts.map(option => <AdminStat key={option.value} label={option.label} value={option.count}
                    onClick={() => setRoleFilter(current => current === option.value ? "" : option.value)}
                    tone={roleFilter === option.value ? "info" : undefined}
                    ariaLabel={tx(`Filter by ${option.label}`, `Filtrer : ${option.label}`)} />)}
            </div>
            <EditableList title="Directory" description="Search and filter people quickly. Open a profile to review access, reporting relationships, and position details."
                items={filteredUsers} fields={fields} onView={setViewUser} toolbar={toolbar} addLabel="Add person" dialogTitle="person"
                emptyTitle={hasFilters ? "No people match these filters" : "No people yet"}
                emptyDescription={hasFilters ? "Adjust the search or clear the filters." : "Add the first person to get started."}
                renderPrimary={item => <span className="adm-identity">
                    <span className="adm-avatar" aria-hidden="true">{initialsOf(item.name)}</span>
                    <span style={{ minWidth: 0 }}><span className="adm-cell-primary" style={{ display: "block" }}>{item.name}</span><span className="adm-cell-sub">{item.email || item.username || "—"}</span></span>
                    {item.isActive === false && <AdminBadge tone="neutral">Inactive</AdminBadge>}
                </span>}
                onAdd={async f => {
                    try { await createUserApi(normalizeUserForm(f)); uxNotify("Person added successfully.", "success"); }
                    catch (error) { uxNotify(error.message || "Could not add the person."); }
                }}
                onUpdate={async (id, f) => {
                    try { await updateUserApi(id, normalizeUserForm(f)); uxNotify("Person details saved.", "success"); }
                    catch (error) { uxNotify(error.message || "Could not update the person."); }
                }}
                onDelete={async id => {
                    try { await archiveUserApi(id); uxNotify("Person deactivated.", "success"); }
                    catch (error) { uxNotify(error.message || "Could not remove the person."); }
                }}
                validate={f => {
                    if (!f.name?.trim()) return "Full name is required.";
                    if (!f.employeeCode?.trim()) return "Employee ID is required.";
                    if (!f.nationalId?.trim()) return "Internal reference is required.";
                    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{1,31}$/.test(f.nationalId.trim())) return "Enter a valid internal reference.";
                    if (!f.email?.trim()) return "Email is required.";
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return "Enter a valid email address.";
                    if (!f.username?.trim()) return "Username is required.";
                    if (!f.id && (!f.password?.trim() || f.password.trim().length < 6)) return "Password must be at least 6 characters.";
                    if (f.password?.trim() && f.password.trim().length < 6) return "Password must be at least 6 characters.";
                    if (!f.role) return "Access level is required.";
                    if (!f.posId && !["admin"].includes(f.role)) return "Select a job position.";
                    if (needsOrgUnit(f) && !f.orgUnitId) return "Select an organization unit.";
                    if (needsManager(f) && !f.managerId) return "Select a direct manager.";
                    return null;
                }} />
            {viewUser && <AdminDialog size="lg" onClose={() => setViewUser(null)} title={viewUser.name} description="Profile and organization access"
                footer={<button type="button" className="adm-btn" onClick={() => setViewUser(null)}>Close</button>}>
                <div className="adm-identity" style={{ marginBottom: 16 }}>
                    <span className="adm-avatar" style={{ width: 44, height: 44, fontSize: 15 }} aria-hidden="true">{initialsOf(viewUser.name)}</span>
                    <span><span className="adm-cell-primary" style={{ display: "block", fontSize: 15 }}>{viewUser.name}</span>
                        <span className="adm-row" style={{ marginTop: 4 }}><AdminBadge tone={roleTone(viewUser.role)} plain>{roleLabel(viewUser.role)}</AdminBadge>{viewUser.isActive === false && <AdminBadge tone="neutral">Inactive</AdminBadge>}</span></span>
                </div>
                <h3 className="adm-section-label">Identity</h3>
                <dl className="adm-kv" style={{ marginBottom: 16 }}>
                    {[["Employee ID", viewUser.employeeCode || "—"], ["Internal reference", viewUser.nationalId || "—"], ["Email", viewUser.email || "—"], ["Username", viewUser.username || "—"]]
                        .map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
                </dl>
                <h3 className="adm-section-label">Organization & access</h3>
                <dl className="adm-kv">
                    {[["Access level", roleLabel(viewUser.role)], ["Job position", positionLabel(viewUser.posId)], ["Organizational unit", unitLabel(viewUser.orgUnitId)], ["Direct manager", managerLabel(viewUser.managerId)]]
                        .map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
                </dl>
            </AdminDialog>}
        </AdminPage>
    );
}

// Period configuration policy: reusable organizational defaults are carried forward into a DRAFT period,
// reviewed before activation, and never rewrite locked historical periods.
function PeriodEvaluationConfigurationInfo() {
    return <Card style={{ marginBottom: 16 }}><div style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <UiIcon name="settings" size={17} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>Period Evaluation Settings</div>
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.9 }}>
            Core responsibility questions, evaluation weights, and baseline settings are carried forward between periods.
            Each new period starts from the previous configuration as a suggested baseline, which the administrator can review before activation.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, marginTop: 12 }}>
            {[['Core Configuration','Questions, weights, and evaluation structure'],['Period Review','Review the period configuration before activation'],['Period Data','Targets, deadlines, and results are managed separately for each period']].map(([title,text]) =>
                <div key={title} style={{ border: `1px solid ${C.border}`, borderRadius: 9, padding: 10, background: C.bg }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600 }}>{title}</div><div style={{ fontSize: 10.5, color: C.muted, marginTop: 3, lineHeight: 1.7 }}>{text}</div>
                </div>)}
        </div>
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: C.cyanBg, fontSize: 10.5, color: '#007A8C', lineHeight: 1.8 }}>
            Locked periods remain independent; later configuration changes do not alter their history or finalized CVI results.
        </div>
    </div></Card>;
}

// ##PERIOD_CONFIG_UX_PLACEHOLDER##
function AdminSettings({ tab: controlledTab, onTabChange }) {
    const { activityGroups, impactLevels, routineLevels,
        createActivityGroupApi, updateActivityGroupApi, archiveActivityGroupApi,
        createImpactLevelApi, updateImpactLevelApi, archiveImpactLevelApi,
        createRoutineLevelApi, updateRoutineLevelApi, archiveRoutineLevelApi } = useData();
    const [localTab, setLocalTab] = useState("advanced-model");
    const tab = controlledTab || localTab;
    const setTab = onTabChange || setLocalTab;
    const { language: settingsLanguage } = useI18n();
    const [importanceLevels, setImportanceLevels] = useState([]);
    const settingsApiJson = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}/api${path}`, {
            ...options,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || "The operation could not be completed.");
        return payload;
    };
    const loadImportanceLevels = async () => { try { setImportanceLevels(await settingsApiJson("/admin/templates/importance-scale/list")); } catch (error) { uxNotify(error.message || "Could not load the importance scale."); } };
    useEffect(() => { loadImportanceLevels(); }, []);
    const importanceFields = [{ key: "label", label: "Level name", required: true }, { key: "score", label: "Value", type: "number", required: true, hint: "Relative weight; must be greater than zero." }];
    const groupFields = [
        { key: "label", label: "Contribution category", required: true },
        { key: "maxScore", label: "Maximum score", type: "number" },
        { key: "icon", label: "Icon", type: "icon", options: [{ value: "activity", label: "Activity" }, { value: "target", label: "Goal" }, { value: "performance", label: "Improvement / Performance" }, { value: "team", label: "Collaboration / Team" }] },
        { key: "subsText", label: "Subcategories (comma-separated)", placeholder: "Professional certification, internal training", wide: true },
    ];
    const impactFields = [{ key: "label", label: "Impact level name", required: true }, { key: "score", label: "Points", type: "number" }];
    const routineLevelFields = [{ key: "label", label: "Level name", required: true }, { key: "score", label: "Value", type: "number" }];
    const saveImportance = async (next, failure) => {
        try { setImportanceLevels(await settingsApiJson("/admin/templates/importance-scale", { method: "PUT", body: JSON.stringify({ levels: next }) })); }
        catch (error) { uxNotify(error.message || failure); }
    };

    return <AdminPage>
        <AdminPageHeader eyebrow="CVI Model" title="Advanced Settings" description="Configure organization-wide CVI rules that usually change infrequently." />
        <AdminTabs items={[
            { id: "advanced-model", label: "Evaluation Templates", icon: "settings" },
            { id: "kpi-formulas", label: settingsLanguage === "fr" ? "Modèles de calcul KPI" : "KPI Calculation Templates", icon: "target" },
            { id: "scales", label: "Scales & Ratings", icon: "performance", count: importanceLevels.length + impactLevels.length + routineLevels.length },
            { id: "groups", label: "Additional Contribution Categories", icon: "activity", count: activityGroups.length },
        ]} value={tab} onChange={setTab} ariaLabel="Advanced Settings sections" />
        <div style={{ display: tab === "advanced-model" ? "block" : "none" }} aria-hidden={tab !== "advanced-model"} className="adm-embedded">
            <FlexibleOrgPrototype initialTab="weights" visibleTabs={["weights"]} embedded />
        </div>
        {tab === "kpi-formulas" && <KpiFormulaTemplates language={settingsLanguage} />}
        {tab === "scales" && <div className="adm-stack">
            <EditableList title="Importance Scale" description="Used across CVI wherever an administrator selects qualitative importance instead of entering a raw weight."
                addLabel="Add level" dialogTitle="level" items={importanceLevels} fields={importanceFields}
                onAdd={async f => saveImportance([...importanceLevels, { label: f.label, score: Number(f.score) }], "Could not save the importance scale.")}
                onUpdate={async (id, f) => saveImportance(importanceLevels.map(x => x.id === id ? { ...x, label: f.label, score: Number(f.score) } : x), "Could not save the importance scale.")}
                onDelete={async id => saveImportance(importanceLevels.filter(x => x.id !== id), "Could not delete the level.")}
                validate={f => !f.label?.trim() ? "A name is required." : !(Number(f.score) > 0) ? "Score must be greater than zero." : null} />
            <EditableList title="Impact Rating Scale" description="Defines the rating levels used to describe the impact of additional contributions."
                addLabel="Add level" dialogTitle="level" items={impactLevels} fields={impactFields}
                onAdd={async f => { try { await createImpactLevelApi(f); } catch (error) { uxNotify(error.message || "Could not add the impact level."); } }}
                onUpdate={async (id, f) => { try { await updateImpactLevelApi(id, f); } catch (error) { uxNotify(error.message || "Could not update the impact level."); } }}
                onDelete={async id => { try { await archiveImpactLevelApi(id); } catch (error) { uxNotify(error.message || "Could not delete the impact level."); } }}
                validate={f => !f.label?.trim() ? "A name is required." : null} />
            <EditableList title="Core Responsibility Rating Scale" description="Define the rating choices used when employees are assessed on core responsibilities. Three to five levels are usually enough."
                addLabel="Add level" dialogTitle="level" items={routineLevels} fields={routineLevelFields}
                onAdd={async f => { try { await createRoutineLevelApi(f); } catch (error) { uxNotify(error.message || "Could not add the responsibility level."); } }}
                onUpdate={async (id, f) => { try { await updateRoutineLevelApi(id, f); } catch (error) { uxNotify(error.message || "Could not update the responsibility level."); } }}
                onDelete={async id => { try { await archiveRoutineLevelApi(id); } catch (error) { uxNotify(error.message || "Could not delete the responsibility level."); } }}
                validate={f => !f.label?.trim() ? "A name is required." : null} />
        </div>}
        {tab === "groups" && <EditableList title="Additional Contribution Categories"
            description="Keep this list concise and reusable. These categories organize additional contributions without changing the core CVI model."
            addLabel="Add category" dialogTitle="category"
            items={activityGroups.map(g => ({ ...g, subsText: (g.subs || []).join(", ") }))}
            fields={groupFields}
            onAdd={async f => { try { await createActivityGroupApi(f); } catch (error) { uxNotify(error.message || "Could not add the contribution category."); } }}
            onUpdate={async (id, f) => { try { await updateActivityGroupApi(id, f); } catch (error) { uxNotify(error.message || "Could not update the contribution category."); } }}
            onDelete={async id => { try { await archiveActivityGroupApi(id); } catch (error) { uxNotify(error.message || "Could not delete the contribution category."); } }}
            validate={f => !f.label?.trim() ? "Contribution category name is required." : null} />}
    </AdminPage>;
}

function AdminIndividualPerformance() {
    const [tab, setTab] = useState("routine");
    const { routineQuestions, peerQuestions } = useData();
    return <AdminPage>
        <AdminPageHeader eyebrow="CVI Model" title="Individual Contribution" description="Define how day-to-day responsibilities and peer feedback contribute to Individual Contribution." />
        <AdminTabs items={[
            { id: "routine", label: "Core Responsibilities", icon: "routine", count: routineQuestions.length },
            { id: "peer", label: "Peer Feedback", icon: "peer", count: peerQuestions.length },
        ]} value={tab} onChange={setTab} ariaLabel="Individual Contribution sections" />
        <div style={{ display: tab === "routine" ? "block" : "none" }} aria-hidden={tab !== "routine"}><AdminRoutineQuestions /></div>
        <div style={{ display: tab === "peer" ? "block" : "none" }} aria-hidden={tab !== "peer"}><AdminPeerFeedback /></div>
    </AdminPage>;
}

function AdminCollectiveContribution({ onOpenKpiTemplates, focusRequest = null }) {
    const { indicatorDefs, orgUnits, users, scheduleTemplates, createIndicatorApi, updateIndicatorApi, archiveIndicatorApi } = useData();
    const [assessmentTemplates, setAssessmentTemplates] = useState([]);
    const [kpiFormulas, setKpiFormulas] = useState([]);
    const { language: indicatorsLanguage } = useI18n();
    const tx = useAdminText();
    useEffect(() => {
        let cancelled = false;
        const token = sessionStorage.getItem("cvi_token");
        if (!token) return;
        fetch(`${API_BASE_URL}/api/indicators/calculation-templates`,{headers:{Authorization:`Bearer ${token}`}})
         .then(r => r.ok ? r.json() : []).then(rows => {if(!cancelled)setKpiFormulas(rows)})
         .catch(() => {if(!cancelled)setKpiFormulas([])});
        return () => {cancelled = true};
    }, []);
    const [organizationAssessment, setOrganizationAssessment] = useState(null);
    const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [qualitativeQuestions, setQualitativeQuestions] = useState([]);
    const [qualitativeDraft, setQualitativeDraft] = useState({ text: "", importanceId: "" });
    const [qualitativeFormOpen, setQualitativeFormOpen] = useState(false);
    const [tab, setTab] = useState("indicators");
    const [scope, setScope] = useState("org");
    const [orgUnitId, setOrgUnitId] = useState("");
    const [editingId, setEditingId] = useState("");
    const [indicatorFormOpen, setIndicatorFormOpen] = useState(false);
    const [importanceLevels, setImportanceLevels] = useState([]);
    const [importanceId, setImportanceId] = useState("");
    const activeUnits = orgUnits.filter(unit => unit.isActive !== false);
    // "View indicators" from Organization: open the unit's scope (or organization-wide).
    useEffect(() => {
        if (!focusRequest) return;
        setTab("indicators"); setEditingId(""); setIndicatorFormOpen(false);
        if (focusRequest.unitId) { setScope("org_unit"); setOrgUnitId(focusRequest.unitId); } else setScope("org");
    }, [focusRequest]);
    const apiJson = async (url, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}${url}`, {
            ...options,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        const data = await response.json().catch(() => ({}));
        if (response.status === 401) {
            sessionStorage.removeItem("cvi_user");
            sessionStorage.removeItem("cvi_token");
            window.dispatchEvent(new Event("cvi-session-expired"));
            throw new Error("Session expired.");
        }
        if (!response.ok) throw new Error(data.message || "The operation could not be completed..");
        return data;
    };
    useEffect(() => {
        Promise.all([apiJson("/api/admin/templates"), apiJson("/api/organization"), apiJson("/api/admin/templates/importance-scale/list")]).then(([templates, organization, importance]) => {
            setAssessmentTemplates(templates.assessment || []);
            setOrganizationAssessment(organization.participationIndicatorProfile || null);
            setImportanceLevels(importance || []);
            if (importance?.length) { const defaultImportance = importance[Math.floor(importance.length / 2)]?.id || importance[0].id; setImportanceId(defaultImportance); setQualitativeDraft(d => ({ ...d, importanceId: d.importanceId || defaultImportance })); }
        }).catch(error => uxNotify(error.message || "Could not load evaluation templates.", "error"));
    }, []);
    const selectedUnit = activeUnits.find(unit => unit.id === orgUnitId);
    const assignedTemplate = scope === "org"
        ? organizationAssessment
        : (selectedUnit?.participationIndicatorProfile || assessmentTemplates.find(t => t.id === selectedUnit?.participationIndicatorProfileId) || null);
    const defaultAssessmentTemplate = assessmentTemplates.find(t => t.isDefault === true || String(t.isDefault).toLowerCase() === "true") || null;
    const effectiveTemplate = assignedTemplate || defaultAssessmentTemplate || null;
    const hasKpi = !effectiveTemplate || Number(effectiveTemplate.kpiWeight) > 0;
    const hasQualitative = Boolean(effectiveTemplate && Number(effectiveTemplate.qualitativeWeight) > 0);
    useEffect(() => {
        if (scope !== "org" && !orgUnitId) { setQualitativeQuestions([]); return; }
        const params = new URLSearchParams(scope === "org" ? { level: "ORGANIZATION" } : { level: "ORG_UNIT", orgUnitId });
        apiJson(`/api/admin/templates/qualitative/questions/list?${params.toString()}`).then(setQualitativeQuestions).catch(() => setQualitativeQuestions([]));
    }, [scope, orgUnitId]);
    const changeAssessmentTemplate = async () => {
        if (!selectedTemplateId) return;
        try {
            const targetId = scope === "org" ? (organizationAssessment?.organizationId || null) : orgUnitId;
            let organizationId = targetId;
            if (scope === "org") {
                const organization = await apiJson("/api/organization");
                organizationId = organization.id;
            }
            const updated = await apiJson(`/api/admin/templates/units/${scope === "org" ? "organization" : "org-unit"}/${organizationId}/assessment-template`, { method: "PATCH", body: JSON.stringify({ participationIndicatorProfileId: selectedTemplateId }) });
            if (scope === "org") setOrganizationAssessment(updated.participationIndicatorProfile || assessmentTemplates.find(t => t.id === selectedTemplateId));
            else {
                const chosen = assessmentTemplates.find(t => t.id === selectedTemplateId);
                if (selectedUnit) { selectedUnit.participationIndicatorProfileId = selectedTemplateId; selectedUnit.participationIndicatorProfile = chosen; }
            }
            setTemplateEditorOpen(false); uxNotify("Evaluation template updated.", "success");
        } catch (error) { uxNotify(error.message || "Could not change the evaluation template.", "error"); }
    };
    const addQualitativeQuestion = async () => {
        const selectedImportance = importanceLevels.find(level => level.id === qualitativeDraft.importanceId);
        const score = Number(selectedImportance?.score || 0);
        if (!qualitativeDraft.text.trim() || score <= 0) { uxNotify("Enter the question and select an importance level.", "error"); return; }
        try {
            const saved = await apiJson("/api/admin/templates/qualitative/questions", { method: "POST", body: JSON.stringify({ text: qualitativeDraft.text, weight: score, level: scope === "org" ? "ORGANIZATION" : "ORG_UNIT", orgUnitId: scope === "org_unit" ? orgUnitId : null, sortOrder: qualitativeQuestions.length + 1 }) });
            setQualitativeQuestions(v => [...v, saved]); setQualitativeDraft({ text: "", importanceId: qualitativeDraft.importanceId }); setQualitativeFormOpen(false); uxNotify("Qualitative question added.", "success");
        } catch (error) { uxNotify(error.message || "Could not add the question.", "error"); }
    };
    const people = users.filter(user => user.isActive !== false && String(user.role).toLowerCase() !== "admin");
    const defaultPeople = {
        ownerId: people.find(user => String(user.role).toLowerCase() === "ceo")?.id || people[0]?.id || "",
        registrarId: people[0]?.id || "",
        approverId: people.find(user => String(user.role).toLowerCase() === "ceo")?.id || people[1]?.id || people[0]?.id || "",
    };
    const emptyForm = () => ({
        label: "", level: scope, orgUnitId: scope === "org_unit" ? orgUnitId : null, unit: "%", weight: 0,
        aggregationMethod: "latest", formulaTemplateId: "standard:higher", scheduleTemplateId: scheduleTemplates[0]?.id || "", requiresEvidence: false, ...defaultPeople,
    });
    const [form, setForm] = useState(emptyForm);
    // Snapshot taken when the indicator dialog opens; used only for the unsaved-changes warning.
    const [indicatorSnapshot, setIndicatorSnapshot] = useState("");
    useEffect(() => setForm(current => ({ ...current, level: scope, orgUnitId: scope === "org_unit" ? orgUnitId : null, scheduleTemplateId: current.scheduleTemplateId || scheduleTemplates[0]?.id || "" })), [scope, orgUnitId, scheduleTemplates.length]);
    const visibleIndicators = indicatorDefs.filter(item => scope === "org"
        ? item.level === "org"
        : item.level === "org_unit" && item.orgUnitId === orgUnitId);
    const resetForm = () => { setEditingId(""); setIndicatorFormOpen(false); setForm(emptyForm()); };
    const startEdit = item => {
        setEditingId(item.id);
        setIndicatorFormOpen(true);
        const nextForm = { ...item, label: item.label || item.name || "", level: scope, orgUnitId: scope === "org_unit" ? orgUnitId : null };
        setForm(nextForm);
        const matchingLevel = importanceLevels.find(level => Math.abs(Number(level.score) - Number(item.weight)) < 0.0001);
        setImportanceId(matchingLevel?.id || "");
        setIndicatorSnapshot(JSON.stringify({ form: nextForm, importanceId: matchingLevel?.id || "" }));
    };
    const saveIndicator = async () => {
        const chosenImportance = importanceLevels.find(level => level.id === importanceId);
        const selectedScore = Number(chosenImportance?.score || 0);
        const payload = { ...form, weight: selectedScore, level: scope, orgUnitId: scope === "org_unit" ? orgUnitId : null };
        if (!payload.label.trim() || !payload.unit.trim() || !importanceId || !payload.ownerId || !payload.registrarId || !payload.approverId || !payload.scheduleTemplateId) {
            uxNotify("Complete the indicator name, unit, importance, owners, and schedule.", "error"); return;
        }
        if (payload.registrarId === payload.approverId) { uxNotify("The data submitter and approver must be different people.", "error"); return; }
        try {
            if (editingId) await updateIndicatorApi(editingId, payload); else await createIndicatorApi(payload);
            uxNotify(editingId ? "Indicator updated." : "Indicator added.", "success"); resetForm();
        } catch (error) { uxNotify(error.message || "Could not save the indicator.", "error"); }
    };
    const scopeName = scope === "org" ? "Organization-wide" : (activeUnits.find(unit => unit.id === orgUnitId)?.label || "Organizational unit");
    const scopeReady = scope === "org" || Boolean(orgUnitId);
    const aggregationLabel = method => method === "average" ? "Average" : method === "sum" ? "Sum" : "Latest value";
    const personName = id => users.find(user => user.id === id)?.name || "—";
    const formulaFor = id => kpiFormulas.find(item => item.id === (id || "standard:higher"));
    const formulaLabel = id => {
        const template = formulaFor(id);
        if (template) return kpiTemplateName(template, indicatorsLanguage);
        return id === "standard:higher" || !id ? kpiMethodCopy("HIGHER", indicatorsLanguage).label : "—";
    };
    const selectableFormulas = kpiFormulas.filter(x => x.isActive !== false || x.id === form.formulaTemplateId);
    const normalizedPreview = (() => {
        const selected = importanceLevels.find(level => level.id === importanceId);
        const others = visibleIndicators.filter(item => item.id !== editingId);
        const total = others.reduce((sum, item) => sum + Number(item.weight || 0), 0) + Number(selected?.score || 0);
        return total > 0 ? ((Number(selected?.score || 0) / total) * 100).toFixed(1) : "0.0";
    })();
    const kpiShare = Number(effectiveTemplate?.kpiWeight || 0), qualitativeShare = Number(effectiveTemplate?.qualitativeWeight || 0);

    return <AdminPage>
        <AdminPageHeader eyebrow="CVI Model" title="Collective Contribution" description="Define the few shared indicators that connect team and organization results to CVI." />
        <AdminTabs items={[
            { id: "indicators", label: "Indicators", icon: "target", count: indicatorDefs.length },
            { id: "schedules", label: "Scheduling", icon: "schedule", count: scheduleTemplates.length },
        ]} value={tab} onChange={setTab} ariaLabel="Collective Contribution sections" />
        {tab === "indicators" && <div className="adm-stack">
            <AdminPanel title="Evaluation Scope" description="Choose whether these indicators apply to the whole organization or to a specific unit.">
                <div className="adm-grid-2" style={{ alignItems: "stretch" }}>
                    <div className="adm-stack-sm">
                        <AdminSegmented ariaLabel="Evaluation Scope" value={scope}
                            onChange={value => { setScope(value); setEditingId(""); setIndicatorFormOpen(false); }}
                            options={[{ value: "org", label: "Organization-wide" }, { value: "org_unit", label: "Organizational unit" }]} />
                        {scope === "org_unit"
                            ? <AdminField label="Organizational unit"><select value={orgUnitId} onChange={e => { setOrgUnitId(e.target.value); setEditingId(""); setIndicatorFormOpen(false); }}>
                                <option value="">Select organization unit</option>{activeUnits.map(unit => <option key={unit.id} value={unit.id}>{unit.label}</option>)}
                            </select></AdminField>
                            : <p className="adm-hint" style={{ margin: 0, fontSize: 12 }}>These indicators apply across the organization.</p>}
                    </div>
                    {scopeReady && <div style={{ border: "1px solid var(--adm-border)", borderRadius: 10, padding: 14, background: "var(--adm-surface-2)" }}>
                        <div className="adm-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div style={{ minWidth: 0 }}>
                                <div className="adm-section-label" style={{ marginBottom: 4 }}>Active Evaluation Template</div>
                                <div style={{ fontSize: 14, fontWeight: 650, color: "var(--adm-ink)" }}>{effectiveTemplate?.name || "No evaluation template has been defined yet."}</div>
                            </div>
                            {!templateEditorOpen && <button type="button" className="adm-btn adm-btn--sm" onClick={() => { setSelectedTemplateId(effectiveTemplate?.id || ""); setTemplateEditorOpen(true); }}>Change Template</button>}
                        </div>
                        {effectiveTemplate && <div style={{ marginTop: 12 }}>
                            <div className="adm-split" aria-hidden="true"><span style={{ width: `${kpiShare}%` }} /><span style={{ width: `${qualitativeShare}%` }} /></div>
                            <div className="adm-legend" style={{ marginTop: 8 }}><span><i />{tx(`${kpiShare}% indicators`, `${kpiShare}\u00a0% indicateurs`)}</span><span><i className="is-accent" />{tx(`${qualitativeShare}% qualitative`, `${qualitativeShare}\u00a0% qualitatif`)}</span></div>
                        </div>}
                        {templateEditorOpen && <div className="adm-row" style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--adm-border)" }}>
                            <select className="adm-control" style={{ flex: "1 1 220px" }} aria-label="Change Template" value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}>
                                <option value="">Select a template</option>{assessmentTemplates.map(t => <option key={t.id} value={t.id}>{t.name} — {t.kpiWeight}% KPI / {t.qualitativeWeight}% qualitative</option>)}
                            </select>
                            <button type="button" className="adm-btn adm-btn--sm" onClick={() => setTemplateEditorOpen(false)}>Cancel</button>
                            <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" disabled={!selectedTemplateId} onClick={changeAssessmentTemplate}>Save</button>
                        </div>}
                    </div>}
                </div>
            </AdminPanel>

            {!scopeReady && <AdminPanel><AdminEmpty compact icon="org" title="Select an organizational unit" description="Choose a unit above to review its indicators and qualitative questions." /></AdminPanel>}

            {hasKpi && scopeReady && <AdminPanel flush
                title={<>{tx(`${scopeName} indicators`, `Indicateurs — ${scopeName}`)} <span className="adm-count">{visibleIndicators.length}</span></>}
                description={<>Weights are normalized automatically from each indicator’s importance level. {onOpenKpiTemplates && <button type="button" className="adm-btn adm-btn--link" onClick={onOpenKpiTemplates}>Manage KPI calculation templates</button>}</>}
                actions={<button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => { const fresh = emptyForm(); setEditingId(""); setForm(fresh); setIndicatorSnapshot(JSON.stringify({ form: fresh, importanceId })); setIndicatorFormOpen(true); }}><UiIcon name="plus" size={14} />Add indicator</button>}>
                {!visibleIndicators.length ? <AdminEmpty compact icon="target" title="No indicators have been added for this scope yet." description="Add a small number of shared indicators that reflect the unit’s collective results." /> :
                    <div className="adm-table-wrap"><table className="adm-table is-responsive">
                        <thead><tr>
                            <th scope="col">Indicator</th><th scope="col">Calculation method</th><th scope="col">Indicator Owner</th>
                            <th scope="col">Importance Level</th><th scope="col" className="is-num">Normalized Weight</th><th scope="col" className="is-actions"><span className="adm-sr">Actions</span></th>
                        </tr></thead>
                        <tbody>{visibleIndicators.map(item => {
                            const info = importancePresentation(item.weight, importanceLevels, visibleIndicators);
                            return <tr key={item.id}>
                                <td className="is-primary"><span className="adm-cell-primary">{item.label}</span><span className="adm-cell-sub">{item.unit} · {aggregationLabel(item.aggregationMethod)}{item.requiresEvidence ? <> · {"Evidence required"}</> : null}</span></td>
                                <td data-label="Calculation method">{formulaLabel(item.formulaTemplateId)}</td>
                                <td data-label="Indicator Owner" className="adm-cell-muted">{personName(item.ownerId)}</td>
                                <td data-label="Importance Level">{info.label}</td>
                                <td data-label="Normalized Weight" className="is-num"><AdminWeight value={info.effective} /></td>
                                <td className="is-actions"><span className="adm-row-actions">
                                    <button type="button" className="adm-icon-btn" onClick={() => startEdit(item)} title="Edit" aria-label="Edit"><UiIcon name="edit" size={16} /></button>
                                    <button type="button" className="adm-icon-btn is-danger" title="Delete" aria-label="Delete" onClick={async () => { if (!await uxConfirm(tx(`Delete indicator “${item.label}”? `, `Supprimer l’indicateur « ${item.label} »? `))) return; try { await archiveIndicatorApi(item.id); } catch (error) { uxNotify(error.message || "Could not delete the indicator.", "error"); } }}><UiIcon name="archive" size={16} /></button>
                                </span></td>
                            </tr>;
                        })}</tbody>
                    </table></div>}
            </AdminPanel>}

            {hasKpi && indicatorFormOpen && scopeReady && <AdminDialog size="xl" onClose={resetForm} dirty={JSON.stringify({ form, importanceId }) !== indicatorSnapshot}
                title={editingId ? "Edit Indicator" : tx(`Add Indicator for ${scopeName}`, `Ajouter un indicateur — ${scopeName}`)}
                description={editingId ? "Update the indicator details and save your changes." : "Complete the new indicator details."}
                footer={({ requestClose }) => <><button type="button" onClick={requestClose} className="adm-btn">Cancel</button><button type="button" onClick={saveIndicator} className="adm-btn adm-btn--primary">{editingId ? "Save changes" : "Add indicator"}</button></>}>
                <div className="adm-form-section">
                    <h3 className="adm-form-section-title">Definition</h3>
                    <div className="adm-form-grid is-3">
                        <AdminField label="Indicator" required className="is-wide"><input value={form.label || ""} onChange={e => setForm(x => ({ ...x, label: e.target.value }))} placeholder="e.g., Customer retention rate" /></AdminField>
                        <AdminField label="Unit of Measure" required><input value={form.unit || ""} onChange={e => setForm(x => ({ ...x, unit: e.target.value }))} placeholder="%, count, days, score..." /></AdminField>
                        <AdminField label="Aggregation Method" hint="How several values recorded in one period are combined."><select value={form.aggregationMethod || "latest"} onChange={e => setForm(x => ({ ...x, aggregationMethod: e.target.value }))}><option value="latest">Latest value</option><option value="average">Average</option><option value="sum">Sum</option></select></AdminField>
                        <AdminField label="Importance" required hint={importanceId ? tx(`Current normalized weight: ${normalizedPreview}%`, `Poids normalisé actuel : ${normalizedPreview}\u00a0%`) : undefined}>
                            <select value={importanceId} onChange={e => setImportanceId(e.target.value)}><option value="">Select importance level</option>{importanceLevels.map(level => <option key={level.id} value={level.id}>{level.label}</option>)}</select>
                        </AdminField>
                    </div>
                </div>
                <div className="adm-form-section">
                    <h3 className="adm-form-section-title">{indicatorsLanguage === "fr" ? "Méthode de calcul KPI" : "KPI calculation method"}<small>{indicatorsLanguage === "fr" ? "— comment la valeur réelle est comparée à la cible" : "— how the actual value is compared with the target"}</small></h3>
                    <div className="adm-choice-grid" role="radiogroup" aria-label={indicatorsLanguage === "fr" ? "Méthode de calcul KPI" : "KPI calculation method"} data-cvi-i18n="managed">
                        {(selectableFormulas.length ? selectableFormulas : [{ id: "standard:higher", method: "HIGHER", isSystem: true, isActive: true }]).map(template => {
                            const info = kpiMethodCopy(template.method, indicatorsLanguage);
                            const selected = (form.formulaTemplateId || "standard:higher") === template.id;
                            const standard = isStandardKpiTemplate(template);
                            return <label key={template.id} className={`adm-choice${selected ? " is-selected" : ""}`}>
                                <input type="radio" name="adm-indicator-formula" value={template.id} checked={selected} onChange={() => setForm(x => ({ ...x, formulaTemplateId: template.id }))} />
                                <span className="adm-choice-copy">
                                    <strong><span aria-hidden="true">{info.symbol}</span>{kpiTemplateName(template, indicatorsLanguage)}
                                        {!standard && <AdminBadge tone="accent" plain>{indicatorsLanguage === "fr" ? "Personnalisé" : "Custom"}</AdminBadge>}
                                        {template.isActive === false && <AdminBadge tone="neutral">{indicatorsLanguage === "fr" ? "Inactif" : "Inactive"}</AdminBadge>}</strong>
                                    <small>{template.method === "CUSTOM" ? <code className="adm-formula">{template.parameters?.expression || "—"}</code> : describeKpiParameters(template, indicatorsLanguage)}</small>
                                </span>
                            </label>;
                        })}
                    </div>
                </div>
                <div className="adm-form-section">
                    <h3 className="adm-form-section-title">Accountability</h3>
                    <div className="adm-form-grid is-3">
                        {[["ownerId", "Indicator Owner"], ["registrarId", "Data Submitter"], ["approverId", "Approver"]].map(([key, label]) => <AdminField key={key} label={label} required
                            hint={key === "approverId" ? "The data submitter and approver must be different people." : undefined}>
                            <select value={form[key] || ""} onChange={e => setForm(x => ({ ...x, [key]: e.target.value }))}><option value="">Select person</option>{people.map(person => <option key={person.id} value={person.id}>{person.name}</option>)}</select>
                        </AdminField>)}
                    </div>
                </div>
                <div className="adm-form-section">
                    <h3 className="adm-form-section-title">Schedule & evidence</h3>
                    <div className="adm-form-grid">
                        <AdminField label="Schedule template" required><select value={form.scheduleTemplateId || ""} onChange={e => setForm(x => ({ ...x, scheduleTemplateId: e.target.value }))}><option value="">Select a template</option>{scheduleTemplates.map(t => <option key={t.id} value={t.id}>{t.label || t.name}</option>)}</select></AdminField>
                        <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8 }}>
                            <label className="adm-check"><input type="checkbox" checked={Boolean(form.requiresEvidence)} onChange={e => setForm(x => ({ ...x, requiresEvidence: e.target.checked }))} /><span>Require supporting evidence for performance submissions</span></label>
                        </div>
                    </div>
                </div>
            </AdminDialog>}

            {hasQualitative && scopeReady && <AdminPanel flush
                title={<>{tx(`${scopeName} qualitative assessment`, `Évaluation qualitative — ${scopeName}`)} <span className="adm-count">{qualitativeQuestions.length}</span></>}
                description={tx(`This section is enabled by the “${effectiveTemplate?.name}” template. Question weights are normalized automatically.`, `Cette section est activée par le modèle « ${effectiveTemplate?.name} ». Les poids des questions sont normalisés automatiquement.`)}
                actions={<button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => setQualitativeFormOpen(true)}><UiIcon name="plus" size={14} />Add question</button>}>
                {!qualitativeQuestions.length ? <AdminEmpty compact icon="peer" title="No qualitative questions have been defined yet." /> :
                    <div className="adm-table-wrap"><table className="adm-table is-responsive">
                        <thead><tr><th scope="col">Question</th><th scope="col">Importance Level</th><th scope="col" className="is-num">Normalized Weight</th></tr></thead>
                        <tbody>{qualitativeQuestions.map(q => {
                            const info = importancePresentation(q.weight, importanceLevels, qualitativeQuestions);
                            return <tr key={q.id}><td className="is-primary"><span className="adm-cell-primary" style={{ fontWeight: 500 }}>{q.text}</span></td><td data-label="Importance Level">{info.label}</td><td data-label="Normalized Weight" className="is-num"><AdminWeight value={info.effective} /></td></tr>;
                        })}</tbody>
                    </table></div>}
                {qualitativeFormOpen && <AdminDialog onClose={() => { setQualitativeFormOpen(false); setQualitativeDraft(d => ({ ...d, text: "" })); }} dirty={Boolean(qualitativeDraft.text.trim())} title="Add qualitative question"
                    description="Enter the question and select an importance level. The normalized weight is calculated automatically."
                    footer={({ requestClose }) => <><button type="button" className="adm-btn" onClick={requestClose}>Cancel</button><button type="button" className="adm-btn adm-btn--primary" onClick={addQualitativeQuestion}>Add question</button></>}>
                    <div className="adm-form-grid">
                        <AdminField label="Question" required wide><input value={qualitativeDraft.text} onChange={e => setQualitativeDraft(x => ({ ...x, text: e.target.value }))} placeholder="e.g., Aligns work with strategic priorities" /></AdminField>
                        <AdminField label="Importance Level" required wide><select value={qualitativeDraft.importanceId} onChange={e => setQualitativeDraft(x => ({ ...x, importanceId: e.target.value }))}><option value="">Select importance level</option>{importanceLevels.map(level => <option key={level.id} value={level.id}>{level.label}</option>)}</select></AdminField>
                    </div>
                </AdminDialog>}
            </AdminPanel>}
        </div>}
        {tab === "schedules" && <AdminIndicatorSchedules />}
    </AdminPage>;
}

// Schedule templates: the backend currently supports listing and creating templates only
// (GET/POST /api/indicators/schedule-templates). Editing, deleting and per-indicator exceptions
// have no API, so they are disabled with an explanation instead of being saved only in the browser.

function AdminIndicatorSchedules() {
    const { scheduleTemplates, createScheduleTemplateApi, indicatorDefs } = useData();
    const tx = useAdminText();
    const fields = [
        { key: "label", label: "Template name", placeholder: "Standard Quarterly Review", required: true, wide: true },
        { key: "frequency", label: "Frequency", type: "select", required: true, options: [{ value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }, { value: "annual", label: "Annual" }] },
        { key: "openOffsetDays", label: "Submission opens (days after period end)", columnLabel: "Opens", type: "number" },
        { key: "submitAfterDays", label: "Submission deadline (days after period end)", columnLabel: "Submission due", type: "number", required: true },
        { key: "approveAfterDays", label: "Approval deadline (days after period end)", columnLabel: "Approval due", type: "number", required: true },
        { key: "lockAfterDays", label: "Lock date (days after period end)", columnLabel: "Locks", type: "number", required: true },
        { key: "reminderDays", label: "Reminder (days before deadline)", columnLabel: "Reminder", type: "number" },
        { key: "usage", label: "Used by", computed: true, render: item => {
            const count = indicatorDefs.filter(indicator => indicator.scheduleTemplateId === item.id).length;
            return tx(`${count} ${count === 1 ? "indicator" : "indicators"}`, `${count} indicateur${count === 1 ? "" : "s"}`);
        } },
    ];
    const unsupported = "Existing schedule templates can't be edited or deleted. Create a new template instead.";
    return <div className="adm-stack">
        <EditableList title="Indicator Scheduling" addLabel="Add schedule template" dialogTitle="schedule template"
            description="Dates are counted in days from the end of each period (monthly, quarterly or annual). The reminder is sent the given number of days before the submission deadline."
            notice={<AdminCallout tone="neutral">Existing schedule templates can't be edited or deleted. To change a schedule, create a new template and assign it to the indicators that should use it. Dates already stored for previous periods do not change.</AdminCallout>}
            items={scheduleTemplates} fields={fields}
            onAdd={f => createScheduleTemplateApi(f)}
            onUpdate={() => Promise.reject(new Error(unsupported))}
            onDelete={() => uxNotify(unsupported)}
            editDisabledReason={unsupported} deleteDisabledReason={unsupported}
            validate={scheduleTemplateValidation} />
        <AdminPanel title="Indicator Exceptions" description="Override the schedule dates of one indicator for one period.">
            <AdminCallout tone="warning">
                <strong>Not available yet.</strong> Indicator-specific date exceptions are not supported in this version. Every indicator follows the dates of its schedule template.
            </AdminCallout>
        </AdminPanel>
    </div>;
}

// Core responsibility questions are scoped by organizational unit and period.
// Reusable editor for core responsibility questions scoped to an organizational level.
function importancePresentation(rawWeight, levels, items = []) {
    const score = Number(rawWeight) || 0;
    const level = levels.find(row => Math.abs(Number(row.score) - score) < 0.0001);
    const total = items.reduce((sum, item) => sum + Math.max(0, Number(item.weight) || 0), 0);
    const effective = total > 0 ? (score / total) * 100 : 0;
    return { label: level?.label || "—", effective };
}

function useImportanceScaleOptions() {
    const [levels, setLevels] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = sessionStorage.getItem("cvi_token");
                const response = await fetch(`${API_BASE_URL}/api/admin/templates/importance-scale/list`, { headers: { Authorization: `Bearer ${token}` } });
                const payload = await response.json().catch(() => []);
                if (!response.ok) throw new Error(payload.message || "Importance scale load failed");
                if (!cancelled) setLevels(Array.isArray(payload) ? payload : []);
            } catch (error) { console.error("Importance scale load failed:", error); }
        })();
        return () => { cancelled = true; };
    }, []);
    return levels.map(level => ({ value: String(level.score), label: level.label }));
}

function ScopedRoutineQuestionsEditor({ scopeItems, scopeKey, questions, scopeLabel, questionBank = [] }) {
    const { createRoutineQuestionApi, updateRoutineQuestionApi, archiveRoutineQuestionApi } = useData();
    const importanceOptions = useImportanceScaleOptions();
    const tx = useAdminText();
    const [scopeId, setScopeId] = useState(scopeItems[0]?.id || null);
    const [bankOpen, setBankOpen] = useState(false);
    const [bankSelection, setBankSelection] = useState([]);
    const fields = [
        { key: "label", label: "Question", placeholder: "e.g., Meets agreed delivery deadlines", required: true, wide: true },
        { key: "weight", label: "Importance Level", type: "select", placeholder: "Select importance level", options: importanceOptions, wide: true },
        { key: "computationalWeight", label: "Normalized Weight", computed: true, suffix: "%" },
    ];
    const scope = scopeKey === "orgUnitId" ? "ORG_UNIT" : scopeKey === "teamId" ? "TEAM" : "DEPARTMENT";
    const scopePayload = () => scope === "ORG_UNIT" ? { orgUnitId: scopeId } : scope === "TEAM" ? { teamId: scopeId } : { departmentId: scopeId };

    useEffect(() => {
        if (scopeItems.length && !scopeItems.some(item => item.id === scopeId)) setScopeId(scopeItems[0].id);
    }, [scopeItems, scopeId]);

    if (!scopeItems.length) return <AdminPanel><AdminEmpty compact icon="org" title={tx(`Create a ${scopeLabel} first.`, "Créez d’abord une unité organisationnelle.")} /></AdminPanel>;

    const addFromBank = async () => {
        if (!bankSelection.length) return;
        try {
            for (const id of bankSelection) {
                const source = questionBank.find(item => item.id === id);
                if (!source) continue;
                await createRoutineQuestionApi({ scope, ...scopePayload(), month: Number(source.month) || 1, text: source.label || source.text, weight: Number(source.weight) || 0 });
            }
            setBankSelection([]); setBankOpen(false);
        } catch (error) { uxNotify(error.message || "Could not add the selected question."); }
    };
    const scopedQuestions = questions.filter(q => q[scopeKey] === scopeId);
    const scopeName = scopeItems.find(item => item.id === scopeId)?.label || scopeItems.find(item => item.id === scopeId)?.name || "";
    const monthLabel = month => month === 1 ? "Month 1" : month === 2 ? "Month 2" : "Month 3";

    return <div className="adm-stack">
        <AdminPanel title={scopeLabel} description="Questions are defined per unit. Normalized weights are relative to all questions of the selected unit across the three months."
            actions={<button type="button" className="adm-btn adm-btn--sm" onClick={() => setBankOpen(true)}><UiIcon name="book" size={14} />Select from Question Bank</button>}>
            <div className="adm-row" style={{ alignItems: "flex-end", gap: 16 }}>
                <AdminField label={scopeLabel} className="adm-unit-picker"><select value={scopeId || ""} onChange={e => setScopeId(e.target.value)} style={{ minWidth: 260 }}>
                    {scopeItems.map(item => <option key={item.id} value={item.id}>{item.label || item.name}</option>)}
                </select></AdminField>
                <div className="adm-row" style={{ paddingBottom: 6 }}>
                    {[1, 2, 3].map(month => <AdminBadge key={month} tone="neutral" plain>{monthLabel(month)} · {scopedQuestions.filter(q => q.month === month).length}</AdminBadge>)}
                </div>
            </div>
        </AdminPanel>
        {bankOpen && <AdminDialog size="lg" onClose={() => { setBankOpen(false); setBankSelection([]); }} dirty={bankSelection.length > 0} title="Select from Question Bank"
            description={tx(`Selected questions are copied to ${scopeName || "the chosen unit"} and can be edited independently.`, `Les questions sélectionnées sont copiées dans ${scopeName || "l’unité choisie"} et peuvent être modifiées séparément.`)}
            footNote={bankSelection.length ? tx(`${bankSelection.length} selected`, `${bankSelection.length} sélectionnée(s)`) : undefined}
            footer={({ requestClose }) => <><button type="button" className="adm-btn" onClick={requestClose}>Cancel</button><button type="button" className="adm-btn adm-btn--primary" disabled={!bankSelection.length} onClick={addFromBank}>Add to unit</button></>}>
            {questionBank.length === 0 ? <AdminEmpty compact icon="book" title="No questions have been added to the Question Bank yet." /> :
                <div className="adm-stack-sm">{questionBank.map(item => <label key={item.id} className={`adm-choice${bankSelection.includes(item.id) ? " is-selected" : ""}`}>
                    <input type="checkbox" checked={bankSelection.includes(item.id)} onChange={e => setBankSelection(current => e.target.checked ? [...current, item.id] : current.filter(id => id !== item.id))} />
                    <span className="adm-choice-copy" style={{ flex: 1 }}><strong style={{ fontWeight: 500 }}>{item.label || item.text}</strong></span>
                    <AdminBadge tone="neutral" plain>{monthLabel(item.month)}</AdminBadge>
                </label>)}</div>}
        </AdminDialog>}
        {[1, 2, 3].map(month => {
            const totalWeight = scopedQuestions.reduce((sum, q) => sum + (Number(q.weight) || 0), 0);
            const monthItems = scopedQuestions.filter(q => q.month === month).map(q => ({
                ...q,
                computationalWeight: totalWeight > 0 ? ((Number(q.weight) || 0) / totalWeight * 100).toFixed(1) : "0.0"
            }));
            return <EditableList key={month} title={monthLabel(month)} addLabel="Add question" dialogTitle="question"
                emptyTitle="No questions for this month" emptyDescription="Add a question or copy one from the Question Bank."
                items={monthItems} fields={fields}
                onAdd={async f => {
                    try {
                        await createRoutineQuestionApi({ scope, ...scopePayload(), month, text: f.label, weight: Number(f.weight) || 0 });
                    } catch (error) { uxNotify(error.message || "Could not add the core responsibility question."); }
                }}
                onUpdate={async (id, f) => {
                    try {
                        await updateRoutineQuestionApi(id, { scope, ...scopePayload(), month, text: f.label, weight: Number(f.weight) || 0 });
                    } catch (error) { uxNotify(error.message || "Could not update the core responsibility question."); }
                }}
                onDelete={async id => {
                    try { await archiveRoutineQuestionApi(id); }
                    catch (error) { uxNotify(error.message || "Could not delete the core responsibility question."); }
                }}
                validate={f => !f.label?.trim() ? "Question is required." : null} />;
        })}
    </div>;
}

// Question Bank entries are reusable templates and do not directly affect evaluation scores.
function OrgRoutineQuestionsEditor({ questions }) {
    const { createRoutineQuestionApi, updateRoutineQuestionApi, archiveRoutineQuestionApi } = useData();
    const importanceOptions = useImportanceScaleOptions();
    const fields = [
        { key: "label", label: "Question", placeholder: "e.g., Meets agreed delivery deadlines", required: true, wide: true },
        { key: "weight", label: "Importance Level", type: "select", placeholder: "Select importance level", options: importanceOptions, wide: true },
        { key: "computationalWeight", label: "Normalized Weight", computed: true, suffix: "%" },
    ];
    return <div className="adm-stack">
        <AdminCallout tone="neutral">Question Bank entries are reusable templates. They do not affect scores until they are copied into a unit; normalized weights here are shown within each month.</AdminCallout>
        {[1, 2, 3].map(month => {
            const monthQuestions = questions.filter(q => q.month === month);
            const totalWeight = monthQuestions.reduce((sum, q) => sum + (Number(q.weight) || 0), 0);
            return <EditableList key={month} title={month === 1 ? "Month 1" : month === 2 ? "Month 2" : "Month 3"} addLabel="Add question" dialogTitle="question"
                emptyTitle="No questions for this month"
                items={monthQuestions.map(q => ({
                    ...q,
                    computationalWeight: totalWeight > 0 ? ((Number(q.weight) || 0) / totalWeight * 100).toFixed(1) : "0.0"
                }))} fields={fields}
                onAdd={async f => {
                    try { await createRoutineQuestionApi({ scope: "ORGANIZATION", month, text: f.label, weight: Number(f.weight) || 0 }); }
                    catch (error) { uxNotify(error.message || "Could not add the core responsibility question."); }
                }}
                onUpdate={async (id, f) => {
                    try { await updateRoutineQuestionApi(id, { scope: "ORGANIZATION", month, text: f.label, weight: Number(f.weight) || 0 }); }
                    catch (error) { uxNotify(error.message || "Could not update the core responsibility question."); }
                }}
                onDelete={async id => {
                    try { await archiveRoutineQuestionApi(id); }
                    catch (error) { uxNotify(error.message || "Could not delete the core responsibility question."); }
                }}
                validate={f => !f.label?.trim() ? "Question is required." : null} />;
        })}
    </div>;
}

function AdminRoutineQuestions() {
    const { orgUnits, routineQuestions, orgRoutineQuestions } = useData();
    const [level, setLevel] = useState("unit");
    const tx = useAdminText();
    const activeUnits = orgUnits.filter(unit => unit.isActive !== false);

    return (
        <div className="adm-stack">
            <div className="adm-row" style={{ justifyContent: "space-between", gap: 16 }}>
                <p className="adm-desc" style={{ margin: 0, fontSize: 13, maxWidth: 720 }}>
                    Evaluation questions are defined for each organization unit. The Question Bank stores reusable prompts that can be copied into unit-level evaluations.
                </p>
                <AdminSegmented ariaLabel="Core Responsibility Question Levels" value={level} onChange={setLevel} options={[
                    { value: "unit", label: tx(`Organizational Units · ${routineQuestions.length}`, `Unités organisationnelles · ${routineQuestions.length}`) },
                    { value: "org", label: tx(`Question Bank · ${orgRoutineQuestions.length}`, `Banque de questions · ${orgRoutineQuestions.length}`) },
                ]} />
            </div>

            <div style={{ display: level === "unit" ? "block" : "none" }} aria-hidden={level !== "unit"}>
                <ScopedRoutineQuestionsEditor scopeItems={activeUnits} scopeKey="orgUnitId"
                    questions={routineQuestions} scopeLabel="Organizational unit" questionBank={orgRoutineQuestions} />
            </div>
            <div style={{ display: level === "org" ? "block" : "none" }} aria-hidden={level !== "org"}>
                <OrgRoutineQuestionsEditor questions={orgRoutineQuestions} />
            </div>
        </div>
    );
}

// Shared by period planning (target) and performance monitoring (actual).
function PeriodDataEditor({ field, title, subtitle, controlledPeriodId = null, onPeriodChange = null }) {
    const { periods, indicatorDefs, indicatorData, teams, departments, orgUnits,
        periodAudit, closePeriod, reopenPeriod, users, setIndicatorTargetApi, indicatorError,
        createPeriodApi, activatePeriodApi } = useData();
    const activePeriod = periods.find(p => p.active && p.status === "active") || null;
    const [localSelectedPeriodId, setLocalSelectedPeriodId] = useState(activePeriod?.id);
    const selectedPeriodId = controlledPeriodId || localSelectedPeriodId;
    const setSelectedPeriodId = (id) => { if (onPeriodChange) onPeriodChange(id); else setLocalSelectedPeriodId(id); };
    const [newPeriod, setNewPeriod] = useState({ label: "", start: "", end: "" });
    const [localValues, setLocalValues] = useState({});
    const [showNewPeriod, setShowNewPeriod] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const periodYear = p => String(p?.label || "").match(/20\d{2}/)?.[0] || String(p?.start || p?.startDate || "").match(/20\d{2}/)?.[0] || "Other";
    const periodYears = [...new Set(periods.map(periodYear))].sort((a, b) => String(b).localeCompare(String(a), "en"));
    const [selectedYear, setSelectedYear] = useState(() => periodYear(activePeriod) || periodYears[0] || "");

    const period = periods.find(p => p.id === selectedPeriodId) || activePeriod;
    const periodsInYear = periods.filter(p => periodYear(p) === selectedYear);
    useEffect(() => {
        if (!selectedYear && periodYears.length) setSelectedYear(periodYears[0]);
    }, [periods.length]);

    const scopeLabel = (def) => {
        if (def.level === "org_unit") return orgUnits.find(unit => unit.id === def.orgUnitId)?.label || "—";
        if (def.level === "team") return teams.find(t => t.id === def.teamId)?.label || "—";
        if (def.level === "dept") return departments.find(d => d.id === def.deptId)?.label || "—";
        return "Organization-wide";
    };

    const dataFor = (indId) => period && indicatorData.find(d => d.periodId === period.id && d.indicatorId === indId);

    const getValue = (indId) => {
        const key = `${period?.id}_${indId}`;
        if (localValues[key] !== undefined) return localValues[key];
        return dataFor(indId)?.[field] ?? "";
    };
    const setValue = (indId, val) => {
        const key = `${period?.id}_${indId}`;
        setLocalValues(v => ({ ...v, [key]: val }));
    };
    const saveRow = async (indId) => {
        if (!period || period.status === "locked" || field === "actual") return;
        const key = period.id + "_" + indId;
        const val = localValues[key];
        if (val === undefined || val === "") return;
        try {
            await setIndicatorTargetApi(indId, period.id, Number(val), "period");
        } catch (error) {
            console.error("Indicator target save failed:", error);
            uxNotify(error.message || "Could not save the indicator target.");
        }
    };
    const activatePeriod = async (id) => {
        try {
            await activatePeriodApi(id);
            setSelectedPeriodId(id);
        } catch (error) { uxNotify(error.message || "Could not activate the period."); }
    };
    const addPeriod = async () => {
        if (!newPeriod.label.trim() || !newPeriod.start || !newPeriod.end) return;
        try {
            const created = await createPeriodApi(newPeriod);
            setSelectedPeriodId(created.id);
            setSelectedYear(periodYear(created));
            setNewPeriod({ label: "", start: "", end: "" });
            setShowNewPeriod(false);
        } catch (error) { uxNotify(error.message || "Could not create the period."); }
    };

    const smallInput = {
        padding: "7px 10px", border: `1.5px solid ${C.borderMd}`,
        borderRadius: 7, fontSize: 12, direction: "ltr", fontFamily: "inherit"
    };
    const statusLabel = { draft: "Draft", preparation: "Draft", active: "Active", finalizing: "Finalizing", locked: "Finalized and locked" };
    const isLocked = period?.status === "locked";
    const isPlanning = field === "target";
    const periodRows = period ? indicatorDefs.map(def => ({ def, data: dataFor(def.id) })) : [];
    const targetReady = periodRows.filter(row => row.data?.target !== null && row.data?.target !== undefined && row.data?.target !== "").length;
    const recorded = periodRows.filter(row => row.data?.actual !== null && row.data?.actual !== undefined && row.data?.actual !== "").length;
    const pendingApproval = periodRows.filter(row => ["submitted", "pending", "pending_approval"].includes(row.data?.status)).length;
    const missing = Math.max(0, indicatorDefs.length - recorded);
    const completion = indicatorDefs.length ? Math.round((recorded / indicatorDefs.length) * 100) : 0;
    const planningReady = indicatorDefs.length > 0 && targetReady === indicatorDefs.length;

    return (
        <div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>{subtitle}</div>
            {indicatorError && <div style={{ marginBottom: 10, color: C.red, fontSize: 11 }}>{indicatorError}</div>}

            <Card style={{ marginBottom: 16 }}>
                <div style={{ padding: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                        <div>
                            <div style={{ fontSize: 10, color: C.hint, marginBottom: 3 }}>Selected Period</div>
                            <div style={{ fontSize: 15, fontWeight: 700 }}>{period?.label || "No periods have been created yet."}</div>
                            {period && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>{displayDate(period.start || period.startDate) || "—"} to {displayDate(period.end || period.endDate) || "—"} · {statusLabel[period.status] || "Draft"}</div>}
                        </div>
                        {isPlanning && <button className="cvi-button-secondary" onClick={() => setShowNewPeriod(v => !v)}>+ New period</button>}
                    </div>
                    {!!periods.length && <div style={{ display: "grid", gridTemplateColumns: "minmax(120px,.45fr) minmax(220px,1fr)", gap: 8, marginTop: 12, maxWidth: 520 }}>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>Year</span><select value={selectedYear} onChange={e => { const year = e.target.value; setSelectedYear(year); const first = periods.find(p => periodYear(p) === year); if (first) setSelectedPeriodId(first.id); }} style={{ width: "100%", padding: 9, border: `1px solid ${C.borderMd}`, borderRadius: 8, background: "#fff", fontFamily: "inherit" }}>{periodYears.map(year => <option key={year} value={year}>{year}</option>)}</select></label>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>Quarter / Period</span><select value={period?.id || ""} onChange={e => setSelectedPeriodId(e.target.value)} style={{ width: "100%", padding: 9, border: `1px solid ${C.borderMd}`, borderRadius: 8, background: "#fff", fontFamily: "inherit" }}>{periodsInYear.map(p => <option key={p.id} value={p.id}>{p.label}{p.active ? " — Current" : ""}</option>)}</select></label>
                    </div>}
                    {isPlanning && period && !period.active && !isLocked && <div style={{ marginTop: 12 }}><button className="cvi-button-primary" onClick={() => activatePeriod(period.id)}>Activate period</button></div>}
                    {isPlanning && showNewPeriod && <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 12, paddingTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>Period name</span><input value={newPeriod.label} onChange={e => setNewPeriod(x => ({ ...x, label: e.target.value }))} placeholder="Q4 2026" style={smallInput} /></label>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>Start</span><input type="date" value={newPeriod.start} onChange={e => setNewPeriod(x => ({ ...x, start: e.target.value }))} style={smallInput} /></label>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>Ends</span><input type="date" value={newPeriod.end} onChange={e => setNewPeriod(x => ({ ...x, end: e.target.value }))} style={smallInput} /></label>
                        <button className="cvi-button-primary" onClick={addPeriod}>Create period</button>
                    </div>}
                </div>
            </Card>

            {period && <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
                <Card><div style={{ padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{isPlanning ? "Period readiness" : "Period progress"}</div>
                            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4, lineHeight: 1.8 }}>
                                {isPlanning
                                    ? "Use this step to confirm the period setup, indicator targets, and activation readiness."
                                    : "Use this step to monitor submissions, approvals, and remaining actions."}
                            </div>
                        </div>
                        <div style={{ minWidth: 110, textAlign: "center", padding: "8px 12px", borderRadius: 10, background: C.bg }}>
                            <div style={{ fontSize: 9.5, color: C.hint }}>{isPlanning ? "Ready" : "Submitted"}</div>
                            <div style={{ fontSize: 22, fontWeight: 750, marginTop: 2 }}>{isPlanning ? (indicatorDefs.length ? Math.round((targetReady / indicatorDefs.length) * 100) : 0) : completion}%</div>
                        </div>
                    </div>
                    <div style={{ height: 7, borderRadius: 999, background: C.bg, overflow: "hidden", marginTop: 14 }}>
                        <div style={{ width: `${isPlanning ? (indicatorDefs.length ? Math.round((targetReady / indicatorDefs.length) * 100) : 0) : completion}%`, height: "100%", background: C.navy, borderRadius: 999 }} />
                    </div>
                </div></Card>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10 }}>
                    {(isPlanning
                        ? [["Period indicators", indicatorDefs.length], ["Targets Ready", `${targetReady} of ${indicatorDefs.length}`], ["Period status", statusLabel[period.status] || "Draft"]]
                        : [["Period indicators", indicatorDefs.length], ["Submitted", recorded], ["Pending Approval", pendingApproval], ["Not Submitted", missing]]
                    ).map(([label, value]) => <Card key={label}><div style={{ padding: "12px 14px" }}><div style={{ fontSize: 9.5, color: C.hint }}>{label}</div><div style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>{value}</div></div></Card>)}
                </div>

                {isPlanning ? (
                    <Card><div style={{ padding: 15, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                            <div style={{ fontSize: 12.5, fontWeight: 650 }}>Collective Indicator Targets</div>
                            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>
                                {planningReady ? "All indicator targets are ready for this period." : `${Math.max(0, indicatorDefs.length - targetReady)} targets still need review or setup.`}
                            </div>
                        </div>
                        <button className="cvi-button-secondary" onClick={() => setShowDetails(v => !v)}>{showDetails ? "Hide details" : "Review targets"}</button>
                    </div></Card>
                ) : (
                    <>
                        {(missing > 0 || pendingApproval > 0) && <Card><div style={{ padding: 15 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 650, marginBottom: 7 }}>Needs Attention</div>
                            <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.9 }}>
                                {missing > 0 && <div>• {missing} indicators have not been submitted yet.</div>}
                                {pendingApproval > 0 && <div>• {pendingApproval} indicators are waiting for approval.</div>}
                            </div>
                        </div></Card>}
                        <Card><div style={{ padding: 15, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                            <div>
                                <div style={{ fontSize: 12.5, fontWeight: 650 }}>Submission & Approval Details</div>
                                <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>Open indicator-level detail only when you need to investigate or follow up.</div>
                            </div>
                            <button className="cvi-button-secondary" onClick={() => setShowDetails(v => !v)}>{showDetails ? "Hide details" : "Review details"}</button>
                        </div></Card>
                    </>
                )}
            </div>}

            {showDetails && (indicatorDefs.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                    No indicators are configured yet. Add the required indicators under Collective Contribution first.
                </div>
            ) : !period ? (
                <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                    No periods have been created yet.
                </div>
            ) : (
                <Card>
                    <div style={{ padding: "11px 14px", borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.muted }}>
                        {isPlanning ? "Indicator Target Review" : "Indicator Submission & Approval Status"}
                    </div>
                    {indicatorDefs.map((def, i) => (
                        <div key={def.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: i < indicatorDefs.length - 1 ? `1px solid ${C.bg}` : "none", flexWrap: "wrap" }}>
                            <div style={{ flex: 1, minWidth: 180 }}>
                                <div style={{ fontSize: 12, fontWeight: 600 }}>{def.label}</div>
                                <div style={{ fontSize: 10, color: C.hint, marginTop: 2 }}>{scopeLabel(def)} · {def.unit || "—"}</div>
                            </div>
                            <div style={{ fontSize: 10.5, color: C.muted, minWidth: 85, textAlign: "center" }}>{field === "target" ? `Actual: ${dataFor(def.id)?.actual ?? "—"}` : `Target: ${dataFor(def.id)?.target ?? "—"}`}</div>
                            <input type="number" value={getValue(def.id)}
                                onChange={e => field !== "actual" && setValue(def.id, e.target.value)}
                                onBlur={() => saveRow(def.id)}
                                disabled={isLocked || field === "actual"}
                                placeholder={field === "target" ? "Target" : "Actual"}
                                style={{ width: 90, padding: "7px 10px", border: `1.5px solid ${C.borderMd}`, borderRadius: 7, fontSize: 12, textAlign: "center" }} />
                            {field === "actual" && <div style={{ fontSize: 9.5, color: C.hint, minWidth: 130 }}>Submitter: {users.find(u => u.id === def.registrarId)?.name || "Not set"}<br/>Status: {indicatorStatusLabel(dataFor(def.id)?.status)}</div>}
                        </div>
                    ))}
                </Card>
            ))}
            {!isPlanning && period?.status === "active" && <Card style={{ marginTop: 12 }}><div style={{ padding: 14, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div><div style={{ fontSize: 12.5, fontWeight: 600 }}>Finalize Period</div><div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>Once submissions and approvals are complete, finalize the period to lock the results.</div></div>
                <button className="cvi-button-primary" onClick={async () => {
                    if (!await uxConfirm(`Finalizing “${period.label}” will lock the results. Continue?`)) return;
                    try { await closePeriod(period.id, "Administrator"); uxNotify("Period finalized and locked successfully."); }
                    catch (error) {
                        const blockers = error?.blockers;
                        if (blockers) {
                            const parts = [];
                            if (blockers.incompleteIndicators?.length) parts.push(`Incomplete indicators: ${blockers.incompleteIndicators.length}`);
                            if (blockers.pendingActivities) parts.push(`Pending activities: ${blockers.pendingActivities}`);
                            if (blockers.pendingPeerAssignments) parts.push(`Pending peer reviews: ${blockers.pendingPeerAssignments}`);
                            uxNotify(`The period is not ready to be finalized. ${parts.join(" | ")}`);
                        } else uxNotify(error?.message || "Could not finalize the period.");
                    }
                }}>Finalize & Lock</button>
            </div></Card>}
            {!isPlanning && isLocked && <Card style={{ marginTop: 12 }}><div style={{ padding: 14, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div><div style={{ fontSize: 12.5, fontWeight: 600 }}>Period Finalized</div><div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>Results are read-only. Reopen the period only when a correction is required.</div></div>
                <button className="cvi-button-secondary" onClick={async () => { const reason = await uxPrompt("Enter a reason for reopening the period:", ""); if (!reason) return; try { await reopenPeriod(period.id, "Administrator", reason); uxNotify("Period reopened."); } catch (error) { uxNotify(error?.message || "Could not reopen the period."); } }}>Reopen for Correction</button>
            </div></Card>}

            {period && periodAudit.some(e => e.periodId === period.id) && (
                <Card style={{ marginTop: 12 }}>
                    <div style={{ padding: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Period History</div>
                        {periodAudit.filter(e => e.periodId === period.id).slice().reverse().map(e => (
                            <div key={e.id} style={{ padding: "8px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 11 }}>
                                <div style={{ fontWeight: 500 }}>{e.type === "locked" ? "Finalize & Lock Period" : "Reopen Period"} · {e.by}</div>
                                <div style={{ color: C.muted, marginTop: 2 }}>{displayDate(e.date)} — {e.note}</div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}

function AdminPeerFeedback() {
    const importanceOptions = useImportanceScaleOptions();
    const { users, orgUnits, peerQuestions, peerConfigs, setPeerConfigs,
        createPeerQuestionAdminApi, updatePeerQuestionAdminApi, archivePeerQuestionAdminApi,
        savePeerConfigAdminApi, createPeerAssignmentsAdminApi, activePeriod } = useData();
    const tx = useAdminText();
    const people = users.filter(u => u.role !== "admin");
    const [tab, setTab] = useState("questions");
    const [selectedUserId, setSelectedUserId] = useState(people[0]?.id || "");
    const [bulkOrgUnit, setBulkOrgUnit] = useState("all");
    const [bulkRole, setBulkRole] = useState("all");
    const [reviewerCount, setReviewerCount] = useState(3);
    const [assignmentPreview, setAssignmentPreview] = useState([]);
    const [reviewerQuery, setReviewerQuery] = useState("");
    useEffect(() => {
        setPeerConfigs(prev => prev.map(c => {
            const target = users.find(u => u.id === c.userId);
            return target?.managerId && c.reviewerIds?.includes(target.managerId)
                ? { ...c, reviewerIds: c.reviewerIds.filter(id => id !== target.managerId) } : c;
        }));
    }, [users, setPeerConfigs]);
    const selectedUser = users.find(u => u.id === selectedUserId);
    const config = peerConfigs.find(c => c.userId === selectedUserId) || {
        userId: selectedUserId, enabled: false, reviewerIds: [], visibility: "anonymous", minResponses: 3
    };
    const updateConfig = patch => setPeerConfigs(prev => {
        const exists = prev.some(c => c.userId === selectedUserId);
        return exists ? prev.map(c => c.userId === selectedUserId ? { ...c, ...patch } : c) : [...prev, { ...config, ...patch }];
    });
    const toggleReviewer = id => updateConfig({
        reviewerIds: config.reviewerIds.includes(id)
            ? config.reviewerIds.filter(x => x !== id) : [...config.reviewerIds, id]
    });
    const parentUnitOf = p => orgUnits.find(unit => unit.id === p.orgUnitId)?.parentId || null;
    const generateAssignments = () => {
        const targetUnitIds = bulkOrgUnit === "all" ? null : orgUnitSubtreeIds(orgUnits, bulkOrgUnit);
        const targets = people.filter(p => (!targetUnitIds || targetUnitIds.includes(p.orgUnitId)) && (bulkRole === "all" || p.role === bulkRole));
        const load = {};
        peerConfigs.forEach(c => (c.reviewerIds || []).forEach(id => { load[id] = (load[id] || 0) + 1; }));
        const rows = targets.map(target => {
            const targetParent = parentUnitOf(target);
            const candidates = people.filter(p => p.id !== target.id && p.id !== target.managerId && p.role === target.role)
                .map(p => ({ person: p, tier: p.orgUnitId && p.orgUnitId === target.orgUnitId ? 0 : parentUnitOf(p) === targetParent ? 1 : 2 }))
                .sort((a, b) => a.tier - b.tier || (load[a.person.id] || 0) - (load[b.person.id] || 0) || a.person.name.localeCompare(b.person.name, "fa"));
            const selected = candidates.slice(0, Math.max(1, reviewerCount)).map(c => c.person.id);
            selected.forEach(id => { load[id] = (load[id] || 0) + 1; });
            return { userId: target.id, reviewerIds: selected, complete: selected.length >= reviewerCount };
        });
        setAssignmentPreview(rows);
    };
    const fields = [{ key: "label", label: "Question", placeholder: "e.g., Collaborates effectively across the team", required: true, wide: true },
    { key: "weight", label: "Importance Level", type: "select", placeholder: "Select importance level", options: importanceOptions, wide: true },
    { key: "computationalWeight", label: "Normalized Weight", computed: true, suffix: "%" }];
    const applyAssignmentsServer = async () => {
        if (!assignmentPreview.length) return;
        try {
            for (const row of assignmentPreview) {
                const old = peerConfigs.find(c => c.userId === row.userId);
                const cfg = {
                    userId: row.userId,
                    enabled: true,
                    reviewerIds: row.reviewerIds,
                    visibility: old?.visibility === "identified" ? "identified" : "anonymous",
                    minResponses: reviewerCount,
                };
                await savePeerConfigAdminApi(row.userId, cfg);
                if (activePeriod?.id && row.reviewerIds.length) {
                    await createPeerAssignmentsAdminApi(row.userId, activePeriod.id, row.reviewerIds);
                }
            }
            setAssignmentPreview([]);
            uxNotify(activePeriod?.id ? "Active-period settings and assignments saved." : "Settings saved. Activate a period before generating feedback provider assignments.");
        } catch (error) { uxNotify(error.message || "Could not apply feedback provider assignments."); }
    };
    const enabledCount = peerConfigs.filter(c => c.enabled).length;
    const candidates = selectedUser ? people.filter(p => p.id !== selectedUserId && p.id !== selectedUser.managerId) : [];
    const normalizedReviewerQuery = reviewerQuery.trim().toLowerCase();
    const visibleCandidates = candidates.filter(p => !normalizedReviewerQuery || config.reviewerIds.includes(p.id) || String(p.name || "").toLowerCase().includes(normalizedReviewerQuery));

    return <div className="adm-stack">
        <div className="adm-row" style={{ justifyContent: "space-between", gap: 16 }}>
            <p className="adm-desc" style={{ margin: 0, fontSize: 13, maxWidth: 720 }}>Manage shared questions, feedback provider assignments and feedback privacy.</p>
            <AdminSegmented ariaLabel="Peer Feedback Settings" value={tab} onChange={setTab} options={[
                { value: "questions", label: tx(`Question Bank · ${peerQuestions.length}`, `Banque de questions · ${peerQuestions.length}`) },
                { value: "assignments", label: tx(`Feedback providers · ${enabledCount}`, `Répondants · ${enabledCount}`) },
            ]} />
        </div>
        {tab === "questions" && <EditableList title="Peer feedback questions" addLabel="Add question" dialogTitle="question"
            description="Feedback providers answer each question on a fixed 1–5 scale; the average answer is converted to a score out of 100. This scale is separate from the Core Responsibility Rating Scale."
            items={(() => {
                const totalWeight = peerQuestions.reduce((sum, q) => sum + (Number(q.weight) || 0), 0);
                return peerQuestions.map(q => ({
                    ...q,
                    computationalWeight: totalWeight > 0 ? ((Number(q.weight) || 0) / totalWeight * 100).toFixed(1) : "0.0"
                }));
            })()} fields={fields}
            onAdd={async f => { try { await createPeerQuestionAdminApi({ text: f.label, weight: Number(f.weight) || 0 }); } catch (error) { uxNotify(error.message || "Could not add the feedback question."); } }}
            onUpdate={async (id, f) => { try { await updatePeerQuestionAdminApi(id, { text: f.label, weight: Number(f.weight) || 0 }); } catch (error) { uxNotify(error.message || "Could not update the feedback question."); } }}
            onDelete={async id => { try { await archivePeerQuestionAdminApi(id); } catch (error) { uxNotify(error.message || "Could not delete the feedback question."); } }}
            validate={f => !f.label?.trim() ? "Question is required." : null} />}
        {tab === "assignments" && <div className="adm-stack">
            <AdminPanel title="Automatic feedback provider assignment"
                description="CVI prioritizes peers in the same unit, then nearby units in the organization structure, while balancing review workload. Direct managers are excluded from peer assignments."
                footer={<>
                    {!activePeriod && <span className="adm-foot-note">Settings saved. Activate a period before generating feedback provider assignments.</span>}
                    <button type="button" className="adm-btn adm-btn--primary" onClick={generateAssignments}>Generate Suggestions</button>
                </>}>
                <div className="adm-form-grid is-3">
                    <AdminField label="Organization unit"><select value={bulkOrgUnit} onChange={e => { setBulkOrgUnit(e.target.value); setAssignmentPreview([]); }}>
                        <option value="all">All organization units</option>{orgUnits.filter(unit => unit.isActive !== false).map(unit => <option key={unit.id} value={unit.id}>{unit.label}</option>)}</select></AdminField>
                    <AdminField label="Role"><select value={bulkRole} onChange={e => { setBulkRole(e.target.value); setAssignmentPreview([]); }}>
                        <option value="all">All roles</option><option value="specialist">Employee</option><option value="manager">Manager</option><option value="deputy">Director</option><option value="ceo">Executive</option></select></AdminField>
                    <AdminField label="Number of feedback providers" hint="Between 1 and 10."><input type="number" min="1" max="10" value={reviewerCount} onChange={e => { setReviewerCount(Math.max(1, Math.min(10, Number(e.target.value) || 1))); setAssignmentPreview([]); }} /></AdminField>
                </div>
            </AdminPanel>
            {assignmentPreview.length > 0 && <AdminPanel flush title="Assignment Preview"
                description={tx(`${assignmentPreview.length} people · ${assignmentPreview.reduce((s, r) => s + r.reviewerIds.length, 0)} feedback assignments`, `${assignmentPreview.length} personnes · ${assignmentPreview.reduce((s, r) => s + r.reviewerIds.length, 0)} affectations`)}
                actions={<><button type="button" className="adm-btn adm-btn--sm" onClick={() => setAssignmentPreview([])}>Cancel</button><button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={applyAssignmentsServer}>Apply Assignments</button></>}>
                <div className="adm-table-wrap"><table className="adm-table is-responsive">
                    <thead><tr><th scope="col">Person receiving feedback</th><th scope="col">Suggested feedback providers</th><th scope="col">Status</th></tr></thead>
                    <tbody>{assignmentPreview.map(row => {
                        const target = users.find(u => u.id === row.userId);
                        return <tr key={row.userId}>
                            <td className="is-primary"><span className="adm-cell-primary">{target?.name}</span></td>
                            <td data-label="Suggested feedback providers" className="adm-cell-muted">{row.reviewerIds.map(id => users.find(u => u.id === id)?.name).filter(Boolean).join(", ") || "No feedback provider suggested"}</td>
                            <td data-label="Status"><AdminBadge tone={row.complete ? "success" : "warning"}>{row.complete ? "Valid" : "Needs manual assignment"}</AdminBadge></td>
                        </tr>;
                    })}</tbody>
                </table></div>
            </AdminPanel>}
            <AdminPanel title="Individual Feedback Settings" description="Review and adjust peer feedback for one person at a time."
                actions={<AdminField label="Person receiving feedback" className="adm-person-picker"><select value={selectedUserId} onChange={e => { setSelectedUserId(e.target.value); setReviewerQuery(""); }} style={{ minWidth: 240 }}>
                    {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></AdminField>}
                footer={selectedUser ? <>
                    {config.reviewerIds.length < (config.minResponses || 1) && <span className="adm-foot-note" style={{ color: "var(--adm-warning)" }}>The number of selected feedback providers is below the minimum number of responses.</span>}
                    <button type="button" className="adm-btn adm-btn--primary" onClick={async () => {
                        try { await savePeerConfigAdminApi(selectedUserId, config); uxNotify("Feedback settings saved."); }
                        catch (error) { uxNotify(error.message || "Could not save feedback settings."); }
                    }}>Save Feedback Settings</button>
                </> : null}>
                {!selectedUser ? <AdminEmpty compact icon="people" title="No people yet" /> : <div className="adm-stack">
                    <div className="adm-row" style={{ justifyContent: "space-between", padding: "12px 14px", border: "1px solid var(--adm-border)", borderRadius: 10, background: "var(--adm-surface-2)" }}>
                        <span className="adm-identity"><span className="adm-avatar" aria-hidden="true">{initialsOf(selectedUser.name)}</span>
                            <span><span className="adm-cell-primary" style={{ display: "block", fontWeight: 600, color: "var(--adm-ink)" }}>{selectedUser.name}</span><span className="adm-hint">Include peer feedback in Individual Contribution</span></span></span>
                        <AdminSwitch checked={config.enabled} onChange={value => updateConfig({ enabled: value })} ariaLabel="Include peer feedback in Individual Contribution" />
                    </div>
                    <div className="adm-form-grid">
                        <AdminField label="Feedback visibility"><select value={config.visibility} onChange={e => updateConfig({ visibility: e.target.value })}>
                            <option value="anonymous">Confidential — anonymous</option><option value="identified">Identified — feedback provider names visible</option>
                        </select></AdminField>
                        <AdminField label="Minimum responses required"><input type="number" min="1" value={config.minResponses} onChange={e => updateConfig({ minResponses: Math.max(1, Number(e.target.value) || 1) })} /></AdminField>
                    </div>
                    <div>
                        <div className="adm-row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
                            <h3 className="adm-form-section-title" style={{ margin: 0 }}>{tx(`Selected feedback providers (${config.reviewerIds.length})`, `Répondants sélectionnés (${config.reviewerIds.length})`)}</h3>
                            {candidates.length > 8 && <input className="adm-control" type="search" style={{ maxWidth: 240 }} value={reviewerQuery} onChange={e => setReviewerQuery(e.target.value)} placeholder="Search people…" aria-label="Search people…" />}
                        </div>
                        <div className="adm-choice-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))" }}>
                            {visibleCandidates.map(p => <label key={p.id} className={`adm-choice${config.reviewerIds.includes(p.id) ? " is-selected" : ""}`} style={{ alignItems: "center", padding: "9px 11px" }}>
                                <input type="checkbox" checked={config.reviewerIds.includes(p.id)} onChange={() => toggleReviewer(p.id)} />
                                <span className="adm-choice-copy" style={{ flex: 1 }}><strong style={{ fontWeight: 500 }}>{p.name}</strong></span>
                                {p.role === selectedUser.role && p.orgUnitId && p.orgUnitId === selectedUser.orgUnitId && <AdminBadge tone="accent" plain>Same unit</AdminBadge>}
                            </label>)}
                        </div>
                        <p className="adm-hint" style={{ marginTop: 10 }}>{tx(`Direct manager “${users.find(u => u.id === selectedUser.managerId)?.name || "Not set"}” is excluded from peer feedback.`, `Le gestionnaire immédiat « ${users.find(u => u.id === selectedUser.managerId)?.name || "Non défini"} » est exclu de la rétroaction des collègues.`)}</p>
                    </div>
                </div>}
            </AdminPanel>
        </div>}
    </div>;
}

function AdminOrganization({ onViewPeople = null, onViewIndicators = null }) {
    const { orgSettings, updateOrganizationSettingsApi, reloadBaseData } = useData();
    const [tab, setTab] = useState("identity");
    const [brandDraft, setBrandDraft] = useState({ ...orgSettings });
    const [brandSaving, setBrandSaving] = useState(false);
    useEffect(() => setBrandDraft({ ...orgSettings }), [orgSettings.name, orgSettings.logo, orgSettings.defaultLanguage, orgSettings.accentColor, orgSettings.timezone, orgSettings.dateFormat, orgSettings.calendar]);
    const saveBrandSettings = async () => {
        setBrandSaving(true);
        try {
            await updateOrganizationSettingsApi({
                organizationName: brandDraft.name || "CVI Organization", logoUrl: brandDraft.logo || null,
                defaultLanguage: brandDraft.defaultLanguage || "en", accentColor: brandDraft.accentColor || "#00C8E6",
                timezone: brandDraft.timezone || "UTC", dateFormat: brandDraft.dateFormat || "YYYY-MM-DD",
                calendar: brandDraft.calendar || "gregorian",
            });
            uxNotify("Organization settings saved.", "success");
        } catch (error) { uxNotify(error.message || "Could not save organization settings.", "error"); }
        finally { setBrandSaving(false); }
    };
    const dirty = ["name", "logo", "defaultLanguage", "accentColor", "timezone", "dateFormat"].some(key => (brandDraft[key] || "") !== (orgSettings[key] || ""));
    return <AdminPage>
        <AdminPageHeader eyebrow="Management" title="Organization" description="Manage organization details, organizational structure, and positions in one place." />
        <AdminTabs
            items={[
                { id: "identity", label: "Organization Profile", icon: "company" },
                { id: "structure", label: "Organizational Structure", icon: "org" },
                { id: "positions", label: "Job Positions", icon: "people" },
            ]}
            value={tab}
            onChange={setTab}
            ariaLabel="Organization"
        />
        <div style={{ display: tab === "identity" ? "block" : "none" }} aria-hidden={tab !== "identity"}>
            <AdminPanel title="Organization profile" description="Keep the company name, logo, language, and regional display settings consistent across CVI."
                footer={<>
                    {dirty && <span className="adm-foot-note">Unsaved changes</span>}
                    <button type="button" className="adm-btn" disabled={!dirty || brandSaving} onClick={() => setBrandDraft({ ...orgSettings })}>Discard</button>
                    <button type="button" className="adm-btn adm-btn--primary" disabled={brandSaving || !brandDraft.name?.trim()} onClick={saveBrandSettings}>{brandSaving ? "Saving..." : "Save Organization Settings"}</button>
                </>}>
                <div className="adm-org-profile">
                    <div className="adm-org-logo">
                        <div className="cvi-brand-logo-preview">{brandDraft.logo ? <img src={brandDraft.logo} alt="Organization Logo" /> : <UiIcon name="building" size={28} />}</div>
                        <label className="adm-btn adm-btn--sm" style={{ cursor: "pointer" }}>{brandDraft.logo ? "Change Logo" : "Upload Logo"}<input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={e => { const file=e.target.files?.[0]; if(!file) return; if(file.size > 750*1024){ uxNotify("Logo file must be smaller than 750 KB.", "error"); return; } const reader=new FileReader(); reader.onload=ev=>setBrandDraft(d=>({...d,logo:ev.target.result})); reader.readAsDataURL(file); e.target.value=""; }} /></label>
                        {brandDraft.logo && <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setBrandDraft(d => ({ ...d, logo: null }))}>Remove logo</button>}
                        <span className="adm-hint" style={{ textAlign: "center" }}>PNG, JPG, WebP or SVG · max 750 KB</span>
                    </div>
                    <div className="adm-form-grid">
                        <AdminField label="Organization Name" required wide><input value={brandDraft.name || ""} onChange={e => setBrandDraft(d=>({...d,name:e.target.value}))} placeholder="Organization Name" /></AdminField>
                        <AdminField label="Default language"><select value={brandDraft.defaultLanguage || "en"} onChange={e=>setBrandDraft(d=>({...d,defaultLanguage:e.target.value}))}>{Object.entries(SUPPORTED_LANGUAGES).map(([code,info])=><option key={code} value={code}>{info.label}</option>)}</select></AdminField>
                        <AdminField label="Time zone"><select value={brandDraft.timezone || "UTC"} onChange={e=>setBrandDraft(d=>({...d,timezone:e.target.value}))}><option value="UTC">UTC</option><option value="America/Toronto">Canada — Eastern</option><option value="America/Vancouver">Canada — Pacific</option><option value="America/Edmonton">Canada — Mountain</option><option value="America/Halifax">Canada — Atlantic</option></select></AdminField>
                        <AdminField label="Date format"><select value={brandDraft.dateFormat || "YYYY-MM-DD"} onChange={e=>setBrandDraft(d=>({...d,dateFormat:e.target.value}))}><option value="YYYY-MM-DD">YYYY-MM-DD</option><option value="DD/MM/YYYY">DD/MM/YYYY</option><option value="MM/DD/YYYY">MM/DD/YYYY</option></select></AdminField>
                        <AdminField label="Accent colour" hint="Used for highlights across CVI. The navy brand colour is unchanged."><span className="adm-row" style={{ flexWrap: "nowrap" }}><input type="color" value={brandDraft.accentColor || "#00C8E6"} onChange={e=>setBrandDraft(d=>({...d,accentColor:e.target.value.toUpperCase()}))} style={{ width: 44, height: 36, padding: 2, border: "1px solid var(--adm-border-strong)", borderRadius: 8, background: "#fff", cursor: "pointer" }} /><code className="adm-formula">{brandDraft.accentColor || "#00C8E6"}</code></span></AdminField>
                    </div>
                </div>
            </AdminPanel>
        </div>
        <div style={{ display: tab === "structure" || tab === "positions" ? "block" : "none" }} aria-hidden={tab === "identity"} className="adm-embedded">
            <FlexibleOrgPrototype
                initialTab={tab === "positions" ? "positions" : "org"}
                visibleTabs={[tab === "positions" ? "positions" : "org"]}
                embedded
                onViewPeople={onViewPeople}
                onViewIndicators={onViewIndicators}
                onDataChanged={() => reloadBaseData({ silent: true })}
            />
        </div>
    </AdminPage>;
}

function AdminMobileMore({ setNav }) {
    const items = [
        ["org", "Organization", "org", "Management"], ["individual-performance", "Individual Contribution", "performance", "CVI Model"],
        ["collective-contribution", "Collective Contribution", "target", "CVI Model"], ["settings", "Advanced Settings", "settings", "CVI Model"],
        ["experience", "Employee Experience", "flag", "Period & Operations"],
        ["help", "Help", "help", "Account"], ["profile", "Profile", "profile", "Account"],
    ];
    return <AdminPage className="cvi-mobile-more-page">
        <AdminPageHeader title="More" description="Administration and settings" />
        <AdminPanel flush>
            <ul className="adm-list">{items.map(([id, label, icon, group]) => <li key={id}><button type="button" className="adm-list-item" onClick={() => setNav(id)}>
                <span className="adm-list-icon" aria-hidden="true"><UiIcon name={icon} size={17} /></span>
                <span><strong>{label}</strong><small>{group}</small></span>
                <span className="adm-list-go" aria-hidden="true"><UiIcon name="chevronDown" size={14} /></span>
            </button></li>)}</ul>
        </AdminPanel>
    </AdminPage>;
}


function AdminCycleManagement() {
    const { activePeriod, periods, createPeriodApi, activatePeriodApi, closePeriod, reopenPeriod } = useData();
    const { language } = useI18n();
    const preferredPeriodId = activePeriod?.id || periods.find(p => ["draft", "preparation"].includes(String(p.status).toLowerCase()))?.id || periods[0]?.id || "";
    const [selectedPeriodId, setSelectedPeriodId] = useState(preferredPeriodId);
    const [cycleStatus, setCycleStatus] = useState(null);
    const [cycleStatusLoading, setCycleStatusLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!periods.some(p => p.id === selectedPeriodId)) setSelectedPeriodId(preferredPeriodId);
    }, [periods, preferredPeriodId, selectedPeriodId]);

    const selectedPeriod = periods.find(p => p.id === selectedPeriodId) || activePeriod || periods[0] || null;

    useEffect(() => {
        let cancelled = false;
        if (!selectedPeriod?.id) { setCycleStatus(null); return undefined; }
        const token = sessionStorage.getItem("cvi_token");
        if (!token) return undefined;
        setCycleStatusLoading(true);
        fetch(`${API_BASE_URL}/api/periods/${encodeURIComponent(selectedPeriod.id)}/cycle-status`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(async response => {
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.message || "Could not load period status.");
                return payload;
            })
            .then(payload => { if (!cancelled) setCycleStatus(payload); })
            .catch(error => { if (!cancelled) console.error("Cycle status load failed:", error); })
            .finally(() => { if (!cancelled) setCycleStatusLoading(false); });
        return () => { cancelled = true; };
    }, [selectedPeriod?.id, refreshKey]);

    const createCycle = async form => {
        try {
            const created = await createPeriodApi(form);
            if (created?.id) setSelectedPeriodId(created.id);
            setRefreshKey(k => k + 1);
            uxNotify("Period created.", "success");
            return created;
        } catch (error) {
            uxNotify(error.message || "The period could not be created.");
            return null;
        }
    };

    const activateCycle = async id => {
        try {
            await activatePeriodApi(id);
            setSelectedPeriodId(id);
            setRefreshKey(k => k + 1);
            uxNotify("Period activated.", "success");
        } catch (error) { uxNotify(error.message || "The period could not be activated."); }
    };

    const finalizeCycle = async id => {
        if (!await uxConfirm("Finalizing this period locks its results. People will no longer be able to change their entries. Continue?")) return;
        try {
            await closePeriod(id, "Administrator");
            setRefreshKey(k => k + 1);
            uxNotify("Period finalized and locked.", "success");
        } catch (error) {
            uxNotify(error.message || "The period could not be finalized.");
        }
    };

    const reopenCycle = async id => {
        const reason = await uxPrompt("Enter a reason for reopening this period:", "");
        if (!reason) return;
        try {
            await reopenPeriod(id, "Administrator", reason);
            setRefreshKey(k => k + 1);
            uxNotify("Period reopened.", "success");
        } catch (error) { uxNotify(error.message || "The period could not be reopened."); }
    };

    return <CycleManagement
        locale={language === "fr" ? "fr" : "en"}
        periods={periods}
        selectedPeriodId={selectedPeriod?.id || ""}
        onPeriodChange={setSelectedPeriodId}
        cycleStatus={cycleStatus}
        loading={cycleStatusLoading}
        onCreateCycle={createCycle}
        onActivate={activateCycle}
        onFinalize={finalizeCycle}
        onReopen={reopenCycle}
    />;
}

function AdminApp({ user, onLogout }) {
    const isMobile = useIsMobile();
    const { activePeriod, periods } = useData();
    const { language: experienceLanguage } = useI18n();
    const [nav, setNav] = useState("overview");
    const [settingsTab, setSettingsTab] = useState("advanced-model");
    const contentRef = useRef(null);
    // Keep every admin destination mounted from the start. Admin sections with
    // mount-time data loading can finish in the background, so the first click
    // as well as later revisits never creates a transient loading/empty page.

    // Each destination starts at the top instead of inheriting the previous page's scroll position.
    useEffect(() => { if (contentRef.current) contentRef.current.scrollTop = 0; }, [nav]);

    const sidebarItems = [
        { id: "overview", label: "Overview", icon: "overview", group: "Management" },
        { id: "org", label: "Organization", icon: "org", group: "Management" },
        { id: "people", label: "People & Access", icon: "people", group: "Management" },
        { id: "individual-performance", label: "Individual Contribution", icon: "performance", group: "CVI Model" },
        { id: "collective-contribution", label: "Collective Contribution", icon: "target", group: "CVI Model" },
        { id: "settings", label: "Advanced Settings", icon: "settings", group: "CVI Model" },
        { id: "cycle-management", label: "Period Management", icon: "planning", group: "Period & Operations" },
        { id: "experience", label: "Employee Experience", icon: "flag", group: "Period & Operations" },
        { id: "help", label: "Help", icon: "help", group: "Account" },
        { id: "profile", label: "Profile", icon: "profile", group: "Account" },
    ];

    const mobileItems = [
        { id: "overview", label: "Home", icon: "overview" },
        { id: "people", label: "People", icon: "people" },
        { id: "cycle-management", label: "Periods", icon: "planning" },
        { id: "more", label: "More", icon: "more" },
    ];

    const openKpiTemplates = () => { setSettingsTab("kpi-formulas"); setNav("settings"); };
    const [peopleFocus, setPeopleFocus] = useState(null);
    const [indicatorFocus, setIndicatorFocus] = useState(null);
    const viewUnitPeople = unitId => { setPeopleFocus({ unitId, at: Date.now() }); setNav("people"); };
    const viewUnitIndicators = unitId => { setIndicatorFocus({ unitId, at: Date.now() }); setNav("collective-contribution"); };

    const adminPages = [
        ["overview", <AdminOverview setNav={setNav} />],
        ["org", <AdminOrganization onViewPeople={viewUnitPeople} onViewIndicators={viewUnitIndicators} />],
        ["people", <AdminPeople focusRequest={peopleFocus} />],
        ["help", <CviHelpCenter />],
        ["individual-performance", <AdminIndividualPerformance />],
        ["collective-contribution", <AdminCollectiveContribution onOpenKpiTemplates={openKpiTemplates} focusRequest={indicatorFocus} />],
        ["settings", <AdminSettings tab={settingsTab} onTabChange={setSettingsTab} />],
        ["cycle-management", <AdminCycleManagement />],
        ["experience", <AdminExperience language={experienceLanguage} periods={periods} activePeriodId={activePeriod?.id} />],
        ["goals", <GoalsPage user={user} />],
        ["profile", <PersonProfile user={user} />],
        ["more", <AdminMobileMore setNav={setNav} />],
    ];

    return (
        <div className="cvi-role-viewport-shell cvi-admin-app" style={{
            display: "flex", flexDirection: "column", height: "100vh",
            fontFamily: "system-ui,sans-serif", direction: "ltr", textAlign: "left",
            maxWidth: isMobile ? 420 : "100%", margin: isMobile ? "0 auto" : 0,
            border: isMobile ? `1px solid ${C.border}` : "none",
            boxShadow: isMobile ? "0 4px 32px rgba(0,0,64,0.08)" : "none"
        }}>

            <TopBar right={
                <UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} />
            } />

            <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
                {!isMobile && <Sidebar items={sidebarItems} nav={nav} onNav={setNav} onLogout={onLogout} />}
                <main ref={contentRef} className="cvi-admin-content" style={{ flex: 1, minWidth: 0, overflowY: "auto" }} aria-label="Administration">
                    <div className="cvi-admin-content-inner"><TabTransitionContent transitionKey={nav}>
                        {adminPages.map(([id, page]) => (
                            <div key={id} style={{ display: nav === id ? "block" : "none" }} aria-hidden={nav !== id}>
                                {page}
                            </div>
                        ))}
                    </TabTransitionContent></div>
                </main>
            </div>

            {isMobile && <AppBottomNav items={mobileItems} active={nav} onNav={setNav} onLogout={onLogout} />}
        </div>
    );
}

// ── PLACEHOLDER (نقش‌هایی که هنوز پنل اختصاصی ندارند: مدیرعامل، معاون) ──
function PlaceholderPanel({ user, onLogout }) {
    const isMobile = useIsMobile();
    return (
        <div className="cvi-role-viewport-shell" style={{
            display: "flex", flexDirection: "column", height: "100vh",
            fontFamily: "system-ui,sans-serif", direction: "inherit", background: C.bg,
            maxWidth: isMobile ? 420 : "100%", margin: isMobile ? "0 auto" : 0,
            border: isMobile ? `1px solid ${C.border}` : "none",
            boxShadow: isMobile ? "0 4px 32px rgba(0,0,64,0.08)" : "none"
        }}>

            <TopBar right={
                <UserMenu user={user} onLogout={onLogout} onSettings={() => document.getElementById("cvi-profile")?.scrollIntoView({ behavior: "smooth" })} />
            } />

            <div style={{ flex: 1, overflowY: "auto" }}>
                <PersonProfile user={user} />
                <div style={{ padding: "0 16px 16px", textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
                        🚧 More sections of the “{user.name}” workspace will be added in a future release
                    </div>
                    <button onClick={onLogout}
                        style={{
                            padding: "10px 20px", background: C.navy, color: "#fff",
                            border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13
                        }}>
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── ROOT: لاگین واحد + مسیردهی بر اساس نقش ─────────────────────────────────────

function OrganizationThemeSync() {
    const { orgSettings } = useData();
    useEffect(() => {
        const accent = /^#[0-9A-Fa-f]{6}$/.test(orgSettings?.accentColor || "") ? orgSettings.accentColor : "#00C8E6";
        document.documentElement.style.setProperty("--cvi-cyan-500", accent);
    }, [orgSettings?.accentColor]);
    return null;
}

function AppRouter() {
    const { baseDataLoading, baseDataError, orgSettings, users, invalidateSessionData } = useData();
    const { language, setLanguage } = useI18n();
    const [user, setUser] = useState(() => {
        try {
            const savedUser = sessionStorage.getItem("cvi_user");
            return savedUser ? JSON.parse(savedUser) : null;
        } catch {
            return null;
        }
    });

    const onLogin = (loggedInUser, token) => {
        invalidateSessionData();
        setUser(loggedInUser);
        sessionStorage.setItem("cvi_user", JSON.stringify(loggedInUser));
        sessionStorage.setItem("cvi_token", token);
        window.dispatchEvent(new Event("cvi-auth-changed"));
    };

    const onLogout = () => {
        invalidateSessionData();
        setUser(null);
        sessionStorage.removeItem("cvi_user");
        sessionStorage.removeItem("cvi_token");
        window.dispatchEvent(new Event("cvi-auth-changed"));
    };

    useEffect(() => {
        const handleSessionExpired = () => setUser(null);
        window.addEventListener("cvi-session-expired", handleSessionExpired);
        return () => window.removeEventListener("cvi-session-expired", handleSessionExpired);
    }, []);

    useEffect(() => {
        if (!user) return;
        const preferred = user.preferredLanguage;
        const fallback = orgSettings?.defaultLanguage || "en";
        const resolved = SUPPORTED_LANGUAGES[preferred] ? preferred : SUPPORTED_LANGUAGES[fallback] ? fallback : "en";
        if (resolved !== language) setLanguage(resolved);
    }, [user?.id, user?.preferredLanguage, orgSettings?.defaultLanguage]);

    if (!user) return <LoginPage onLogin={onLogin} />;
    if (baseDataLoading) return <AppSkeleton />;
    if (baseDataError) return <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", gap: 12, alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}><div style={{ color: C.red }}>{baseDataError}</div><button onClick={() => window.dispatchEvent(new Event("cvi-auth-changed"))} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.borderMd}`, background: C.white, cursor: "pointer" }}>Try again</button></div>;
    const appUser = users.find(item => item.id === user.id) || user;
    if (appUser.role === "specialist") return <SpecialistApp user={appUser} onLogout={onLogout} />;
    if (appUser.role === "manager") return <ManagerApp user={appUser} onLogout={onLogout} />;
    if (appUser.role === "deputy") return <DeputyApp user={appUser} onLogout={onLogout} />;
    if (appUser.role === "ceo") return <CeoApp user={appUser} onLogout={onLogout} />;
    if (appUser.role === "admin") return <AdminApp user={appUser} onLogout={onLogout} />;
    return <PlaceholderPanel user={appUser} onLogout={onLogout} />;
}

export default function App() {
    return (
        <I18nProvider>
            <UxProvider>
                <DataProvider>
                    <OrganizationThemeSync />
                    <AppRouter />
                </DataProvider>
            </UxProvider>
        </I18nProvider>
    );
}
